import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Layout } from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import { filterByDomain } from "../lib/domainAccess";
import {
	fetchAPI,
	formatDate,
	formatRestDisplay,
	normalizeSmartRestInput,
	parseSmartRatio,
	formatFullName,
} from "../lib/utils";
import {
	hasExerciseDragPayload,
	readExerciseDragPayload,
	useProgramClipboard,
} from "../lib/programClipboard";
import {
	rankAndFilterExercises,
	getYouTubeEmbedUrl,
	normalizeConditioningRounds,
	dedupeExercisesByScope,
	getExerciseScopeBadgeMeta as getExerciseScopeBadgeMetaShared,
	isEnduranceCategory,
	getConditioningQuantityPlaceholder,
	matchesCoachAthleteSearch,
} from "../lib/coachUtils";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../components/ui/select";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
	DialogDescription,
} from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from "../components/ui/sheet";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "../components/ui/popover";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "../components/ui/tooltip";
import { Calendar } from "../components/ui/calendar";
import {
	Dumbbell,
	Plus,
	Trash2,
	Search,
	ChevronDown,
	ChevronUp,
	GripVertical,
	Clock,
	RotateCcw,
	Copy,
	Edit,
	Eye,
	EyeOff,
	X,
	Save,
	Share2,
	CheckCircle,
	ChevronLeft,
	ChevronRight,
	ArrowLeft,
	FolderOpen,
	Folder,
	FolderInput,
	FileText,
	Loader2,
	Wand2,
	Paperclip,
	Link2,
	Building2,
	Trophy,
	Timer,
	Users,
	UserPlus,
	UserCog,
	Calendar as CalendarIcon,
	CopyPlus,
	PlusCircle,
	Layers,
	Pencil,
	Bot,
	ClipboardPaste,
	ClipboardCopy,
	CheckCircle2,
	Play,
	Pause,
	Globe,
	User,
	PanelLeftClose,
	Maximize2,
	Minimize2,
	Columns2,
	ArrowUp,
	ArrowDown,
	Equal,
	MoreVertical,
	CalendarRange,
	LayoutGrid,
	LayoutList,
} from "lucide-react";
import { toast } from "sonner";
import { AgentChatPanel } from "../components/ai/AgentChatPanel";
import {
	BuilderDock,
	useIsSplitViewport,
} from "../components/programBuilder/BuilderDock";
import { HintTooltip } from "../components/programBuilder/HintTooltip";
import { ExerciseLinkCombobox } from "../components/programBuilder/ExerciseLinkCombobox";
import { ComplexLoadReference } from "../components/programBuilder/ComplexLoadReference";
import { ReferencePanel } from "../components/programBuilder/ReferencePanel";
import {
	SeasonStrip,
	SeasonWeekContext,
} from "../components/programBuilder/SeasonStrip";
import { SeasonAgendaPanel } from "../components/programBuilder/SeasonAgendaPanel";
import { WeekOverview } from "../components/programBuilder/WeekOverview";
import { useMediaQuery } from "../lib/useMediaQuery";
import {
	clusterCompetitions,
	getProgramDateRange,
	getSeasonAthleteIds,
	getWeekCalendarRanges,
	shiftISODate,
	weekGapAfterDelete,
} from "../lib/seasonAgenda";
import AthleteDashboard from "../components/AthleteDashboard";
import ConditioningResultSchemaEditor from "../components/ConditioningResultSchemaEditor";
import { NewExerciseForm } from "../components/NewExerciseForm";
import { exerciseTypeIcon } from "../components/programBuilder/exerciseIcons";
import { ExerciseTagStrip } from "../components/programBuilder/ExerciseTagStrip";
import { Textarea } from "../components/programBuilder/Textarea";
import { SwimForm } from "../components/programBuilder/SwimForm";
import { BikeErgForm } from "../components/programBuilder/BikeErgForm";
import { RunningForm } from "../components/programBuilder/RunningForm";
import { ConditioningForm } from "../components/programBuilder/ConditioningForm";
import { ShootingForm } from "../components/programBuilder/ShootingForm";
import { WarmupLibraryPicker } from "../components/programBuilder/WarmupLibraryPicker";
import { useLanguage } from "../contexts/LanguageContext";
import { useUnsavedChangesGuard } from "../contexts/UnsavedChangesContext";
import { enUS, it } from "date-fns/locale";
import {
	getEmomRoundAssignmentLabel,
	getEmomRoundLabel,
	formatConditioningDuration,
	formatHiitIntervalShort,
	parseHiitWorkRestRatio,
	parseDurationStringToSeconds,
	getHiitWorkSecondsPerRound,
	getHiitRestSecondsPerRound,
	getHiitOffLabel,
	getForTimeRoundsDisplay,
	getRoundUnitLabel,
	toLocalISODate,
	parseLocalDate,
	createRuntimeId,
	getWeekMondayFromDate,
	getLatestDateFromWeeks,
	inferWeekdayOffset,
	getWeekAnchorMonday,
	getWeekVisibilityForAthlete,
	describeWeekVisibility,
	formatWeekVisibilityLabel,
	nextISODay,
	toggleWeekVisibility,
	setWeekHold,
	autoPublishActivationPlan,
	applyAutoPublish,
	getNormalizedDayExercises,
	resolveExerciseLoadReferencesForStats,
	cloneExerciseAsIndependent,
	markDayExercisesAsSharedOrigin,
	cloneDayAsIndependent,
	cloneDayForExport,
	getCurrentCalendarWeekRange,
	findCurrentDatedWeekIndex,
	isWeekContentLoaded,
	shiftWeekDates,
	fillMissingDayDates,
	normalizeProgramWeek,
	buildWeeksSavePayload,
	computeProgramDomains,
	normalizeLoadExpression,
	normalizeSetExpression,
	createStrengthSetRow,
	createEmomRoundTemplate,
	normalizeEmomRoundTemplates,
	hasConfiguredEmomRoundTemplates,
	serializeEmomRoundTemplates,
	extractPercentageNumbers,
	normalizeFartlekSegmentType,
	getRunningBenchmarkPaceValue,
	getRunningPaceModifierHint,
	getRunningTargetPaceMode,
	resolveRunningReferencedPace,
	getFartlekSegmentPaceMode,
	isErgBenchmarkSelection,
	isErgMixedType,
	getErgTypeLabel,
	getErgTypeLabelLocalized,
	formatConditioningDisplayName,
	formatExercisePrescription,
	localizeWeekLabel,
	localizeDayLabel,
	getExerciseLoadPercentage,
	buildWeekLoadIndex,
	findReferenceLoad,
	ensureExerciseIdIntegrity,
	normalizeRestFieldValue,
	EXERCISE_TYPES,
	RUNNING_TYPES,
	SWIM_TYPES,
	BIKE_TYPES,
	BIKE_LIKE_INTERVAL_TYPES,
	BIKE_LIKE_SIMPLE_TYPES,
	isShootingExerciseIncomplete,
	suggestedFirearmType,
	complexMissingLoadReference,
	isStrengthComplex,
	isTimedStrengthReps,
	isDistanceStrengthReps,
} from "../lib/programBuilderUtils";
import {
	getAutoPublishLeadDays,
	isAutoPublishEnabled,
} from "../lib/programVisibility";
import { joinEmomItemLoads, splitEmomItemLoad } from "../lib/emomLoadUtils";
import {
	buildEmomAutoName,
	getEmomStructureParts,
	isEmomAutoName,
} from "../lib/emomSummary";

// Builder layout preferences, persisted per browser (same `kraveon:` namespace
// used by the app shell in Layout.js).
const SETTINGS_COLLAPSED_KEY = "kraveon:pb-settings-collapsed";
const WIDE_MODE_KEY = "kraveon:pb-wide";
const DOCK_TAB_KEY = "kraveon:pb-dock-tab";
// Season strip over the week tabs (docs/program-builder-season-strip). On by
// default: its empty state is what nudges the coach to plan the athlete.
const SEASON_STRIP_KEY = "kraveon:pb-season";
// How the selected week is shown: the vertical editor (default, unchanged)
// or the read-only column overview (docs/program-builder-season-strip §6).
const WEEK_VIEW_KEY = "kraveon:pb-week-view";

const readBuilderPreference = (key, fallback) => {
	try {
		const stored = localStorage.getItem(key);
		return stored === null ? fallback : stored === "true";
	} catch {
		return fallback;
	}
};

const readBuilderChoice = (key, allowed, fallback) => {
	try {
		const stored = localStorage.getItem(key);
		return allowed.includes(stored) ? stored : fallback;
	} catch {
		return fallback;
	}
};

const writeBuilderPreference = (key, value) => {
	try {
		localStorage.setItem(key, String(value));
	} catch {
		// Ignore storage failures (private mode / quota): the preference simply
		// falls back to the default on the next load.
	}
};

// How many recent weeks the editor loads with their exercises. Programs that
// gain a week every weekend reach ~550 KB, and the coach works on the last one
// or two: older weeks arrive as metadata-only skeletons and are fetched on
// demand. Six covers planning several weeks ahead with room to spare.
const RECENT_WEEKS_WINDOW = 6;

export default function ProgramBuilder() {
	const { programId } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const { t, language } = useLanguage();
	const { user } = useAuth();
	const calendarLocale = useMemo(
		() => (language === "it" ? it : enUS),
		[language],
	);

	// Option constants live at module scope, so they carry a `labelKey` rather
	// than a translated string. The canonical `label` stays for the places that
	// must not follow the UI language: exercise-name generation and the AI
	// parser's type matching.
	const optionLabel = useCallback(
		(options, value) => {
			const option = options.find((item) => item.value === value);
			if (!option) return "";
			return option.labelKey ? t(option.labelKey) : option.label || "";
		},
		[t],
	);
	const isCoachAiDisabled =
		user?.role === "coach" && !user?.is_admin && user?.ai_enabled === false;
	const aiDisabledMessage = t("pbAiDisabledForCoach");
	const LOAD_REFERENCE_BODYWEIGHT_ID = "__bodyweight__";
	const [program, setProgram] = useState(null);
	const [exercises, setExercises] = useState([]);
	const [athletes, setAthletes] = useState([]);

	const [groups, setGroups] = useState([]);
	const [coachOptions, setCoachOptions] = useState([]);
	const [coachTeamOptions, setCoachTeamOptions] = useState([]);
	const [selectedAccessCoachId, setSelectedAccessCoachId] = useState("");
	const [selectedAccessTeamId, setSelectedAccessTeamId] = useState("");
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [savedSnapshot, setSavedSnapshot] = useState(null);
	const [isAddExerciseOpen, setIsAddExerciseOpen] = useState(false);
	const [selectedDayIndex, setSelectedDayIndex] = useState(null);
	const [editingExerciseIndex, setEditingExerciseIndex] = useState(null);
	// True when the conditioning exercise being edited already has athlete
	// results: the result-schema tipologia is then locked (label-only edits),
	// mirroring _enforce_result_schema_lock on the backend.
	const [editingConditioningHasResults, setEditingConditioningHasResults] =
		useState(false);
	const [expandedDays, setExpandedDays] = useState({});
	const [expandedDaySetupPanels, setExpandedDaySetupPanels] = useState({});
	const [editingDayHeaderId, setEditingDayHeaderId] = useState(null);
	const [headerDatePickerDayId, setHeaderDatePickerDayId] = useState(null);
	const [selectedSessionIndex, setSelectedSessionIndex] = useState(null);
	const [newSessionLabelByDay, setNewSessionLabelByDay] = useState({});
	const [selectedWeekIndex, setSelectedWeekIndex] = useState(0);
	// A swap navigates here carrying the program we came from, so the dock can
	// reopen already pointed at it (see handleSwapWithReference).
	const incomingReference = location.state?.pbReference || null;
	// One dock, two tenants (see components/programBuilder/BuilderDock.js).
	const [isDockOpen, setIsDockOpen] = useState(Boolean(incomingReference));
	// Con l'assistente sospeso il dock ha un solo inquilino: la preferenza
	// salvata potrebbe ancora dire "ai", quindi va ignorata (rework Fase 0,
	// docs/ai-program/10-byok-and-mcp-rework.md).
	// L'assistente nel dock e' l'agente BYOK (Fase 2): paga il coach con la sua
	// chiave, quindi la scheda c'e' sempre — il pannello stesso spiega cosa fare
	// se la chiave manca. Resta fuori solo per i coach a cui l'admin ha spento
	// l'AI. docs/ai-program/10-byok-and-mcp-rework.md
	const [dockTab, setDockTab] = useState(() =>
		incomingReference
			? "reference"
			: readBuilderChoice(
					DOCK_TAB_KEY,
					["reference", "agenda", "ai"],
					"reference",
				),
	);
	const [pendingSwap, setPendingSwap] = useState(null);
	// After a swap the new editor should open on the week the reference panel was
	// showing, not on the default one. Consumed by loadData.
	const pendingOpenWeekRef = useRef(null);

	const openDock = (nextTab) => {
		setDockTab(nextTab);
		writeBuilderPreference(DOCK_TAB_KEY, nextTab);
		setIsDockOpen(true);
	};

	const handleDockTabChange = (nextTab) => {
		setDockTab(nextTab);
		writeBuilderPreference(DOCK_TAB_KEY, nextTab);
	};
	// Builder layout preferences. Collapsing the program-setup column and
	// lifting the container width cap give the day/exercise editor the width it
	// actually needs; both are opt-in and persisted per browser.
	// Hidden by default: the panel holds setup you touch once, and the editor
	// reads far cleaner without it. Exception for a brand-new program, whose
	// required title field lives inside — landing on a builder with nowhere to
	// type the name would be baffling.
	const [settingsCollapsed, setSettingsCollapsed] = useState(() =>
		// Unconditional for a new program: a stored "collapsed" preference must not
		// leave the coach with nowhere to type the required title.
		programId === "new"
			? false
			: readBuilderPreference(SETTINGS_COLLAPSED_KEY, true),
	);
	const [isSettingsSheetOpen, setIsSettingsSheetOpen] = useState(false);
	const [expandedGroupIds, setExpandedGroupIds] = useState({});
	// Mobile only: below md an exercise is a compact row and its inline editor
	// opens on tap, one at a time. On desktop every editor stays open as before.
	const [expandedExercises, setExpandedExercises] = useState({});
	// Mobile only: the week's publish/share/notes panels start collapsed.
	const [weekActionsOpen, setWeekActionsOpen] = useState(false);
	const [wideMode, setWideMode] = useState(() =>
		readBuilderPreference(WIDE_MODE_KEY, false),
	);
	const [seasonStripEnabled, setSeasonStripEnabled] = useState(() =>
		readBuilderPreference(SEASON_STRIP_KEY, true),
	);
	const toggleSeasonStrip = () => {
		setSeasonStripEnabled((prev) => {
			const next = !prev;
			writeBuilderPreference(SEASON_STRIP_KEY, next);
			return next;
		});
	};
	const [weekView, setWeekView] = useState(() =>
		readBuilderChoice(WEEK_VIEW_KEY, ["editor", "overview"], "editor"),
	);
	const setWeekViewAndPersist = (next) => {
		setWeekView(next);
		writeBuilderPreference(WEEK_VIEW_KEY, next);
	};
	// One column on a phone is the vertical list the editor already is, so the
	// overview exists from md up; a stored "overview" never applies below.
	const isMdViewport = useMediaQuery("(min-width: 768px)");
	const isOverviewView = weekView === "overview" && isMdViewport;
	// Whose agenda the strip shows when the program reaches several athletes
	// (individual program) or several groups (group program).
	const [seasonAthleteId, setSeasonAthleteId] = useState("");
	const [seasonGroupId, setSeasonGroupId] = useState("");
	const [seasonAgendaData, setSeasonAgendaData] = useState({
		mesocycles: [],
		competitions: [],
		events: [],
		loading: false,
		failed: false,
	});
	// Bumped after a write from the agenda dock so strip and dock refetch.
	const [seasonRefreshToken, setSeasonRefreshToken] = useState(0);

	const isSplitViewport = useIsSplitViewport();

	// With the dock open in side-by-side mode the setup column would squeeze the
	// editor below the ~720px it needs (its inner grids use viewport breakpoints
	// and do not reflow), so the column moves into the Sheet for as long as the
	// dock is open. Nothing becomes unreachable — only its container changes.
	const isSettingsColumnVisible =
		!settingsCollapsed && !(isDockOpen && isSplitViewport);

	const toggleSettingsCollapsed = () => {
		setSettingsCollapsed((prev) => {
			const next = !prev;
			writeBuilderPreference(SETTINGS_COLLAPSED_KEY, next);
			if (!next) setIsSettingsSheetOpen(false);
			return next;
		});
	};

	const toggleWideMode = () => {
		setWideMode((prev) => {
			const next = !prev;
			writeBuilderPreference(WIDE_MODE_KEY, next);
			return next;
		});
	};
	const [aiStrengthParsing, setAiStrengthParsing] = useState(false);
	const [aiEnduranceParsing, setAiEnduranceParsing] = useState(false);
	// Cross-tab exercise clipboard (see lib/programClipboard.js): copying in one
	// browser tab makes the payload available in every other open builder.
	const {
		clipboard,
		copy: copyToClipboard,
		clear: clearClipboard,
	} = useProgramClipboard(user?.user_id);
	const clipboardItemCount = clipboard?.items?.length || 0;
	const clipboardSourceLabel = useMemo(() => {
		const source = clipboard?.source || {};
		return [source.programTitle, source.weekLabel, source.dayTitle]
			.map((part) => (part || "").trim())
			.filter(Boolean)
			.join(" · ");
	}, [clipboard]);
	const [isReferenceExerciseDialogOpen, setIsReferenceExerciseDialogOpen] =
		useState(false);
	const [referenceExerciseSearchQuery, setReferenceExerciseSearchQuery] =
		useState("");
	const [pendingReferenceUpdate, setPendingReferenceUpdate] = useState(null);
	const [isAssignAthleteOpen, setIsAssignAthleteOpen] = useState(false);
	const [athleteSearchQuery, setAthleteSearchQuery] = useState("");
	// Athletes ticked in the assign dialog, added together on confirm: one
	// athlete per opening meant N openings for N athletes.
	const [pendingAssignIds, setPendingAssignIds] = useState([]);
	// `{ weekIndex }` of a week athletes currently see, waiting for the coach
	// to confirm hiding it: the same button publishes and hides, and a slip on
	// a running week took it away from everyone at the next save.
	const [pendingWeekHide, setPendingWeekHide] = useState(null);
	// Activation plan of the automatic release waiting for the coach's yes.
	const [pendingAutoPublish, setPendingAutoPublish] = useState(null);
	const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
	const [isAthleteViewPreviewOpen, setIsAthleteViewPreviewOpen] =
		useState(false);
	const [conditioningSchemaFocusToken, setConditioningSchemaFocusToken] =
		useState(0);
	const [isConditioningSchemaConfirmOpen, setIsConditioningSchemaConfirmOpen] =
		useState(false);
	// `{ weekIndex }` of the week waiting for the "shift the following ones?"
	// answer before it is removed.
	const [pendingWeekDeletion, setPendingWeekDeletion] = useState(null);
	const [exportType, setExportType] = useState(null);
	const [exportSource, setExportSource] = useState(null);
	const [exportPrograms, setExportPrograms] = useState([]);
	const [exportFolders, setExportFolders] = useState([]);
	const [exportSearchQuery, setExportSearchQuery] = useState("");
	const [exportCurrentFolderId, setExportCurrentFolderId] = useState(null);
	const [exportSelectedProgramId, setExportSelectedProgramId] = useState("");
	const [exportSelectedWeekIndex, setExportSelectedWeekIndex] = useState("");
	const [exportSelectedDayIndex, setExportSelectedDayIndex] = useState("");
	const [exportProgramDetails, setExportProgramDetails] = useState({});
	const [sharedOriginProgramNames, setSharedOriginProgramNames] = useState({});
	const [sharedExerciseUsage, setSharedExerciseUsage] = useState({});
	const [loadingExportTargets, setLoadingExportTargets] = useState(false);
	const [savingExport, setSavingExport] = useState(false);

	const isIndividualProgram = (program?.type || "individual") === "individual";
	const getLeaderboardDefaultEnabled = () => !isIndividualProgram;

	// --- Season strip (docs/program-builder-season-strip/ANALYSIS.md §3) ---
	// Read-only overlay: it never touches the save payload, the dirty check or
	// the week lazy loading. Shown only where it can mean something: a saved,
	// dated program that reaches at least one athlete.
	const seasonAthleteIds = useMemo(
		() => getSeasonAthleteIds(program),
		[program?.athlete_ids, program?.athlete_assignments], // eslint-disable-line react-hooks/exhaustive-deps
	);
	const seasonAthleteIdsKey = seasonAthleteIds.join(",");
	const isSeasonStripAvailable =
		programId !== "new" &&
		Boolean(program?.program_id) &&
		program?.is_dated !== false &&
		seasonAthleteIds.length > 0;
	const activeSeasonAthleteId = seasonAthleteIds.includes(seasonAthleteId)
		? seasonAthleteId
		: seasonAthleteIds[0] || "";
	// Group programs plan on the group: its phases are inherited by every
	// member (Fase 2). A "group" program with athletes but no group has no
	// agenda subject and falls back to race markers only.
	const seasonGroupIds = useMemo(
		() => (Array.isArray(program?.group_ids) ? program.group_ids : []),
		[program?.group_ids],
	);
	const activeSeasonGroupId = seasonGroupIds.includes(seasonGroupId)
		? seasonGroupId
		: seasonGroupIds[0] || "";
	const seasonScope = useMemo(() => {
		if (isIndividualProgram) {
			return activeSeasonAthleteId
				? { kind: "athlete", id: activeSeasonAthleteId }
				: null;
		}
		return activeSeasonGroupId ? { kind: "group", id: activeSeasonGroupId } : null;
	}, [isIndividualProgram, activeSeasonAthleteId, activeSeasonGroupId]);
	const seasonScopeKey = seasonScope ? `${seasonScope.kind}:${seasonScope.id}` : "";
	const isAgendaDockAvailable = isSeasonStripAvailable && Boolean(seasonScope);
	const isAgendaDockShowing =
		isDockOpen && dockTab === "agenda" && isAgendaDockAvailable;
	const needsSeasonAgenda = seasonStripEnabled || isAgendaDockShowing;
	// Only the outer bounds matter for the fetch, so edits inside the program
	// do not refetch unless they move the first or last dated day.
	const seasonRange = useMemo(
		() => getProgramDateRange(program?.weeks),
		[program?.weeks],
	);
	// Where the selected week sits on the calendar, also when it has no day yet.
	const selectedWeekCalendarRange = useMemo(
		() => getWeekCalendarRanges(program?.weeks)[selectedWeekIndex] || null,
		[program?.weeks, selectedWeekIndex],
	);
	const seasonRangeKey = seasonRange
		? `${seasonRange.start}|${seasonRange.end}`
		: "";

	useEffect(() => {
		if (!needsSeasonAgenda || !isSeasonStripAvailable) {
			return undefined;
		}
		let cancelled = false;
		// A program with no dated day yet (every week just added, days still to
		// come) used to abort the fetch entirely, so nothing the coach wrote in
		// the agenda dock ever came back — not even after a refresh token. Fall
		// back to a window around today instead.
		const [fromDate, toDate] = seasonRangeKey
			? seasonRangeKey.split("|")
			: (() => {
					const today = toLocalISODate(new Date());
					return [shiftISODate(today, -180), shiftISODate(today, 180)];
				})();
		const athleteSet = new Set(seasonAthleteIdsKey.split(",").filter(Boolean));
		setSeasonAgendaData((prev) => ({ ...prev, loading: true }));
		const [scopeKind, scopeId] = seasonScopeKey.split(":");
		let failed = false;
		const onFailure = (fallback) => (error) => {
			console.error("Season agenda fetch failed:", error);
			failed = true;
			return fallback;
		};
		Promise.all([
			// Phases of the subject: the athlete (own + inherited from groups) or
			// the group itself.
			scopeId
				? fetchAPI(`/mesocycles/${scopeKind}/${scopeId}`).catch(onFailure([]))
				: Promise.resolve([]),
			// One call for every athlete of the program: the aggregated endpoint
			// already carries `shared_group_key`, so the same race shows once.
			fetchAPI(
				`/coach/competitions?from_date=${fromDate}&to_date=${toDate}`,
			).catch(onFailure({ events: [] })),
		]).then(([mesocycles, competitionsData]) => {
			if (cancelled) return;
			const events = (competitionsData?.events || []).filter((event) =>
				athleteSet.has(event?.athlete_id),
			);
			setSeasonAgendaData({
				mesocycles: Array.isArray(mesocycles) ? mesocycles : [],
				competitions: clusterCompetitions(events),
				events,
				loading: false,
				// Without this an unreachable agenda looked exactly like an empty
				// one: "no phases planned", and the coach redrew them for nothing.
				failed,
			});
		});
		return () => {
			cancelled = true;
		};
	}, [
		needsSeasonAgenda,
		isSeasonStripAvailable,
		seasonRangeKey,
		seasonAthleteIdsKey,
		seasonScopeKey,
		seasonRefreshToken,
	]);

	const refreshSeasonAgenda = () => setSeasonRefreshToken((prev) => prev + 1);
	// Raw rows of the athlete shown in the agenda dock (the strip uses the
	// clustered view, the calendar wants one row per race).
	const seasonAthleteEvents = useMemo(
		() =>
			(seasonAgendaData.events || []).filter(
				(event) => event?.athlete_id === activeSeasonAthleteId,
			),
		[seasonAgendaData.events, activeSeasonAthleteId],
	);

	const seasonAgenda = useMemo(() => {
		if (!seasonStripEnabled || !isSeasonStripAvailable) return null;
		let subjectName = "";
		if (seasonScope?.kind === "athlete") {
			subjectName =
				athletes.find((item) => item.user_id === seasonScope.id)?.name || "";
		} else if (seasonScope?.kind === "group") {
			subjectName =
				groups.find((item) => item.group_id === seasonScope.id)?.name || "";
		}
		return {
			...seasonAgendaData,
			showPhases: Boolean(seasonScope),
			athleteCount: seasonAthleteIds.length,
			subjectName,
		};
	}, [
		seasonStripEnabled,
		isSeasonStripAvailable,
		seasonAgendaData,
		seasonScope,
		seasonAthleteIds.length,
		athletes,
		groups,
	]);

	const seasonAthleteOptions = useMemo(
		() =>
			seasonAthleteIds.map((athleteId) => ({
				user_id: athleteId,
				name:
					athletes.find((item) => item.user_id === athleteId)?.name || athleteId,
			})),
		[seasonAthleteIds, athletes],
	);

	const seasonGroupOptions = useMemo(
		() =>
			seasonGroupIds.map((groupId) => ({
				user_id: groupId,
				name: groups.find((item) => item.group_id === groupId)?.name || groupId,
			})),
		[seasonGroupIds, groups],
	);
	const seasonSubjectOptions = isIndividualProgram
		? seasonAthleteOptions
		: seasonGroupOptions;
	const handleSelectSeasonSubject = isIndividualProgram
		? setSeasonAthleteId
		: setSeasonGroupId;
	const seasonGroupMemberIds = useMemo(() => {
		if (seasonScope?.kind !== "group") return [];
		const group = groups.find((item) => item.group_id === seasonScope.id);
		return Array.isArray(group?.athlete_ids) ? group.athlete_ids : [];
	}, [seasonScope, groups]);
	// Calendar rows for the dock: an athlete's own races, or for a group one
	// row per race (the first member's) so the same meet is not drawn N times.
	const seasonCalendarEvents = useMemo(() => {
		if (seasonScope?.kind === "group") {
			const memberSet = new Set(seasonGroupMemberIds);
			const memberEvents = (seasonAgendaData.events || []).filter((event) =>
				memberSet.has(event?.athlete_id),
			);
			return clusterCompetitions(memberEvents).map((cluster) => cluster.events[0]);
		}
		return seasonAthleteEvents;
	}, [seasonScope, seasonGroupMemberIds, seasonAgendaData.events, seasonAthleteEvents]);

	// With an agenda subject (athlete or group) planning happens right here,
	// in the dock's agenda tab. Without one, the race calendar opens in a new
	// tab and the builder (with any unsaved edit) stays put.
	const handlePlanSeason = () => {
		if (isAgendaDockAvailable) {
			openDock("agenda");
			return;
		}
		window.open("/competition-hub", "_blank", "noopener,noreferrer");
	};

	const weekStatusDotClass = (week, weekIndex) => {
		const description = describeWeekVisibility(program, week, weekIndex);
		if (description.state === "live") return "bg-green-400";
		// Hidden by its date, or held back by the coach: amber, a coach's choice.
		if (description.state === "expired" || description.held) return "bg-amber-400";
		// Scheduled (by a date or by the calendar): sky. Volt is the theme green
		// and read as "visible"; yellow and amber were the same dot.
		if (description.state === "scheduled") return "bg-sky-400";
		return "bg-muted-foreground/30";
	};

	// The last saved program, parsed once per save: the status line says "after
	// save" while a week's visibility differs from it, and the save toast tells
	// what changed for athletes.
	const savedProgram = useMemo(() => {
		if (!savedSnapshot) return null;
		try {
			return JSON.parse(savedSnapshot);
		} catch {
			return null;
		}
	}, [savedSnapshot]);

	const programAthleteCount = (program?.athlete_ids || []).length;
	// Which weeks the assigned athletes see right now, for the line under the
	// assignment list: "assigned" and "sees something" are two different things.
	const visibleWeekLabels = (program?.weeks || [])
		.map((week, index) => ({ week, index }))
		.filter(
			({ week, index }) =>
				getWeekVisibilityForAthlete(program, week, index).isVisible,
		)
		.map(({ week, index }) => localizeWeekLabel(week?.label, t, index));

	// "lun 3/11": short, with the weekday, because the whole sentence has to fit
	// one line under the week name on a phone.
	const formatDayShort = useCallback(
		(isoDate) => {
			const parsed = parseLocalDate(isoDate);
			if (!parsed || Number.isNaN(parsed.getTime())) return "";
			return parsed.toLocaleDateString(language === "en" ? "en-GB" : "it-IT", {
				weekday: "short",
				day: "numeric",
				month: "numeric",
			});
		},
		[language],
	);

	// "After save" only when what athletes see differs from the last save. A
	// field-by-field diff flagged a freshly duplicated draft (new week_id) that
	// changes nothing for anyone.
	const isWeekVisibilityPendingSave = (week, weekIndex) => {
		if (!savedProgram || !week) return false;
		const current = describeWeekVisibility(program, week, weekIndex);
		const savedIndex = (savedProgram.weeks || []).findIndex(
			(item) => item?.week_id === week.week_id,
		);
		const saved =
			savedIndex >= 0
				? describeWeekVisibility(
						savedProgram,
						savedProgram.weeks[savedIndex],
						savedIndex,
					)
				: { state: "draft", from: null, hiddenFrom: null, previewCount: 0 };
		return ["state", "from", "hiddenFrom", "previewCount"].some(
			(key) =>
				JSON.stringify(current[key] ?? null) !==
				JSON.stringify(saved[key] ?? null),
		);
	};

	const weekStatusText = (week, weekIndex) =>
		formatWeekVisibilityLabel(describeWeekVisibility(program, week, weekIndex), {
			t,
			formatDay: formatDayShort,
			athleteCount: programAthleteCount,
			pendingSave: isWeekVisibilityPendingSave(week, weekIndex),
		});

	// What the save changed for athletes, for the toast: weeks that became
	// visible or hidden, athletes added. "Programma salvato!" alone left the
	// coach guessing whether the publish had gone through.
	const describeSaveOutcome = () => {
		if (!savedProgram || !program) return [];
		const parts = [];
		const savedWeeks = savedProgram.weeks || [];
		const athleteCount = programAthleteCount;
		(program.weeks || []).forEach((week, weekIndex) => {
			const nowVisible = getWeekVisibilityForAthlete(
				program,
				week,
				weekIndex,
			).isVisible;
			const savedIndex = savedWeeks.findIndex(
				(item) => item?.week_id === week.week_id,
			);
			const wasVisible =
				savedIndex >= 0
					? getWeekVisibilityForAthlete(
							savedProgram,
							savedWeeks[savedIndex],
							savedIndex,
						).isVisible
					: false;
			const weekLabel = localizeWeekLabel(week.label, t, weekIndex);
			if (nowVisible && !wasVisible) {
				parts.push(
					athleteCount > 0
						? `${weekLabel} ${t("pbSavedWeekPublished")} ${athleteCount} ${(athleteCount === 1 ? t("athlete") : t("athletes")).toLowerCase()}`
						: `${weekLabel} ${t("pbSavedWeekPublishedNoAthletes")}`,
				);
			} else if (!nowVisible && wasVisible) {
				// Back to the calendar («si pubblica dom 8/11») is not «hidden».
				const now = describeWeekVisibility(program, week, weekIndex);
				parts.push(
					now.state === "scheduled" && now.from
						? `${weekLabel} ${t("pbSavedWeekScheduled")} ${formatDayShort(now.from)}`
						: `${weekLabel} ${t("pbSavedWeekHidden")}`,
				);
			}
		});
		if (
			Boolean(savedProgram.auto_publish_weeks) !==
			Boolean(program.auto_publish_weeks)
		) {
			parts.unshift(
				t(program.auto_publish_weeks ? "pbSavedAutoPublishOn" : "pbSavedAutoPublishOff"),
			);
		}
		const savedAthleteIds = new Set(savedProgram.athlete_ids || []);
		const addedAthletes = (program.athlete_ids || []).filter(
			(id) => !savedAthleteIds.has(id),
		).length;
		if (addedAthletes > 0) {
			parts.push(
				addedAthletes === 1
					? t("pbSavedAthleteAdded")
					: `${addedAthletes} ${t("pbSavedAthletesAdded")}`,
			);
		}
		return parts;
	};

	const createInitialExerciseState = () => ({
		exercise_id: "",
		exercise_name: "",
		exercise_type: "strength",
		methodology: "standard",
		load_reference_exercise_id: null,
		load_reference_exercise_name: null,
		allow_result_entry: true,
		leaderboard_enabled: getLeaderboardDefaultEnabled(),
		load_kg: null,
		time_seconds: null,
		notes: "",
		set_rows: [createStrengthSetRow()],
		// Superset / Giant Set
		superset_exercises: [],
		superset_rest_seconds: null,
		superset_sets: null,
		// Complex
		complex_exercises: [],
		// EMOM
		emom_intervals: null,
		emom_interval: "1:00",
		emom_rounds: [],
		// Running
		running_type: null,
		distance_meters: null,
		duration_minutes: null,
		target_pace: "",
		target_pace_mode: "manual",
		target_pace_reference_benchmark_id: null,
		target_pace_reference_benchmark_name: null,
		target_pace_reference_percentage: null,
		target_pace_reference_pace: "",
		target_pace_resolved: "",
		intervals: null,
		warmup_description: "",
		cooldown_description: "",
		interval_rows: [],
		fartlek_segments: [],
		fartlek_repeat: null,
		// Swimming
		swim_type: null,
		swim_sets: [],
		swim_equipment: "",
		// Cycling
		bike_type: null,
		bike_intervals: [],
		bike_power_target: "",
		bike_cadence_target: "",
		bike_terrain: null,
		// Benchmark & Pace Reference (running/bike/swim/erg)
		benchmark_exercise_id: null,
		benchmark_exercise_name: null,
		pace_reference_benchmark_id: null,
		pace_reference_benchmark_name: null,
		pace_reference_percentage: null,
		// Conditioning
		conditioning_type: null,
		time_cap_seconds: null,
		rounds: null,
		interval_seconds: 60,
		emom_conditioning_interval: "1:00",
		hiit_cycles: null,
		hiit_work_mode: "for_time",
		hiit_work_seconds: null,
		hiit_work_interval: "",
		hiit_work_rounds: "",
		hiit_work_custom_text: "",
		hiit_rest_mode: "fixed",
		hiit_rest_seconds: null,
		hiit_rest_interval: "",
		hiit_rest_ratio: "",
		conditioning_exercises: [],
		conditioning_free_text: "",
		library_conditioning_id: null,
		result_schema: [],
		custom_workout_text: null,
	});

	const [newExercise, setNewExercise] = useState(() =>
		createInitialExerciseState(),
	);
	const allowCustomConditioningWithoutSchemaRef = useRef(false);

	// Drag & drop state for exercise reordering
	const dragExercise = useRef(null);
	const dragOverExercise = useRef(null);
	const dragDay = useRef(null);
	const dragOverDay = useRef(null);

	// Dirty tracking: compare current program to last saved snapshot
	const isDirty = useMemo(() => {
		if (!program || savedSnapshot === null) return false;
		return JSON.stringify(program) !== savedSnapshot;
	}, [program, savedSnapshot]);

	const isDirtyRef = useRef(false);
	useEffect(() => {
		isDirtyRef.current = isDirty;
	}, [isDirty]);
	// Navigazione interna (menu laterale, link): chiede conferma se il
	// programma ha modifiche non salvate. Il tasto indietro del browser e la
	// chiusura della scheda sono gia' coperti qui sotto.
	useUnsavedChangesGuard(isDirty);

	// Warn on browser close/refresh with unsaved changes
	useEffect(() => {
		const handleBeforeUnload = (e) => {
			if (isDirtyRef.current) {
				e.preventDefault();
			}
		};
		window.addEventListener("beforeunload", handleBeforeUnload);
		return () => window.removeEventListener("beforeunload", handleBeforeUnload);
	}, []);

	useEffect(() => {
		if (!editingDayHeaderId) return undefined;

		const handleEscapeCloseDayHeaderEdit = (event) => {
			if (event.key !== "Escape") return;
			setEditingDayHeaderId(null);
			setHeaderDatePickerDayId(null);
		};

		document.addEventListener("keydown", handleEscapeCloseDayHeaderEdit);
		return () => {
			document.removeEventListener("keydown", handleEscapeCloseDayHeaderEdit);
		};
	}, [editingDayHeaderId]);

	useEffect(() => {
		const hasOpenDaySetupPanel = Object.values(
			expandedDaySetupPanels || {},
		).some(Boolean);
		if (!hasOpenDaySetupPanel) return undefined;

		const handleEscapeCloseDaySetupPanel = (event) => {
			if (event.key !== "Escape") return;
			setExpandedDaySetupPanels({});
		};

		document.addEventListener("keydown", handleEscapeCloseDaySetupPanel);
		return () => {
			document.removeEventListener("keydown", handleEscapeCloseDaySetupPanel);
		};
	}, [expandedDaySetupPanels]);

	const handleNavigateBack = useCallback(() => {
		const returnContext = location.state?.programBuilderReturn;

		const goBack = () => {
			if (returnContext?.from === "programs-tab") {
				navigate("/programs", {
					state: {
						programBuilderReturn: returnContext,
					},
				});
				return;
			}

			navigate(-1);
		};

		if (isDirtyRef.current) {
			if (window.confirm(t("pbUnsavedLeaveConfirm"))) {
				goBack();
			}
		} else {
			goBack();
		}
	}, [location.state, navigate, t]);

	const handleDragStart = (dayIndex, exIndex) => {
		dragExercise.current = { dayIndex, exIndex };
		dragOverExercise.current = null;
	};

	const mapDraggedDayIndex = (index, from, to) => {
		if (index === null || index === undefined) return index;
		if (index === from) return to;
		if (from < to && index > from && index <= to) return index - 1;
		if (from > to && index >= to && index < from) return index + 1;
		return index;
	};

	const remapIndexedState = (stateObj, from, to) => {
		const next = {};
		Object.entries(stateObj || {}).forEach(([key, value]) => {
			const idx = Number(key);
			if (!Number.isFinite(idx)) return;
			next[mapDraggedDayIndex(idx, from, to)] = value;
		});
		return next;
	};

	const handleDayDragStart = (dayIndex) => {
		if (program?.is_dated) return;
		dragDay.current = dayIndex;
	};

	const handleDayDragOver = (e, dayIndex) => {
		if (program?.is_dated) return;
		e.preventDefault();
		dragOverDay.current = dayIndex;
	};

	// `expandedExercises` is keyed "dayIndex:exIndex", so a day reorder has to
	// rewrite the day part of every key or an unrelated exercise ends up marked
	// as expanded.
	const remapExerciseKeys = (stateObj, from, to) => {
		const next = {};
		Object.entries(stateObj || {}).forEach(([key, value]) => {
			const [dayPart, exPart] = String(key).split(":");
			const dayIdx = Number(dayPart);
			if (!Number.isFinite(dayIdx)) return;
			next[`${mapDraggedDayIndex(dayIdx, from, to)}:${exPart}`] = value;
		});
		return next;
	};

	// Shared by the desktop drag handle and by the mobile "move up/down" menu.
	const reorderDays = (from, to) => {
		if (program?.is_dated) return;
		if (from === to) return;

		updateCurrentWeekDays((prevDays) => {
			const days = [...(prevDays || [])];
			if (!days[from] || !days[to]) return prevDays;

			const [movedDay] = days.splice(from, 1);
			days.splice(to, 0, movedDay);

			return days.map((day, index) => ({
				...day,
				day_number: index + 1,
			}));
		});

		setExpandedDays((prev) => remapIndexedState(prev, from, to));
		setExpandedDaySetupPanels((prev) => remapIndexedState(prev, from, to));
		setNewSessionLabelByDay((prev) => remapIndexedState(prev, from, to));
		setExpandedExercises((prev) => remapExerciseKeys(prev, from, to));
		setSelectedDayIndex((prev) => mapDraggedDayIndex(prev, from, to));
	};

	// Touch replacement for the day drag handle, which is hidden below md.
	const handleMoveDay = (dayIndex, direction) => {
		const target = dayIndex + direction;
		if (target < 0 || target >= currentWeekDays.length) return;
		reorderDays(dayIndex, target);
	};

	const handleDayDragEnd = () => {
		if (program?.is_dated) {
			dragDay.current = null;
			dragOverDay.current = null;
			return;
		}

		const from = dragDay.current;
		const to = dragOverDay.current;

		dragDay.current = null;
		dragOverDay.current = null;

		if (
			from === null ||
			from === undefined ||
			to === null ||
			to === undefined
		) {
			return;
		}

		reorderDays(from, to);
	};

	const handleDragOver = (e, dayIndex, exIndex) => {
		e.preventDefault();
		dragOverExercise.current = { dayIndex, exIndex };
	};

	const handleDragEnd = () => {
		const from = dragExercise.current;
		const to = dragOverExercise.current;
		dragExercise.current = null;
		dragOverExercise.current = null;
		if (!from || !to) return;
		if (from.dayIndex !== to.dayIndex) return;
		if (from.exIndex === to.exIndex) return;

		updateCurrentWeekDays((prev) =>
			prev.map((day, di) => {
				if (di !== from.dayIndex) return day;
				const exercises = [...(day.exercises || [])];
				const [moved] = exercises.splice(from.exIndex, 1);
				exercises.splice(to.exIndex, 0, moved);
				return {
					...day,
					exercises: exercises.map((ex, i) => ({ ...ex, order: i })),
				};
			}),
		);
	};

	// State for exercise combobox and quick-add
	const [exerciseSearchQuery, setExerciseSearchQuery] = useState("");
	const [showExerciseDropdown, setShowExerciseDropdown] = useState(false);
	const [isCreatingNewExercise, setIsCreatingNewExercise] = useState(false);
	const [createExerciseTarget, setCreateExerciseTarget] = useState("standard");
	const [newExerciseDetails, setNewExerciseDetails] = useState({
		name: "",
		description: "",
		video_url: "",
		image_url: "",
		category: "strength",
	});
	const [savingNewExercise, setSavingNewExercise] = useState(false);
	const exerciseSearchRef = useRef(null);

	// Superset exercise search state
	const [supersetSearchQuery, setSupersetSearchQuery] = useState("");
	const [showSupersetDropdown, setShowSupersetDropdown] = useState(false);
	const supersetSearchRef = useRef(null);
	// Complex exercise search state
	const [complexSearchQuery, setComplexSearchQuery] = useState("");
	const [showComplexDropdown, setShowComplexDropdown] = useState(false);
	const complexSearchRef = useRef(null);

	// Conditioning exercise search state (per-row)
	const [conditioningSearchQuery, setConditioningSearchQuery] = useState("");
	const [emomSearchQuery, setEmomSearchQuery] = useState("");
	const [activeEmomRoundIndex, setActiveEmomRoundIndex] = useState(null);
	const [activeEmomItemIndex, setActiveEmomItemIndex] = useState(null);
	const [editingComplexLinkIndex, setEditingComplexLinkIndex] = useState(null);
	const [editingSupersetLinkIndex, setEditingSupersetLinkIndex] =
		useState(null);
	const [editingEmomLinkTarget, setEditingEmomLinkTarget] = useState({
		roundIndex: null,
		itemIndex: null,
	});
	const [activeConditioningIndex, setActiveConditioningIndex] = useState(-1);
	const [videoPreviewState, setVideoPreviewState] = useState(null);
	const conditioningSearchRef = useRef(null);
	const [isCreatingCondExercise, setIsCreatingCondExercise] = useState(false);
	const [condNewExerciseIndex, setCondNewExerciseIndex] = useState(-1);
	const [condNewExerciseDetails, setCondNewExerciseDetails] = useState({
		name: "",
		description: "",
		video_url: "",
		image_url: "",
		category: "strength",
	});
	const [savingCondNewExercise, setSavingCondNewExercise] = useState(false);
	const [conditioningTemplates, setConditioningTemplates] = useState([]);
	const [isSavingConditioningTemplate, setIsSavingConditioningTemplate] =
		useState(false);
	const [loadReferenceExercises, setLoadReferenceExercises] = useState([]);
	const [benchmarkOptions, setBenchmarkOptions] = useState({
		running: [],
		bike: [],
		swim: [],
		erg: [],
	});
	const strengthReferenceOptions = useMemo(() => {
		const sourceExercises =
			Array.isArray(loadReferenceExercises) && loadReferenceExercises.length > 0
				? loadReferenceExercises
				: exercises || [];
		const exerciseOptions = sourceExercises
			.filter((exercise) => exercise.exercise_id && exercise.name)
			.map((exercise) => ({
				value: exercise.exercise_id,
				label: exercise.name,
			}))
			.sort((a, b) => a.label.localeCompare(b.label));

		return [
			{
				value: LOAD_REFERENCE_BODYWEIGHT_ID,
				label: t("athleteBodyweightReference"),
			},
			...exerciseOptions,
		];
	}, [loadReferenceExercises, exercises, t]);

	const libraryExerciseOptions = useMemo(() => {
		return dedupeExercisesByScope(exercises)
			.filter((exercise) => exercise.exercise_id && exercise.name)
			.map((exercise) => ({
				value: exercise.exercise_id,
				label: exercise.name,
				library_scope: exercise.library_scope,
				has_personal_scope: exercise.has_personal_scope,
				has_global_scope: exercise.has_global_scope,
				load_reference_exercise_id: exercise.load_reference_exercise_id || null,
				load_reference_exercise_name:
					exercise.load_reference_exercise_name || null,
			}))
			.sort((a, b) => a.label.localeCompare(b.label));
	}, [exercises]);

	// Maps every known library exercise_id to its canonical name, used to detect
	// program exercises whose id points to a DIFFERENT library exercise (would
	// contaminate that exercise's performance profile).
	const libraryExerciseNameById = useMemo(() => {
		const map = new Map();
		(exercises || []).forEach((exercise) => {
			if (exercise?.exercise_id) {
				map.set(exercise.exercise_id, exercise.name);
			}
		});
		return map;
	}, [exercises]);

	useEffect(() => {
		loadData();
	}, [programId]);

	// Close conditioning dropdown when clicking outside
	useEffect(() => {
		const handleCondClickOutside = (event) => {
			if (
				conditioningSearchRef.current &&
				!conditioningSearchRef.current.contains(event.target)
			) {
				setActiveConditioningIndex(-1);
			}
		};
		document.addEventListener("mousedown", handleCondClickOutside);
		return () =>
			document.removeEventListener("mousedown", handleCondClickOutside);
	}, []);

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (
				exerciseSearchRef.current &&
				!exerciseSearchRef.current.contains(event.target)
			) {
				setShowExerciseDropdown(false);
			}
			if (
				supersetSearchRef.current &&
				!supersetSearchRef.current.contains(event.target)
			) {
				setShowSupersetDropdown(false);
			}
			if (
				complexSearchRef.current &&
				!complexSearchRef.current.contains(event.target)
			) {
				setShowComplexDropdown(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	const loadData = async () => {
		try {
			const [
				programData,
				exercisesData,
				loadReferenceOptionsData,
				athletesData,
				groupsData,
				condTemplatesData,
				accessOptionsData,
				benchmarkOptionsData,
			] = await Promise.all([
				programId !== "new"
					? fetchAPI(
							`/programs/${programId}?weeks_window=${RECENT_WEEKS_WINDOW}`,
						)
					: null,
				fetchAPI("/exercises"),
				fetchAPI("/exercises/load-reference-options").catch(() => []),
				fetchAPI("/users/athletes"),
				fetchAPI("/groups").catch(() => []),
				fetchAPI("/conditioning-library").catch(() => []),
				fetchAPI("/programs/access-options").catch(() => ({
					coaches: [],
					teams: [],
				})),
				fetchAPI("/exercises/benchmark-options").catch(() => ({})),
			]);

			if (Array.isArray(condTemplatesData)) {
				setConditioningTemplates(condTemplatesData);
			}

			setLoadReferenceExercises(
				Array.isArray(loadReferenceOptionsData) ? loadReferenceOptionsData : [],
			);
			if (benchmarkOptionsData && typeof benchmarkOptionsData === "object") {
				setBenchmarkOptions({
					running: Array.isArray(benchmarkOptionsData.running)
						? benchmarkOptionsData.running
						: [],
					bike: Array.isArray(benchmarkOptionsData.bike)
						? benchmarkOptionsData.bike
						: [],
					swim: Array.isArray(benchmarkOptionsData.swim)
						? benchmarkOptionsData.swim
						: [],
					erg: Array.isArray(benchmarkOptionsData.erg)
						? benchmarkOptionsData.erg
						: [],
				});
			}
			const accessCoaches = accessOptionsData?.coaches || [];
			const accessCoachIds = new Set(accessCoaches.map((c) => c.user_id));

			if (programData) {
				// Un programma condiviso non si scrive: il builder non e' il posto
				// dove aprirlo. Il reindirizzamento sta qui, e non solo sui link
				// della lista, perche' copre anche i link diretti, il tasto
				// indietro e le aperture dalla chat dell'assistente. Senza, il
				// coach si troverebbe un editor pieno di campi su cui digitare per
				// poi perdere tutto con un 403 al salvataggio.
				// Spec: docs/coach-teams/DECISIONS.md §2.
				const isOwner = programData.coach_id === user?.user_id;
				const isAdminViewer = user?.is_admin || user?.role === "admin";
				if (!isOwner && !isAdminViewer) {
					navigate(`/programs/${programId}/view`, { replace: true });
					return;
				}

				// Backward compat: migrate flat days to weeks
				if (
					(!programData.weeks || programData.weeks.length === 0) &&
					programData.days?.length > 0
				) {
					programData.weeks = [
						{
							week_id: `week_${Date.now()}`,
							week_number: 1,
							label: `${t("week")} 1`,
							notes: "",
							days: programData.days,
						},
					];
					programData.days = [];
				}
				if (!programData.weeks || programData.weeks.length === 0) {
					programData.weeks = [
						{
							week_id: `week_${Date.now()}`,
							week_number: 1,
							label: `${t("week")} 1`,
							notes: "",
							days: [],
						},
					];
				}
				if (programData.is_dated === undefined) {
					programData.is_dated = true;
				}
				if (programData.auto_disable_weeks === undefined) {
					programData.auto_disable_weeks = true;
				}
				// `auto_publish_weeks` is NOT defaulted here on purpose: absent means
				// manual, the behaviour every existing program had. Only the lead
				// gets its default, and only where the release is already on.
				if (
					programData.auto_publish_weeks === true &&
					(programData.auto_publish_lead_days === undefined ||
						programData.auto_publish_lead_days === null)
				) {
					programData.auto_publish_lead_days = 1;
				}
				programData.weeks = (programData.weeks || []).map(normalizeProgramWeek);
				programData.coach_ids = Array.from(
					new Set(
						(Array.isArray(programData.coach_ids)
							? programData.coach_ids
							: []
						).filter((id) => accessCoachIds.has(id)),
					),
				);
				programData.coach_team_ids = Array.from(
					new Set(programData.coach_team_ids || []),
				);
				const initialWeekIndex = programData.is_dated
					? findCurrentDatedWeekIndex(programData.weeks)
					: -1;

				setProgram(programData);
				setSharedExerciseUsage({});
				// Shared-usage drives only a secondary badge; fetch it AFTER the
				// editor opens so a slow scan can never block program rendering.
				if (programData.program_id) {
					fetchAPI(`/programs/${programData.program_id}/shared-exercises/usage`)
						.then((d) => setSharedExerciseUsage(d?.usage || {}))
						.catch(() => {});
				}
				if (programData.program_id && programData.title) {
					setSharedOriginProgramNames((prev) => ({
						...prev,
						[programData.program_id]: programData.title,
					}));
				}
				setSavedSnapshot(JSON.stringify(programData));
				const requestedWeekIndex = pendingOpenWeekRef.current;
				pendingOpenWeekRef.current = null;
				const weekCount = (programData.weeks || []).length;
				setSelectedWeekIndex(
					requestedWeekIndex !== null && weekCount > 0
						? Math.min(Math.max(requestedWeekIndex, 0), weekCount - 1)
						: initialWeekIndex >= 0
							? initialWeekIndex
							: 0,
				);
				setExpandedDays({});
				// Reachable with a different program since the editor/reference swap
				// navigates between two builder routes without remounting.
				setSelectedDayIndex(null);
			} else {
				const newProgram = {
					title: "",
					description: "",
					type: "individual",
					is_dated: true,
					auto_disable_weeks: true,
					// New dated programs release their weeks by themselves (the
					// Sunday before each week); the coach switches it off in Details.
					auto_publish_weeks: true,
					auto_publish_lead_days: 1,
					athlete_ids: [],
					coach_ids: [],
					coach_team_ids: [],
					athlete_assignments: [],
					weeks: [
						{
							week_id: `week_${Date.now()}`,
							week_number: 1,
							label: `${t("week")} 1`,
							notes: "",
							days: [],
						},
					],
					days: [],
				};
				setProgram(newProgram);
				setSharedExerciseUsage({});
				setSavedSnapshot(JSON.stringify(newProgram));
			}

			setExercises(exercisesData);
			setAthletes(athletesData);
			setGroups(groupsData || []);
			setCoachOptions(accessCoaches);
			setCoachTeamOptions(accessOptionsData?.teams || []);
		} catch (error) {
			console.error("Failed to load data:", error);
			toast.error(
				`${t("pbLoadError")}: ${
					error?.response?.data?.detail ||
					error?.message ||
					t("pbUnknownError")
				}`,
			);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (!program) return;

		const originProgramIds = new Set();
		(program.weeks || []).forEach((week) => {
			(week?.days || []).forEach((day) => {
				(day?.exercises || []).forEach((exercise) => {
					const isSharedReadonly =
						exercise?.shared_readonly === true &&
						exercise?.shared_is_origin !== true;
					if (
						isSharedReadonly &&
						exercise?.shared_origin_program_id &&
						!exercise?.shared_origin_program_name
					) {
						originProgramIds.add(exercise.shared_origin_program_id);
					}
				});
			});
		});

		const missingIds = Array.from(originProgramIds).filter(
			(originProgramId) =>
				!Object.prototype.hasOwnProperty.call(
					sharedOriginProgramNames,
					originProgramId,
				),
		);

		if (missingIds.length === 0) return;

		let cancelled = false;

		Promise.allSettled(
			missingIds.map((originProgramId) =>
				fetchAPI(`/programs/${originProgramId}`),
			),
		).then((results) => {
			if (cancelled) return;

			setSharedOriginProgramNames((prev) => {
				const next = { ...prev };
				results.forEach((result, index) => {
					const originProgramId = missingIds[index];
					if (result.status === "fulfilled") {
						next[originProgramId] = result.value?.title || null;
					} else {
						next[originProgramId] = null;
					}
				});
				return next;
			});
		});

		return () => {
			cancelled = true;
		};
	}, [program, sharedOriginProgramNames]);

	// Returns whether the save actually went through, so callers that need to do
	// something afterwards (e.g. swapping editor and reference) can chain on it.
	const handleSaveProgram = async () => {
		if (!program.title.trim()) {
			toast.error(t("programTitleRequired"));
			// Focus sul campo titolo. Se il pannello impostazioni e' collassato il
			// campo non e' montato: apriamo lo Sheet e attendiamo il mount.
			const focusTitleInput = () => {
				const titleInput = document.querySelector(
					'[data-testid="program-title-input"]',
				);
				if (!titleInput) return;
				titleInput.focus();
				titleInput.classList.add("ring-2", "ring-blaze");
				setTimeout(
					() => titleInput.classList.remove("ring-2", "ring-blaze"),
					2000,
				);
			};
			if (!isSettingsColumnVisible) {
				setIsSettingsSheetOpen(true);
				setTimeout(focusTitleInput, 250);
			} else {
				focusTitleInput();
			}
			return false;
		}

		setSaving(true);
		try {
			// Helper to parse distance strings like "10km", "500m" to meters
			const parseDistanceToMeters = (val) => {
				if (val === "" || val === null || val === undefined) return null;
				const str = String(val).trim().toLowerCase();
				const kmMatch = str.match(/^([\d.]+)\s*km$/);
				if (kmMatch) return Math.round(parseFloat(kmMatch[1]) * 1000);
				const mMatch = str.match(/^([\d.]+)\s*m$/);
				if (mMatch) return Math.round(parseFloat(mMatch[1]));
				const num = Number(str);
				return isNaN(num) ? null : num;
			};

			// Helper to normalize numeric fields on exercises
			const normalizeExercises = (exercises) =>
				(exercises || []).map((rawEx) => {
					const ex = ensureExerciseIdIntegrity(rawEx, libraryExerciseNameById);
					return {
						...ex,
						sets:
							ex.sets === "" || ex.sets === null || ex.sets === undefined
								? 0
								: Number(ex.sets),
						load_percentage:
							ex.load_percentage === "" ||
							ex.load_percentage === null ||
							ex.load_percentage === undefined
								? null
								: normalizeLoadExpression(ex.load_percentage),
						rest_seconds: normalizeSmartRestInput(ex.rest_seconds),
						superset_rest_seconds: normalizeSmartRestInput(
							ex.superset_rest_seconds,
						),
						distance_meters: parseDistanceToMeters(ex.distance_meters),
						duration_minutes:
							ex.duration_minutes === "" ||
							ex.duration_minutes === null ||
							ex.duration_minutes === undefined
								? null
								: Number(ex.duration_minutes),
						time_cap_seconds:
							ex.time_cap_seconds === "" ||
							ex.time_cap_seconds === null ||
							ex.time_cap_seconds === undefined
								? null
								: Number(ex.time_cap_seconds),
						rounds:
							ex.rounds === "" || ex.rounds === null || ex.rounds === undefined
								? null
								: String(ex.rounds).trim() || null,
						interval_seconds:
							ex.interval_seconds === "" ||
							ex.interval_seconds === null ||
							ex.interval_seconds === undefined
								? null
								: Number(ex.interval_seconds),
						intervals:
							ex.intervals === "" ||
							ex.intervals === null ||
							ex.intervals === undefined
								? null
								: Number(ex.intervals),
						set_rows: (ex.set_rows || []).map((row) => {
							const parsedSets = normalizeSetExpression(
								row.sets_label ?? row.sets,
							);
							return {
								...row,
								sets: parsedSets.sets,
								sets_label: parsedSets.sets_label,
								load_percentage:
									row.load_percentage === "" ||
									row.load_percentage === null ||
									row.load_percentage === undefined
										? null
										: normalizeLoadExpression(row.load_percentage),
								load_reference_exercise_id:
									row.load_percentage === "" ||
									row.load_percentage === null ||
									row.load_percentage === undefined ||
									row.load_reference_exercise_id === "" ||
									row.load_reference_exercise_id === null ||
									row.load_reference_exercise_id === undefined
										? null
										: row.load_reference_exercise_id,
								load_reference_exercise_name:
									row.load_percentage === "" ||
									row.load_percentage === null ||
									row.load_percentage === undefined ||
									row.load_reference_exercise_name === "" ||
									row.load_reference_exercise_name === null ||
									row.load_reference_exercise_name === undefined
										? null
										: row.load_reference_exercise_name,
								rest_seconds: normalizeSmartRestInput(row.rest_seconds),
							};
						}),
					};
				});

			const normalizeDays = (days) =>
				(days || []).map((d) => {
					const result = { ...d };

					const normalizedSessions = (d.sessions || [])
						.map((session, index) => ({
							...session,
							order: typeof session.order === "number" ? session.order : index,
						}))
						.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
						.map((session, index) => ({ ...session, order: index }));

					const validSessionIds = new Set(
						normalizedSessions.map((session) => session.session_id),
					);

					const exercises = normalizeExercises(d.exercises || []).map(
						(exercise) => ({
							...exercise,
							session_id: validSessionIds.has(exercise.session_id)
								? exercise.session_id
								: null,
						}),
					);

					result.exercises = exercises.map((exercise, index) => {
						const sessionOrder =
							exercise.session_id === null
								? null
								: exercises
										.slice(0, index + 1)
										.filter((item) => item.session_id === exercise.session_id)
										.length - 1;
						return {
							...exercise,
							order: index,
							session_order: sessionOrder,
						};
					});

					result.sessions = normalizedSessions.map((session) => ({
						...session,
						exercises: result.exercises
							.filter((exercise) => exercise.session_id === session.session_id)
							.map((exercise, exIndex) => ({
								...exercise,
								session_order: exIndex,
							})),
					}));

					return result;
				});

			const programWithoutLegacyDays = { ...program };
			delete programWithoutLegacyDays.days;

			// Weeks identical to the saved snapshot travel as skeletons: the backend
			// keeps their stored days, so only what the coach touched goes over the wire.
			let savedWeeks = [];
			if (savedSnapshot) {
				try {
					savedWeeks = JSON.parse(savedSnapshot)?.weeks || [];
				} catch {
					savedWeeks = [];
				}
			}
			const weeksPayload = buildWeeksSavePayload(program.weeks, normalizeDays, {
				savedWeeks,
			});

			const payload = {
				...programWithoutLegacyDays,
				// Weeks never loaded in this session travel without their `days`, and
				// weeks_partial tells the backend to restore the stored ones.
				...weeksPayload,
			};

			// Ambiti (tiro / fisico / ibrido) ricalcolati qui, dove il programma è
			// già in memoria. Il coach non li tocca: li può però forzare, e in quel
			// caso `domains_auto` è false e il calcolo non interviene.
			if (program.domains_auto !== false) {
				payload.domains = computeProgramDomains(program, {
					partial: weeksPayload.weeks_partial,
				});
			}

			if (programId === "new") {
				const result = await fetchAPI("/programs", {
					method: "POST",
					body: JSON.stringify(payload),
				});
				toast.success(t("programCreatedSuccess"));
				setSavedSnapshot(JSON.stringify(program));
				navigate(`/programs/${result.program_id}`);
				return true;
			} else {
				await fetchAPI(`/programs/${programId}`, {
					method: "PUT",
					body: JSON.stringify(payload),
				});
				// Computed before the snapshot moves, or there is no diff left.
				const outcome = describeSaveOutcome();
				setSavedSnapshot(JSON.stringify(program));
				toast.success(
					outcome.length > 0
						? `${t("programSaved")} · ${outcome.join(" · ")}`
						: t("programSaved"),
				);
				return true;
			}
		} catch (error) {
			console.error("Save error:", error);
			const detail = String(error?.detail || error?.message || "");
			const lower = detail.toLowerCase();
			const isSchemaLock =
				error?.status === 409 ||
				lower.includes("schema risultato bloccato") ||
				lower.includes("risultati inseriti");
			if (isSchemaLock) {
				toast.error(t("pbSchemaLockedToastSaved"));
				return false;
			}
			toast.error(`${t("saveError")}: ${error.message}`);
			return false;
		} finally {
			setSaving(false);
		}
	};

	// The delta chip always compares with the PREVIOUS week of this program,
	// whatever the reference panel shows: programs progress week on week, and a
	// chip that changed meaning when the coach opened "Confronta" (or vanished
	// when the panel was closed, e.g. on reopening a saved program) read as a
	// bug. The panel stays for comparing by eye. Week 1 has no previous week.
	const previousWeekIndex = selectedWeekIndex - 1;
	const previousWeek =
		previousWeekIndex >= 0 ? program?.weeks?.[previousWeekIndex] : null;
	const previousWeekLabel = previousWeek
		? localizeWeekLabel(previousWeek.label, t, previousWeekIndex)
		: "";
	const referenceLoadIndex = useMemo(() => {
		if (!previousWeek || !isWeekContentLoaded(previousWeek)) return null;
		const index = buildWeekLoadIndex(previousWeek);
		return index.length > 0 ? index : null;
	}, [previousWeek]);

	const getReferenceDeltaTag = useCallback(
		(exercise, dayIndex) => {
			if (!referenceLoadIndex) return null;
			const previous = findReferenceLoad(referenceLoadIndex, exercise, dayIndex);
			if (previous === undefined || previous === null) return null;
			const current = getExerciseLoadPercentage(exercise);
			if (current === null) return null;
			// Same top load still gets a chip: without it "unchanged" looked
			// exactly like "not compared" (the case Alex reported on C&J).
			if (current === previous) {
				return {
					key: "reference-delta",
					icon: Equal,
					label: `${current}%`,
					tone: "muted",
					showLabel: true,
					tooltip: `${t("pbDeltaVsPreviousWeek")} (${previousWeekLabel}): ${t("pbDeltaSameTopLoad")} (${current}%)`,
				};
			}
			const delta = Math.round((current - previous) * 10) / 10;
			const isUp = delta > 0;
			return {
				key: "reference-delta",
				icon: isUp ? ArrowUp : ArrowDown,
				label: `${isUp ? "+" : ""}${delta}%`,
				tone: isUp ? "emerald" : "amber",
				showLabel: true,
				tooltip: `${t("pbDeltaVsPreviousWeek")} (${previousWeekLabel}): ${t("pbDeltaTopLoad")} ${previous}% → ${current}%`,
			};
		},
		[referenceLoadIndex, previousWeekLabel, t],
	);

	// Helper: get days of the currently selected week
	// Weeks older than RECENT_WEEKS_WINDOW arrive as metadata-only skeletons; this
	// fills one in when something is about to show it.
	const requestedWeekIdsRef = useRef(new Set());

	useEffect(() => {
		requestedWeekIdsRef.current = new Set();
	}, [programId]);

	const loadWeeksContent = useCallback(
		async (weekIds) => {
			if (programId === "new") return;
			// Requested ids are never cleared: a success needs no retry and a failure
			// must not turn the effect below into a fetch loop.
			const wanted = (weekIds || []).filter(
				(weekId) => weekId && !requestedWeekIdsRef.current.has(weekId),
			);
			if (wanted.length === 0) return;
			wanted.forEach((weekId) => requestedWeekIdsRef.current.add(weekId));

			try {
				const data = await fetchAPI(
					`/programs/${programId}/weeks?week_ids=${encodeURIComponent(
						wanted.join(","),
					)}`,
				);
				const loaded = new Map(
					(data?.weeks || [])
						.filter((week) => week?.week_id)
						.map((week) => [week.week_id, normalizeProgramWeek(week)]),
				);
				if (loaded.size === 0) return;

				const fillWeeks = (programState) => ({
					...programState,
					weeks: (programState?.weeks || []).map(
						(week) => loaded.get(week?.week_id) || week,
					),
				});

				setProgram((prev) => (prev ? fillWeeks(prev) : prev));
				// The saved snapshot has to move with it, or filling a skeleton would
				// read as an unsaved edit and raise the leave confirmation.
				setSavedSnapshot((prev) =>
					prev === null ? prev : JSON.stringify(fillWeeks(JSON.parse(prev))),
				);
			} catch (error) {
				toast.error(
					`${t("pbWeekLoadFailed")}: ${
						error?.detail || error?.message || t("pbErrorShort")
					}`,
				);
			}
		},
		[programId, t],
	);

	// The week being edited always needs its content, and so does the one
	// before it: the delta chips compare with it.
	useEffect(() => {
		const wanted = [selectedWeekIndex, selectedWeekIndex - 1]
			.map((index) => (index >= 0 ? program?.weeks?.[index] : null))
			.filter((week) => week && !isWeekContentLoaded(week))
			.map((week) => week.week_id);
		if (wanted.length > 0) loadWeeksContent(wanted);
	}, [program, selectedWeekIndex, loadWeeksContent]);

	// A dated program has no dateless days. Days created while the program was
	// undated get one when it switches to dated (see `handleProgramModeChange`),
	// and so do the weeks that load only later, or a program saved before this
	// existed with days on "1 January 1970". Filling them is a real edit: the
	// coach sees the proposed dates and saves them.
	useEffect(() => {
		if (!program?.is_dated) return;
		setProgram((prev) => {
			if (!prev?.is_dated) return prev;
			const weeks = fillMissingDayDates(prev.weeks);
			return weeks === prev.weeks ? prev : { ...prev, weeks };
		});
	}, [program?.is_dated, program?.weeks]);

	// The strip holds one tab per week, so on a long program the selected week sits
	// far off-screen to the right and the coach lands looking at week 1. Bring it
	// into view instead.
	const selectedWeekTabRef = useRef(null);

	useEffect(() => {
		const tab = selectedWeekTabRef.current;
		if (!tab) return;
		// block: "nearest" keeps this from scrolling the page vertically too.
		tab.scrollIntoView({ block: "nearest", inline: "center" });
	}, [selectedWeekIndex, program?.weeks?.length]);

	const currentWeek = program?.weeks?.[selectedWeekIndex];
	const currentWeekDays = currentWeek?.days || [];
	const resizeTextareaToContent = useCallback((textareaElement) => {
		if (!(textareaElement instanceof HTMLTextAreaElement)) {
			return;
		}

		textareaElement.style.height = "auto";
		textareaElement.style.height = `${textareaElement.scrollHeight}px`;
	}, []);
	const exportFolderMap = useMemo(() => {
		const map = {};
		(exportFolders || []).forEach((folder) => {
			if (folder?.folder_id) {
				map[folder.folder_id] = folder;
			}
		});
		return map;
	}, [exportFolders]);

	const exportBreadcrumb = useMemo(() => {
		const path = [];
		let currentId = exportCurrentFolderId;
		while (currentId && exportFolderMap[currentId]) {
			const folder = exportFolderMap[currentId];
			path.unshift(folder);
			currentId = folder.parent_id;
		}
		return path;
	}, [exportCurrentFolderId, exportFolderMap]);

	const exportCurrentFolders = useMemo(
		() =>
			exportFolders.filter((folder) =>
				exportCurrentFolderId
					? folder.parent_id === exportCurrentFolderId
					: !folder.parent_id,
			),
		[exportFolders, exportCurrentFolderId],
	);

	const exportVisiblePrograms = useMemo(() => {
		const query = exportSearchQuery.trim().toLowerCase();
		let filtered = exportPrograms.filter((programOption) =>
			exportCurrentFolderId
				? programOption.folder_id === exportCurrentFolderId
				: !programOption.folder_id,
		);

		// Exclude the current program as a destination for all export types EXCEPT
		// `week_share`, which supports sharing within the same program (different week).
		if (exportType !== "week_share") {
			filtered = filtered.filter(
				(programOption) => programOption.program_id !== programId,
			);
		}

		if (query) {
			filtered = filtered.filter(
				(programOption) =>
					(programOption.title || "").toLowerCase().includes(query) ||
					(programOption.description || "").toLowerCase().includes(query),
			);
		}

		return filtered.sort((a, b) => {
			const aTimestamp = Date.parse(a?.updated_at || a?.created_at || "") || 0;
			const bTimestamp = Date.parse(b?.updated_at || b?.created_at || "") || 0;
			if (aTimestamp !== bTimestamp) {
				return bTimestamp - aTimestamp;
			}
			return (a.title || "").localeCompare(b.title || "");
		});
	}, [
		exportPrograms,
		exportCurrentFolderId,
		exportSearchQuery,
		exportType,
		programId,
	]);

	const selectedExportProgram = useMemo(() => {
		if (!exportSelectedProgramId) return null;
		return (
			exportProgramDetails[exportSelectedProgramId] ||
			exportPrograms.find(
				(programOption) => programOption.program_id === exportSelectedProgramId,
			) ||
			null
		);
	}, [exportProgramDetails, exportPrograms, exportSelectedProgramId]);

	const selectedExportWeek = useMemo(() => {
		if (!selectedExportProgram || exportSelectedWeekIndex === "") return null;
		const parsedIndex = Number(exportSelectedWeekIndex);
		if (Number.isNaN(parsedIndex)) return null;
		return selectedExportProgram.weeks?.[parsedIndex] || null;
	}, [selectedExportProgram, exportSelectedWeekIndex]);

	const assignedAthletePreviewOptions = useMemo(() => {
		const assignedIds = program?.athlete_ids || [];
		return assignedIds.map((athleteId) => {
			const athlete = athletes.find((item) => item.user_id === athleteId);
			return {
				user_id: athleteId,
				name: athlete?.name || athleteId,
			};
		});
	}, [program?.athlete_ids, athletes]);

	const resetExportDialogState = () => {
		setExportType(null);
		setExportSource(null);
		setExportSearchQuery("");
		setExportCurrentFolderId(null);
		setExportSelectedProgramId("");
		setExportSelectedWeekIndex("");
		setExportSelectedDayIndex("");
	};

	const loadExportTargets = async () => {
		setLoadingExportTargets(true);
		try {
			const [minePrograms, sharedPrograms, mineFolders, sharedFolders] =
				await Promise.all([
					fetchAPI("/programs?scope=mine&compact=true").catch(() => []),
					fetchAPI("/programs?scope=shared&compact=true").catch(() => []),
					fetchAPI("/programs/folders/list?scope=mine").catch(() => []),
					fetchAPI("/programs/folders/list?scope=shared").catch(() => []),
				]);

			const mergeUniqueById = (items, keyField) => {
				const map = new Map();
				(items || []).forEach((item) => {
					const key = item?.[keyField];
					if (key && !map.has(key)) {
						map.set(key, item);
					}
				});
				return Array.from(map.values());
			};

			const mergedPrograms = mergeUniqueById(
				[...(minePrograms || []), ...(sharedPrograms || [])],
				"program_id",
			);

			const mergedFolders = mergeUniqueById(
				[...(mineFolders || []), ...(sharedFolders || [])],
				"folder_id",
			);

			setExportPrograms(mergedPrograms);
			setSharedOriginProgramNames((prev) => {
				const next = { ...prev };
				mergedPrograms.forEach((programOption) => {
					if (programOption?.program_id && programOption?.title) {
						next[programOption.program_id] = programOption.title;
					}
				});
				return next;
			});
			setExportFolders(mergedFolders);
		} catch (error) {
			toast.error(
				`${t("programsLoadError")}: ${error?.message || t("pbUnknownError")}`,
			);
		}
		setLoadingExportTargets(false);
	};

	const handleOpenExportDialog = async (type, source) => {
		if ((type === "exercise" || type === "week_share") && isDirty) {
			toast.error(t("pbSaveBeforeSharing"));
			return;
		}

		if ((type === "exercise" || type === "week_share") && programId === "new") {
			toast.error(t("pbSaveSourceBeforeSharing"));
			return;
		}

		resetExportDialogState();
		setExportType(type);
		setExportSource(source);
		setIsExportDialogOpen(true);
		await loadExportTargets();
	};

	const handleSelectExportProgram = async (targetProgramId) => {
		if (!targetProgramId) return;
		setExportSelectedProgramId(targetProgramId);
		setExportSelectedWeekIndex("");
		setExportSelectedDayIndex("");

		// A plain week export renders no week/day picker: it only appends a week at
		// confirm time. Skip pulling the whole target program here — for long
		// programs that download is the bulk of the "export is slow" complaint.
		if (exportType === "week") return;

		if (exportProgramDetails[targetProgramId]) return;

		try {
			const detail = await fetchAPI(`/programs/${targetProgramId}`);
			setExportProgramDetails((prev) => ({
				...prev,
				[targetProgramId]: detail,
			}));
		} catch (error) {
			toast.error(
				`${t("pbSelectedProgramLoadError")}: ${
					error?.message || t("pbUnknownError")
				}`,
			);
		}
	};

	const handleConfirmExport = async () => {
		if (!exportType || !exportSource || !exportSelectedProgramId) return;
		if (exportType !== "week" && exportSelectedWeekIndex === "") {
			toast.error(t("pbSelectTargetWeek"));
			return;
		}
		if (
			(exportType === "exercise" || exportType === "exercise_copy") &&
			exportSelectedDayIndex === ""
		) {
			toast.error(t("pbSelectTargetDay"));
			return;
		}

		setSavingExport(true);
		try {
			// A plain week export only appends one week, so fetch the target with its
			// older weeks as skeletons (weeks_window). buildWeeksSavePayload then
			// re-sends those as omitted-days weeks (weeks_partial), turning a full
			// program re-upload into just the new week. Other export types edit an
			// existing target week/day and need its full content, so they reuse the
			// copy fetched when the target was selected.
			const targetProgram =
				exportType === "week"
					? await fetchAPI(
							`/programs/${exportSelectedProgramId}?weeks_window=${RECENT_WEEKS_WINDOW}`,
						)
					: exportProgramDetails[exportSelectedProgramId] ||
						(await fetchAPI(`/programs/${exportSelectedProgramId}`));
			const updatedWeeks = JSON.parse(
				JSON.stringify(targetProgram?.weeks || []),
			);

			if (exportType === "week") {
				const sourceWeek = program?.weeks?.[exportSource.weekIndex];
				if (!sourceWeek) {
					throw new Error(t("pbSourceWeekNotFound"));
				}

				let nextWeekMonday = null;
				if (targetProgram?.is_dated) {
					const latestDate = getLatestDateFromWeeks(updatedWeeks);
					if (latestDate) {
						nextWeekMonday = getWeekMondayFromDate(latestDate);
						nextWeekMonday.setDate(nextWeekMonday.getDate() + 7);
					} else {
						nextWeekMonday = getWeekAnchorMonday(
							updatedWeeks,
							updatedWeeks.length,
						);
					}
				}

				const duplicatedDays = (sourceWeek.days || [])
					.map((sourceDay, index) => {
						let dateValue = undefined;
						let dayNumberValue = sourceDay?.day_number ?? index + 1;
						if (targetProgram?.is_dated) {
							const weekdayOffset = inferWeekdayOffset(sourceDay, index);
							const targetDate = new Date(nextWeekMonday);
							targetDate.setDate(targetDate.getDate() + weekdayOffset);
							dateValue = toLocalISODate(targetDate);
							dayNumberValue = undefined;
						}

						return cloneDayForExport(sourceDay, {
							dateValue,
							dayNumberValue,
						});
					})
					.sort((a, b) => {
						if (!targetProgram?.is_dated) return 0;
						return (a?.date || "").localeCompare(b?.date || "");
					});

				updatedWeeks.push({
					...sourceWeek,
					week_id: createRuntimeId("week"),
					week_number: updatedWeeks.length + 1,
					label: `${t("week")} ${updatedWeeks.length + 1}`,
					is_published: false,
					publish_date: null,
					days: duplicatedDays,
				});
			}

			if (exportType === "day") {
				const sourceDay = currentWeekDays?.[exportSource.dayIndex];
				if (!sourceDay) {
					throw new Error(t("pbSourceDayNotFound"));
				}

				const targetWeekIndex = Number(exportSelectedWeekIndex);
				const targetWeek = updatedWeeks[targetWeekIndex];
				if (!targetWeek) {
					throw new Error(t("pbTargetWeekNotFound"));
				}

				let dateValue = undefined;
				let dayNumberValue = (targetWeek.days || []).length + 1;
				if (targetProgram?.is_dated) {
					const weekMonday = getWeekAnchorMonday(updatedWeeks, targetWeekIndex);
					const weekdayOffset = inferWeekdayOffset(
						sourceDay,
						(targetWeek.days || []).length,
					);
					const targetDate = new Date(weekMonday);
					targetDate.setDate(targetDate.getDate() + weekdayOffset);
					dateValue = toLocalISODate(targetDate);
					dayNumberValue = undefined;
				}

				const clonedDay = cloneDayForExport(sourceDay, {
					dateValue,
					dayNumberValue,
				});

				targetWeek.days = [...(targetWeek.days || []), clonedDay];
				if (targetProgram?.is_dated) {
					targetWeek.days = [...targetWeek.days].sort((a, b) =>
						(a?.date || "").localeCompare(b?.date || ""),
					);
				}
			}

			let updatedSourceWeeks = null;

			if (exportType === "exercise_copy") {
				const sourceWeek = program?.weeks?.[selectedWeekIndex];
				const sourceDay = sourceWeek?.days?.[exportSource.dayIndex];
				const sourceExercise =
					sourceDay?.exercises?.[exportSource.exerciseIndex];
				if (!sourceExercise || !sourceDay || !sourceWeek) {
					throw new Error(t("pbSourceExerciseNotFound"));
				}

				const targetWeekIndex = Number(exportSelectedWeekIndex);
				const targetDayIndex = Number(exportSelectedDayIndex);
				const targetWeek = updatedWeeks[targetWeekIndex];
				const targetDay = targetWeek?.days?.[targetDayIndex];
				if (!targetWeek || !targetDay) {
					throw new Error(t("pbInvalidTarget"));
				}

				const targetExercises = getNormalizedDayExercises(targetDay);
				const clonedExercise = cloneExerciseAsIndependent(sourceExercise, {
					orderValue: targetExercises.length,
					sessionIdValue: null,
					sessionOrderValue: null,
				});
				targetDay.exercises = [...targetExercises, clonedExercise];
			}

			if (exportType === "exercise") {
				if (programId === "new") {
					throw new Error(
						t("pbSaveSourceToShareExercises"),
					);
				}

				const sourceWeek = program?.weeks?.[selectedWeekIndex];
				const sourceDay = sourceWeek?.days?.[exportSource.dayIndex];
				const sourceExercise =
					sourceDay?.exercises?.[exportSource.exerciseIndex];
				if (!sourceExercise || !sourceDay || !sourceWeek) {
					throw new Error(t("pbSourceExerciseNotFound"));
				}

				const sharedExerciseId =
					sourceExercise.shared_exercise_id || createRuntimeId("shx");
				const resolvedSourceExercise =
					resolveExerciseLoadReferencesForStats(sourceExercise);
				const sourceOriginPayload = {
					...resolvedSourceExercise,
					shared_exercise_id: sharedExerciseId,
					shared_origin_program_id: programId,
					shared_origin_week_id: sourceWeek.week_id || null,
					shared_origin_day_id: sourceDay.day_id || null,
					shared_origin_exercise_id:
						sourceExercise.shared_origin_exercise_id ||
						sourceExercise.exercise_id ||
						null,
					shared_is_origin: true,
					shared_readonly: false,
				};

				updatedSourceWeeks = JSON.parse(JSON.stringify(program?.weeks || []));
				if (
					!updatedSourceWeeks[selectedWeekIndex]?.days?.[exportSource.dayIndex]
						?.exercises
				) {
					throw new Error(t("pbCannotUpdateSource"));
				}
				updatedSourceWeeks[selectedWeekIndex].days[
					exportSource.dayIndex
				].exercises[exportSource.exerciseIndex] = sourceOriginPayload;

				const targetWeekIndex = Number(exportSelectedWeekIndex);
				const targetDayIndex = Number(exportSelectedDayIndex);
				const targetWeek = updatedWeeks[targetWeekIndex];
				const targetDay = targetWeek?.days?.[targetDayIndex];
				if (!targetWeek || !targetDay) {
					throw new Error(t("pbInvalidTarget"));
				}

				const targetExercises = getNormalizedDayExercises(targetDay);
				targetDay.exercises = [
					...targetExercises,
					{
						...sourceOriginPayload,
						shared_is_origin: false,
						shared_readonly: true,
						order: targetExercises.length,
						session_id: null,
						session_order: null,
					},
				];
			}

			if (exportType === "week_share") {
				if (programId === "new") {
					throw new Error(
						t("pbSaveSourceToShareWeek"),
					);
				}

				const sourceWeek = program?.weeks?.[exportSource.weekIndex];
				if (!sourceWeek) {
					throw new Error(t("pbSourceWeekNotFound"));
				}

				const targetWeekIndex = Number(exportSelectedWeekIndex);
				const isSameProgram = exportSelectedProgramId === programId;

				// If same program, work on a clone of local (unsaved) state to
				// preserve pending edits. Otherwise use the freshly fetched target.
				let workingTargetWeeks;
				if (isSameProgram) {
					workingTargetWeeks = JSON.parse(JSON.stringify(program?.weeks || []));
				} else {
					// Deep-clone so the end-of-block `updatedWeeks.length = 0` does
					// not also clear workingTargetWeeks (they must be independent objects).
					workingTargetWeeks = JSON.parse(JSON.stringify(updatedWeeks));
				}

				const targetWeek = workingTargetWeeks[targetWeekIndex];
				if (!targetWeek) {
					throw new Error(t("pbTargetWeekNotFound"));
				}
				if (isSameProgram && targetWeekIndex === exportSource.weekIndex) {
					throw new Error(
						t("pbCannotShareWeekWithItself"),
					);
				}

				targetWeek.days = Array.isArray(targetWeek.days)
					? [...targetWeek.days]
					: [];

				// Build the source modification target.
				// Same program → mutate sourceWeek inside workingTargetWeeks.
				// Different program → maintain updatedSourceWeeks for source PUT.
				if (isSameProgram) {
					updatedSourceWeeks = workingTargetWeeks;
				} else {
					updatedSourceWeeks = JSON.parse(JSON.stringify(program?.weeks || []));
				}
				const sourceWeekInSourceClone =
					updatedSourceWeeks[exportSource.weekIndex];
				if (!sourceWeekInSourceClone) {
					throw new Error(t("pbCannotUpdateSource"));
				}
				sourceWeekInSourceClone.days = Array.isArray(
					sourceWeekInSourceClone.days,
				)
					? [...sourceWeekInSourceClone.days]
					: [];

				// Track which target day indices have been already consumed so
				// that multiple source days with the same weekday match different
				// target days (deterministic pairing).
				const consumedTargetDayIdx = new Set();

				const pickTargetDayIndex = (sourceDay, sourceIdx) => {
					const srcKey = inferWeekdayOffset(sourceDay, sourceIdx);
					// 1st pass: same weekday / day_number
					for (let ti = 0; ti < targetWeek.days.length; ti += 1) {
						if (consumedTargetDayIdx.has(ti)) continue;
						const tk = inferWeekdayOffset(targetWeek.days[ti], ti);
						if (tk === srcKey) return ti;
					}
					// 2nd pass: same position index
					if (
						sourceIdx < targetWeek.days.length &&
						!consumedTargetDayIdx.has(sourceIdx)
					) {
						return sourceIdx;
					}
					return -1;
				};

				const createTargetDayFromSource = (sourceDay, sourceIdx) => {
					let dateValue;
					let dayNumberValue;
					const effectiveIsDated = isSameProgram
						? program?.is_dated
						: targetProgram?.is_dated;
					if (effectiveIsDated) {
						const weekMonday = getWeekAnchorMonday(
							workingTargetWeeks,
							targetWeekIndex,
						);
						const weekdayOffset = inferWeekdayOffset(sourceDay, sourceIdx);
						const targetDate = new Date(weekMonday);
						targetDate.setDate(targetDate.getDate() + weekdayOffset);
						dateValue = toLocalISODate(targetDate);
						dayNumberValue = undefined;
					} else {
						dateValue = undefined;
						dayNumberValue =
							typeof sourceDay?.day_number === "number"
								? sourceDay.day_number
								: targetWeek.days.length + 1;
					}
					const clonedDay = cloneDayForExport(sourceDay, {
						dateValue,
						dayNumberValue,
					});
					// Strip all exercises — they'll be re-added as shared below.
					clonedDay.exercises = [];
					clonedDay.sessions = (clonedDay.sessions || []).map((s) => ({
						...s,
						exercises: [],
					}));
					return clonedDay;
				};

				let sharedExercisesCount = 0;

				(sourceWeekInSourceClone.days || []).forEach(
					(sourceDayClone, sourceIdx) => {
						const sourceDayExercises =
							getNormalizedDayExercises(sourceDayClone);
						if (sourceDayExercises.length === 0) return;

						let targetDayIdx = pickTargetDayIndex(sourceDayClone, sourceIdx);
						let targetDay;
						if (targetDayIdx === -1) {
							targetDay = createTargetDayFromSource(sourceDayClone, sourceIdx);
							targetWeek.days.push(targetDay);
							targetDayIdx = targetWeek.days.length - 1;
						} else {
							targetDay = targetWeek.days[targetDayIdx];
							targetDay.exercises = Array.isArray(targetDay.exercises)
								? [...targetDay.exercises]
								: [];
						}
						consumedTargetDayIdx.add(targetDayIdx);

						const targetExercisesNormalized =
							getNormalizedDayExercises(targetDay);
						let nextOrder = targetExercisesNormalized.length;

						// Mark every row of the source day as shared origin. Each row gets
						// its own shared id: matching rows by `exercise_id` collapsed two
						// rows with the same (or empty) id onto one payload, which turned
						// "HSPU + circuit" into "circuit + circuit" in the source program.
						const { originExercises } = markDayExercisesAsSharedOrigin(
							sourceDayClone,
							{ programId, weekId: sourceWeek.week_id || null },
						);

						originExercises.forEach((originExercise) => {
							// Append shared copy to target day (flat exercises list).
							targetDay.exercises.push({
								...originExercise,
								exercise_id: createRuntimeId("ex"),
								shared_is_origin: false,
								shared_readonly: true,
								order: nextOrder,
								session_id: null,
								session_order: null,
							});
							nextOrder += 1;
							sharedExercisesCount += 1;
						});
					},
				);

				if (sharedExercisesCount === 0) {
					throw new Error(
						t("pbSourceWeekHasNoExercises"),
					);
				}

				// Replace workingTargetWeeks into updatedWeeks so the PUT below
				// uses it. For same-program case, source and target are the same
				// object => updatedSourceWeeks === workingTargetWeeks.
				updatedWeeks.length = 0;
				workingTargetWeeks.forEach((w) => updatedWeeks.push(w));
			}

			await fetchAPI(`/programs/${exportSelectedProgramId}`, {
				method: "PUT",
				body: JSON.stringify({
					...buildWeeksSavePayload(updatedWeeks),
				}),
			});

			if (exportType === "week") {
				// updatedWeeks holds skeletons for the untouched weeks; caching that
				// would feed a later exercise/day export a program missing day content
				// (and a partial `days`). Drop the entry so it is refetched full on next use.
				setExportProgramDetails((prev) => {
					const next = { ...prev };
					delete next[exportSelectedProgramId];
					return next;
				});
			} else {
				// Keep the cached target in sync so a second share to the same program
				// in this dialog session builds on the just-written weeks (not a stale copy).
				setExportProgramDetails((prev) => ({
					...prev,
					[exportSelectedProgramId]: {
						...(prev[exportSelectedProgramId] || targetProgram),
						weeks: updatedWeeks,
						days: (updatedWeeks || []).flatMap((week) => week.days || []),
					},
				}));
			}

			if (
				exportType === "exercise" &&
				updatedSourceWeeks &&
				programId !== "new"
			) {
				await fetchAPI(`/programs/${programId}`, {
					method: "PUT",
					body: JSON.stringify({
						...buildWeeksSavePayload(updatedSourceWeeks),
					}),
				});
				const nextProgramState = {
					...program,
					weeks: updatedSourceWeeks,
					days: (updatedSourceWeeks || []).flatMap((week) => week.days || []),
				};
				setProgram(nextProgramState);
				setSavedSnapshot(JSON.stringify(nextProgramState));
			} else if (
				exportType === "week_share" &&
				updatedSourceWeeks &&
				programId !== "new" &&
				exportSelectedProgramId !== programId
			) {
				await fetchAPI(`/programs/${programId}`, {
					method: "PUT",
					body: JSON.stringify({
						...buildWeeksSavePayload(updatedSourceWeeks),
					}),
				});
				const nextProgramState = {
					...program,
					weeks: updatedSourceWeeks,
					days: (updatedSourceWeeks || []).flatMap((week) => week.days || []),
				};
				setProgram(nextProgramState);
				setSavedSnapshot(JSON.stringify(nextProgramState));
			} else if (
				exportType === "week_share" &&
				exportSelectedProgramId === programId &&
				programId !== "new"
			) {
				// Same program: source + target changes are already in updatedWeeks
				const nextProgramState = {
					...program,
					weeks: updatedWeeks,
					days: (updatedWeeks || []).flatMap((week) => week.days || []),
				};
				setProgram(nextProgramState);
				setSavedSnapshot(JSON.stringify(nextProgramState));
			}

			if (
				(exportType === "exercise" || exportType === "week_share") &&
				programId !== "new"
			) {
				const sharedUsageData = await fetchAPI(
					`/programs/${programId}/shared-exercises/usage`,
				).catch(() => null);
				if (sharedUsageData?.usage) {
					setSharedExerciseUsage(sharedUsageData.usage);
				}
			}

			toast.success(
				exportType === "exercise"
					? t("pbExerciseShareDone")
					: exportType === "week_share"
						? t("pbWeekShareDone")
						: t("pbExportDone"),
			);
			setIsExportDialogOpen(false);
			resetExportDialogState();
		} catch (error) {
			toast.error(
				`${t("pbExportError")}: ${error?.message || t("pbUnknownError")}`,
			);
		} finally {
			setSavingExport(false);
		}
	};

	// Helper: update days within the selected week
	const updateCurrentWeekDays = (updater) => {
		setProgram((prev) => ({
			...prev,
			weeks: (prev?.weeks || []).map((w, wi) =>
				wi === selectedWeekIndex
					? {
							...w,
							days: (() => {
								const updatedDays = updater(w.days || []);
								if (!prev?.is_dated) return updatedDays;
								return [...updatedDays].sort((a, b) => {
									const ad = a?.date || "9999-12-31";
									const bd = b?.date || "9999-12-31";
									return ad.localeCompare(bd);
								});
							})(),
						}
					: w,
			),
		}));
	};

	// Switching to dated gives a date to the days that have none, in the same
	// update: left null they rendered as "1 January 1970". Skeleton weeks are
	// loaded too, or their days would reach the database still without a date.
	const handleProgramModeChange = (mode) => {
		const isDated = mode === "dated";
		setProgram((prev) =>
			isDated
				? { ...prev, is_dated: true, weeks: fillMissingDayDates(prev?.weeks) }
				: { ...prev, is_dated: false },
		);
		if (isDated) {
			loadWeeksContent(
				(program?.weeks || [])
					.filter((week) => !isWeekContentLoaded(week))
					.map((week) => week.week_id),
			);
		}
	};

	const handleAddDay = () => {
		const days = currentWeekDays;
		let date = null;
		let dayNumber = null;

		if (program?.is_dated) {
			const lastDatedDay = [...days].reverse().find((d) => d?.date);
			if (lastDatedDay?.date) {
				const lastDate = parseLocalDate(lastDatedDay.date);
				lastDate.setDate(lastDate.getDate() + 1);
				date = toLocalISODate(lastDate);
			} else {
				const weekMonday = getWeekAnchorMonday(
					program?.weeks || [],
					selectedWeekIndex,
				);
				const existingOffsets = days
					.map((dayItem, idx) => inferWeekdayOffset(dayItem, idx))
					.filter((offset) => Number.isFinite(offset));
				const nextOffset =
					existingOffsets.length > 0
						? Math.min(6, Math.max(...existingOffsets) + 1)
						: 0;
				const targetDate = new Date(weekMonday);
				targetDate.setDate(targetDate.getDate() + nextOffset);
				date = toLocalISODate(targetDate);
			}
		} else {
			// Undated: progressive day number
			dayNumber = days.length + 1;
		}

		const day = {
			day_id: `day_${Date.now()}`,
			date,
			day_number: dayNumber,
			title: `${t("day")} ${days.length + 1}`,
			warmup: "",
			cooldown: "",
			notes: "",
			exercises: [],
		};

		updateCurrentWeekDays((prev) => [...prev, day]);

		setExpandedDays((prev) => ({ ...prev, [days.length]: true }));
		toast.success(t("pbDayAdded"));
	};

	const handleRemoveDay = (dayIndex) => {
		if (!window.confirm(t("pbDeleteDayConfirm"))) return;
		updateCurrentWeekDays((prev) => prev.filter((_, i) => i !== dayIndex));
		setExpandedDays((prev) => {
			const next = {};
			Object.entries(prev || {}).forEach(([key, value]) => {
				const idx = Number(key);
				if (!Number.isFinite(idx) || idx === dayIndex) return;
				next[idx > dayIndex ? idx - 1 : idx] = value;
			});
			return next;
		});
		setExpandedDaySetupPanels((prev) => {
			const next = {};
			Object.entries(prev || {}).forEach(([key, value]) => {
				const idx = Number(key);
				if (!Number.isFinite(idx) || idx === dayIndex) return;
				next[idx > dayIndex ? idx - 1 : idx] = value;
			});
			return next;
		});
		setNewSessionLabelByDay((prev) => {
			const next = {};
			Object.entries(prev || {}).forEach(([key, value]) => {
				const idx = Number(key);
				if (!Number.isFinite(idx) || idx === dayIndex) return;
				next[idx > dayIndex ? idx - 1 : idx] = value;
			});
			return next;
		});
		setSelectedDayIndex((prev) => {
			if (prev === null || prev === undefined) return prev;
			if (prev === dayIndex) return null;
			return prev > dayIndex ? prev - 1 : prev;
		});
		toast.success(t("pbDayRemoved"));
	};

	const handleDuplicateDay = (dayIndex) => {
		const dayToCopy = currentWeekDays[dayIndex];
		if (!dayToCopy) return;

		let date = null;
		let dayNumber = null;

		if (program?.is_dated && dayToCopy.date) {
			const newDate = parseLocalDate(dayToCopy.date);
			newDate.setDate(newDate.getDate() + 1);
			date = toLocalISODate(newDate);
		} else {
			dayNumber = currentWeekDays.length + 1;
		}

		const duplicatedDay = cloneDayAsIndependent(dayToCopy, {
			dateValue: date,
			dayNumberValue: dayNumber,
			titleValue: `${dayToCopy.title} (${t("pbCopySuffix")})`,
		});

		updateCurrentWeekDays((prev) => [
			...prev.slice(0, dayIndex + 1),
			duplicatedDay,
			...prev.slice(dayIndex + 1),
		]);
		toast.success(t("pbDayDuplicated"));
	};

	const isSharedReadonlyExercise = (exercise) =>
		exercise?.shared_readonly === true && exercise?.shared_is_origin !== true;

	// Open edit modal for an existing exercise
	const handleEditExercise = (dayIndex, exIndex) => {
		const exercise = currentWeekDays[dayIndex]?.exercises?.[exIndex];
		if (!exercise) return;
		if (isSharedReadonlyExercise(exercise)) {
			toast.error(
				t("pbSharedExerciseSourceOnly"),
			);
			return;
		}
		const daySessions = currentWeekDays[dayIndex]?.sessions || [];
		const matchingSessionIndex = daySessions.findIndex(
			(session) => session.session_id === exercise.session_id,
		);
		setSelectedDayIndex(dayIndex);
		setSelectedSessionIndex(
			matchingSessionIndex >= 0 ? matchingSessionIndex : null,
		);
		setEditingExerciseIndex(exIndex);
		setNewExercise({
			...exercise,
			set_rows: (exercise.set_rows || []).map((row) => ({
				...row,
				sets: row.sets_label || row.sets,
			})),
			allow_result_entry: exercise.allow_result_entry !== false,
			// Il valore dell'ESERCIZIO, non il default del programma. Prima
			// `true` (o assente) ricadeva su `getLeaderboardDefaultEnabled()`,
			// che su un programma individuale è `false`: la modale mostrava
			// sempre "Hidden" e il primo click non cambiava nulla (niente
			// dirty, niente Salva). Peggio: aprire e salvare senza toccare
			// nulla spegneva la visibilità di un esercizio che era acceso.
			// Il default del programma vale solo quando il campo non è mai
			// stato impostato.
			leaderboard_enabled:
				exercise.leaderboard_enabled === undefined ||
				exercise.leaderboard_enabled === null
					? getLeaderboardDefaultEnabled()
					: exercise.leaderboard_enabled !== false,
			emom_rounds: normalizeEmomRoundTemplates(exercise),
		});
		if (exercise.exercise_type === "strength" && exercise.exercise_name) {
			setExerciseSearchQuery(exercise.exercise_name);
		}
		setSupersetSearchQuery("");
		setIsAddExerciseOpen(true);

		// Lock the result-schema tipologia when this conditioning exercise already
		// has athlete results (label-only edits stay allowed).
		setEditingConditioningHasResults(false);
		if (
			exercise.exercise_type === "conditioning" &&
			programId &&
			programId !== "new"
		) {
			const dayId = currentWeekDays[dayIndex]?.day_id;
			if (dayId) {
				const params = new URLSearchParams({
					program_id: programId,
					day_id: dayId,
				});
				if (exercise.exercise_id)
					params.set("exercise_id", exercise.exercise_id);
				if (exercise.exercise_name)
					params.set("exercise_name", exercise.exercise_name);
				if (exercise.shared_exercise_id)
					params.set("shared_exercise_id", exercise.shared_exercise_id);
				fetchAPI(`/results/exercise-has-results?${params.toString()}`)
					.then((res) =>
						setEditingConditioningHasResults(Boolean(res?.has_results)),
					)
					.catch(() => setEditingConditioningHasResults(false));
			}
		}
	};

	const sanitizeExercisePayload = (exerciseInput) => {
		const sanitized = { ...exerciseInput };
		sanitized.allow_result_entry = sanitized.allow_result_entry !== false;
		sanitized.leaderboard_enabled = sanitized.leaderboard_enabled !== false;
		const exType = sanitized.exercise_type || "strength";
		const methodology = sanitized.methodology || "standard";
		const conditioningType = sanitized.conditioning_type || null;
		const runningType = sanitized.running_type || null;
		const swimType = sanitized.swim_type || null;
		const bikeType = sanitized.bike_type || null;
		const isErgMixed = exType === "erg" && isErgMixedType(bikeType);
		const isBikeLikeBenchmark =
			exType === "erg"
				? isErgBenchmarkSelection(bikeType)
				: bikeType === "benchmark";
		const runningIntervalTypes = ["ripetute", "salite"];
		const runningSegmentTypes = ["fartlek", "progressivo"];
		const swimSetTypes = ["intervals", "threshold", "sprint", "drill"];
		const swimSimpleTypes = ["endurance", "technique", "mixed", "recovery"];

		// Clear strength payload when current type is not strength
		if (exType !== "strength") {
			sanitized.methodology = "standard";
			sanitized.set_rows = [];
			sanitized.superset_exercises = [];
			sanitized.superset_rest_seconds = null;
			sanitized.superset_sets = null;
			sanitized.complex_exercises = [];
			sanitized.emom_intervals = null;
			sanitized.emom_interval = "";
			if (!(exType === "conditioning" && conditioningType === "emom")) {
				sanitized.emom_rounds = [];
			}
		}

		// Clear conditioning payload when current type is not conditioning (except ERG misto)
		if (exType !== "conditioning" && !isErgMixed) {
			sanitized.conditioning_type = null;
			sanitized.time_cap_seconds = null;
			sanitized.rounds = null;
			sanitized.interval_seconds = null;
			sanitized.emom_conditioning_interval = "";
			sanitized.hiit_cycles = null;
			sanitized.hiit_work_mode = "for_time";
			sanitized.hiit_work_seconds = null;
			sanitized.hiit_work_interval = "";
			sanitized.hiit_work_rounds = "";
			sanitized.hiit_work_custom_text = "";
			sanitized.hiit_rest_mode = "fixed";
			sanitized.hiit_rest_seconds = null;
			sanitized.hiit_rest_interval = "";
			sanitized.hiit_rest_ratio = "";
			sanitized.conditioning_exercises = [];
			sanitized.conditioning_free_text = "";
			sanitized.library_conditioning_id = null;
			sanitized.result_schema = [];
		}

		// Clear running payload when current type is not running
		if (exType !== "running") {
			sanitized.running_type = null;
			sanitized.interval_rows = [];
			sanitized.fartlek_segments = [];
			sanitized.fartlek_repeat = null;
		}

		// Clear swim payload when current type is not swim
		if (exType !== "swim") {
			sanitized.swim_type = null;
			sanitized.swim_sets = [];
			sanitized.swim_equipment = "";
		}

		// Clear bike/erg payload when current type is not bike-like
		if (exType !== "bike" && exType !== "erg") {
			sanitized.bike_type = null;
			sanitized.bike_intervals = [];
			sanitized.bike_power_target = "";
			sanitized.bike_cadence_target = "";
			sanitized.bike_terrain = null;
		}

		// Clear benchmark & pace reference for non-endurance types
		if (!["running", "bike", "swim", "erg"].includes(exType)) {
			sanitized.benchmark_exercise_id = null;
			sanitized.benchmark_exercise_name = null;
			sanitized.pace_reference_benchmark_id = null;
			sanitized.pace_reference_benchmark_name = null;
			sanitized.pace_reference_percentage = null;
		}

		if (exType === "strength") {
			sanitized.conditioning_type = null;
			sanitized.time_cap_seconds = null;
			sanitized.rounds = null;
			sanitized.interval_seconds = null;
			sanitized.emom_conditioning_interval = "";
			sanitized.hiit_cycles = null;
			sanitized.hiit_work_mode = "for_time";
			sanitized.hiit_work_seconds = null;
			sanitized.hiit_work_interval = "";
			sanitized.hiit_work_rounds = "";
			sanitized.hiit_work_custom_text = "";
			sanitized.hiit_rest_mode = "fixed";
			sanitized.hiit_rest_seconds = null;
			sanitized.hiit_rest_interval = "";
			sanitized.hiit_rest_ratio = "";
			sanitized.conditioning_exercises = [];
			sanitized.conditioning_free_text = "";
			if (methodology !== "emom") {
				sanitized.result_schema = [];
			}

			if (methodology !== "standard" && methodology !== "complex") {
				sanitized.set_rows = [];
			}
			if (methodology !== "superset") {
				sanitized.superset_exercises = [];
				sanitized.superset_rest_seconds = null;
				sanitized.superset_sets = null;
			}
			if (methodology !== "complex") {
				sanitized.complex_exercises = [];
			}
			if (methodology !== "emom") {
				sanitized.emom_intervals = null;
				sanitized.emom_interval = "";
				sanitized.emom_rounds = [];
			}
			if (methodology !== "custom") {
				sanitized.custom_workout_text = null;
			}
		}

		if (exType === "conditioning") {
			sanitized.methodology = "standard";
			sanitized.set_rows = [];
			sanitized.superset_exercises = [];
			sanitized.superset_rest_seconds = null;
			sanitized.superset_sets = null;
			sanitized.complex_exercises = [];
			sanitized.emom_intervals = null;
			sanitized.emom_interval = "";
			sanitized.custom_workout_text = null;

			if (conditioningType !== "emom") {
				sanitized.emom_rounds = [];
				sanitized.emom_conditioning_interval = "";
				sanitized.interval_seconds = null;
			}
			if (conditioningType !== "for_time" && conditioningType !== "emom") {
				sanitized.rounds = null;
			}
			if (conditioningType !== "amrap" && conditioningType !== "for_time") {
				sanitized.time_cap_seconds = null;
			}
			if (conditioningType !== "hiit") {
				sanitized.hiit_cycles = null;
				sanitized.hiit_work_mode = "for_time";
				sanitized.hiit_work_seconds = null;
				sanitized.hiit_work_interval = "";
				sanitized.hiit_work_rounds = "";
				sanitized.hiit_work_custom_text = "";
				sanitized.hiit_rest_mode = "fixed";
				sanitized.hiit_rest_seconds = null;
				sanitized.hiit_rest_interval = "";
				sanitized.hiit_rest_ratio = "";
			}
			if (conditioningType === "free" || conditioningType === "custom") {
				sanitized.conditioning_exercises = [];
			}
			if (conditioningType !== "free" && conditioningType !== "custom") {
				sanitized.conditioning_free_text = "";
			}
			if (conditioningType === "emom") {
				// EMOM uses round builder only; avoid keeping legacy conditioning rows.
				sanitized.conditioning_exercises = [];
			}
			if (conditioningType === "hiit") {
				sanitized.rounds = null;
				sanitized.time_cap_seconds = null;
				sanitized.interval_seconds = null;
				sanitized.emom_conditioning_interval = "";
				sanitized.emom_rounds = [];
				sanitized.conditioning_free_text = "";
			}
		}

		if (exType === "running") {
			// Clear benchmark or pace reference based on subtype
			if (runningType === "benchmark") {
				sanitized.pace_reference_benchmark_id = null;
				sanitized.pace_reference_benchmark_name = null;
				sanitized.pace_reference_percentage = null;
			} else {
				sanitized.benchmark_exercise_id = null;
				sanitized.benchmark_exercise_name = null;
			}

			// Running now uses per-target reference pace fields; keep legacy global
			// pace reference fields cleared to avoid stale cross-segment data.
			sanitized.pace_reference_benchmark_id = null;
			sanitized.pace_reference_benchmark_name = null;
			sanitized.pace_reference_percentage = null;
			sanitized.custom_workout_text =
				runningType === "custom" ? sanitized.custom_workout_text : null;

			sanitized.interval_rows = (
				Array.isArray(sanitized.interval_rows) ? sanitized.interval_rows : []
			).map((row) => {
				const paceMode = getRunningTargetPaceMode(row);
				const resolvedPace =
					paceMode === "reference"
						? resolveRunningReferencedPace(
								row?.target_pace_reference_pace,
								row?.target_pace_reference_percentage,
							)
						: "";
				return {
					...row,
					target_pace_mode: paceMode,
					target_pace: paceMode === "manual" ? row?.target_pace || "" : "",
					target_pace_resolved: resolvedPace,
				};
			});

			sanitized.fartlek_segments = (
				Array.isArray(sanitized.fartlek_segments)
					? sanitized.fartlek_segments
					: []
			).map((segment) => {
				const paceMode = getFartlekSegmentPaceMode(segment);
				const resolvedPace =
					paceMode === "reference"
						? resolveRunningReferencedPace(
								segment?.pace_reference_pace,
								segment?.pace_reference_percentage,
							)
						: "";
				return {
					...segment,
					pace_mode: paceMode,
					pace: paceMode === "manual" ? segment?.pace || "" : "",
					pace_resolved: resolvedPace,
				};
			});

			const runningPaceMode = getRunningTargetPaceMode(sanitized);
			sanitized.target_pace_mode = runningPaceMode;
			sanitized.target_pace =
				runningPaceMode === "manual" ? sanitized.target_pace || "" : "";
			sanitized.target_pace_resolved =
				runningPaceMode === "reference"
					? resolveRunningReferencedPace(
							sanitized.target_pace_reference_pace,
							sanitized.target_pace_reference_percentage,
						)
					: "";

			if (!runningIntervalTypes.includes(runningType)) {
				sanitized.interval_rows = [];
			}
			if (!runningSegmentTypes.includes(runningType)) {
				sanitized.fartlek_segments = [];
			}
			if (runningType !== "fartlek") {
				sanitized.fartlek_repeat = null;
			}

			if (runningType === "custom") {
				sanitized.distance_meters = null;
				sanitized.duration_minutes = null;
				sanitized.target_pace = "";
				sanitized.target_pace_mode = "manual";
				sanitized.target_pace_reference_benchmark_id = null;
				sanitized.target_pace_reference_benchmark_name = null;
				sanitized.target_pace_reference_percentage = null;
				sanitized.target_pace_reference_pace = "";
				sanitized.target_pace_resolved = "";
				sanitized.intervals = null;
				sanitized.interval_rows = [];
				sanitized.fartlek_segments = [];
				sanitized.fartlek_repeat = null;
				sanitized.warmup_description = "";
				sanitized.cooldown_description = "";
			}
		}

		if (exType === "swim") {
			// Clear benchmark or pace reference based on subtype
			if (swimType === "benchmark") {
				sanitized.pace_reference_benchmark_id = null;
				sanitized.pace_reference_benchmark_name = null;
				sanitized.pace_reference_percentage = null;
			} else {
				sanitized.benchmark_exercise_id = null;
				sanitized.benchmark_exercise_name = null;
			}
			sanitized.custom_workout_text =
				swimType === "custom" ? sanitized.custom_workout_text : null;

			if (!swimSetTypes.includes(swimType)) {
				sanitized.swim_sets = [];
			}
			if (!swimSimpleTypes.includes(swimType)) {
				sanitized.swim_equipment = "";
			}

			if (swimSetTypes.includes(swimType)) {
				sanitized.distance_meters = null;
				sanitized.duration_minutes = null;
				sanitized.target_pace = "";
			}
			if (swimType === "custom") {
				sanitized.distance_meters = null;
				sanitized.duration_minutes = null;
				sanitized.target_pace = "";
				sanitized.swim_sets = [];
				sanitized.swim_equipment = "";
				sanitized.warmup_description = "";
				sanitized.cooldown_description = "";
			}
		}

		if (exType === "bike" || exType === "erg") {
			// Clear benchmark or pace reference based on subtype
			if (isBikeLikeBenchmark) {
				sanitized.pace_reference_benchmark_id = null;
				sanitized.pace_reference_benchmark_name = null;
				sanitized.pace_reference_percentage = null;
			} else {
				sanitized.benchmark_exercise_id = null;
				sanitized.benchmark_exercise_name = null;
			}
			sanitized.custom_workout_text =
				bikeType === "custom" ? sanitized.custom_workout_text : null;

			if (!BIKE_LIKE_INTERVAL_TYPES.includes(bikeType)) {
				sanitized.bike_intervals = [];
			}
			if (!BIKE_LIKE_SIMPLE_TYPES.includes(bikeType)) {
				sanitized.bike_power_target = "";
				sanitized.bike_cadence_target = "";
			}

			if (BIKE_LIKE_INTERVAL_TYPES.includes(bikeType)) {
				sanitized.distance_meters = null;
				sanitized.duration_minutes = null;
				sanitized.target_pace = "";
			}
			if (bikeType === "custom") {
				sanitized.distance_meters = null;
				sanitized.duration_minutes = null;
				sanitized.target_pace = "";
				sanitized.bike_intervals = [];
				sanitized.bike_power_target = "";
				sanitized.bike_cadence_target = "";
				sanitized.bike_terrain = null;
				sanitized.warmup_description = "";
				sanitized.cooldown_description = "";
			}
			if (isErgMixed) {
				sanitized.bike_intervals = [];
				sanitized.bike_power_target = "";
				sanitized.bike_cadence_target = "";
				sanitized.distance_meters = null;
				sanitized.duration_minutes = null;
				sanitized.target_pace = "";
				sanitized.bike_terrain = null;
				sanitized.warmup_description = "";
				sanitized.cooldown_description = "";
				sanitized.benchmark_exercise_id = null;
				sanitized.benchmark_exercise_name = null;
				sanitized.pace_reference_benchmark_id = null;
				sanitized.pace_reference_benchmark_name = null;
				sanitized.pace_reference_percentage = null;
			}
			if (exType === "erg") {
				sanitized.bike_terrain = null;
			}
		}

		return sanitized;
	};

	const hasConfiguredEmomRounds = hasConfiguredEmomRoundTemplates(
		newExercise.emom_rounds,
	);

	// --- AI Parse strength custom (magic wand) ---
	const handleParseStrength = async () => {
		if (isCoachAiDisabled) {
			toast.error(aiDisabledMessage);
			return;
		}
		const text = newExercise.custom_workout_text?.trim();
		if (!text) {
			toast.error(t("pbAiWriteExerciseFirst"));
			return;
		}
		setAiStrengthParsing(true);
		try {
			const exerciseNames = exercises.map((e) => e.name).filter(Boolean);
			const res = await fetchAPI("/ai/parse-strength", {
				method: "POST",
				body: JSON.stringify({
					free_text: text,
					exercise_names: exerciseNames,
				}),
			});
			const r = res.result;
			if (!r || !r.methodology) {
				toast.error(t("pbAiParseFailed"));
				return;
			}

			// La nota e' una sola, su tutto l'esercizio: righe di serie e
			// movimenti non hanno un campo nota nel builder, quindi una nota
			// annidata finirebbe all'atleta senza che il coach possa leggerla
			// qui. Se il parser ne restituisce una, confluisce nella nota
			// dell'esercizio, che e' visibile e correggibile.
			const collectNestedNotes = (rows, label) =>
				(rows || [])
					.map((row, index) => {
						const note = String(row?.notes || "").trim();
						if (!note) return "";
						return (rows || []).length > 1
							? `${label ? `${label}${index + 1}` : index + 1}. ${note}`
							: note;
					})
					.filter(Boolean);

			const nestedNotes = [
				...collectNestedNotes(r.set_rows),
				...collectNestedNotes(r.superset_exercises, "A"),
			];

			const base = {
				exercise_type: "strength",
				methodology: r.methodology,
				exercise_name: r.exercise_name || "",
				notes: [String(r.notes || "").trim(), ...nestedNotes]
					.filter(Boolean)
					.join(" · "),
				allow_result_entry: true,
				leaderboard_enabled: getLeaderboardDefaultEnabled(),
				custom_workout_text: null,
			};

			if (r.methodology === "standard") {
				setNewExercise((prev) => ({
					...prev,
					...base,
					exercise_id: r.exercise_id || prev.exercise_id || "",
					source_exercise_id: r.exercise_id ? null : prev.source_exercise_id,
					set_rows: (r.set_rows || []).map((sr) =>
						createStrengthSetRow({
							sets: sr.sets || 1,
							reps: sr.reps || "",
							load_percentage: sr.load_percentage || null,
							rest_seconds: normalizeSmartRestInput(sr.rest_seconds) || null,
							rpe: sr.rpe || null,
							rir: sr.rir || null,
						}),
					),
				}));
			} else if (r.methodology === "superset") {
				setNewExercise((prev) => ({
					...prev,
					...base,
					superset_exercises: (r.superset_exercises || []).map((se) => ({
						exercise_id: se.exercise_id || "",
						exercise_name: se.exercise_name || "",
						sets: se.sets || 3,
						reps: se.reps || "",
						load_percentage: se.load_percentage || null,
						notes: "",
					})),
					superset_rest_seconds:
						normalizeSmartRestInput(r.superset_rest_seconds) || null,
					superset_sets: r.superset_sets || null,
				}));
			} else if (r.methodology === "emom") {
				setNewExercise((prev) => ({
					...prev,
					...base,
					emom_intervals: r.emom_intervals || null,
					emom_interval: r.emom_interval || "",
					emom_rounds: (r.emom_rounds || []).map((er) =>
						createEmomRoundTemplate({
							assignment_type: er.assignment_type || "cycle",
							input_mode: "library",
							items: (er.items || []).map((item) => ({
								exercise_id: item.exercise_id || "",
								exercise_name: item.exercise_name || "",
								reps: item.reps || "",
								load: item.load || "",
							})),
						}),
					),
				}));
			} else if (r.methodology === "complex") {
				setNewExercise((prev) => ({
					...prev,
					...base,
					complex_exercises: (r.complex_exercises || []).map((ce) => ({
						exercise_id: ce.exercise_id || "",
						exercise_name: ce.exercise_name || "",
						reps: ce.reps || "",
					})),
					set_rows: (r.set_rows || []).map((sr) =>
						createStrengthSetRow({
							sets: sr.sets || 1,
							reps: sr.reps || "",
							load_percentage: sr.load_percentage || null,
							rest_seconds: normalizeSmartRestInput(sr.rest_seconds) || null,
							rpe: sr.rpe || null,
							rir: sr.rir || null,
						}),
					),
				}));
			} else {
				// custom fallback — keep as custom text
				setNewExercise((prev) => ({
					...prev,
					exercise_type: "strength",
					methodology: "custom",
					exercise_name: r.exercise_name || prev.exercise_name || "",
					custom_workout_text: r.custom_workout_text || text,
					notes: r.notes || prev.notes || "",
				}));
			}

			const methodLabels = {
				standard: "Standard",
				superset: "Superset",
				emom: "EMOM",
				complex: "Complex",
				custom: "Custom",
			};
			toast.success(
				`${t("pbAiConvertedTo")} ${methodLabels[r.methodology] || r.methodology}!`,
			);
		} catch (error) {
			toast.error(error.message || t("pbAiParseError"));
		} finally {
			setAiStrengthParsing(false);
		}
	};

	const handleParseEnduranceCustom = async (mode) => {
		if (isCoachAiDisabled) {
			toast.error(aiDisabledMessage);
			return;
		}
		const text = newExercise.custom_workout_text?.trim();
		if (!text) {
			toast.error(t("pbAiWriteWorkoutFirst"));
			return;
		}

		setAiEnduranceParsing(true);
		try {
			const res = await fetchAPI("/ai/parse-endurance-custom", {
				method: "POST",
				body: JSON.stringify({
					mode,
					free_text: text,
				}),
			});
			const parsed = res?.result;
			if (!parsed) {
				toast.error(t("pbAiParseFailed"));
				return;
			}

			if (mode === "running") {
				const parsedRunningType = parsed.running_type || "custom";
				const runningLabel =
					RUNNING_TYPES.find((x) => x.value === parsedRunningType)?.label ||
					"Personalizzato";

				setNewExercise((prev) => ({
					...prev,
					exercise_type: "running",
					running_type: parsedRunningType,
					exercise_name: parsed.exercise_name || `Running - ${runningLabel}`,
					custom_workout_text: parsed.custom_workout_text || text,
					notes: parsed.notes || prev.notes || "",
				}));

				toast.success(`${t("pbAiInterpretedAs")} ${runningLabel}`);
				return;
			}

			const parsedBikeType = parsed.bike_type || "custom";
			const ergLabel = getErgTypeLabel(parsedBikeType);

			setNewExercise((prev) => ({
				...prev,
				exercise_type: "erg",
				bike_type: parsedBikeType,
				bike_terrain: null,
				exercise_name: parsed.exercise_name || `Erg - ${ergLabel}`,
				custom_workout_text: parsed.custom_workout_text || text,
				notes: parsed.notes || prev.notes || "",
			}));

			toast.success(`${t("pbAiInterpretedAs")} ${ergLabel}`);
		} catch (error) {
			toast.error(error.message || t("pbAiParseError"));
		} finally {
			setAiEnduranceParsing(false);
		}
	};

	const handleAddExercise = () => {
		const exType = newExercise.exercise_type || "strength";
		const methodology = newExercise.methodology || "standard";
		const daySessions = currentWeekDays[selectedDayIndex]?.sessions || [];
		const selectedSession =
			selectedSessionIndex !== null ? daySessions[selectedSessionIndex] : null;
		const selectedSessionId = selectedSession?.session_id || null;
		const serializedEmomRounds =
			(exType === "strength" && methodology === "emom") ||
			(exType === "conditioning" && newExercise.conditioning_type === "emom")
				? serializeEmomRoundTemplates(newExercise.emom_rounds)
				: [];

		// Il tiro non passa dalla libreria esercizi: il drill si nomina, e il
		// nome è l'unica cosa davvero obbligatoria — tutto il resto della
		// prescrizione è opzionale perché un coach può fissare solo il focus.
		if (
			(exType === "dry_fire" || exType === "live_fire") &&
			!String(newExercise.exercise_name || "").trim()
		) {
			toast.error(t("pbNameTheDrill"));
			return;
		}

		// For strength standard, require exercise from library
		if (
			exType === "strength" &&
			methodology === "standard" &&
			!newExercise.exercise_id
		) {
			toast.error(t("pbSelectExercise"));
			return;
		}

		// For complex, require at least 2 exercises in sequence
		if (exType === "strength" && methodology === "complex") {
			if ((newExercise.complex_exercises || []).length < 2) {
				toast.error(t("pbComplexNeedsTwo"));
				return;
			}
			// A complex has no max of its own: a % must say which movement.
			if (complexMissingLoadReference(newExercise)) {
				toast.error(t("complexLoadReferenceRequired"));
				return;
			}
			const generatedComplexName = (newExercise.complex_exercises || [])
				.map((item) => item?.exercise_name)
				.filter(Boolean)
				.join(" + ");
			newExercise.exercise_name = generatedComplexName || "Strength Complex";
		}

		// For superset, require at least 2 exercises
		if (exType === "strength" && methodology === "superset") {
			if (
				!newExercise.superset_exercises ||
				newExercise.superset_exercises.length < 2
			) {
				toast.error(t("pbSupersetNeedsTwo"));
				return;
			}
			// Auto-generate name from superset exercises
			if (!newExercise.exercise_name) {
				newExercise.exercise_name = newExercise.superset_exercises
					.map((se) => se.exercise_name)
					.join(" + ");
			}
		}

		// For EMOM, require intervals and at least 1 movement
		if (exType === "strength" && methodology === "emom") {
			if (!newExercise.emom_intervals || newExercise.emom_intervals <= 0) {
				toast.error(t("pbEnterSetCount"));
				return;
			}
			if (!newExercise.emom_interval) {
				toast.error(t("intervalDurationRequired"));
				return;
			}
			if (!hasConfiguredEmomRounds) {
				toast.error(t("pbAddAtLeastOneEmomRound"));
				return;
			}
			// Il coach può dare un titolo al blocco, come nel conditioning. Se non
			// lo fa — o se ha lasciato quello che generiamo noi — il titolo resta
			// la sola sigla: round e cadenza li scrive già la riga sotto.
			newExercise.exercise_name = isEmomAutoName(newExercise.exercise_name)
				? buildEmomAutoName()
				: newExercise.exercise_name.trim();
		}

		if (exType === "strength" && methodology === "custom") {
			if (!newExercise.custom_workout_text?.trim()) {
				toast.error(t("pbEnterCustomDescription"));
				return;
			}
			if (!newExercise.exercise_name?.trim()) {
				newExercise.exercise_name = "Forza - Personalizzato";
			}
		}

		// For running/swim/bike/erg, auto-generate name if not set
		if (
			["running", "swim", "bike", "erg"].includes(exType) &&
			!newExercise.exercise_name
		) {
			const typeLabel =
				EXERCISE_TYPES.find((x) => x.value === exType)?.label || exType;
			const subLabel =
				exType === "running" && newExercise.running_type
					? ` - ${RUNNING_TYPES.find((x) => x.value === newExercise.running_type)?.label || newExercise.running_type}`
					: exType === "swim" && newExercise.swim_type
						? ` - ${SWIM_TYPES.find((x) => x.value === newExercise.swim_type)?.label || newExercise.swim_type}`
						: exType === "bike" && newExercise.bike_type
							? ` - ${BIKE_TYPES.find((x) => x.value === newExercise.bike_type)?.label || newExercise.bike_type}`
							: exType === "erg" && newExercise.bike_type
								? ` - ${getErgTypeLabel(newExercise.bike_type)}`
								: "";
			newExercise.exercise_name = `${typeLabel}${subLabel}`;
		}
		// For conditioning, require type
		if (exType === "conditioning" && !newExercise.conditioning_type) {
			toast.error(t("pbSelectConditioningType"));
			return;
		}
		if (exType === "conditioning") {
			const customConditioningName =
				typeof newExercise.exercise_name === "string"
					? newExercise.exercise_name.trim()
					: "";
			const normalizedCustomConditioningName = customConditioningName
				.toLowerCase()
				.replace(/\s+/g, " ")
				.trim();
			const isGenericCustomConditioningName = [
				"custom",
				"conditioning",
				"conditioning-custom",
				"conditioning - custom",
			].includes(normalizedCustomConditioningName);
			const isCustomConditioningType =
				newExercise.conditioning_type === "free" ||
				newExercise.conditioning_type === "custom";
			const stripTrailingCustom = (name) =>
				(name || "").replace(/\s*-\s*custom\s*$/i, "").trim();
			const defaultConditioningName = "CONDITIONING";

			newExercise.exercise_name = isCustomConditioningType
				? isGenericCustomConditioningName
					? defaultConditioningName
					: stripTrailingCustom(customConditioningName) ||
						defaultConditioningName
				: customConditioningName
					? customConditioningName
					: formatConditioningDisplayName("", newExercise.conditioning_type);

			// Convert emom_conditioning_interval (MM:SS string) to interval_seconds (number)
			if (
				newExercise.conditioning_type === "emom" &&
				newExercise.emom_conditioning_interval
			) {
				const intervalStr = newExercise.emom_conditioning_interval.trim();
				if (intervalStr.includes(":")) {
					const [minStr, secStr] = intervalStr.split(":");
					const mins = parseInt(minStr || "0") || 0;
					const secs = parseInt(secStr || "0") || 0;
					newExercise.interval_seconds = mins * 60 + secs;
				} else if (/^\d+$/.test(intervalStr)) {
					// If just a number, assume seconds
					newExercise.interval_seconds = parseInt(intervalStr) || null;
				}
			}

			const isCustomConditioningWithoutSchema =
				(newExercise.conditioning_type === "free" ||
					newExercise.conditioning_type === "custom") &&
				newExercise.allow_result_entry !== false &&
				(!Array.isArray(newExercise.result_schema) ||
					newExercise.result_schema.length === 0);

			if (isCustomConditioningWithoutSchema) {
				if (!allowCustomConditioningWithoutSchemaRef.current) {
					setIsConditioningSchemaConfirmOpen(true);
					return;
				}

				allowCustomConditioningWithoutSchemaRef.current = false;
				toast.info(t("pbSavedWithoutSchema"));
			}
		}

		const finalExercise = ensureExerciseIdIntegrity(
			sanitizeExercisePayload({
				...newExercise,
				emom_rounds: serializedEmomRounds,
			}),
			libraryExerciseNameById,
		);

		if (editingExerciseIndex !== null) {
			// Edit mode: update in-place
			updateCurrentWeekDays((prev) =>
				prev.map((day, i) =>
					i === selectedDayIndex
						? {
								...day,
								exercises: (day.exercises || []).map((ex, j) =>
									j === editingExerciseIndex
										? {
												...finalExercise,
												order: ex.order,
												session_id: selectedSessionId,
												session_order: null,
												shared_exercise_id: ex.shared_exercise_id || null,
												shared_origin_program_id:
													ex.shared_origin_program_id || null,
												shared_origin_week_id: ex.shared_origin_week_id || null,
												shared_origin_day_id: ex.shared_origin_day_id || null,
												shared_origin_exercise_id:
													ex.shared_origin_exercise_id || null,
												shared_is_origin: ex.shared_is_origin === true,
												shared_readonly: ex.shared_readonly === true,
											}
										: ex,
								),
							}
						: day,
				),
			);
		} else {
			// Add mode: append
			const currentDayExercises =
				currentWeekDays[selectedDayIndex]?.exercises || [];
			const sessionExerciseCount = selectedSessionId
				? currentDayExercises.filter(
						(exercise) => exercise.session_id === selectedSessionId,
					).length
				: null;
			const exercise = {
				...finalExercise,
				allow_result_entry: finalExercise.allow_result_entry !== false,
				leaderboard_enabled: finalExercise.leaderboard_enabled !== false,
				exercise_id:
					finalExercise.exercise_id ||
					`ex_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
				order: currentDayExercises.length,
				session_id: selectedSessionId,
				session_order: sessionExerciseCount,
			};

			updateCurrentWeekDays((prev) =>
				prev.map((day, i) =>
					i === selectedDayIndex
						? { ...day, exercises: [...(day.exercises || []), exercise] }
						: day,
				),
			);
		}

		setNewExercise(createInitialExerciseState());
		setIsAddExerciseOpen(false);
		setEditingExerciseIndex(null);
		setSelectedSessionIndex(null);
		toast.success(
			editingExerciseIndex !== null
				? t("pbExerciseUpdated")
				: t("pbExerciseAdded"),
		);
	};

	const handleQuickSaveConditioningTemplate = async (draft) => {
		if (isSavingConditioningTemplate) return null;

		const rawName =
			typeof draft?.exercise_name === "string"
				? draft.exercise_name.trim()
				: "";
		const fallbackName = formatConditioningDisplayName(
			"",
			draft?.conditioning_type,
		);
		const templateName = rawName || fallbackName;

		if (!templateName) {
			toast.error(t("pbEnterNameBeforeSaving"));
			return null;
		}

		if (!draft?.conditioning_type) {
			toast.error(t("pbSelectConditioningType"));
			return null;
		}

		setIsSavingConditioningTemplate(true);
		try {
			const payload = {
				name: templateName,
				conditioning_type: draft.conditioning_type,
				time_cap_seconds: draft.time_cap_seconds,
				rounds: normalizeConditioningRounds(draft.rounds),
				interval_seconds: draft.interval_seconds,
				hiit_cycles: draft.hiit_cycles,
				hiit_work_mode: draft.hiit_work_mode,
				hiit_work_seconds: draft.hiit_work_seconds,
				hiit_work_interval: draft.hiit_work_interval,
				hiit_work_rounds: draft.hiit_work_rounds,
				hiit_work_custom_text: draft.hiit_work_custom_text,
				hiit_rest_mode: draft.hiit_rest_mode,
				hiit_rest_seconds: draft.hiit_rest_seconds,
				hiit_rest_interval: draft.hiit_rest_interval,
				hiit_rest_ratio: draft.hiit_rest_ratio,
				conditioning_exercises: Array.isArray(draft.conditioning_exercises)
					? draft.conditioning_exercises
					: [],
				conditioning_free_text: draft.conditioning_free_text || "",
				emom_rounds: Array.isArray(draft.emom_rounds) ? draft.emom_rounds : [],
				emom_conditioning_interval: draft.emom_conditioning_interval || "",
				result_schema: Array.isArray(draft.result_schema)
					? draft.result_schema
					: [],
				notes: draft.notes || "",
				library_scope: "personal",
			};

			const created = await fetchAPI("/conditioning-library", {
				method: "POST",
				body: JSON.stringify(payload),
			});

			if (!created?.conditioning_id) {
				throw new Error(t("pbTemplateNotCreated"));
			}

			setConditioningTemplates((prev) => {
				const next = [
					...(Array.isArray(prev) ? prev : []).filter(
						(item) => item.conditioning_id !== created.conditioning_id,
					),
					created,
				];
				return next.sort((a, b) =>
					String(a?.name || "").localeCompare(String(b?.name || "")),
				);
			});

			setNewExercise((prev) => ({
				...prev,
				exercise_name: created.name || prev.exercise_name,
				library_conditioning_id: created.conditioning_id,
			}));

			toast.success(t("pbConditioningSavedToLibrary"));
			return created;
		} catch (error) {
			toast.error(error?.message || t("pbLibrarySaveError"));
			return null;
		} finally {
			setIsSavingConditioningTemplate(false);
		}
	};


	const handleRemoveExercise = (dayIndex, exerciseIndex) => {
		updateCurrentWeekDays((prev) =>
			prev.map((day, i) =>
				i === dayIndex
					? {
							...day,
							exercises: (day.exercises || []).filter(
								(_, j) => j !== exerciseIndex,
							),
						}
					: day,
			),
		);
	};

	const handleAssignExerciseSession = (
		dayIndex,
		exerciseIndex,
		sessionIdValue,
	) => {
		const sessionId = sessionIdValue === "none" ? null : sessionIdValue;
		updateCurrentWeekDays((prev) =>
			prev.map((day, i) =>
				i === dayIndex
					? {
							...day,
							exercises: (day.exercises || []).map((exercise, j) =>
								j === exerciseIndex
									? {
											...exercise,
											session_id: sessionId,
											session_order: null,
										}
									: exercise,
							),
						}
					: day,
			),
		);
	};

	const handleUpdateSharedLocalNote = (dayIndex, exerciseIndex, noteValue) => {
		updateCurrentWeekDays((prev) =>
			prev.map((day, i) =>
				i === dayIndex
					? {
							...day,
							exercises: (day.exercises || []).map((exercise, j) => {
								if (j !== exerciseIndex) return exercise;
								if (!isSharedReadonlyExercise(exercise)) return exercise;

								return {
									...exercise,
									shared_local_note: noteValue,
								};
							}),
						}
					: day,
			),
		);
	};

	// Where the clipboard payload came from, shown in the clipboard chip so the
	// coach can tell at a glance what is about to be pasted — especially when the
	// copy happened in a different browser tab.
	const buildClipboardSource = (dayIndex) => ({
		programTitle: (program?.title || "").trim(),
		weekLabel:
			currentWeek?.label || `${t("weekShort")} ${selectedWeekIndex + 1}`,
		dayTitle: currentWeekDays[dayIndex]?.title || "",
	});

	const notifyClipboardWrite = (persisted, count) => {
		toast.success(
			count === 1
				? t("pbClipboardCopiedOne")
				: `${count} ${t("pbClipboardCopiedMany")}`,
		);
		if (!persisted) {
			toast.info(t("pbClipboardNotShared"));
		}
	};

	const handleCopyExercise = (dayIndex, exerciseIndex) => {
		const exercise = currentWeekDays[dayIndex]?.exercises?.[exerciseIndex];
		if (!exercise) return;
		const { persisted } = copyToClipboard({
			kind: "exercises",
			items: [{ ...exercise }],
			source: buildClipboardSource(dayIndex),
		});
		notifyClipboardWrite(persisted, 1);
	};

	// Copies every exercise of a day. Shared read-only exercises are skipped:
	// they cannot be copied individually either (their copy button is hidden).
	const handleCopyDay = (dayIndex) => {
		const dayExercises = (currentWeekDays[dayIndex]?.exercises || []).filter(
			(exercise) => !isSharedReadonlyExercise(exercise),
		);
		if (dayExercises.length === 0) {
			toast.error(t("pbClipboardNothingToCopy"));
			return;
		}
		const { persisted } = copyToClipboard({
			kind: "day",
			items: dayExercises.map((exercise) => ({ ...exercise })),
			source: buildClipboardSource(dayIndex),
		});
		notifyClipboardWrite(persisted, dayExercises.length);
	};

	// Turn the reference into the editor and vice versa. This covers "I am
	// editing two programs" without ever mounting two editors (ANALYSIS §4/§9):
	// we navigate to the other program and hand it the current one as reference.
	const performSwapWithReference = useCallback(
		(referenceProgramId, referenceWeekIndex) => {
			pendingOpenWeekRef.current =
				typeof referenceWeekIndex === "number" ? referenceWeekIndex : null;
			navigate(`/programs/${referenceProgramId}`, {
				state: {
					pbReference: {
						programId,
						weekIndex: selectedWeekIndex,
					},
					// Preserve where "back to programs" should return to.
					programBuilderReturn: location.state?.programBuilderReturn,
				},
			});
			setPendingSwap(null);
		},
		[navigate, programId, selectedWeekIndex, location.state],
	);

	const handleSwapWithReference = useCallback(
		(referenceProgramId, referenceWeekIndex) => {
			if (!referenceProgramId || referenceProgramId === programId) return;
			if (programId === "new") {
				toast.error(t("pbSwapSaveFirst"));
				return;
			}
			// Never lose unsaved work silently: ask, then save, then swap.
			if (isDirtyRef.current) {
				setPendingSwap({ referenceProgramId, referenceWeekIndex });
				return;
			}
			performSwapWithReference(referenceProgramId, referenceWeekIndex);
		},
		[programId, t, performSwapWithReference],
	);

	// Jump the editor to a cell picked in the progression matrix. Only the
	// viewport moves — nothing is modified.
	// Stable identity so the memoised ProgressionMatrix is not invalidated on
	// every render of the builder.
	const handleNavigateFromReference = useCallback((weekIndex, dayIndex) => {
		setSelectedWeekIndex(weekIndex);
		setSelectedDayIndex(dayIndex);
		setExpandedDays((prev) => ({ ...prev, [dayIndex]: true }));
		// The target day only exists in the DOM after the new week has rendered.
		setTimeout(() => {
			document
				.querySelector(`[data-testid="day-${dayIndex}"]`)
				?.scrollIntoView({ behavior: "smooth", block: "start" });
		}, 80);
	}, []);

	// From the column overview to the editor, landed on the clicked day. The
	// editor mounts on the view change; the navigation helper already waits a
	// tick before scrolling for exactly this kind of late-mounting target.
	const handleOpenDayFromOverview = (dayIndex) => {
		setWeekViewAndPersist("editor");
		handleNavigateFromReference(selectedWeekIndex, dayIndex);
	};

	// Copy coming from the read-only reference panel: it already carries its own
	// source metadata (which may be a different program), so it does not go
	// through buildClipboardSource.
	const handleCopyFromReference = ({ kind, items, source }) => {
		const list = Array.isArray(items) ? items : [];
		if (list.length === 0) return;
		const { persisted } = copyToClipboard({ kind, items: list, source });
		notifyClipboardWrite(persisted, list.length);
	};

	// Day currently under an exercise dragged in from the reference panel.
	const [dragOverDropDayIndex, setDragOverDropDayIndex] = useState(null);

	// Without this, an exercise can only be dropped on a day that already happens
	// to be on screen: HTML5 drag & drop does not scroll containers by itself.
	useEffect(() => {
		const EDGE_ZONE_PX = 120;
		const MAX_SPEED_PX = 28;
		let pointerY = null;
		let animationFrame = null;

		const stopAutoScroll = () => {
			pointerY = null;
			if (animationFrame !== null) {
				cancelAnimationFrame(animationFrame);
				animationFrame = null;
			}
		};

		const scrollAtEdge = () => {
			animationFrame = null;
			if (pointerY === null) return;

			const scroller = document.getElementById("main-scroll-area");
			if (!scroller) {
				stopAutoScroll();
				return;
			}

			const bounds = scroller.getBoundingClientRect();
			const topDistance = pointerY - bounds.top;
			const bottomDistance = bounds.bottom - pointerY;
			let speed = 0;

			if (topDistance < EDGE_ZONE_PX) {
				const intensity = Math.min(
					1,
					Math.max(0, (EDGE_ZONE_PX - topDistance) / EDGE_ZONE_PX),
				);
				speed = -MAX_SPEED_PX * intensity * intensity;
			} else if (bottomDistance < EDGE_ZONE_PX) {
				const intensity = Math.min(
					1,
					Math.max(0, (EDGE_ZONE_PX - bottomDistance) / EDGE_ZONE_PX),
				);
				speed = MAX_SPEED_PX * intensity * intensity;
			}

			if (speed !== 0) {
				scroller.scrollBy({ top: speed });
				animationFrame = requestAnimationFrame(scrollAtEdge);
			}
		};

		const handleDragOver = (event) => {
			const isExerciseDrag =
				dragExercise.current !== null ||
				hasExerciseDragPayload(event.dataTransfer);
			if (!isExerciseDrag) return;
			pointerY = event.clientY;
			if (animationFrame === null) {
				animationFrame = requestAnimationFrame(scrollAtEdge);
			}
		};

		const clearDragState = () => {
			stopAutoScroll();
			setDragOverDropDayIndex(null);
		};

		document.addEventListener("dragover", handleDragOver);
		document.addEventListener("dragend", clearDragState);
		document.addEventListener("drop", clearDragState);
		window.addEventListener("blur", clearDragState);
		return () => {
			stopAutoScroll();
			document.removeEventListener("dragover", handleDragOver);
			document.removeEventListener("dragend", clearDragState);
			document.removeEventListener("drop", clearDragState);
			window.removeEventListener("blur", clearDragState);
		};
	}, []);

	// Returns true when the drop was ours, so the caller knows not to fall
	// through to the editor's own day-reordering drop handler.
	const handleDropExercisesOnDay = (event, dayIndex) => {
		const items = readExerciseDragPayload(event.dataTransfer);
		setDragOverDropDayIndex(null);
		if (!items) return false;
		event.preventDefault();
		event.stopPropagation();
		insertExercisesIntoDay(dayIndex, items);
		toast.success(
			items.length === 1
				? t("pbClipboardPastedOne")
				: `${items.length} ${t("pbClipboardPastedMany")}`,
		);
		return true;
	};

	const insertExercisesIntoDay = (dayIndex, items) => {
		updateCurrentWeekDays((prev) =>
			prev.map((day, i) => {
				if (i !== dayIndex) return day;
				const existing = day.exercises || [];
				return {
					...day,
					exercises: [
						...existing,
						...items.map((item, itemIndex) =>
							cloneExerciseAsIndependent(item, {
								orderValue: existing.length + itemIndex,
								sessionIdValue: null,
								sessionOrderValue: null,
							}),
						),
					],
				};
			}),
		);
		setExpandedDays((prev) => ({ ...prev, [dayIndex]: true }));
	};

	const handlePasteExercises = (dayIndex) => {
		const items = clipboard?.items || [];
		if (items.length === 0) return;
		insertExercisesIntoDay(dayIndex, items);
		toast.success(
			items.length === 1
				? t("pbClipboardPastedOne")
				: `${items.length} ${t("pbClipboardPastedMany")}`,
		);
	};

	// Touch replacement for the drag handle, which is hidden below md.
	const handleMoveExercise = (dayIndex, exerciseIndex, direction) => {
		const target = exerciseIndex + direction;
		updateCurrentWeekDays((prev) =>
			prev.map((day, i) => {
				if (i !== dayIndex) return day;
				const exercises = [...(day.exercises || [])];
				if (target < 0 || target >= exercises.length) return day;
				const [moved] = exercises.splice(exerciseIndex, 1);
				exercises.splice(target, 0, moved);
				return {
					...day,
					exercises: exercises.map((exercise, index) => ({
						...exercise,
						order: index,
					})),
				};
			}),
		);
	};

	const handleDuplicateExerciseToNextWeek = (dayIndex, exerciseIndex) => {
		const exercise = currentWeekDays[dayIndex]?.exercises?.[exerciseIndex];
		if (!exercise) return;
		const weeks = program?.weeks || [];
		const nextWeekIndex = selectedWeekIndex + 1;
		if (nextWeekIndex >= weeks.length) {
			toast.error(t("noNextWeek"));
			return;
		}
		const nextWeek = weeks[nextWeekIndex];
		const nextWeekDays = nextWeek?.days || [];
		const targetDayIndex = Math.min(dayIndex, nextWeekDays.length - 1);
		if (targetDayIndex < 0 || nextWeekDays.length === 0) {
			toast.error(t("noDaysNextWeek"));
			return;
		}
		setProgram((prev) => ({
			...prev,
			weeks: (prev?.weeks || []).map((w, wi) =>
				wi === nextWeekIndex
					? {
							...w,
							days: (w.days || []).map((day, di) =>
								di === targetDayIndex
									? {
											...day,
											exercises: [
												...(day.exercises || []),
												cloneExerciseAsIndependent(exercise, {
													orderValue: (day.exercises || []).length,
													sessionIdValue: null,
													sessionOrderValue: null,
												}),
											],
										}
									: day,
							),
						}
					: w,
			),
		}));
		const targetDay = nextWeekDays[targetDayIndex];
		const dayLabel = localizeDayLabel(targetDay?.title, t, targetDayIndex);
		toast.success(
			`${t("pbDuplicatedIn")} ${nextWeek.label || t("pbNextWeekShort")} → ${dayLabel}`,
		);
	};

	const handleUpdateExercise = (dayIndex, exerciseIndex, field, value) => {
		updateCurrentWeekDays((prev) =>
			prev.map((day, i) =>
				i === dayIndex
					? {
							...day,
							exercises: (day.exercises || []).map((ex, j) =>
								j === exerciseIndex ? { ...ex, [field]: value } : ex,
							),
						}
					: day,
			),
		);
	};

	const handleToggleExerciseResultEntry = (dayIndex, exerciseIndex) => {
		const exercise = currentWeekDays[dayIndex]?.exercises?.[exerciseIndex];
		if (!exercise) return;
		if (isSharedReadonlyExercise(exercise)) {
			toast.error(
				t("pbSharedExerciseLockedTarget"),
			);
			return;
		}
		const isEnabled = exercise.allow_result_entry !== false;
		handleUpdateExercise(
			dayIndex,
			exerciseIndex,
			"allow_result_entry",
			!isEnabled,
		);
	};

	const handleToggleExerciseLeaderboard = (dayIndex, exerciseIndex) => {
		const exercise = currentWeekDays[dayIndex]?.exercises?.[exerciseIndex];
		if (!exercise) return;
		if (isSharedReadonlyExercise(exercise)) {
			toast.error(
				t("pbSharedExerciseLockedTarget"),
			);
			return;
		}
		const isEnabled = exercise.leaderboard_enabled !== false;
		handleUpdateExercise(
			dayIndex,
			exerciseIndex,
			"leaderboard_enabled",
			!isEnabled,
		);
	};

	const handleAddSetRow = (dayIndex, exerciseIndex) => {
		updateCurrentWeekDays((prev) =>
			prev.map((day, i) =>
				i === dayIndex
					? {
							...day,
							exercises: (day.exercises || []).map((ex, j) => {
								if (j !== exerciseIndex) return ex;
								return {
									...ex,
									set_rows: [...(ex.set_rows || []), createStrengthSetRow()],
								};
							}),
						}
					: day,
			),
		);
	};

	const handleRemoveSetRow = (dayIndex, exerciseIndex, rowIndex) => {
		updateCurrentWeekDays((prev) =>
			prev.map((day, i) =>
				i === dayIndex
					? {
							...day,
							exercises: (day.exercises || []).map((ex, j) => {
								if (j !== exerciseIndex) return ex;
								const newRows = (ex.set_rows || []).filter(
									(_, ri) => ri !== rowIndex,
								);
								if (newRows.length === 0) {
									return {
										...ex,
										set_rows: [createStrengthSetRow()],
									};
								}
								return { ...ex, set_rows: newRows };
							}),
						}
					: day,
			),
		);
	};

	const handleUpdateSetRow = (
		dayIndex,
		exerciseIndex,
		rowIndex,
		field,
		value,
	) => {
		const isEmptyLoadExpression =
			field === "load_percentage" && String(value ?? "").trim() === "";
		updateCurrentWeekDays((prev) =>
			prev.map((day, i) =>
				i === dayIndex
					? {
							...day,
							exercises: (day.exercises || []).map((ex, j) => {
								if (j !== exerciseIndex) return ex;
								return {
									...ex,
									set_rows: (ex.set_rows || []).map((row, ri) =>
										ri === rowIndex
											? {
													...row,
													[field]: value,
													...(isEmptyLoadExpression
														? {
																load_reference_exercise_id: null,
																load_reference_exercise_name: null,
															}
														: {}),
												}
											: row,
									),
								};
							}),
						}
					: day,
			),
		);
	};

	const handleUpdateSetRowsLoadReference = (
		dayIndex,
		exerciseIndex,
		referenceId,
		referenceName,
	) => {
		updateCurrentWeekDays((prev) =>
			prev.map((day, i) =>
				i === dayIndex
					? {
							...day,
							exercises: (day.exercises || []).map((ex, j) => {
								if (j !== exerciseIndex) return ex;
								return {
									...ex,
									set_rows: (ex.set_rows || []).map((row) => ({
										...row,
										load_reference_exercise_id: referenceId,
										load_reference_exercise_name: referenceName,
									})),
								};
							}),
						}
					: day,
			),
		);
	};

	// The reference dialog serves both a row already in the day and the
	// complex being composed in the Add-Exercise dialog.
	const applyPendingLoadReference = (referenceId, referenceName) => {
		if (!pendingReferenceUpdate) return;
		if (pendingReferenceUpdate.target === "newExercise") {
			setNewExercise((prev) => ({
				...prev,
				set_rows: (prev.set_rows || []).map((row) => ({
					...row,
					load_reference_exercise_id: referenceId,
					load_reference_exercise_name: referenceName,
				})),
			}));
		} else {
			const { dayIndex, exIndex } = pendingReferenceUpdate;
			handleUpdateSetRowsLoadReference(
				dayIndex,
				exIndex,
				referenceId,
				referenceName,
			);
		}
		setIsReferenceExerciseDialogOpen(false);
		setPendingReferenceUpdate(null);
	};

	const openLoadReferenceDialog = (target) => {
		setPendingReferenceUpdate(target);
		setIsReferenceExerciseDialogOpen(true);
		setReferenceExerciseSearchQuery("");
	};

	const handleUpdateDay = (dayIndex, field, value) => {
		updateCurrentWeekDays((prev) =>
			prev.map((day, i) => (i === dayIndex ? { ...day, [field]: value } : day)),
		);
	};

	const handleAddSession = (dayIndex, sessionLabel) => {
		const sessions = currentWeekDays[dayIndex]?.sessions || [];
		const trimmedLabel = String(sessionLabel || "").trim();
		const finalLabel =
			trimmedLabel || `${t("sessionLabel")} ${sessions.length + 1}`;
		const newSession = {
			session_id: `sess-${Date.now()}`,
			label: finalLabel,
			order: sessions.length,
			exercises: [],
			notes: "",
		};

		updateCurrentWeekDays((prev) =>
			prev.map((day, i) =>
				i === dayIndex
					? { ...day, sessions: [...(day.sessions || []), newSession] }
					: day,
			),
		);
	};

	const handleMoveSession = (dayIndex, sessionIndex, direction) => {
		updateCurrentWeekDays((prev) =>
			prev.map((day, i) => {
				if (i !== dayIndex) return day;
				const sessions = [...(day.sessions || [])];
				const targetIndex = sessionIndex + direction;
				if (targetIndex < 0 || targetIndex >= sessions.length) {
					return day;
				}
				[sessions[sessionIndex], sessions[targetIndex]] = [
					sessions[targetIndex],
					sessions[sessionIndex],
				];
				return {
					...day,
					sessions: sessions.map((session, index) => ({
						...session,
						order: index,
					})),
				};
			}),
		);
	};

	const handleRemoveSession = (dayIndex, sessionIndex) => {
		const removedSessionId =
			currentWeekDays[dayIndex]?.sessions?.[sessionIndex]?.session_id || null;
		updateCurrentWeekDays((prev) =>
			prev.map((day, i) =>
				i === dayIndex
					? (() => {
							const remainingSessions = (day.sessions || [])
								.filter((_, idx) => idx !== sessionIndex)
								.map((session, idx) => ({ ...session, order: idx }));

							const shouldResetAllExerciseSessions =
								remainingSessions.length === 0;

							return {
								...day,
								sessions: remainingSessions,
								exercises: (day.exercises || []).map((exercise) =>
									shouldResetAllExerciseSessions ||
									exercise.session_id === removedSessionId
										? {
												...exercise,
												session_id: null,
												session_order: null,
											}
										: exercise,
								),
							};
						})()
					: day,
			),
		);
		setSelectedSessionIndex(null);
	};

	const toggleAthleteSelection = (athleteId) => {
		setProgram((prev) => {
			const isSelected = (prev?.athlete_ids || []).includes(athleteId);
			if (isSelected) {
				return {
					...prev,
					athlete_ids: (prev?.athlete_ids || []).filter(
						(id) => id !== athleteId,
					),
					athlete_assignments: (prev?.athlete_assignments || []).filter(
						(a) => a.athlete_id !== athleteId,
					),
				};
			} else {
				return {
					...prev,
					athlete_ids: [...(prev?.athlete_ids || []), athleteId],
					athlete_assignments: [
						...(prev?.athlete_assignments || []),
						{ athlete_id: athleteId },
					],
				};
			}
		});
	};

	const toggleGroupSelection = (groupId) => {
		const group = groups.find((g) => g.group_id === groupId);
		if (!group) return;
		setProgram((prev) => {
			const currentIds = prev?.athlete_ids || [];
			const groupAthleteIds = (group.athlete_ids || []).filter((id) =>
				athletes.some((a) => a.user_id === id),
			);
			const isEmptyGroup = groupAthleteIds.length === 0;
			const allSelected = groupAthleteIds.every((id) =>
				currentIds.includes(id),
			);
			const currentGroupIds = prev?.group_ids || [];
			const isGroupSelected = currentGroupIds.includes(groupId);

			// Empty groups must still be assignable to a program.
			if (isEmptyGroup) {
				return {
					...prev,
					group_ids: isGroupSelected
						? currentGroupIds.filter((id) => id !== groupId)
						: [...currentGroupIds, groupId],
				};
			}

			if (allSelected) {
				// Deselect all group athletes + remove group_id
				return {
					...prev,
					athlete_ids: currentIds.filter((id) => !groupAthleteIds.includes(id)),
					athlete_assignments: (prev?.athlete_assignments || []).filter(
						(a) => !groupAthleteIds.includes(a.athlete_id),
					),
					group_ids: currentGroupIds.filter((id) => id !== groupId),
				};
			} else {
				// Select all group athletes + add group_id
				const newIds = groupAthleteIds.filter((id) => !currentIds.includes(id));
				return {
					...prev,
					athlete_ids: [...currentIds, ...newIds],
					athlete_assignments: [
						...(prev?.athlete_assignments || []),
						...newIds.map((id) => ({ athlete_id: id })),
					],
					group_ids: currentGroupIds.includes(groupId)
						? currentGroupIds
						: [...currentGroupIds, groupId],
				};
			}
		});
	};

	const addCoachProgramAccess = (coachId) => {
		if (!coachId) {
			toast.error(t("pbSelectCoach"));
			return;
		}
		setProgram((prev) => ({
			...prev,
			coach_ids: Array.from(new Set([...(prev?.coach_ids || []), coachId])),
		}));
		setSelectedAccessCoachId("");
	};

	const removeCoachProgramAccess = (coachId) => {
		setProgram((prev) => ({
			...prev,
			coach_ids: (prev?.coach_ids || []).filter((id) => id !== coachId),
		}));
	};

	const addCoachTeamProgramAccess = (teamId) => {
		if (!teamId) {
			toast.error(t("pbSelectCoachTeam"));
			return;
		}
		setProgram((prev) => ({
			...prev,
			coach_team_ids: Array.from(
				new Set([...(prev?.coach_team_ids || []), teamId]),
			),
		}));
		setSelectedAccessTeamId("");
	};

	const removeCoachTeamProgramAccess = (teamId) => {
		setProgram((prev) => ({
			...prev,
			coach_team_ids: (prev?.coach_team_ids || []).filter(
				(id) => id !== teamId,
			),
		}));
	};

	const handleAddWeek = () => {
		setProgram((prev) => {
			const weeks = prev?.weeks || [];
			const newWeekNumber = weeks.length + 1;
			const newWeek = {
				week_id: `week-${Date.now()}`,
				week_number: newWeekNumber,
				label: `${t("week")} ${newWeekNumber}`,
				notes: "",
				is_published: false,
				disable_date: null,
				days: [],
			};
			return { ...prev, weeks: [...weeks, newWeek] };
		});
	};

	const handleDuplicateWeek = (weekIndex) => {
		setProgram((prev) => {
			const weeks = prev?.weeks || [];
			const source = weeks[weekIndex];
			if (!source) return prev;
			const newWeekNumber = weeks.length + 1;
			let duplicatedDays = [];

			if (prev.is_dated) {
				const allDates = weeks
					.flatMap((week) => (week.days || []).map((day) => day?.date))
					.filter(Boolean);

				const latestDateStr =
					allDates.length > 0
						? allDates.reduce((latest, current) =>
								current > latest ? current : latest,
							)
						: toLocalISODate(new Date());

				const latestDate = parseLocalDate(latestDateStr);
				const nextWeekMonday = new Date(latestDate);
				nextWeekMonday.setDate(
					nextWeekMonday.getDate() - ((nextWeekMonday.getDay() + 6) % 7) + 7,
				);

				duplicatedDays = (source.days || [])
					.map((day, index) => {
						let weekdayOffset = index;
						if (day?.date) {
							const sourceDate = parseLocalDate(day.date);
							weekdayOffset = (sourceDate.getDay() + 6) % 7;
						}

						const targetDate = new Date(nextWeekMonday);
						targetDate.setDate(targetDate.getDate() + weekdayOffset);

						return cloneDayAsIndependent(day, {
							dateValue: toLocalISODate(targetDate),
							dayNumberValue: undefined,
						});
					})
					.sort((a, b) => (a?.date || "").localeCompare(b?.date || ""));
			} else {
				duplicatedDays = (source.days || []).map((day) =>
					cloneDayAsIndependent(day, {
						dateValue: undefined,
						dayNumberValue: day.day_number,
					}),
				);
			}
			const newWeek = {
				week_id: `week-${Date.now()}`,
				week_number: newWeekNumber,
				label: `${t("week")} ${newWeekNumber}`,
				notes: source.notes || "",
				is_published: false,
				disable_date: null,
				days: duplicatedDays,
			};
			const newWeeks = [...weeks, newWeek];
			return { ...prev, weeks: newWeeks };
		});
		setSelectedWeekIndex((prev) => prev + 1);
	};

	const handleDeleteWeek = (weekIndex, { shiftFollowing = false } = {}) => {
		setProgram((prev) => {
			const weeks = prev?.weeks || [];
			if (weeks.length <= 1) return prev;
			const shift = shiftFollowing ? -weekGapAfterDelete(weeks, weekIndex) : 0;
			const newWeeks = weeks
				.filter((_, i) => i !== weekIndex)
				.map((w, i) => ({
					...(i >= weekIndex && shift ? shiftWeekDates(w, shift) : w),
					week_number: i + 1,
					label: `${t("week")} ${i + 1}`,
				}));
			return { ...prev, weeks: newWeeks };
		});
		setSelectedWeekIndex((prev) =>
			prev >= (program?.weeks?.length || 1) - 1 ? Math.max(0, prev - 1) : prev,
		);
	};

	// Deleting a week used to leave the following ones on their old dates: the
	// program kept a hole of a week and the agenda band no longer matched the
	// calendar. The coach now chooses, and the shift is the real distance
	// between the week being removed and the next one (7 days, or more when the
	// program already had a gap there).
	const requestDeleteWeek = (weekIndex) => {
		const weeks = program?.weeks || [];
		if (weeks.length <= 1) return;
		if (!program?.is_dated || weekGapAfterDelete(weeks, weekIndex) === 0) {
			handleDeleteWeek(weekIndex);
			return;
		}
		setPendingWeekDeletion({ weekIndex });
	};

	// The shift only persists on weeks the editor holds in full: a skeleton's
	// dates are stripped from the save payload and the backend puts the stored
	// days back untouched. So fill them in before offering the option.
	const pendingDeletionUnloadedWeekIds = useMemo(() => {
		if (!pendingWeekDeletion) return [];
		return (program?.weeks || [])
			.slice(pendingWeekDeletion.weekIndex + 1)
			.filter((week) => !isWeekContentLoaded(week))
			.map((week) => week.week_id)
			.filter(Boolean);
	}, [pendingWeekDeletion, program?.weeks]);

	useEffect(() => {
		if (pendingDeletionUnloadedWeekIds.length > 0) {
			loadWeeksContent(pendingDeletionUnloadedWeekIds);
		}
	}, [pendingDeletionUnloadedWeekIds, loadWeeksContent]);

	const toggleWeekPublished = (weekIndex) => {
		setProgram((prev) => ({
			...prev,
			weeks: (prev?.weeks || []).map((w, i) =>
				i === weekIndex ? toggleWeekVisibility(prev, weekIndex) : w,
			),
		}));
	};

	// Hiding a week athletes currently see is confirmed; showing one is not
	// (an additive gesture, like everywhere else in the app).
	const requestToggleWeekPublished = (weekIndex) => {
		const week = program?.weeks?.[weekIndex];
		const isVisible = getWeekVisibilityForAthlete(
			program,
			week,
			weekIndex,
		).isVisible;
		if (isVisible && programAthleteCount > 0) {
			setPendingWeekHide({ weekIndex });
			return;
		}
		toggleWeekPublished(weekIndex);
	};

	// «Trattieni» / «Rilascia»: hold a week back from the automatic release.
	const updateWeekHold = (weekIndex, held) => {
		setProgram((prev) => ({
			...prev,
			weeks: (prev?.weeks || []).map((w, i) =>
				i === weekIndex ? setWeekHold(w, held) : w,
			),
		}));
	};

	// Switching the automatic release on may take future weeks away from the
	// athletes who see them today: that asks, with the list and the dates.
	// Switching it off, or on with nothing to take away, applies at once.
	const requestAutoPublishToggle = (enabled) => {
		if (enabled) {
			const plan = autoPublishActivationPlan(program);
			if (plan.weeks.length > 0) {
				setPendingAutoPublish(plan);
				return;
			}
		}
		setProgram((prev) => applyAutoPublish(prev, enabled));
	};

	// All the ticked athletes at once, in one state update.
	const addAthletesToProgram = (athleteIds) => {
		const toAdd = (athleteIds || []).filter(Boolean);
		if (toAdd.length === 0) return;
		setProgram((prev) => {
			const current = prev?.athlete_ids || [];
			const newIds = toAdd.filter((id) => !current.includes(id));
			if (newIds.length === 0) return prev;
			return {
				...prev,
				athlete_ids: [...current, ...newIds],
				athlete_assignments: [
					...(prev?.athlete_assignments || []),
					...newIds.map((id) => ({ athlete_id: id })),
				],
			};
		});
	};

	const updateWeekPublishDate = (weekIndex, date) => {
		setProgram((prev) => ({
			...prev,
			weeks: (prev?.weeks || []).map((w, i) =>
				i === weekIndex ? { ...w, publish_date: date } : w,
			),
		}));
	};

	const updateWeekDisableDate = (weekIndex, date) => {
		setProgram((prev) => ({
			...prev,
			weeks: (prev?.weeks || []).map((w, i) =>
				// A chosen date (or the automatic one, on reset) replaces a
				// manual reactivation.
				i === weekIndex ? { ...w, disable_date: date, keep_visible: false } : w,
			),
		}));
	};

	const toggleWeekPreviewAthlete = (weekIndex, athleteId) => {
		setProgram((prev) => ({
			...prev,
			weeks: (prev?.weeks || []).map((w, i) => {
				if (i !== weekIndex) return w;
				const current = w?.preview_athlete_ids || [];
				const next = current.includes(athleteId)
					? current.filter((id) => id !== athleteId)
					: [...current, athleteId];
				return { ...w, preview_athlete_ids: next };
			}),
		}));
	};

	const updateWeekField = (weekIndex, field, value) => {
		setProgram((prev) => ({
			...prev,
			weeks: (prev?.weeks || []).map((w, i) =>
				i === weekIndex ? { ...w, [field]: value } : w,
			),
		}));
	};

	const toggleDayExpansion = (index) => {
		setExpandedDays((prev) => ({ ...prev, [index]: !prev[index] }));
	};

	const toggleDaySetupPanel = (index) => {
		setExpandedDaySetupPanels((prev) => ({
			...prev,
			[index]: !prev[index],
		}));
	};

	const handleQuickAddSession = (dayIndex) => {
		const draftLabel = newSessionLabelByDay[dayIndex] || "";
		handleAddSession(dayIndex, draftLabel);
		setNewSessionLabelByDay((prev) => ({ ...prev, [dayIndex]: "" }));
	};

	const getExerciseLibraryScopeTag = (exercise) => {
		if (exercise?.has_personal_scope && exercise?.has_global_scope) {
			return "global";
		}
		return exercise?.library_scope === "global" ? "global" : "personal";
	};

	const getExerciseScopeBadgeMeta = (exercise) =>
		getExerciseScopeBadgeMetaShared(exercise, t);

	const getExerciseLibraryScopeLabelByTag = (scopeTag) =>
		scopeTag === "global"
			? t("libraryScopeGlobalSingle")
			: t("libraryScopePersonal");

	const resolveProgramExerciseScopeTag = (programExercise) => {
		if (!programExercise || typeof programExercise !== "object") return null;

		if (
			programExercise.library_scope === "global" ||
			programExercise.library_scope === "personal"
		) {
			return programExercise.library_scope;
		}

		const availableExercises = Array.isArray(exercises) ? exercises : [];
		const directExercise = availableExercises.find(
			(item) => item.exercise_id === programExercise.exercise_id,
		);
		if (
			directExercise?.library_scope === "global" ||
			directExercise?.library_scope === "personal"
		) {
			return directExercise.library_scope;
		}

		const sourceExerciseId = programExercise.source_exercise_id;
		if (sourceExerciseId) {
			const sourceExercise = availableExercises.find(
				(item) => item.exercise_id === sourceExerciseId,
			);
			if (
				sourceExercise?.library_scope === "global" ||
				sourceExercise?.library_scope === "personal"
			) {
				return sourceExercise.library_scope;
			}
		}

		// Do not infer scope from exercise name only: custom/renamed exercises
		// can share names with library entries and produce false "global" badges.

		return null;
	};

	const getExerciseLibraryScopeLabel = (exercise) =>
		getExerciseScopeBadgeMeta(exercise).label;

	const scopeFilteredExercises = useMemo(
		() => dedupeExercisesByScope(exercises),
		[exercises],
	);

	const getExerciseScopeById = (exerciseId) => {
		if (!exerciseId) return null;
		return (
			scopeFilteredExercises.find((item) => item.exercise_id === exerciseId) ||
			null
		);
	};

	// Filter exercises based on search query
	const filteredExercises = rankAndFilterExercises(
		scopeFilteredExercises,
		exerciseSearchQuery,
	);

	// Filter exercises for superset/EMOM search
	const filteredSupersetExercises = rankAndFilterExercises(
		scopeFilteredExercises,
		supersetSearchQuery,
	);

	const filteredComplexExercises = rankAndFilterExercises(
		scopeFilteredExercises,
		complexSearchQuery,
	);

	const openExerciseVideoPreview = useCallback(
		(contextKey, exercise) => {
			const embedUrl = getYouTubeEmbedUrl(exercise?.video_url);
			if (!embedUrl) {
				toast.error(t("pbVideoUnavailable"));
				return;
			}

			setVideoPreviewState({
				contextKey,
				exerciseName:
					exercise?.name || exercise?.exercise_name || t("exerciseLabel"),
				embedUrl,
			});
		},
		[t],
	);

	const closeExerciseVideoPreview = useCallback(() => {
		setVideoPreviewState(null);
	}, []);

	const renderInlineVideoPreview = useCallback(
		(contextKey) => {
			if (!videoPreviewState || videoPreviewState.contextKey !== contextKey) {
				return null;
			}

			return (
				<div
					className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 p-4"
					onClick={closeExerciseVideoPreview}
				>
					<div
						className="w-full max-w-3xl rounded-sm border border-volt/40 bg-card p-3 space-y-3"
						onClick={(event) => event.stopPropagation()}
					>
						<div className="flex items-center justify-between gap-2">
							<p className="text-[11px] md:text-[10px] uppercase tracking-wider text-muted-foreground truncate">
								Preview video: {videoPreviewState.exerciseName}
							</p>
							<Button
								type="button"
								variant="ghost"
								size="icon"
								className="h-9 w-9 md:h-7 md:w-7 text-muted-foreground hover:text-foreground"
								onClick={closeExerciseVideoPreview}
							>
								<X className="w-4 h-4" />
							</Button>
						</div>
						<div className="overflow-hidden rounded-sm border border-border bg-black/40">
							<iframe
								src={videoPreviewState.embedUrl}
								title={`${t("pbVideoPreviewOfLabel")} ${videoPreviewState.exerciseName}`}
								className="w-full aspect-video"
								allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
								allowFullScreen
							/>
						</div>
					</div>
				</div>
			);
		},
		[videoPreviewState, closeExerciseVideoPreview, t],
	);

	// Check if superset/EMOM search queries match any existing exercise
	const supersetExactMatch = scopeFilteredExercises.some(
		(ex) => ex.name.toLowerCase() === supersetSearchQuery.toLowerCase(),
	);

	const complexExactMatch = scopeFilteredExercises.some(
		(ex) => ex.name.toLowerCase() === complexSearchQuery.toLowerCase(),
	);

	// Check if search query matches any existing exercise
	const exactMatch = scopeFilteredExercises.some(
		(ex) => ex.name.toLowerCase() === exerciseSearchQuery.toLowerCase(),
	);

	// Handle exercise selection from dropdown
	const handleSelectExercise = (ex) => {
		const refId = ex.load_reference_exercise_id || null;
		const refName = ex.load_reference_exercise_name || null;

		setNewExercise({
			...newExercise,
			exercise_id: ex.exercise_id,
			exercise_name: ex.name,
			// The pick IS the library link now. Keeping the previous
			// `source_exercise_id` (set by `cloneExerciseAsIndependent` when the week
			// was duplicated) would leave the row pointing at the movement it used to
			// be, which then drives results and history to the wrong exercise.
			source_exercise_id: null,
			load_reference_exercise_id: refId,
			load_reference_exercise_name: refName,
			// Propagate reference to set_rows for "current exercise" (esercizio corrente)
			set_rows: (newExercise.set_rows || []).map((row) => ({
				...row,
				load_reference_exercise_id: refId,
				load_reference_exercise_name: refName,
			})),
		});
		setExerciseSearchQuery(ex.name);
		setShowExerciseDropdown(false);
		setVideoPreviewState(null);
	};

	// Open new exercise form (standard)
	const handleOpenNewExerciseForm = () => {
		setNewExerciseDetails({
			name: exerciseSearchQuery,
			description: "",
			video_url: "",
			image_url: "",
			category: "strength",
		});
		setCreateExerciseTarget("standard");
		setIsCreatingNewExercise(true);
	};

	// Open new exercise form for superset
	const handleOpenNewExerciseFormForSuperset = () => {
		setNewExerciseDetails({
			name: supersetSearchQuery,
			description: "",
			video_url: "",
			image_url: "",
			category: "strength",
		});
		setCreateExerciseTarget("superset");
		setIsCreatingNewExercise(true);
	};

	const handleMoveSupersetExercise = (exerciseIndex, direction) => {
		setNewExercise((prev) => {
			const currentExercises = [...(prev.superset_exercises || [])];
			const targetIndex = exerciseIndex + direction;
			if (
				exerciseIndex < 0 ||
				exerciseIndex >= currentExercises.length ||
				targetIndex < 0 ||
				targetIndex >= currentExercises.length
			) {
				return prev;
			}

			[currentExercises[exerciseIndex], currentExercises[targetIndex]] = [
				currentExercises[targetIndex],
				currentExercises[exerciseIndex],
			];

			return {
				...prev,
				superset_exercises: currentExercises,
			};
		});
	};

	// Open new exercise form for complex
	const handleOpenNewExerciseFormForComplex = () => {
		setNewExerciseDetails({
			name: complexSearchQuery,
			description: "",
			video_url: "",
			image_url: "",
			category: "strength",
		});
		setCreateExerciseTarget("complex");
		setIsCreatingNewExercise(true);
	};

	// Open new exercise form for EMOM round library item
	const handleOpenNewExerciseFormForEmom = (roundIndex) => {
		setNewExerciseDetails({
			name: emomSearchQuery,
			description: "",
			video_url: "",
			image_url: "",
			category: "strength",
		});
		setCreateExerciseTarget(`emom_${roundIndex}`);
		setIsCreatingNewExercise(true);
	};

	const resetNewExerciseDetailsState = () => {
		setNewExerciseDetails({
			name: "",
			description: "",
			video_url: "",
			image_url: "",
			category: "strength",
		});
	};

	// Open new exercise form for conditioning row
	const handleOpenCondNewExerciseForm = (ci) => {
		setCondNewExerciseDetails({
			name: conditioningSearchQuery,
			description: "",
			video_url: "",
			image_url: "",
			category: "strength",
		});
		setCondNewExerciseIndex(ci);
		setIsCreatingCondExercise(true);
		setActiveConditioningIndex(-1);
	};

	// Create new exercise from conditioning row
	const handleCreateCondNewExercise = async () => {
		if (!condNewExerciseDetails.name.trim()) {
			toast.error(t("pbEnterExerciseName"));
			return;
		}

		setSavingCondNewExercise(true);
		try {
			const result = await fetchAPI("/exercises", {
				method: "POST",
				body: JSON.stringify({
					name: condNewExerciseDetails.name.trim(),
					description: condNewExerciseDetails.description || null,
					video_url: condNewExerciseDetails.video_url || null,
					image_url: condNewExerciseDetails.image_url || null,
					category: condNewExerciseDetails.category,
					tags: [],
				}),
			});

			setExercises((prev) => [...prev, result]);

			// Set into the conditioning row
			const ces = [...(newExercise.conditioning_exercises || [])];
			if (condNewExerciseIndex >= 0 && condNewExerciseIndex < ces.length) {
				ces[condNewExerciseIndex] = {
					...ces[condNewExerciseIndex],
					exercise_id: result.exercise_id,
					exercise_name: result.name,
				};
				setNewExercise({
					...newExercise,
					conditioning_exercises: ces,
				});
			}

			setIsCreatingCondExercise(false);
			setCondNewExerciseIndex(-1);
			setConditioningSearchQuery("");
			toast.success(
				`${t("exerciseLabel")} "${result.name}" ${t("pbExerciseAddedToPersonalLibrary")}`,
			);
		} catch (error) {
			toast.error(`${t("createError")}: ${error.message}`);
		} finally {
			setSavingCondNewExercise(false);
		}
	};

	// Create new exercise and add to library
	const handleCreateNewExercise = async () => {
		if (!newExerciseDetails.name.trim()) {
			toast.error(t("pbEnterExerciseName"));
			return;
		}

		setSavingNewExercise(true);
		try {
			const result = await fetchAPI("/exercises", {
				method: "POST",
				body: JSON.stringify({
					name: newExerciseDetails.name.trim(),
					description: newExerciseDetails.description || null,
					video_url: newExerciseDetails.video_url || null,
					image_url: newExerciseDetails.image_url || null,
					category: newExerciseDetails.category,
					tags: [],
				}),
			});

			// Add to local exercises list
			setExercises((prev) => [...prev, result]);

			if (createExerciseTarget === "superset") {
				// Add to superset exercises
				const newSe = {
					exercise_id: result.exercise_id,
					exercise_name: result.name,
					set_rows: [createStrengthSetRow({ sets: 1, reps: "10" })],
					notes: "",
				};
				setNewExercise((prev) => ({
					...prev,
					superset_exercises: [...(prev.superset_exercises || []), newSe],
				}));
				setSupersetSearchQuery("");
				setShowSupersetDropdown(false);
			} else if (createExerciseTarget === "complex") {
				// Add to complex sequence
				setNewExercise((prev) => {
					const alreadyIncluded = (prev.complex_exercises || []).some(
						(item) => item.exercise_id === result.exercise_id,
					);
					if (alreadyIncluded) return prev;
					return {
						...prev,
						complex_exercises: [
							...(prev.complex_exercises || []),
							{
								exercise_id: result.exercise_id,
								exercise_name: result.name,
							},
						],
					};
				});
				setComplexSearchQuery("");
				setShowComplexDropdown(false);
			} else if (createExerciseTarget.startsWith("emom_")) {
				// Add to the EMOM round items
				const roundIdx = parseInt(
					createExerciseTarget.replace("emom_", ""),
					10,
				);
				setNewExercise((prev) => {
					const updated = [...(prev.emom_rounds || [])];
					if (roundIdx >= 0 && roundIdx < updated.length) {
						updated[roundIdx] = {
							...updated[roundIdx],
							input_mode: "library",
							items: [
								...(updated[roundIdx].items || []),
								{
									exercise_id: result.exercise_id,
									exercise_name: result.name,
									reps: "",
									load: "",
									load_reference_exercise_id: null,
									load_reference_exercise_name: null,
								},
							],
						};
					}
					return { ...prev, emom_rounds: updated };
				});
				setEmomSearchQuery("");
				setActiveEmomRoundIndex(null);
			} else {
				// Standard: select the new exercise
				setNewExercise((prev) => ({
					...prev,
					exercise_id: result.exercise_id,
					exercise_name: result.name,
					source_exercise_id: null,
				}));
				setExerciseSearchQuery(result.name);
			}

			// Close the new exercise form
			setIsCreatingNewExercise(false);
			setCreateExerciseTarget("standard");
			resetNewExerciseDetailsState();

			toast.success(
				`${t("exerciseLabel")} "${result.name}" ${t("pbExerciseAddedToPersonalLibrary")}`,
			);
		} catch (error) {
			toast.error(`${t("createError")}: ${error.message}`);
		} finally {
			setSavingNewExercise(false);
		}
	};

	// Reset exercise form when dialog closes
	const handleExerciseDialogChange = (open) => {
		setIsAddExerciseOpen(open);
		if (open && editingExerciseIndex === null) {
			setExerciseSearchQuery("");
			setSupersetSearchQuery("");
			setComplexSearchQuery("");
			setShowExerciseDropdown(false);
			setShowSupersetDropdown(false);
			setShowComplexDropdown(false);
			setVideoPreviewState(null);
			setNewExercise(createInitialExerciseState());
			setEditingConditioningHasResults(false);
		}
		if (!open) {
			setEditingExerciseIndex(null);
			setSelectedSessionIndex(null);
			setExerciseSearchQuery("");
			setShowExerciseDropdown(false);
			setSupersetSearchQuery("");
			setShowSupersetDropdown(false);
			setComplexSearchQuery("");
			setShowComplexDropdown(false);
			setIsCreatingNewExercise(false);
			setCreateExerciseTarget("standard");
			resetNewExerciseDetailsState();
			setVideoPreviewState(null);
			setNewExercise(createInitialExerciseState());
			setEditingConditioningHasResults(false);
		}
	};

	if (loading) {
		return (
			<Layout>
				<div className="flex items-center justify-center min-h-[60vh]">
					<div className="spinner"></div>
				</div>
			</Layout>
		);
	}

	// Program-level setup (title, athletes/groups, coach access). Rendered
	// either as the left column or, when the column is collapsed to give the
	// day/exercise editor more width, inside the left Sheet. The two mounts are
	// mutually exclusive so ids and data-testid attributes stay unique.
	// Kept as a factory (not a constant) so the tree is only built when shown.
	const renderProgramSettings = () => (
		<div className="space-y-6">
			<Card className="bg-card border-border rounded-sm">
				<CardHeader className="pb-3">
					<CardTitle className="font-heading text-lg">{t("pbDetails")}</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="space-y-2">
						<Label className="label-tactical">{t("titleLabel")} *</Label>
						<Input
							value={program?.title || ""}
							onChange={(e) =>
								setProgram({ ...program, title: e.target.value })
							}
							className="bg-secondary border-transparent rounded-sm"
							placeholder={t("programTitlePlaceholder")}
							data-testid="program-title-input"
						/>
					</div>

					<div className="space-y-2">
						<Label className="label-tactical">{t("type")}</Label>
						<Select
							value={program?.type || "individual"}
							onValueChange={(v) => setProgram({ ...program, type: v })}
						>
							<SelectTrigger className="bg-secondary border-transparent rounded-sm">
								<SelectValue />
							</SelectTrigger>
							<SelectContent className="bg-card border-border">
								<SelectItem value="individual">{t("individual")}</SelectItem>
								<SelectItem value="group">{t("group")}</SelectItem>
							</SelectContent>
						</Select>
					</div>

					<div className="space-y-2">
						<Label className="label-tactical">{t("mode")}</Label>
						<Select
							value={program?.is_dated ? "dated" : "undated"}
							onValueChange={handleProgramModeChange}
						>
							<SelectTrigger className="bg-secondary border-transparent rounded-sm">
								<SelectValue />
							</SelectTrigger>
							<SelectContent className="bg-card border-border">
								<SelectItem value="dated">{t("datedMode")}</SelectItem>
								<SelectItem value="undated">
									{t("undatedMode")}
								</SelectItem>
							</SelectContent>
						</Select>
						<p className="text-xs text-muted-foreground">
							{program?.is_dated ? t("datedModeHint") : t("undatedModeHint")}
						</p>
					</div>

					{/* «Settimane e calendario»: the two automatic rules side by side —
					    weeks appear by themselves (auto_publish_weeks, the Sunday before)
					    and hide by themselves (auto_disable_weeks). Each rule in plain
					    words under its box, on screen and not behind an icon; the closing
					    line says what the two together mean for athletes. */}
					<div
						className="space-y-3 rounded-sm border border-border/60 p-3"
						data-testid="program-calendar-rules"
					>
						<p className="label-tactical">{t("pbCalendarRulesTitle")}</p>
						<div className="space-y-2">
							<label
								className={`flex items-center gap-2 ${
									program?.is_dated ? "cursor-pointer" : "cursor-not-allowed opacity-60"
								}`}
							>
								<input
									type="checkbox"
									checked={isAutoPublishEnabled(program)}
									disabled={!program?.is_dated}
									onChange={(e) => requestAutoPublishToggle(e.target.checked)}
									className="rounded border-border"
									data-testid="program-auto-publish-toggle"
								/>
								<span className="text-sm">{t("programAutoPublishLabel")}</span>
							</label>
							<p className="text-xs text-muted-foreground pl-6">
								{program?.is_dated
									? t("programAutoPublishHint")
									: t("programAutoPublishUndated")}
							</p>
							{isAutoPublishEnabled(program) && (
								<div className="pl-6 flex items-center gap-2">
									<span className="text-xs text-muted-foreground shrink-0">
										{t("programAutoPublishLeadLabel")}
									</span>
									<Select
										value={String(getAutoPublishLeadDays(program))}
										onValueChange={(value) =>
											setProgram({
												...program,
												auto_publish_lead_days: Number(value),
											})
										}
									>
										<SelectTrigger
											className="bg-secondary border-transparent rounded-sm h-9 text-xs"
											data-testid="program-auto-publish-lead"
										>
											<SelectValue />
										</SelectTrigger>
										<SelectContent className="bg-card border-border">
											<SelectItem value="1">{t("programAutoPublishLead1")}</SelectItem>
											<SelectItem value="0">{t("programAutoPublishLead0")}</SelectItem>
											<SelectItem value="2">{t("programAutoPublishLead2")}</SelectItem>
										</SelectContent>
									</Select>
								</div>
							)}
						</div>
						<div className="space-y-2">
							<label className="flex items-center gap-2 cursor-pointer">
								<input
									type="checkbox"
									checked={program?.auto_disable_weeks !== false}
									onChange={(e) =>
										setProgram({
											...program,
											auto_disable_weeks: e.target.checked,
										})
									}
									className="rounded border-border"
								/>
								<span className="text-sm">{t("programAutoDisableLabel")}</span>
							</label>
							<p className="text-xs text-muted-foreground pl-6">
								{t("programAutoDisableHint")}
							</p>
						</div>
						{isAutoPublishEnabled(program) &&
							program?.auto_disable_weeks !== false && (
								<p className="text-xs text-foreground/80 flex items-start gap-1.5">
									<CheckCircle2
										className="w-3.5 h-3.5 mt-0.5 text-green-400 shrink-0"
										aria-hidden="true"
									/>
									<span>{t("programCalendarBothHint")}</span>
								</p>
							)}
					</div>

					<div className="space-y-2 pt-1">
						<Button
							variant="outline"
							className="w-full border-dashed border-volt/50 text-volt hover:bg-volt/10 rounded-sm"
							onClick={() => {
								setIsAthleteViewPreviewOpen(true);
							}}
						>
							<Eye className="w-4 h-4 mr-2" />
							{t("pbPreviewRendering")}
						</Button>
						<p className="text-xs md:text-[11px] text-muted-foreground">
							{t("pbPreviewRenderingHint")}
						</p>
					</div>
				</CardContent>
			</Card>

			{/* Athletes / Groups Selection */}
			<Card className="bg-card border-border rounded-sm">
				<CardHeader className="pb-3">
					<CardTitle className="font-heading text-lg">
						{program?.type === "group"
							? t("assignedGroups")
							: t("assignedAthletes")}
					</CardTitle>
				</CardHeader>
				<CardContent>
					{program?.type === "group" ? (
						/* Group selection for group programs */
						<ScrollArea className="h-[200px] md:h-[260px]">
							{groups.length > 0 ? (
								<div className="space-y-2">
									{groups.map((group) => {
										const isChecked = (program?.group_ids || []).includes(
											group.group_id,
										);
										const isGroupExpanded = Boolean(
											expandedGroupIds[group.group_id],
										);
										// Only athletes the coach can actually see get assigned
										// (see toggleGroupSelection), so the count shown here is
										// the assignable one, not the raw group size — otherwise
										// it would not match what ticking the group does.
										const groupAthletes = (group.athlete_ids || [])
											.map((athleteId) =>
												athletes.find((item) => item.user_id === athleteId),
											)
											.filter(Boolean);
										const hiddenAthleteCount =
											(group.athlete_ids || []).length - groupAthletes.length;
										const checkboxId = `pb-group-${group.group_id}`;
										return (
											<div
												key={group.group_id}
												className="rounded-sm hover:bg-white/5"
											>
												<div className="flex items-center gap-3 p-2">
													<input
														id={checkboxId}
														type="checkbox"
														checked={isChecked}
														onChange={() =>
															toggleGroupSelection(group.group_id)
														}
														className="rounded border-border"
													/>
													<FolderOpen className="w-4 h-4 text-volt shrink-0" />
													<label
														htmlFor={checkboxId}
														className="text-sm flex-1 min-w-0 truncate cursor-pointer"
													>
														{group.name}
													</label>
													<button
														type="button"
														onClick={() =>
															setExpandedGroupIds((prev) => ({
																...prev,
																[group.group_id]: !prev[group.group_id],
															}))
														}
														title={t("pbGroupShowAthletes")}
														aria-label={t("pbGroupShowAthletes")}
														aria-expanded={isGroupExpanded}
														className="shrink-0 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-volt"
													>
														{groupAthletes.length} {t("athletes").toLowerCase()}
														{isGroupExpanded ? (
															<ChevronUp className="w-3 h-3" />
														) : (
															<ChevronDown className="w-3 h-3" />
														)}
													</button>
												</div>
												{isGroupExpanded && (
													<div className="pl-9 pr-2 pb-2 space-y-0.5">
														{groupAthletes.length === 0 ? (
															<p className="text-xs text-muted-foreground/70">
																{t("pbGroupNoAthletes")}
															</p>
														) : (
															groupAthletes.map((athlete) => (
																<p
																	key={athlete.user_id}
																	className="text-xs text-muted-foreground truncate"
																>
																	{athlete.name}
																</p>
															))
														)}
														{hiddenAthleteCount > 0 && (
															<p className="text-xs text-amber-400/80">
																{`+${hiddenAthleteCount} ${t("pbGroupHiddenAthletes")}`}
															</p>
														)}
													</div>
												)}
											</div>
										);
									})}
								</div>
							) : (
								<p className="text-sm text-muted-foreground text-center py-4">
									{t("pbNoGroupCreated")}
								</p>
							)}
						</ScrollArea>
					) : (
						/* Individual athlete selection */
						<div className="space-y-3">
							{/* Assigned athletes list */}
							{(program?.athlete_ids || []).length > 0 ? (
								<div className="space-y-2">
									{(program?.athlete_ids || []).map((athleteId) => {
										const athlete = athletes.find(
											(a) => a.user_id === athleteId,
										);
										if (!athlete) return null;
										return (
											<div
												key={athleteId}
												className="flex items-center gap-2 p-2 bg-secondary rounded-sm"
											>
												<Users className="w-4 h-4 text-muted-foreground flex-shrink-0" />
												<span className="text-sm flex-1">{athlete.name}</span>
												<Button
													variant="ghost"
													size="icon"
													className="h-9 w-9 md:h-7 md:w-7 rounded-sm text-muted-foreground hover:text-blaze hover:bg-blaze/10"
													onClick={() => toggleAthleteSelection(athleteId)}
												>
													<X className="w-3.5 h-3.5" />
												</Button>
											</div>
										);
									})}
								</div>
							) : (
								<p className="text-sm text-muted-foreground text-center py-3">
									{t("noAssignedAthletes")}
								</p>
							)}
							{/* Assign athletes: tick several, add them together. One athlete per
							    opening meant N openings for N athletes. Search and the two-line
							    row (name + email) follow the coach athlete search invariant. */}
							<Dialog
								open={isAssignAthleteOpen}
								onOpenChange={(v) => {
									setIsAssignAthleteOpen(v);
									if (!v) {
										setAthleteSearchQuery("");
										setPendingAssignIds([]);
									}
								}}
							>
								<DialogTrigger asChild>
									<Button
										variant="outline"
										className="w-full border-dashed border-volt/50 text-volt hover:bg-volt/10 rounded-sm h-9 md:h-8 text-sm"
									>
										<UserPlus className="w-4 h-4 mr-2" />
										{t("assignAthleteBtn")}
									</Button>
								</DialogTrigger>
								<DialogContent className="bg-card border-border rounded-sm max-w-sm">
									<DialogDescription className="hidden" />
									<DialogHeader>
										<DialogTitle className="font-heading">
											{t("assignAthleteBtn")}
										</DialogTitle>
									</DialogHeader>
									{(() => {
										const assignedIds = program?.athlete_ids || [];
										const unassigned = athletes.filter(
											(a) => !assignedIds.includes(a.user_id),
										);
										const matching = unassigned.filter((a) =>
											matchesCoachAthleteSearch(a, athleteSearchQuery),
										);
										const togglePending = (athleteId) =>
											setPendingAssignIds((prev) =>
												prev.includes(athleteId)
													? prev.filter((id) => id !== athleteId)
													: [...prev, athleteId],
											);
										const closeAssignDialog = () => {
											setIsAssignAthleteOpen(false);
											setAthleteSearchQuery("");
											setPendingAssignIds([]);
										};
										return (
											<div className="space-y-3">
												<div className="relative">
													<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
													<Input
														value={athleteSearchQuery}
														onChange={(e) => setAthleteSearchQuery(e.target.value)}
														placeholder={t("searchAthletes")}
														className="pl-9 bg-secondary border-transparent rounded-sm"
														autoFocus
													/>
												</div>
												<ScrollArea className="h-[220px] md:h-[260px]">
													{matching.length > 0 ? (
														<div className="space-y-1">
															{matching.map((athlete) => {
																const checked = pendingAssignIds.includes(athlete.user_id);
																const inputId = `pb-assign-${athlete.user_id}`;
																return (
																	<label
																		key={athlete.user_id}
																		htmlFor={inputId}
																		className={`flex items-center gap-3 px-3 min-h-[44px] rounded-sm cursor-pointer ${
																			checked ? "bg-volt/10" : "hover:bg-white/5"
																		}`}
																	>
																		<input
																			id={inputId}
																			type="checkbox"
																			checked={checked}
																			onChange={() => togglePending(athlete.user_id)}
																			className="rounded border-border shrink-0"
																		/>
																		<div className="min-w-0">
																			<p className="text-sm truncate">
																				{formatFullName(athlete) || athlete.name}
																			</p>
																			{athlete.email && (
																				<p className="text-xs md:text-[11px] text-muted-foreground truncate">
																					{athlete.email}
																				</p>
																			)}
																		</div>
																	</label>
																);
															})}
														</div>
													) : (
														<p className="text-sm text-muted-foreground text-center py-6">
															{unassigned.length === 0
																? t("pbAssignAllAssigned")
																: t("noAthletesFound")}
														</p>
													)}
												</ScrollArea>
												<div className="flex items-center justify-between gap-2 pt-1">
													<span className="text-xs text-muted-foreground">
														{pendingAssignIds.length} {t("pbAssignSelectedCount")}
													</span>
													<div className="flex gap-2">
														<Button
															type="button"
															variant="outline"
															className="rounded-sm"
															onClick={closeAssignDialog}
														>
															{t("cancel")}
														</Button>
														<Button
															type="button"
															disabled={pendingAssignIds.length === 0}
															className="bg-volt hover:bg-volt/90 rounded-sm"
															onClick={() => {
																addAthletesToProgram(pendingAssignIds);
																closeAssignDialog();
															}}
															data-testid="assign-athletes-confirm"
														>
															{t("add")}
															{pendingAssignIds.length > 0
																? ` ${pendingAssignIds.length}`
																: ""}
														</Button>
													</div>
												</div>
											</div>
										);
									})()}
								</DialogContent>
							</Dialog>
						</div>
					)}
					{(programAthleteCount > 0 || (program?.group_ids || []).length > 0) &&
					(visibleWeekLabels.length > 0 ? (
						<p
							className="mt-3 flex items-start gap-1.5 text-xs text-muted-foreground"
							data-testid="assigned-athletes-see"
						>
							<CheckCircle2
								className="w-3.5 h-3.5 mt-0.5 shrink-0 text-green-400"
								aria-hidden="true"
							/>
							<span className="min-w-0">
								{t("pbAthletesSeeWeeks")}: {visibleWeekLabels.join(", ")}
							</span>
						</p>
					) : (
						<p
							className="mt-3 flex items-start gap-1.5 text-xs text-amber-300"
							data-testid="assigned-athletes-see"
						>
							<EyeOff className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden="true" />
							<span className="min-w-0">{t("pbAthletesSeeNothing")}</span>
						</p>
					))}
			</CardContent>
			</Card>

			<Card className="bg-card border-border rounded-sm">
				<CardHeader className="pb-3">
					<CardTitle className="font-heading text-lg">
						{t("pbCoachTeamAccess")}
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="space-y-2">
						<p className="text-xs uppercase tracking-wider text-muted-foreground">
							{t("pbCoachesWithAccess")}
						</p>
						{(program?.coach_ids || []).length > 0 ? (
							<div className="flex flex-wrap gap-2">
								{(program?.coach_ids || []).map((coachId) => {
									const coach = coachOptions.find((c) => c.user_id === coachId);
									return (
										<div
											key={coachId}
											className="flex items-center gap-2 px-2 py-1 bg-secondary rounded-sm"
										>
											<UserCog className="w-3.5 h-3.5 text-volt" />
											<span className="text-xs">{coach?.name || coachId}</span>
											<button
												onClick={() => removeCoachProgramAccess(coachId)}
												className="text-muted-foreground hover:text-blaze"
											>
												<X className="w-3 h-3" />
											</button>
										</div>
									);
								})}
							</div>
						) : (
							<p className="text-xs text-muted-foreground">
								{t("noCoachesAssigned")}
							</p>
						)}
						<div className="flex gap-2">
							<Select
								value={selectedAccessCoachId || undefined}
								onValueChange={setSelectedAccessCoachId}
							>
								<SelectTrigger className="bg-secondary border-transparent rounded-sm h-8">
									<SelectValue placeholder={t("pbSelectCoachPlaceholder")} />
								</SelectTrigger>
								<SelectContent className="bg-card border-border">
									{coachOptions
										.filter(
											(coach) =>
												!(program?.coach_ids || []).includes(coach.user_id),
										)
										.map((coach) => (
											<SelectItem key={coach.user_id} value={coach.user_id}>
												{coach.name}
											</SelectItem>
										))}
								</SelectContent>
							</Select>
							<Button
								variant="outline"
								size="sm"
								className="rounded-sm h-8"
								onClick={() => addCoachProgramAccess(selectedAccessCoachId)}
							>
								{t("add")}
							</Button>
						</div>
					</div>

					<div className="space-y-2 pt-2 border-t border-border/60">
						<p className="text-xs uppercase tracking-wider text-muted-foreground">
							{t("pbCoachTeamsWithAccess")}
						</p>
						{(program?.coach_team_ids || []).length > 0 ? (
							<div className="flex flex-wrap gap-2">
								{(program?.coach_team_ids || []).map((teamId) => {
									const team = coachTeamOptions.find(
										(t) => t.team_id === teamId,
									);
									return (
										<div
											key={teamId}
											className="flex items-center gap-2 px-2 py-1 bg-secondary rounded-sm"
										>
											<Building2 className="w-3.5 h-3.5 text-acid" />
											<span className="text-xs">{team?.name || teamId}</span>
											<button
												onClick={() => removeCoachTeamProgramAccess(teamId)}
												className="text-muted-foreground hover:text-blaze"
											>
												<X className="w-3 h-3" />
											</button>
										</div>
									);
								})}
							</div>
						) : (
							<p className="text-xs text-muted-foreground">
								{t("pbNoCoachTeamAssigned")}
							</p>
						)}
						<div className="flex gap-2">
							<Select
								value={selectedAccessTeamId || undefined}
								onValueChange={setSelectedAccessTeamId}
							>
								<SelectTrigger className="bg-secondary border-transparent rounded-sm h-8">
									<SelectValue placeholder={t("pbSelectCoachTeamPlaceholder")} />
								</SelectTrigger>
								<SelectContent className="bg-card border-border">
									{coachTeamOptions
										.filter(
											(team) =>
												!(program?.coach_team_ids || []).includes(team.team_id),
										)
										.map((team) => (
											<SelectItem key={team.team_id} value={team.team_id}>
												{team.name}
											</SelectItem>
										))}
								</SelectContent>
							</Select>
							<Button
								variant="outline"
								size="sm"
								className="rounded-sm h-8"
								onClick={() => addCoachTeamProgramAccess(selectedAccessTeamId)}
							>
								{t("add")}
							</Button>
						</div>
					</div>
				</CardContent>
			</Card>
		</div>
	);

	return (
		<Layout>
			<div className="flex min-h-full">
				{/* Main content */}
				<div className="flex-1 min-w-0">
					<div
						// overflow-x-hidden only below md: a single element that overflows
						// used to turn into horizontal scrolling of the entire page.
						className={`mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 overflow-x-hidden md:overflow-x-visible ${
							// The column overview wants every pixel it can get, like wide mode.
							wideMode || isOverviewView ? "max-w-[1800px]" : "max-w-7xl"
						}`}
						data-testid="program-builder"
					>
						{/* Header actions */}
						<div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
							<div className="min-w-0">
								<Button
									type="button"
									variant="ghost"
									onClick={handleNavigateBack}
									className="h-8 px-2 mb-1 rounded-sm text-muted-foreground hover:text-foreground hover:bg-secondary"
								>
									<ArrowLeft className="w-4 h-4 mr-1" />
									{t("backToPrograms")}
								</Button>
								<p className="label-tactical text-muted-foreground">
									{t("pbProgramLabel")}
								</p>
								<h1 className="font-heading text-xl uppercase tracking-tight text-foreground truncate">
									{(program?.title || "").trim() || t("pbUntitledProgram")}
								</h1>
							</div>
							<TooltipProvider delayDuration={250}>
								<div className="w-full sm:w-auto flex items-center gap-2">
									<HintTooltip
										label={
											isSettingsColumnVisible
												? t("pbHideSettingsHint")
												: t("pbProgramSettingsHint")
										}
									>
										<Button
											type="button"
											variant="outline"
											onClick={() =>
												isSettingsColumnVisible
													? toggleSettingsCollapsed()
													: setIsSettingsSheetOpen(true)
											}
											className="flex-1 sm:flex-none rounded-sm border-border text-muted-foreground hover:text-foreground"
											data-testid="program-settings-toggle"
										>
											{isSettingsColumnVisible ? (
												<PanelLeftClose className="w-4 h-4 sm:mr-2" />
											) : (
												<Users className="w-4 h-4 sm:mr-2" />
											)}
											<span className="hidden md:inline">
												{isSettingsColumnVisible
													? t("pbHideSettings")
													: t("pbProgramSettings")}
											</span>
										</Button>
									</HintTooltip>
									<HintTooltip label={t("pbOpenReferenceHint")}>
										<Button
											type="button"
											variant="outline"
											onClick={() => openDock("reference")}
											aria-label={t("pbOpenReference")}
											className={`hidden md:inline-flex shrink-0 rounded-sm border-volt/40 ${
												isDockOpen && dockTab === "reference"
													? "bg-volt/10 text-volt"
													: "text-volt hover:bg-volt/10"
											}`}
											data-testid="program-reference-toggle"
										>
											<Columns2 className="w-4 h-4 lg:mr-2" />
											<span className="hidden lg:inline">
												{t("pbOpenReference")}
											</span>
										</Button>
									</HintTooltip>
									{isSeasonStripAvailable && (
										<HintTooltip
											label={
												seasonStripEnabled
													? t("pbSeasonStripOff")
													: t("pbSeasonStripOn")
											}
										>
											<Button
												type="button"
												variant="outline"
												size="icon"
												onClick={toggleSeasonStrip}
												aria-pressed={seasonStripEnabled}
												aria-label={
													seasonStripEnabled
														? t("pbSeasonStripOff")
														: t("pbSeasonStripOn")
												}
												className={`hidden md:inline-flex shrink-0 rounded-sm ${
													seasonStripEnabled
														? "border-volt/40 bg-volt/10 text-volt"
														: "border-border text-muted-foreground hover:text-foreground"
												}`}
												data-testid="program-season-toggle"
											>
												<CalendarRange className="w-4 h-4" />
											</Button>
										</HintTooltip>
									)}
									<HintTooltip
										label={
											weekView === "overview"
												? t("pbWeekViewEditor")
												: t("pbWeekViewOverview")
										}
									>
										<Button
											type="button"
											variant="outline"
											size="icon"
											onClick={() =>
												setWeekViewAndPersist(
													weekView === "overview" ? "editor" : "overview",
												)
											}
											aria-pressed={weekView === "overview"}
											aria-label={
												weekView === "overview"
													? t("pbWeekViewEditor")
													: t("pbWeekViewOverview")
											}
											className={`hidden md:inline-flex shrink-0 rounded-sm ${
												weekView === "overview"
													? "border-volt/40 bg-volt/10 text-volt"
													: "border-border text-muted-foreground hover:text-foreground"
											}`}
											data-testid="program-week-view-toggle"
										>
											{weekView === "overview" ? (
												<LayoutList className="w-4 h-4" />
											) : (
												<LayoutGrid className="w-4 h-4" />
											)}
										</Button>
									</HintTooltip>
									<HintTooltip
										label={wideMode ? t("pbWideModeOff") : t("pbWideModeOn")}
									>
										<Button
											type="button"
											variant="outline"
											size="icon"
											onClick={toggleWideMode}
											aria-label={
												wideMode ? t("pbWideModeOff") : t("pbWideModeOn")
											}
											className="hidden lg:inline-flex shrink-0 rounded-sm border-border text-muted-foreground hover:text-foreground"
										>
											{wideMode ? (
												<Minimize2 className="w-4 h-4" />
											) : (
												<Maximize2 className="w-4 h-4" />
											)}
										</Button>
									</HintTooltip>
									<Button
										onClick={handleSaveProgram}
										disabled={saving || !isDirty}
										className={`flex-1 sm:flex-none sm:w-auto rounded-sm ${
											isDirty
												? "bg-volt hover:bg-volt/90"
												: "bg-muted text-muted-foreground"
										}`}
										data-testid="save-program-btn"
									>
										<Save className="w-4 h-4 mr-2" />
										{saving ? t("savingLabel") : t("save")}
									</Button>
								</div>
							</TooltipProvider>
						</div>

						{/* Clipboard chip — only while something is copied. Its source
						    label matters most when the copy came from another tab. */}
						{clipboardItemCount > 0 && (
							<div className="mb-4 flex items-center gap-2 rounded-sm border border-volt/30 bg-volt/5 px-3 py-2 text-xs">
								<ClipboardCopy className="w-3.5 h-3.5 text-volt shrink-0" />
								<span className="text-volt font-medium shrink-0">
									{clipboardItemCount === 1
										? t("pbClipboardOneItem")
										: `${clipboardItemCount} ${t("pbClipboardManyItems")}`}
								</span>
								{clipboardSourceLabel && (
									<span className="text-muted-foreground truncate min-w-0">
										{clipboardSourceLabel}
									</span>
								)}
								<Button
									type="button"
									variant="ghost"
									size="icon"
									onClick={clearClipboard}
									title={t("pbClipboardClear")}
									aria-label={t("pbClipboardClear")}
									className="ml-auto h-8 w-8 md:h-6 md:w-6 shrink-0 text-muted-foreground hover:text-blaze"
								>
									<X className="w-3.5 h-3.5" />
								</Button>
							</div>
						)}

						{isCoachAiDisabled && (
							<div className="mb-6 rounded-sm border border-blaze/40 bg-blaze/10 p-4 text-sm text-blaze">
								<p className="font-medium mb-1">
									{t("pbCoachAiDisabledTitle")}
								</p>
								<p>
									{t("pbCoachAiDisabledBody")}
								</p>
							</div>
						)}

						<div
							className={`grid gap-6 ${
								isSettingsColumnVisible ? "lg:grid-cols-3" : "grid-cols-1"
							}`}
						>
							{/* Left Column - Program Info (moves into the Sheet when hidden) */}
							{isSettingsColumnVisible && renderProgramSettings()}

							{/* Right Column - Days and Exercises */}
							<div
								className={`space-y-4 ${
									isSettingsColumnVisible ? "lg:col-span-2" : ""
								}`}
							>
								{/* Week Tabs — plus the season strip (phases + race markers) when
								    the program is saved, dated and reaches an athlete. */}
								<SeasonStrip
									weeks={program?.weeks || []}
									selectedWeekIndex={selectedWeekIndex}
									onSelectWeek={setSelectedWeekIndex}
									selectedTabRef={selectedWeekTabRef}
									weekStatusClass={weekStatusDotClass}
									weekStatusLabel={weekStatusText}
									onAddWeek={handleAddWeek}
									agenda={seasonAgenda}
									onPlan={handlePlanSeason}
									onRetryAgenda={refreshSeasonAgenda}
									t={t}
									language={language}
								/>

								{/* Week Actions */}
								{(program?.weeks?.length || 0) > 0 && (
									<div className="space-y-2">
										<div className="flex items-center gap-2 flex-wrap">
											<span className="text-xs text-muted-foreground font-heading uppercase tracking-wider flex-1 min-w-[220px]">
												{localizeWeekLabel(
													program?.weeks?.[selectedWeekIndex]?.label,
													t,
													selectedWeekIndex,
												)}
												{" — "}
												{currentWeekDays.length}{" "}
												{t(currentWeekDays.length === 1 ? "dayOne" : "daysLower")}
											</span>
											{seasonAgenda && (
												<SeasonWeekContext
													week={currentWeek}
													calendarRange={selectedWeekCalendarRange}
													agenda={seasonAgenda}
													subjectOptions={seasonSubjectOptions}
													selectedSubjectId={seasonScope?.id || ""}
													onSelectSubject={handleSelectSeasonSubject}
													onPlan={handlePlanSeason}
													t={t}
													language={language}
												/>
											)}
											{/* Publishing, sharing and week notes are setup a coach rarely
										    touches from a phone, and they filled most of the screen
										    before the day list. Collapsed by default below md. */}
											<Button
												type="button"
												variant="ghost"
												size="sm"
												onClick={() => setWeekActionsOpen((prev) => !prev)}
												aria-expanded={weekActionsOpen}
												className="md:hidden ml-auto h-8 rounded-sm text-xs text-muted-foreground"
											>
												{t("pbWeekActions")}
												{weekActionsOpen ? (
													<ChevronUp className="w-3.5 h-3.5 ml-1" />
												) : (
													<ChevronDown className="w-3.5 h-3.5 ml-1" />
												)}
											</Button>
										</div>

										{/* What athletes see of this week, in one sentence. Always
										    visible, also on a phone where the controls below are
										    collapsed: the state is the thing to know, the buttons
										    are the thing to do. */}
										{(() => {
											const description = describeWeekVisibility(
												program,
												currentWeek,
												selectedWeekIndex,
											);
											const StatusIcon =
												description.state === "live"
													? CheckCircle2
													: description.state === "scheduled"
														? CalendarIcon
														: description.held
															? Pause
															: EyeOff;
											const statusColor =
												description.state === "live"
													? "text-green-400"
													: description.state === "scheduled"
														? "text-sky-400"
														: description.state === "expired" || description.held
															? "text-amber-300"
															: "text-muted-foreground";
											return (
												<p
													className="flex items-start gap-1.5 text-xs text-muted-foreground"
													data-testid="week-visibility-status"
												>
													<StatusIcon
														className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${statusColor}`}
														aria-hidden="true"
													/>
													<span className="min-w-0">
														{weekStatusText(currentWeek, selectedWeekIndex)}
													</span>
												</p>
											);
										})()}

										<div
											className={`space-y-2 ${
												weekActionsOpen ? "" : "hidden md:block"
											}`}
										>
											{/* One toolbar for the week, without sub-boxes: the heading above says
											    which week, the status line says what athletes see, the buttons say
											    what they do. Visibility on the left, the week itself on the right;
											    the rare and the destructive actions share one menu, with "delete"
											    last and apart (it used to sit in a row with duplicate and export). */}
											{(() => {
												const selectedWeek = program?.weeks?.[selectedWeekIndex];
												const description = describeWeekVisibility(
													program,
													selectedWeek,
													selectedWeekIndex,
												);
												const statusAction = description.held
													? "release"
													: description.isDisabled
														? "reactivate"
														: description.isVisible
															? "hide"
															: "publish";
												// 40 px tall on a phone, the usual 32 px on desktop.
												const controlClass = "rounded-sm text-xs h-10 md:h-8";
												const previewCount = (selectedWeek?.preview_athlete_ids || []).length;
												return (
													<div className="flex flex-col md:flex-row md:items-start gap-2">
														<div className="flex flex-wrap items-center gap-2 min-w-0">
															{/* The verb, not the state: the state is the line above. With a
															    publish date set, "publish now" would sit beside it and read
															    as a contradiction, so it moves inside the date popover. */}
															{/* A week the calendar will publish has no date popover to hold
															    «Pubblica ora»: it stays here, as «anticipa». */}
															{(description.state !== "scheduled" ||
																description.releaseReason === "auto") && (
															<Button
																variant="outline"
																size="sm"
																className={`${controlClass} ${
																	statusAction === "publish"
																		? "border-dashed border-volt/50 text-volt hover:bg-volt/10"
																		: statusAction === "release"
																			? "border-sky-400/50 text-sky-400 hover:bg-sky-400/10"
																			: statusAction === "reactivate"
																				? "border-amber-500/50 text-amber-300 hover:bg-amber-500/10"
																				: "border-border text-muted-foreground hover:text-foreground"
																}`}
																onClick={() =>
																	statusAction === "release"
																		? updateWeekHold(selectedWeekIndex, false)
																		: requestToggleWeekPublished(selectedWeekIndex)
																}
																data-testid="week-visibility-toggle"
															>
																{statusAction === "publish" ? (
																	<>
																		<Eye className="w-3.5 h-3.5 mr-1" />
																		{t("pbPublishNow")}
																	</>
																) : statusAction === "release" ? (
																	<>
																		<Play className="w-3.5 h-3.5 mr-1" />
																		{t("pbReleaseWeek")}
																	</>
																) : statusAction === "reactivate" ? (
																	<>
																		<RotateCcw className="w-3.5 h-3.5 mr-1" />
																		{t("pbWeekReactivate")}
																	</>
																) : (
																	<>
																		<EyeOff className="w-3.5 h-3.5 mr-1" />
																		{t("hide")}
																	</>
																)}
															</Button>
															)}
															{/* Scheduled publish date — not in an automatic program: the
															    calendar gives the date, and a chosen one later than it
															    could not hold (the calendar would release first). */}
															{!selectedWeek?.is_published && !description.autoPublish && (
																<Popover>
																	<PopoverTrigger asChild>
																		<Button
																			variant="outline"
																			size="sm"
																			className={`${controlClass} ${
																				selectedWeek?.publish_date
																					? "border-sky-400/40 text-sky-400 hover:bg-sky-400/10"
																					: "border-border text-muted-foreground"
																			}`}
																		>
																			<CalendarIcon className="w-3.5 h-3.5 mr-1" />
																			{selectedWeek?.publish_date
																				? `${t("pbPublishOn")} ${formatDayShort(selectedWeek.publish_date)}`
																				: t("pbSchedulePublish")}
																		</Button>
																	</PopoverTrigger>
																	<PopoverContent
																		className="w-auto p-0 bg-card border-border"
																		align="start"
																	>
																		<div className="p-2 border-b border-border">
																			<p className="text-xs text-muted-foreground">
																				{t("publishWeekAuto")}
																			</p>
																		</div>
																		<Calendar
																			mode="single"
																			locale={calendarLocale}
																			weekStartsOn={1}
																			selected={
																				program?.weeks?.[selectedWeekIndex]
																					?.publish_date
																					? parseLocalDate(
																							program.weeks[selectedWeekIndex]
																								.publish_date,
																						)
																					: undefined
																			}
																			onSelect={(date) => {
																				if (date) {
																					updateWeekPublishDate(
																						selectedWeekIndex,
																						toLocalISODate(date),
																					);
																				}
																			}}
																			disabled={(date) =>
																				date <
																				new Date(new Date().setHours(0, 0, 0, 0))
																			}
																		/>
																		{program?.weeks?.[selectedWeekIndex]
																			?.publish_date && (
																			<div className="p-2 border-t border-border space-y-1">
																				<Button
																					variant="ghost"
																					size="sm"
																					className="w-full text-xs text-volt hover:text-volt"
																					onClick={() =>
																						requestToggleWeekPublished(selectedWeekIndex)
																					}
																				>
																					<Eye className="w-3 h-3 mr-1" />
																					{t("pbPublishNowInstead")}
																				</Button>
																				<Button
																					variant="ghost"
																					size="sm"
																					className="w-full text-xs text-blaze hover:text-blaze"
																					onClick={() =>
																						updateWeekPublishDate(
																							selectedWeekIndex,
																							null,
																						)
																					}
																				>
																					<X className="w-3 h-3 mr-1" />
																					{t("pbRemoveDate")}
																				</Button>
																			</div>
																		)}
																	</PopoverContent>
																</Popover>
															)}
															{/* «Trattieni»: keep a week the calendar is about to publish
															    as a draft until the coach releases it. Hiding a live one
															    already holds it (toggleWeekVisibility). */}
															{description.autoPublish &&
																description.state === "scheduled" &&
																description.releaseReason === "auto" && (
																	<Button
																		variant="outline"
																		size="sm"
																		className={`${controlClass} border-border text-muted-foreground hover:text-foreground`}
																		onClick={() => updateWeekHold(selectedWeekIndex, true)}
																		data-testid="week-hold"
																	>
																		<Pause className="w-3.5 h-3.5 mr-1" />
																		{t("pbHoldWeek")}
																	</Button>
																)}
															{/* Last day athletes see it («Visibile fino a dom 15/11»),
															    the same date the status line reads. The calendar picks
															    that day and stores the day after as `disable_date`,
															    which is the first hidden day. */}
															<Popover>
																<PopoverTrigger asChild>
																	<Button
																		variant="outline"
																		size="sm"
																		className={`${controlClass} border-border text-muted-foreground`}
																	>
																		<Clock className="w-3.5 h-3.5 mr-1" />
																		{description.visibleUntil
																			? `${t("pbVisibleUntil")} ${formatDayShort(description.visibleUntil)}`
																			: description.hideReason === "keeps"
																				? t("pbStaysVisible")
																				: t("pbVisibleUntilPick")}
																	</Button>
																</PopoverTrigger>
																<PopoverContent
																	className="w-auto p-0 bg-card border-border"
																	align="start"
																>
																	<div className="p-2 border-b border-border max-w-[260px]">
																		<p className="text-xs text-muted-foreground">
																			{program?.auto_disable_weeks === false
																				? t("weekDisableManualHint")
																				: selectedWeek?.keep_visible
																					? t("weekKeepVisibleHint")
																					: t("weekDisableAutoHint")}
																		</p>
																	</div>
																	<Calendar
																		mode="single"
																		locale={calendarLocale}
																		weekStartsOn={1}
																		selected={
																			description.visibleUntil
																				? parseLocalDate(description.visibleUntil)
																				: undefined
																		}
																		onSelect={(date) => {
																			if (date) {
																				updateWeekDisableDate(
																					selectedWeekIndex,
																					nextISODay(toLocalISODate(date)),
																				);
																			}
																		}}
																	/>
																	{(selectedWeek?.disable_date || selectedWeek?.keep_visible) && (
																		<div className="p-2 border-t border-border">
																			<Button
																				variant="ghost"
																				size="sm"
																				className="w-full text-xs text-muted-foreground"
																				onClick={() =>
																					updateWeekDisableDate(selectedWeekIndex, null)
																				}
																			>
																				<Clock className="w-3 h-3 mr-1" />
																				{t("weekDisableAutoReset")}
																			</Button>
																		</div>
																	)}
																</PopoverContent>
															</Popover>
															{program?.type === "individual" &&
																!selectedWeek?.is_published &&
																programAthleteCount > 0 && (
																	<Popover>
																		<PopoverTrigger asChild>
																			<Button
																				variant="outline"
																				size="sm"
																				className={`${controlClass} ${
																					previewCount > 0
																						? "border-acid/60 text-acid hover:bg-acid/10"
																						: "border-border text-muted-foreground"
																				}`}
																			>
																				<Users className="w-3.5 h-3.5 mr-1" />
																				{t("pbPreviewAthletes")}
																				{previewCount > 0 ? ` · ${previewCount}` : ""}
																			</Button>
																		</PopoverTrigger>
																		<PopoverContent
																			className="w-[min(300px,calc(100vw-2rem))] p-0 bg-card border-border"
																			align="start"
																		>
																			<div className="p-3 border-b border-border">
																				<p className="text-xs uppercase tracking-wider text-muted-foreground">
																					{t("pbPublishPreviewOnlyFor")}
																				</p>
																			</div>
																			<div className="max-h-64 overflow-y-auto p-2 space-y-1">
																				{assignedAthletePreviewOptions.map(
																					(athleteOption) => {
																						const selectedPreviewIds =
																							program?.weeks?.[selectedWeekIndex]
																								?.preview_athlete_ids || [];
																						const isSelected =
																							selectedPreviewIds.includes(
																								athleteOption.user_id,
																							);

																						return (
																							<button
																								key={athleteOption.user_id}
																								type="button"
																								onClick={() =>
																									toggleWeekPreviewAthlete(
																										selectedWeekIndex,
																										athleteOption.user_id,
																									)
																								}
																								className={`w-full px-2 py-2 rounded-sm text-left text-sm border transition-colors ${
																									isSelected
																										? "border-acid/60 bg-acid/10 text-acid"
																										: "border-border hover:border-volt/50 hover:bg-secondary/40"
																								}`}
																							>
																								<div className="flex items-center justify-between gap-2">
																									<span className="truncate min-w-0">
																										{athleteOption.name}
																									</span>
																									{isSelected && (
																										<CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
																									)}
																								</div>
																							</button>
																						);
																					},
																				)}
																			</div>
																		</PopoverContent>
																	</Popover>
																)}
														</div>
														<div className="flex items-center gap-2 md:ml-auto shrink-0">
															<Button
																variant="outline"
																size="sm"
																className={controlClass}
																onClick={() => handleDuplicateWeek(selectedWeekIndex)}
															>
																<Copy className="w-3.5 h-3.5 mr-1" />
																{t("pbDuplicateWeek")}
															</Button>
															<DropdownMenu>
																<DropdownMenuTrigger asChild>
																	<Button
																		variant="outline"
																		size="icon"
																		aria-label={t("pbWeekMoreActions")}
																		title={t("pbWeekMoreActions")}
																		className="rounded-sm h-10 w-10 md:h-8 md:w-8 border-border text-muted-foreground hover:text-foreground"
																		data-testid="week-more-actions"
																	>
																		<MoreVertical className="w-4 h-4" />
																	</Button>
																</DropdownMenuTrigger>
																<DropdownMenuContent align="end" className="bg-card border-border w-56">
																	<DropdownMenuItem
																		onClick={() =>
																			handleOpenExportDialog("week_share", {
																				weekIndex: selectedWeekIndex,
																			})
																		}
																	>
																		<Share2 className="w-4 h-4 mr-2" />
																		{t("pbShareWeek")}
																	</DropdownMenuItem>
																	<DropdownMenuItem
																		onClick={() =>
																			handleOpenExportDialog("week", {
																				weekIndex: selectedWeekIndex,
																			})
																		}
																	>
																		<FolderInput className="w-4 h-4 mr-2" />
																		{t("pbExportWeek")}
																	</DropdownMenuItem>
																	{(program?.weeks?.length || 0) > 1 && (
																		<>
																			<DropdownMenuSeparator className="bg-border" />
																			<DropdownMenuItem
																				onClick={() => requestDeleteWeek(selectedWeekIndex)}
																				className="text-blaze focus:text-blaze"
																			>
																				<Trash2 className="w-4 h-4 mr-2" />
																				{t("pbDeleteWeekAction")}
																			</DropdownMenuItem>
																		</>
																	)}
																</DropdownMenuContent>
															</DropdownMenu>
														</div>
													</div>
												);
											})()}
											{/* Il nome della settimana e' modificabile da qui: prima si
											    poteva solo subire quello scritto alla creazione, e una
											    settimana battezzata male restava battezzata male. Vuoto =
											    "Settimana N", che e' il nome di default. */}
											<div className="flex items-center gap-2 w-full">
												<Label className="label-tactical text-[11px] md:text-[10px] shrink-0">
													{t("pbWeekName")}
												</Label>
												<Input
													value={
														program?.weeks?.[selectedWeekIndex]?.label || ""
													}
													onChange={(e) =>
														updateWeekField(
															selectedWeekIndex,
															"label",
															e.target.value,
														)
													}
													className="bg-secondary border-transparent rounded-sm text-xs h-9"
													placeholder={localizeWeekLabel(
														null,
														t,
														selectedWeekIndex,
													)}
													data-testid="week-name-input"
												/>
											</div>
											<div className="flex items-start gap-2 w-full">
												<Label className="label-tactical text-[11px] md:text-[10px] mt-2 shrink-0">
													{t("weekNotesLabel")}
												</Label>
												<Textarea
													value={
														program?.weeks?.[selectedWeekIndex]?.notes || ""
													}
													onChange={(e) =>
														updateWeekField(
															selectedWeekIndex,
															"notes",
															e.target.value,
														)
													}
													className="bg-secondary border-transparent rounded-sm text-xs min-h-[52px]"
													placeholder={t("weekGuidancePlaceholder")}
												/>
											</div>
										</div>
									</div>
								)}

								{/* Add Day Button */}
								<Button
									variant="secondary"
									onClick={handleAddDay}
									className="w-full border border-dashed border-border rounded-sm h-12"
									data-testid="add-day-btn"
								>
									<Plus className="w-4 h-4 mr-2" />
									{t("addDay")}
								</Button>

								{/* Days List — or, as an alternative read-only view of the
								    same week, the column overview. The editor below is
								    untouched by the switch. */}
								{isOverviewView && currentWeekDays.length > 0 ? (
									<WeekOverview
										days={currentWeekDays}
										dayLabel={(day, dayIndex) =>
											localizeDayLabel(day?.title, t, dayIndex)
										}
										dayDateLabel={(day) =>
											program?.is_dated && day?.date ? formatDate(day.date) : ""
										}
										onOpenDay={handleOpenDayFromOverview}
										onCopyExercise={handleCopyExercise}
										dropDayIndex={dragOverDropDayIndex}
										onDropDayIndexChange={setDragOverDropDayIndex}
										onDropExercises={handleDropExercisesOnDay}
									/>
								) : currentWeekDays.length > 0 ? (
									<div className="space-y-4">
										{currentWeekDays.map((day, dayIndex) => {
											const isExpanded = Boolean(expandedDays[dayIndex]);
											const isSelected = selectedDayIndex === dayIndex;
											const isActive = isExpanded || isSelected;
											const hasDayWarmup = Boolean(day.warmup?.trim());
											const hasDayCooldown = Boolean(day.cooldown?.trim());
											const hasDayNotes = Boolean(day.notes?.trim());
											const hasDaySetupConfigured =
												hasDayWarmup || hasDayCooldown || hasDayNotes;
											// Shared read-only exercises cannot be copied, so a day made
											// only of those offers nothing to put on the clipboard.
											const hasCopyableExercises = (day.exercises || []).some(
												(exercise) => !isSharedReadonlyExercise(exercise),
											);
											const isDaySetupExpanded = Boolean(
												expandedDaySetupPanels[dayIndex],
											);
											const isHeaderEditing = editingDayHeaderId === day.day_id;

											return (
												<Card
													key={day.day_id}
													className={`bg-card rounded-sm overflow-hidden transition-all ${
														dragOverDropDayIndex === dayIndex
															? "border-volt ring-2 ring-volt/60"
															: isActive
																? "border-volt/70 ring-1 ring-volt/35"
																: "border-border"
													}`}
													data-testid={`day-${dayIndex}`}
													// An exercise dragged in from the reference panel and the
													// editor's own day reordering share this drop target, so the
													// custom MIME type decides which is happening. The handlers
													// are now always bound: day reordering stays disabled on dated
													// programs, but dropping an exercise must work there too.
													onDragOver={(e) => {
														if (hasExerciseDragPayload(e.dataTransfer)) {
															e.preventDefault();
															e.dataTransfer.dropEffect = "copy";
															if (dragOverDropDayIndex !== dayIndex) {
																setDragOverDropDayIndex(dayIndex);
															}
															return;
														}
														if (!program?.is_dated)
															handleDayDragOver(e, dayIndex);
													}}
													onDragLeave={(e) => {
														// Ignore moves between children of this same card.
														if (e.currentTarget.contains(e.relatedTarget))
															return;
														if (dragOverDropDayIndex === dayIndex) {
															setDragOverDropDayIndex(null);
														}
													}}
													onDrop={(e) => {
														if (handleDropExercisesOnDay(e, dayIndex)) return;
														if (!program?.is_dated) handleDayDragEnd();
													}}
												>
													{/* Day Header */}
													<div
														className={`p-4 border-b flex items-center justify-between cursor-pointer transition-colors ${
															isActive
																? "border-volt/50 bg-volt/10"
																: "border-border hover:bg-white/5"
														}`}
														onClick={() => toggleDayExpansion(dayIndex)}
													>
														<div className="flex items-center gap-3 min-w-0">
															<button
																type="button"
																draggable={!program?.is_dated}
																onClick={(e) => e.stopPropagation()}
																onDragStart={(e) => {
																	e.stopPropagation();
																	handleDayDragStart(dayIndex);
																}}
																onDragEnd={handleDayDragEnd}
																className={`hidden md:inline-flex h-8 w-8 items-center justify-center rounded-sm border border-border ${
																	program?.is_dated
																		? "text-muted-foreground/40 cursor-not-allowed"
																		: "text-muted-foreground hover:text-volt hover:border-volt/50 cursor-grab active:cursor-grabbing"
																}`}
																title={
																	program?.is_dated
																		? t("pbDatedProgramDragHint")
																		: t("pbDragToReorderDay")
																}
															>
																<GripVertical
																	className={`w-4 h-4 ${
																		isActive
																			? "text-volt"
																			: "text-muted-foreground"
																	}`}
																/>
															</button>
															<div className="min-w-0">
																{/* Truncation is undone above md so a long day title keeps
																    wrapping on desktop exactly as before. */}
																<p
																	className={`font-heading truncate md:overflow-visible md:whitespace-normal ${
																		isActive ? "text-volt" : ""
																	}`}
																>
																	{localizeDayLabel(day.title, t, dayIndex)}
																</p>
																{isHeaderEditing && (
																	<p className="text-xs md:text-[11px] uppercase tracking-widest text-volt/90">
																		{t("pbEditing")}
																	</p>
																)}
																<p className="text-sm text-muted-foreground truncate md:overflow-visible md:whitespace-normal">
																	{program?.is_dated
																		? formatDate(day.date, language)
																		: localizeDayLabel(
																			null,
																			t,
																			(day.day_number || dayIndex + 1) - 1,
																		)}
																</p>
															</div>
														</div>
														<div className="flex items-center gap-1 md:gap-2 shrink-0">
															<span
																className={`text-xs ${
																	isActive
																		? "text-foreground/80"
																		: "text-muted-foreground"
																}`}
															>
																{day.exercises?.length || 0} {t("exerciseCountLabel")}
															</span>
															{/* The four icon buttons stay desktop-only; below md they become
															    one labelled menu, which also carries the day reordering the
															    hidden drag handle can no longer provide on touch. */}
															<div className="hidden md:flex items-center gap-2">
																<button
																	type="button"
																	onClick={(e) => {
																		e.stopPropagation();
																		setEditingDayHeaderId((prev) =>
																			prev === day.day_id ? null : day.day_id,
																		);
																		setHeaderDatePickerDayId(null);
																	}}
																	className={`h-8 w-8 inline-flex items-center justify-center rounded-sm border transition-colors ${
																		isHeaderEditing
																			? "border-volt/60 bg-volt/15 text-volt"
																			: "border-border text-muted-foreground hover:text-volt hover:border-volt/50"
																	}`}
																	title={
																		isHeaderEditing
																			? t("pbDayActionEditClose")
																			: t("pbDayActionEdit")
																	}
																	aria-pressed={isHeaderEditing}
																	aria-expanded={isHeaderEditing}
																>
																	<Pencil className="w-4 h-4" />
																</button>
																<button
																	type="button"
																	onClick={(e) => {
																		e.stopPropagation();
																		handleDuplicateDay(dayIndex);
																	}}
																	className="h-8 w-8 inline-flex items-center justify-center rounded-sm border border-border text-muted-foreground hover:text-volt hover:border-volt/50"
																	title={t("pbDayActionDuplicate")}
																>
																	<Copy className="w-4 h-4" />
																</button>
																<button
																	type="button"
																	onClick={(e) => {
																		e.stopPropagation();
																		handleOpenExportDialog("day", { dayIndex });
																	}}
																	className="h-8 w-8 inline-flex items-center justify-center rounded-sm border border-border text-muted-foreground hover:text-volt hover:border-volt/50"
																	title={t("pbDayActionExport")}
																>
																	<FolderInput className="w-4 h-4" />
																</button>
																<button
																	type="button"
																	onClick={(e) => {
																		e.stopPropagation();
																		handleRemoveDay(dayIndex);
																	}}
																	className="h-8 w-8 inline-flex items-center justify-center rounded-sm border border-border text-muted-foreground hover:text-blaze hover:border-blaze/50"
																	title={t("pbDayActionRemove")}
																>
																	<Trash2 className="w-4 h-4" />
																</button>
															</div>
															<DropdownMenu>
																<DropdownMenuTrigger asChild>
																	<Button
																		variant="ghost"
																		size="icon"
																		onClick={(e) => e.stopPropagation()}
																		aria-label={t("pbDayActions")}
																		className="md:hidden h-10 w-10 text-muted-foreground"
																	>
																		<MoreVertical className="w-4 h-4" />
																	</Button>
																</DropdownMenuTrigger>
																<DropdownMenuContent
																	align="end"
																	className="bg-card border-border w-56"
																	onClick={(e) => e.stopPropagation()}
																>
																	<DropdownMenuItem
																		onClick={() => {
																			setEditingDayHeaderId((prev) =>
																				prev === day.day_id ? null : day.day_id,
																			);
																			setHeaderDatePickerDayId(null);
																		}}
																	>
																		<Pencil className="w-4 h-4 mr-2" />
																		{isHeaderEditing
																			? t("pbDayActionEditClose")
																			: t("pbDayActionEdit")}
																	</DropdownMenuItem>
																	{!program?.is_dated && (
																		<>
																			<DropdownMenuSeparator className="bg-border" />
																			<DropdownMenuItem
																				disabled={dayIndex === 0}
																				onClick={() =>
																					handleMoveDay(dayIndex, -1)
																				}
																			>
																				<ArrowUp className="w-4 h-4 mr-2" />
																				{t("pbActionMoveUp")}
																			</DropdownMenuItem>
																			<DropdownMenuItem
																				disabled={
																					dayIndex ===
																					currentWeekDays.length - 1
																				}
																				onClick={() =>
																					handleMoveDay(dayIndex, 1)
																				}
																			>
																				<ArrowDown className="w-4 h-4 mr-2" />
																				{t("pbActionMoveDown")}
																			</DropdownMenuItem>
																		</>
																	)}
																	<DropdownMenuSeparator className="bg-border" />
																	<DropdownMenuItem
																		onClick={() => handleDuplicateDay(dayIndex)}
																	>
																		<Copy className="w-4 h-4 mr-2" />
																		{t("pbDayActionDuplicate")}
																	</DropdownMenuItem>
																	<DropdownMenuItem
																		onClick={() =>
																			handleOpenExportDialog("day", {
																				dayIndex,
																			})
																		}
																	>
																		<FolderInput className="w-4 h-4 mr-2" />
																		{t("pbDayActionExport")}
																	</DropdownMenuItem>
																	<DropdownMenuSeparator className="bg-border" />
																	<DropdownMenuItem
																		onClick={() => handleRemoveDay(dayIndex)}
																		className="text-blaze focus:text-blaze"
																	>
																		<Trash2 className="w-4 h-4 mr-2" />
																		{t("pbDayActionRemove")}
																	</DropdownMenuItem>
																</DropdownMenuContent>
															</DropdownMenu>
															{isExpanded ? (
																<ChevronUp className="w-4 h-4" />
															) : (
																<ChevronDown className="w-4 h-4" />
															)}
														</div>
													</div>

													{isHeaderEditing && (
														<div
															className="px-4 py-3 border-b border-volt/40 bg-volt/10 space-y-3"
															onClick={(e) => e.stopPropagation()}
														>
															<div className="flex items-start justify-between gap-3">
																<div>
																	<p className="text-xs uppercase tracking-widest text-volt">
																		{t("pbEditDayTitleAndDate")}
																	</p>
																	<p className="text-xs md:text-[11px] text-muted-foreground mt-1">
																		{t("pbSectionOpenHintEsc")}
																	</p>
																</div>
																<Button
																	variant="outline"
																	size="sm"
																	className="rounded-sm border-border text-xs"
																	onClick={() => {
																		setEditingDayHeaderId(null);
																		setHeaderDatePickerDayId(null);
																	}}
																>
																	<X className="w-3.5 h-3.5 mr-1" />
																	{t("close")}
																</Button>
															</div>

															<div className="space-y-1">
																<Label className="label-tactical text-[11px] md:text-[10px]">
																	{t("pbDayTitle")}
																</Label>
																<Input
																	value={day.title || ""}
																	onChange={(e) =>
																		handleUpdateDay(
																			dayIndex,
																			"title",
																			e.target.value,
																		)
																	}
																	className="bg-background border-transparent rounded-sm h-9"
																	placeholder={localizeDayLabel(
																		null,
																		t,
																		(day.day_number || dayIndex + 1) - 1,
																	)}
																/>
															</div>

															{program?.is_dated && (
																<div className="space-y-1">
																	<Label className="label-tactical text-[11px] md:text-[10px]">
																		{t("pbDayDate")}
																	</Label>
																	<Popover
																		open={headerDatePickerDayId === day.day_id}
																		onOpenChange={(open) =>
																			setHeaderDatePickerDayId(
																				open ? day.day_id : null,
																			)
																		}
																	>
																		<PopoverTrigger asChild>
																			<Button
																				variant="outline"
																				className="w-full justify-start bg-background border-transparent rounded-sm"
																			>
																				<CalendarIcon className="w-4 h-4 mr-2" />
																				{day.date
																					? formatDate(day.date, language)
																					: t("pbSelectDate")}
																			</Button>
																		</PopoverTrigger>
																		<PopoverContent className="w-auto p-0 bg-card border-border">
																			<Calendar
																				mode="single"
																				locale={calendarLocale}
																				weekStartsOn={1}
																				selected={
																					day.date
																						? parseLocalDate(day.date)
																						: undefined
																				}
																				onSelect={(date) => {
																					if (date) {
																						handleUpdateDay(
																							dayIndex,
																							"date",
																							toLocalISODate(date),
																						);
																						setHeaderDatePickerDayId(null);
																					}
																				}}
																			/>
																		</PopoverContent>
																	</Popover>
																</div>
															)}
														</div>
													)}

													{/* Day Content */}
													{isExpanded && (
														<CardContent className="p-4 space-y-4">
															{/* Compact Day Setup Panel */}
															<div
																className={`border rounded-sm transition-colors ${
																	isDaySetupExpanded
																		? "border-volt/50 bg-volt/10"
																		: "border-border bg-secondary/30"
																}`}
															>
																<button
																	type="button"
																	onClick={() => toggleDaySetupPanel(dayIndex)}
																	className={`w-full px-3 py-2 flex items-center justify-between text-left rounded-sm transition-colors ${
																		isDaySetupExpanded
																			? "bg-volt/10 border-b border-volt/30"
																			: hasDaySetupConfigured
																				? "bg-acid/10 hover:bg-acid/15"
																				: "hover:bg-secondary/50"
																	}`}
																>
																	<div className="flex items-center gap-2">
																		<span className="text-[11px] md:text-[10px] uppercase tracking-widest text-muted-foreground">
																			{t("pbDayDetails")}
																		</span>
																		{isDaySetupExpanded && (
																			<span className="inline-flex items-center gap-1 rounded-sm border border-volt/50 bg-volt/15 px-1.5 py-0.5 text-[11px] md:text-[10px] uppercase tracking-widest text-volt">
																				<span className="h-1.5 w-1.5 rounded-full bg-volt" />
																				{t("pbEditing")}
																			</span>
																		)}
																		{hasDaySetupConfigured &&
																			!isDaySetupExpanded && (
																				<span className="inline-flex items-center gap-1 rounded-sm border border-acid/50 bg-acid/15 px-1.5 py-0.5 text-[11px] md:text-[10px] uppercase tracking-widest text-acid">
																					<span className="h-1.5 w-1.5 rounded-full bg-acid" />
																					{t("pbConfigured")}
																				</span>
																			)}
																		<span className="text-xs md:text-[11px] text-muted-foreground">
																			{day.title?.trim()
																				? t("pbHasTitle")
																				: t("pbNoTitle")}{" "}
																			·{" "}
																			{hasDayWarmup ? t("pbWarmup") : t("pbNoWarmup")}{" "}
																			·{" "}
																			{hasDayCooldown
																				? t("pbCooldown")
																				: t("pbNoCooldown")}{" "}
																			· {hasDayNotes ? t("pbHasNotes") : t("pbNoNotes")} ·
																			{t("pbSessions")}{" "}
																			{Math.max(1, (day.sessions || []).length)}
																		</span>
																	</div>
																	{isDaySetupExpanded ? (
																		<ChevronUp className="w-4 h-4 text-muted-foreground" />
																	) : (
																		<ChevronDown className="w-4 h-4 text-muted-foreground" />
																	)}
																</button>

																{isDaySetupExpanded && (
																	<div className="px-3 pb-3 space-y-3 border-t border-border/70">
																		<div className="pt-3 flex items-start justify-between gap-3">
																			<div>
																				<p className="text-xs uppercase tracking-widest text-volt">
																					{t("pbEditDayDetails")}
																				</p>
																				<p className="text-xs md:text-[11px] text-muted-foreground mt-1">
																					{t("pbSectionOpenHintShort")}
																				</p>
																			</div>
																			<Button
																				variant="outline"
																				size="sm"
																				className="rounded-sm border-border text-xs"
																				onClick={() =>
																					setExpandedDaySetupPanels((prev) => ({
																						...prev,
																						[dayIndex]: false,
																					}))
																				}
																			>
																				<X className="w-3.5 h-3.5 mr-1" />
																				{t("close")}
																			</Button>
																		</div>

																		<div className="grid grid-cols-1 lg:grid-cols-12 gap-2">
																			<div className="space-y-1 lg:col-span-2">
																				<Label className="label-tactical text-[11px] md:text-[10px]">
																					{t("titleLabel")}
																				</Label>
																				<Input
																					value={day.title || ""}
																					onChange={(e) =>
																						handleUpdateDay(
																							dayIndex,
																							"title",
																							e.target.value,
																						)
																					}
																					className="bg-background border-transparent rounded-sm text-xs h-8"
																					placeholder={localizeDayLabel(
																						null,
																						t,
																						(day.day_number || dayIndex + 1) - 1,
																					)}
																				/>
																			</div>
																			<div className="space-y-1 lg:col-span-4">
																				<div className="flex items-center justify-between gap-2">
																					<Label className="label-tactical text-[11px] md:text-[10px]">
																						{t("pbWarmup")}
																					</Label>
																					<WarmupLibraryPicker
																						value={day.warmup}
																						onApply={(content) =>
																							handleUpdateDay(
																								dayIndex,
																								"warmup",
																								content,
																							)
																						}
																						t={t}
																					/>
																				</div>
																				<Textarea
																					value={day.warmup || ""}
																					onChange={(e) =>
																						handleUpdateDay(
																							dayIndex,
																							"warmup",
																							e.target.value,
																						)
																					}
																					onInput={(e) =>
																						resizeTextareaToContent(
																							e.currentTarget,
																						)
																					}
																					textareaRef={(node) => {
																						if (node && hasDayWarmup) {
																							resizeTextareaToContent(node);
																						}
																					}}
																					className="bg-background border-transparent rounded-sm text-xs min-h-[44px]"
																					placeholder={t("pbWarmupPlaceholder")}
																				/>
																			</div>
																			<div className="space-y-1 lg:col-span-3">
																				<Label className="label-tactical text-[11px] md:text-[10px]">
																					{t("pbCooldown")}
																				</Label>
																				<Textarea
																					value={day.cooldown || ""}
																					onChange={(e) =>
																						handleUpdateDay(
																							dayIndex,
																							"cooldown",
																							e.target.value,
																						)
																					}
																					onInput={(e) =>
																						resizeTextareaToContent(
																							e.currentTarget,
																						)
																					}
																					textareaRef={(node) => {
																						if (node && hasDayCooldown) {
																							resizeTextareaToContent(node);
																						}
																					}}
																					className="bg-background border-transparent rounded-sm text-xs min-h-[44px]"
																					placeholder={t("pbCooldownPlaceholder")}
																				/>
																			</div>
																			<div className="space-y-1 lg:col-span-3">
																				<Label className="label-tactical text-[11px] md:text-[10px]">
																					{t("pbDayNotes")}
																				</Label>
																				<Textarea
																					value={day.notes || ""}
																					onChange={(e) =>
																						handleUpdateDay(
																							dayIndex,
																							"notes",
																							e.target.value,
																						)
																					}
																					onInput={(e) =>
																						resizeTextareaToContent(
																							e.currentTarget,
																						)
																					}
																					textareaRef={(node) => {
																						if (node && hasDayNotes) {
																							resizeTextareaToContent(node);
																						}
																					}}
																					className="bg-background border-transparent rounded-sm text-xs min-h-[44px]"
																					placeholder={t("pbQuickNotesPlaceholder")}
																				/>
																			</div>
																		</div>

																		<div className="space-y-2">
																			<div className="flex items-center justify-between">
																				<span className="text-[11px] md:text-[10px] uppercase tracking-widest text-muted-foreground">
																					{t("pbSessions")}
																				</span>
																				<span className="text-xs md:text-[11px] text-muted-foreground">
																					{Math.max(
																						1,
																						(day.sessions || []).length,
																					)}
																				</span>
																			</div>

																			{(day.sessions || []).length > 0 && (
																				<div className="flex flex-wrap gap-1.5">
																					{day.sessions.map((session, sIdx) => (
																						<div
																							key={session.session_id || sIdx}
																							className="h-7 inline-flex items-center gap-1 px-2 bg-background rounded-sm border border-volt/30"
																						>
																							<span className="text-xs md:text-[11px] text-volt max-w-[130px] truncate">
																								{session.label}
																							</span>
																							<button
																								type="button"
																								onClick={() =>
																									handleMoveSession(
																										dayIndex,
																										sIdx,
																										-1,
																									)
																								}
																								className="text-muted-foreground hover:text-volt disabled:opacity-30"
																								disabled={sIdx === 0}
																								title={t("pbMoveSessionUp")}
																							>
																								<ChevronUp className="w-3 h-3" />
																							</button>
																							<button
																								type="button"
																								onClick={() =>
																									handleMoveSession(
																										dayIndex,
																										sIdx,
																										1,
																									)
																								}
																								className="text-muted-foreground hover:text-volt disabled:opacity-30"
																								disabled={
																									sIdx ===
																									(day.sessions || []).length -
																										1
																								}
																								title={t("pbMoveSessionDown")}
																							>
																								<ChevronDown className="w-3 h-3" />
																							</button>
																							<button
																								type="button"
																								onClick={() =>
																									handleRemoveSession(
																										dayIndex,
																										sIdx,
																									)
																								}
																								className="text-blaze hover:text-blaze/80"
																								title={t("pbRemoveSession")}
																							>
																								<X className="w-3 h-3" />
																							</button>
																						</div>
																					))}
																				</div>
																			)}

																			<div className="flex gap-2">
																				<Input
																					type="text"
																					value={
																						newSessionLabelByDay[dayIndex] || ""
																					}
																					onChange={(e) =>
																						setNewSessionLabelByDay((prev) => ({
																							...prev,
																							[dayIndex]: e.target.value,
																						}))
																					}
																					onKeyDown={(e) => {
																						if (e.key === "Enter") {
																							e.preventDefault();
																							handleQuickAddSession(dayIndex);
																						}
																					}}
																					className="bg-background border-transparent rounded-sm text-xs h-8"
																					placeholder={t("pbNewSession")}
																				/>
																				<Button
																					type="button"
																					size="sm"
																					variant="outline"
																					onClick={() =>
																						handleQuickAddSession(dayIndex)
																					}
																					className="border-volt/30 text-volt hover:bg-volt/5 rounded-sm h-8 text-xs"
																				>
																					<Plus className="w-3 h-3 mr-1" />
																					{t("sessionLabel")}
																				</Button>
																			</div>
																		</div>
																	</div>
																)}
															</div>

															{/* Exercises */}
															<div className="space-y-2">
																<Label className="label-tactical text-xs">
																	{t("exercises")}
																</Label>
																{day.exercises?.length > 0 ? (
																	<div className="space-y-2">
																		{day.exercises.map((exercise, exIndex) => {
																			const exType =
																				exercise.exercise_type || "strength";
																			const TypeIcon = exerciseTypeIcon(exType);
																			const typeColor =
																				EXERCISE_TYPES.find(
																					(t) => t.value === exType,
																				)?.color || "text-volt";
																			const daySessions = day.sessions || [];
																			const assignedSession = daySessions.find(
																				(session) =>
																					session.session_id ===
																					exercise.session_id,
																			);
																			const isResultEntryEnabled =
																				exercise.allow_result_entry !== false;
																			const isLeaderboardEnabled =
																				exercise.leaderboard_enabled !== false;
																			const isSharedReadonly =
																				exercise.shared_readonly === true &&
																				exercise.shared_is_origin !== true;
																			const isSharedOrigin =
																				exercise.shared_is_origin === true &&
																				exercise.shared_origin_program_id ===
																					program?.program_id;
																			const sharedUsageEntry =
																				exercise.shared_exercise_id
																					? sharedExerciseUsage[
																							exercise.shared_exercise_id
																						]
																					: null;
																			const sharedUsageTargets =
																				sharedUsageEntry?.targets || [];
																			const sharedOriginProgramLabel =
																				exercise.shared_origin_program_name ||
																				sharedOriginProgramNames[
																					exercise.shared_origin_program_id
																				] ||
																				t("pbSourceProgramFallback");
																			const displayRows =
																				exercise.set_rows?.length > 0
																					? exercise.set_rows
																					: [
																							{
																								sets: exercise.sets || 1,
																								reps: exercise.reps || "",
																								load_percentage:
																									exercise.load_percentage ||
																									null,
																								rest_seconds:
																									normalizeSmartRestInput(
																										exercise.rest_seconds,
																									) || null,
																								rpe: exercise.rpe || null,
																								rir: exercise.rir || null,
																								notes: "",
																							},
																						];
																			const exerciseKey = `${dayIndex}:${exIndex}`;
																			const isExerciseExpanded = Boolean(
																				expandedExercises[exerciseKey],
																			);
																			const exercisePrescription =
																				formatExercisePrescription(exercise, t);
																			const exerciseDisplayName =
																				exType === "conditioning"
																					? formatConditioningDisplayName(
																							exercise.exercise_name,
																							exercise.conditioning_type,
																						)
																					: exercise.exercise_name;
																			const methodologyTagLabel =
																				exType === "strength"
																					? {
																							superset: "Superset",
																							emom: "EMOM",
																							complex: "Complex",
																						}[exercise.methodology] || null
																					: null;
																			const typeVariantLabel =
																				exType === "running" && exercise.running_type
																					? optionLabel(RUNNING_TYPES, exercise.running_type)
																					: exType === "swim" && exercise.swim_type
																						? optionLabel(SWIM_TYPES, exercise.swim_type)
																						: exType === "bike" && exercise.bike_type
																							? optionLabel(BIKE_TYPES, exercise.bike_type)
																							: exType === "erg" && exercise.bike_type
																								? getErgTypeLabelLocalized(
																										exercise.bike_type,
																										t,
																									)
																								: "";
																			const headerScopeTag = (() => {
																				if (
																					exType !== "strength" ||
																					!exercise.exercise_id
																				)
																					return null;
																				const libraryExercise =
																					getExerciseScopeById(
																						exercise.exercise_id,
																					);
																				return (
																					resolveProgramExerciseScopeTag({
																						...exercise,
																						exercise_name:
																							exercise.exercise_name ||
																							exercise.name,
																					}) ||
																					(libraryExercise
																						? getExerciseLibraryScopeTag(
																								libraryExercise,
																							)
																						: null)
																				);
																			})();
																			const headerTags = [];
																			// First so it survives the tag strip's "+N" overflow: while
																			// comparing weeks the delta is the reason you are looking.
																			const referenceDeltaTag =
																				getReferenceDeltaTag(exercise, dayIndex);
																			if (referenceDeltaTag) {
																				headerTags.push(referenceDeltaTag);
																			}
																			if (methodologyTagLabel) {
																				headerTags.push({
																					key: "methodology",
																					label: methodologyTagLabel,
																					tone: "volt",
																					showLabel: true,
																					tooltip: `Metodologia: ${methodologyTagLabel}`,
																				});
																			}
																			if (typeVariantLabel) {
																				headerTags.push({
																					key: "type-variant",
																					label: typeVariantLabel,
																					tone: "muted",
																					showLabel: true,
																					// "RowErg - Ripetute" is the whole point of the chip:
																					// 92px cut it to "ROWERG - ...".
																					labelWidthClass: "max-w-[180px]",
																				});
																			}
																			if (assignedSession) {
																				headerTags.push({
																					key: "session",
																					icon: Layers,
																					label: assignedSession.label,
																					tone: "volt",
																					showLabel: true,
																					tooltip: `${t("pbSessionPrefix")}: ${assignedSession.label}`,
																				});
																			}
																			if (isSharedReadonly) {
																				headerTags.push({
																					key: "shared-from",
																					icon: Link2,
																					label: `Condiviso da ${sharedOriginProgramLabel}`,
																					tone: "amber",
																					tooltip: (
																						<div className="space-y-1">
																							<p className="font-medium">
																								{t("pbSharedExerciseReadonly")}
																							</p>
																							<p>
																								Origine:{" "}
																								{sharedOriginProgramLabel}
																							</p>
																						</div>
																					),
																				});
																			}
																			if (
																				isSharedOrigin &&
																				exercise.shared_exercise_id
																			) {
																				headerTags.push({
																					key: "shared-with",
																					icon: Share2,
																					badgeValue:
																						sharedUsageTargets.length > 0
																							? sharedUsageTargets.length
																							: null,
																					label:
																						sharedUsageTargets.length > 0
																							? `${t("pbSharedWith")} ${sharedUsageTargets.length} ${t(
																									sharedUsageTargets.length === 1
																										? "pbProgramOne"
																										: "pbProgramMany",
																								)}`
																							: t("pbSharingActive"),
																					tone: "emerald",
																					tooltip:
																						sharedUsageTargets.length > 0 ? (
																							<div className="space-y-1">
																								<p className="font-medium">
																									{t("pbSharedWith")}{" "}
																									{sharedUsageTargets.length}{" "}
																									{sharedUsageTargets.length ===
																									1
																										? t("pbProgramOne")
																										: t("pbProgramMany")}
																								</p>
																								{sharedUsageTargets.map(
																									(targetProgram) => (
																										<p
																											key={
																												targetProgram.program_id
																											}
																										>
																											{
																												targetProgram.program_title
																											}
																										</p>
																									),
																								)}
																							</div>
																						) : (
																							t("pbSharingActiveNoTarget")
																						),
																				});
																			}
																			if (headerScopeTag) {
																				const isScopeGlobal =
																					headerScopeTag === "global";
																				headerTags.push({
																					key: "scope",
																					icon: isScopeGlobal ? Globe : User,
																					label:
																						getExerciseLibraryScopeLabelByTag(
																							headerScopeTag,
																						),
																					tone: isScopeGlobal
																						? "amber"
																						: "emerald",
																					tooltip: isScopeGlobal
																						? t("pbExerciseGlobalLibrary")
																						: t("pbExercisePersonalLibrary"),
																				});
																			}
																			return (
																				<div
																					key={exIndex}
																					className="p-3 bg-secondary rounded-sm space-y-2"
																					onDragOver={(e) =>
																						handleDragOver(e, dayIndex, exIndex)
																					}
																				>
																					{/* Exercise header */}
																					<div className="flex items-center gap-1.5 md:gap-3">
																						<button
																							type="button"
																							draggable
																							onDragStart={(event) => {
																								event.stopPropagation();
																								handleDragStart(
																									dayIndex,
																									exIndex,
																								);
																							}}
																							onDragEnd={handleDragEnd}
																							className="hidden md:inline-flex h-7 w-7 items-center justify-center rounded-sm border border-border text-muted-foreground hover:text-volt hover:border-volt/50 cursor-grab active:cursor-grabbing flex-shrink-0"
																							title={t("pbDragToReorderExercise")}
																						>
																							<GripVertical className="w-4 h-4" />
																						</button>
																						{/* Icon nested with the name so that, when the header stacks
																						    into two rows below md, it rides with the name instead of
																						    sitting on a row of its own. The gap-3 / gap-2 split keeps
																						    desktop spacing identical to before. */}
																						{/* Tapping the row toggles the editor: a 36px chevron is a poor
																						    target on a phone. On desktop the handler is inert — the
																						    editor is `md:block` regardless of the flag. */}
																						<div
																							onClick={() =>
																								setExpandedExercises(
																									(prev) => ({
																										...prev,
																										[exerciseKey]:
																											!prev[exerciseKey],
																									}),
																								)
																							}
																							className="flex-1 min-w-0 flex items-center gap-3 cursor-pointer md:cursor-auto"
																						>
																							<TypeIcon
																								className={`w-4 h-4 ${typeColor} flex-shrink-0`}
																							/>
																							<div className="flex-1 min-w-0 flex flex-col md:flex-row md:items-center md:gap-2">
																								<span
																									className="text-sm font-medium truncate"
																									title={exerciseDisplayName}
																								>
																									{exerciseDisplayName}
																								</span>
																								<div className="flex items-center gap-2 min-w-0">
																									{exercisePrescription && (
																										<span className="md:hidden font-mono text-xs md:text-[11px] text-muted-foreground truncate">
																											{exercisePrescription}
																										</span>
																									)}
																									<ExerciseTagStrip
																										tags={headerTags}
																									/>
																								</div>
																							</div>
																						</div>
																						<TooltipProvider delayDuration={90}>
																							<div className="hidden md:flex items-center gap-3 flex-shrink-0">
																								{!isSharedReadonly &&
																									exType === "strength" &&
																									(!exercise.methodology ||
																										exercise.methodology ===
																											"standard" ||
																										exercise.methodology ===
																											"complex") && (
																										<>
																											<Tooltip>
																												<TooltipTrigger asChild>
																													<Button
																														variant="ghost"
																														size="icon"
																														className="h-7 w-7 text-volt hover:text-volt/80"
																														onClick={() =>
																															handleAddSetRow(
																																dayIndex,
																																exIndex,
																															)
																														}
																													>
																														<Plus className="w-3.5 h-3.5" />
																													</Button>
																												</TooltipTrigger>
																												<TooltipContent side="top">
																													{t("pbActionAddSetRow")}
																												</TooltipContent>
																											</Tooltip>
																										</>
																									)}

																								<Tooltip>
																									<TooltipTrigger asChild>
																										<Button
																											variant="ghost"
																											size="icon"
																											className={`h-7 w-7 ${
																												isLeaderboardEnabled
																													? "text-amber-400 hover:text-amber-300"
																													: "text-muted-foreground hover:text-amber-400"
																											}`}
																											onClick={() =>
																												handleToggleExerciseLeaderboard(
																													dayIndex,
																													exIndex,
																												)
																											}
																										>
																											{isLeaderboardEnabled ? (
																												<Trophy className="w-3.5 h-3.5" />
																											) : (
																												<EyeOff className="w-3.5 h-3.5" />
																											)}
																										</Button>
																									</TooltipTrigger>
																									<TooltipContent side="top">
																										{isLeaderboardEnabled
																											? "Disattiva leaderboard"
																											: "Attiva leaderboard"}
																									</TooltipContent>
																								</Tooltip>

																								<Tooltip>
																									<TooltipTrigger asChild>
																										<Button
																											variant="ghost"
																											size="icon"
																											className={`h-7 w-7 ${
																												isResultEntryEnabled
																													? "text-emerald-400 hover:text-emerald-300"
																													: "text-muted-foreground hover:text-amber-400"
																											}`}
																											onClick={() =>
																												handleToggleExerciseResultEntry(
																													dayIndex,
																													exIndex,
																												)
																											}
																										>
																											{isResultEntryEnabled ? (
																												<CheckCircle2 className="w-3.5 h-3.5" />
																											) : (
																												<EyeOff className="w-3.5 h-3.5" />
																											)}
																										</Button>
																									</TooltipTrigger>
																									<TooltipContent side="top">
																										{isResultEntryEnabled
																											? "Disattiva inserimento risultato"
																											: "Attiva inserimento risultato"}
																									</TooltipContent>
																								</Tooltip>

																								{!isSharedReadonly && (
																									<Tooltip>
																										<TooltipTrigger asChild>
																											<Button
																												variant="ghost"
																												size="icon"
																												className="h-7 w-7 text-muted-foreground hover:text-volt"
																												onClick={() =>
																													handleCopyExercise(
																														dayIndex,
																														exIndex,
																													)
																												}
																											>
																												<Copy className="w-3.5 h-3.5" />
																											</Button>
																										</TooltipTrigger>
																										<TooltipContent side="top">
																											{t("pbActionCopyExercise")}
																										</TooltipContent>
																									</Tooltip>
																								)}

																								{!isSharedReadonly && (
																									<Tooltip>
																										<TooltipTrigger asChild>
																											<Button
																												variant="ghost"
																												size="icon"
																												className="h-7 w-7 text-muted-foreground hover:text-volt"
																												onClick={() =>
																													handleOpenExportDialog(
																														"exercise_copy",
																														{
																															dayIndex,
																															exerciseIndex:
																																exIndex,
																														},
																													)
																												}
																											>
																												<FolderInput className="w-3.5 h-3.5" />
																											</Button>
																										</TooltipTrigger>
																										<TooltipContent side="top">
																											{t("pbActionExportExercise")}
																										</TooltipContent>
																									</Tooltip>
																								)}

																								{!isSharedReadonly && (
																									<Tooltip>
																										<TooltipTrigger asChild>
																											<Button
																												variant="ghost"
																												size="icon"
																												className="h-7 w-7 text-muted-foreground hover:text-volt"
																												onClick={() =>
																													handleOpenExportDialog(
																														"exercise",
																														{
																															dayIndex,
																															exerciseIndex:
																																exIndex,
																														},
																													)
																												}
																											>
																												<Share2 className="w-3.5 h-3.5" />
																											</Button>
																										</TooltipTrigger>
																										<TooltipContent side="top">
																											{t("pbActionShareExercise")}
																										</TooltipContent>
																									</Tooltip>
																								)}

																								{!isSharedReadonly &&
																									(program?.weeks || [])
																										.length > 1 &&
																									selectedWeekIndex <
																										(program?.weeks || [])
																											.length -
																											1 && (
																										<Tooltip>
																											<TooltipTrigger asChild>
																												<Button
																													variant="ghost"
																													size="icon"
																													className="h-7 w-7 text-muted-foreground hover:text-volt"
																													onClick={() =>
																														handleDuplicateExerciseToNextWeek(
																															dayIndex,
																															exIndex,
																														)
																													}
																												>
																													<CopyPlus className="w-3.5 h-3.5" />
																												</Button>
																											</TooltipTrigger>
																											<TooltipContent side="top">
																												{t("pbActionDuplicateNextWeek")}
																											</TooltipContent>
																										</Tooltip>
																									)}

																								{!isSharedReadonly && (
																									<Tooltip>
																										<TooltipTrigger asChild>
																											<Button
																												variant="ghost"
																												size="icon"
																												className="h-7 w-7 text-muted-foreground hover:text-volt"
																												onClick={() =>
																													handleEditExercise(
																														dayIndex,
																														exIndex,
																													)
																												}
																											>
																												<Pencil className="w-3.5 h-3.5" />
																											</Button>
																										</TooltipTrigger>
																										<TooltipContent side="top">
																											{t("pbActionEditExercise")}
																										</TooltipContent>
																									</Tooltip>
																								)}

																								<Tooltip>
																									<TooltipTrigger asChild>
																										<Button
																											variant="ghost"
																											size="icon"
																											className="h-7 w-7 text-muted-foreground hover:text-blaze"
																											onClick={() =>
																												handleRemoveExercise(
																													dayIndex,
																													exIndex,
																												)
																											}
																										>
																											<Trash2 className="w-4 h-4" />
																										</Button>
																									</TooltipTrigger>
																									<TooltipContent side="top">
																										{t("pbActionRemoveExercise")}
																									</TooltipContent>
																								</Tooltip>
																							</div>
																						</TooltipProvider>

																						{/* Mobile only: the nine icon actions become one labelled
																						    menu — finally readable — plus a toggle for the inline
																						    editor, which below md stays closed until asked for. */}
																						<div className="md:hidden flex items-center gap-0.5 shrink-0">
																							<DropdownMenu>
																								<DropdownMenuTrigger asChild>
																									<Button
																										variant="ghost"
																										size="icon"
																										aria-label={t(
																											"pbExerciseActions",
																										)}
																										className="h-10 w-10 text-muted-foreground"
																									>
																										<MoreVertical className="w-4 h-4" />
																									</Button>
																								</DropdownMenuTrigger>
																								<DropdownMenuContent
																									align="end"
																									className="bg-card border-border w-56"
																								>
																									{!isSharedReadonly && (
																										<DropdownMenuItem
																											onClick={() =>
																												handleEditExercise(
																													dayIndex,
																													exIndex,
																												)
																											}
																										>
																											<Pencil className="w-4 h-4 mr-2" />
																											{t(
																												"pbActionEditExercise",
																											)}
																										</DropdownMenuItem>
																									)}
																									{!isSharedReadonly &&
																										exType === "strength" &&
																										(!exercise.methodology ||
																											exercise.methodology ===
																												"standard" ||
																											exercise.methodology ===
																												"complex") && (
																											<DropdownMenuItem
																												onClick={() =>
																													handleAddSetRow(
																														dayIndex,
																														exIndex,
																													)
																												}
																											>
																												<Plus className="w-4 h-4 mr-2" />
																												{t("pbActionAddSetRow")}
																											</DropdownMenuItem>
																										)}
																									<DropdownMenuSeparator className="bg-border" />
																									<DropdownMenuItem
																										disabled={exIndex === 0}
																										onClick={() =>
																											handleMoveExercise(
																												dayIndex,
																												exIndex,
																												-1,
																											)
																										}
																									>
																										<ArrowUp className="w-4 h-4 mr-2" />
																										{t("pbActionMoveUp")}
																									</DropdownMenuItem>
																									<DropdownMenuItem
																										disabled={
																											exIndex ===
																											(day.exercises || [])
																												.length -
																												1
																										}
																										onClick={() =>
																											handleMoveExercise(
																												dayIndex,
																												exIndex,
																												1,
																											)
																										}
																									>
																										<ArrowDown className="w-4 h-4 mr-2" />
																										{t("pbActionMoveDown")}
																									</DropdownMenuItem>
																									<DropdownMenuSeparator className="bg-border" />
																									<DropdownMenuItem
																										onClick={() =>
																											handleToggleExerciseLeaderboard(
																												dayIndex,
																												exIndex,
																											)
																										}
																									>
																										<Trophy className="w-4 h-4 mr-2" />
																										{isLeaderboardEnabled
																											? t(
																													"pbActionLeaderboardOff",
																												)
																											: t(
																													"pbActionLeaderboardOn",
																												)}
																									</DropdownMenuItem>
																									<DropdownMenuItem
																										onClick={() =>
																											handleToggleExerciseResultEntry(
																												dayIndex,
																												exIndex,
																											)
																										}
																									>
																										<CheckCircle2 className="w-4 h-4 mr-2" />
																										{isResultEntryEnabled
																											? t(
																													"pbActionResultEntryOff",
																												)
																											: t(
																													"pbActionResultEntryOn",
																												)}
																									</DropdownMenuItem>
																									{!isSharedReadonly && (
																										<>
																											<DropdownMenuSeparator className="bg-border" />
																											<DropdownMenuItem
																												onClick={() =>
																													handleCopyExercise(
																														dayIndex,
																														exIndex,
																													)
																												}
																											>
																												<Copy className="w-4 h-4 mr-2" />
																												{t(
																													"pbActionCopyExercise",
																												)}
																											</DropdownMenuItem>
																											<DropdownMenuItem
																												onClick={() =>
																													handleOpenExportDialog(
																														"exercise_copy",
																														{
																															dayIndex,
																															exerciseIndex:
																																exIndex,
																														},
																													)
																												}
																											>
																												<FolderInput className="w-4 h-4 mr-2" />
																												{t(
																													"pbActionExportExercise",
																												)}
																											</DropdownMenuItem>
																											<DropdownMenuItem
																												onClick={() =>
																													handleOpenExportDialog(
																														"exercise",
																														{
																															dayIndex,
																															exerciseIndex:
																																exIndex,
																														},
																													)
																												}
																											>
																												<Share2 className="w-4 h-4 mr-2" />
																												{t(
																													"pbActionShareExercise",
																												)}
																											</DropdownMenuItem>
																										</>
																									)}
																									{!isSharedReadonly &&
																										(program?.weeks || [])
																											.length > 1 &&
																										selectedWeekIndex <
																											(program?.weeks || [])
																												.length -
																												1 && (
																											<DropdownMenuItem
																												onClick={() =>
																													handleDuplicateExerciseToNextWeek(
																														dayIndex,
																														exIndex,
																													)
																												}
																											>
																												<CopyPlus className="w-4 h-4 mr-2" />
																												{t(
																													"pbActionDuplicateNextWeek",
																												)}
																											</DropdownMenuItem>
																										)}
																									<DropdownMenuSeparator className="bg-border" />
																									<DropdownMenuItem
																										onClick={() =>
																											handleRemoveExercise(
																												dayIndex,
																												exIndex,
																											)
																										}
																										className="text-blaze focus:text-blaze"
																									>
																										<Trash2 className="w-4 h-4 mr-2" />
																										{t(
																											"pbActionRemoveExercise",
																										)}
																									</DropdownMenuItem>
																								</DropdownMenuContent>
																							</DropdownMenu>
																							<Button
																								variant="ghost"
																								size="icon"
																								onClick={() =>
																									setExpandedExercises(
																										(prev) => ({
																											...prev,
																											[exerciseKey]:
																												!prev[exerciseKey],
																										}),
																									)
																								}
																								aria-expanded={
																									isExerciseExpanded
																								}
																								aria-label={t(
																									"pbExerciseToggleEditor",
																								)}
																								className="h-10 w-10 text-muted-foreground"
																							>
																								{isExerciseExpanded ? (
																									<ChevronUp className="w-4 h-4" />
																								) : (
																									<ChevronDown className="w-4 h-4" />
																								)}
																							</Button>
																						</div>
																					</div>

																					{/* Inline editor. Below md it is collapsed until the row is
																					    tapped, so a day of six exercises stays scannable; on
																					    desktop it is always open, exactly as before. */}
																					<div
																						className={`space-y-2 ${
																							isExerciseExpanded
																								? ""
																								: "hidden md:block"
																						}`}
																					>
																						{daySessions.length > 0 && (
																							<div className="pl-7 flex items-center gap-2">
																								<span className="text-[11px] md:text-[10px] text-muted-foreground uppercase tracking-wider">
																									{t("sessionLabel")}
																								</span>
																								<Select
																									value={
																										exercise.session_id ||
																										"none"
																									}
																									onValueChange={(value) =>
																										handleAssignExerciseSession(
																											dayIndex,
																											exIndex,
																											value,
																										)
																									}
																								>
																									<SelectTrigger className="w-44 h-8 bg-background border-transparent rounded-sm text-xs">
																										<SelectValue placeholder={t("pbNoSession")} />
																									</SelectTrigger>
																									<SelectContent className="bg-card border-border">
																										<SelectItem value="none">
																											{t("pbNoSession")}
																										</SelectItem>
																										{daySessions.map(
																											(session) => (
																												<SelectItem
																													key={
																														session.session_id
																													}
																													value={
																														session.session_id
																													}
																												>
																													{session.label}
																												</SelectItem>
																											),
																										)}
																									</SelectContent>
																								</Select>
																							</div>
																						)}

																						{isSharedReadonly && (
																							<div className="pl-7 space-y-1">
																								<span className="text-[11px] md:text-[10px] text-amber-300 uppercase tracking-wider">
																									{t("pbLocalNote")}
																								</span>
																								<Textarea
																									value={
																										exercise.shared_local_note ||
																										""
																									}
																									onChange={(e) =>
																										handleUpdateSharedLocalNote(
																											dayIndex,
																											exIndex,
																											e.target.value,
																										)
																									}
																									className="bg-background border-amber-500/20 rounded-sm text-xs min-h-[62px]"
																									placeholder={t("pbLocalNotePlaceholder")}
																								/>
																							</div>
																						)}

																						{/* STRENGTH STANDARD / COMPLEX inline */}
																						{exType === "strength" &&
																							(!exercise.methodology ||
																								exercise.methodology ===
																									"standard" ||
																								exercise.methodology ===
																									"complex") && (
																								<>
																									{exercise.methodology ===
																										"complex" &&
																										(
																											exercise.complex_exercises ||
																											[]
																										).length > 0 && (
																											<div className="pl-7 pb-1">
																												<p className="text-[11px] md:text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
																													{t("pbSequence")}
																												</p>
																												<div className="flex flex-wrap gap-1">
																													{(
																														exercise.complex_exercises ||
																														[]
																													).map((item, idx) => (
																														<span
																															key={`${item.exercise_id || item.exercise_name}_${idx}`}
																															className="text-xs md:text-[11px] px-2 py-0.5 rounded-sm bg-background border border-border text-muted-foreground"
																														>
																															{idx + 1}.{" "}
																															{
																																item.exercise_name
																															}
																														</span>
																													))}
																												</div>
																											</div>
																										)}
																									<div className="flex items-center gap-2 pl-7 text-[11px] md:text-[10px] text-muted-foreground uppercase tracking-wider">
																										<span className="w-4"></span>
																										<span className="w-20 text-center">
																											{t("sets")}
																										</span>
																										<span className="w-32 text-center">
																											{t("repsLabel")}
																										</span>
																										<span className="w-40 text-center">
																											{t("load")}
																										</span>
																										<span
																											className="w-7"
																											aria-hidden="true"
																										></span>
																										<span className="w-28 text-center">
																											{t("restLabel")}
																										</span>
																										<span className="w-16 text-center">
																											{t("rpePlaceholder")}
																										</span>
																										<span className="w-16 text-center">
																											{t("rirPlaceholder")}
																										</span>
																									</div>
																									{displayRows.map(
																										(row, rowIndex) => (
																											<div
																												key={rowIndex}
																												className="pl-7"
																											>
																												<div className="flex items-center gap-2">
																													<span className="text-xs text-muted-foreground w-4 text-right flex-shrink-0">
																														{displayRows.length >
																														1
																															? `${rowIndex + 1}.`
																															: ""}
																													</span>
																													<Input
																														value={
																															row.sets ?? ""
																														}
																														onChange={(e) =>
																															handleUpdateSetRow(
																																dayIndex,
																																exIndex,
																																rowIndex,
																																"sets",
																																e.target
																																	.value === ""
																																	? ""
																																	: e.target
																																			.value,
																															)
																														}
																														className="bg-background border-transparent rounded-sm h-8 text-xs w-20"
																														placeholder={t("sets")}
																														type="text"
																														inputMode="numeric"
																														disabled={
																															isSharedReadonly
																														}
																													/>
																													<Input
																														value={
																															row.reps ?? ""
																														}
																														onChange={(e) =>
																															handleUpdateSetRow(
																																dayIndex,
																																exIndex,
																																rowIndex,
																																"reps",
																																e.target.value,
																															)
																														}
																														className="bg-background border-transparent rounded-sm h-8 text-xs w-32"
																														placeholder={t("repsLabel")}
																														disabled={
																															isSharedReadonly
																														}
																													/>
																													<Input
																														value={
																															row.load_percentage ??
																															""
																														}
																														onChange={(e) =>
																															handleUpdateSetRow(
																																dayIndex,
																																exIndex,
																																rowIndex,
																																"load_percentage",
																																e.target
																																	.value === ""
																																	? ""
																																	: e.target
																																			.value,
																															)
																														}
																														className="bg-background border-transparent rounded-sm h-8 text-xs w-40"
																														placeholder={t("pbPercentOrLoad")}
																														type="text"
																														inputMode="text"
																														disabled={
																															isSharedReadonly
																														}
																													/>
																													{(() => {
																														// The reference of a complex is chosen once, below the rows
																														// (ComplexLoadReference): a complex has no max of its own.
																														if (isStrengthComplex(exercise)) {
																															return (
																																<span
																																	className="h-9 w-9 md:h-7 md:w-7 flex-shrink-0"
																																	aria-hidden="true"
																																/>
																															);
																														}
																														const effectiveReferenceId =
																															row.load_reference_exercise_id ||
																															exercise.load_reference_exercise_id ||
																															null;
																														const effectiveReferenceName =
																															row.load_reference_exercise_name ||
																															exercise.load_reference_exercise_name ||
																															null;
																														const isExternalReference =
																															Boolean(
																																effectiveReferenceId,
																															) &&
																															effectiveReferenceId !==
																																exercise.exercise_id;

																														return (
																															<TooltipProvider
																																delayDuration={
																																	120
																																}
																															>
																																<Tooltip>
																																	<TooltipTrigger
																																		asChild
																																	>
																																		<Button
																																			type="button"
																																			variant="ghost"
																																			size="icon"
																																			disabled={
																																				isSharedReadonly
																																			}
																																			className={`h-9 w-9 md:h-7 md:w-7 rounded-sm border border-dashed ${
																																				isExternalReference
																																					? "border-emerald-400/60 text-emerald-400"
																																					: "border-border text-muted-foreground hover:text-emerald-400 hover:border-emerald-400/50"
																																			}`}
																																			onClick={() => {
																																				setPendingReferenceUpdate(
																																					{
																																						dayIndex,
																																						exIndex,
																																						rowIndex,
																																					},
																																				);
																																				setIsReferenceExerciseDialogOpen(
																																					true,
																																				);
																																				setReferenceExerciseSearchQuery(
																																					"",
																																				);
																																			}}
																																		>
																																			<Paperclip className="w-3.5 h-3.5" />
																																			<span className="sr-only">
																																				{t(
																																					"loadReferenceExercise",
																																				)}
																																			</span>
																																		</Button>
																																	</TooltipTrigger>
																																	<TooltipContent
																																		side="top"
																																		className="max-w-[320px] whitespace-normal break-words"
																																	>
																																		{isExternalReference
																																			? `${t("loadReferenceSummary")}: ${effectiveReferenceName || t("loadReferenceExercise")}`
																																			: `${t("loadReferenceSummary")}: ${t("currentExerciseLoadReference")}`}
																																	</TooltipContent>
																																</Tooltip>
																															</TooltipProvider>
																														);
																													})()}
																													<Input
																														value={
																															row.rest_seconds ??
																															""
																														}
																														onChange={(e) =>
																															handleUpdateSetRow(
																																dayIndex,
																																exIndex,
																																rowIndex,
																																"rest_seconds",
																																e.target.value,
																															)
																														}
																														onBlur={(e) =>
																															handleUpdateSetRow(
																																dayIndex,
																																exIndex,
																																rowIndex,
																																"rest_seconds",
																																normalizeRestFieldValue(
																																	e.target
																																		.value,
																																),
																															)
																														}
																														className="bg-background border-transparent rounded-sm h-8 text-xs w-28"
																														placeholder={t("pbEg230")}
																														type="text"
																														inputMode="text"
																														disabled={
																															isSharedReadonly
																														}
																													/>
																													<Input
																														value={
																															row.rpe ?? ""
																														}
																														onChange={(e) =>
																															handleUpdateSetRow(
																																dayIndex,
																																exIndex,
																																rowIndex,
																																"rpe",
																																e.target.value,
																															)
																														}
																														className="bg-background border-transparent rounded-sm h-8 text-xs w-16"
																														placeholder={t("rpePlaceholder")}
																														disabled={
																															isSharedReadonly
																														}
																													/>
																													<Input
																														value={
																															row.rir ?? ""
																														}
																														onChange={(e) =>
																															handleUpdateSetRow(
																																dayIndex,
																																exIndex,
																																rowIndex,
																																"rir",
																																e.target.value,
																															)
																														}
																														className="bg-background border-transparent rounded-sm h-8 text-xs w-16"
																														placeholder={t("rirPlaceholder")}
																														disabled={
																															isSharedReadonly
																														}
																													/>
																													{displayRows.length >
																														1 &&
																														!isSharedReadonly && (
																															<Button
																																variant="ghost"
																																size="icon"
																																className="h-9 w-9 md:h-7 md:w-7 ml-auto md:ml-0 text-muted-foreground hover:text-blaze flex-shrink-0"
																																onClick={() =>
																																	handleRemoveSetRow(
																																		dayIndex,
																																		exIndex,
																																		rowIndex,
																																	)
																																}
																															>
																																<X className="w-3 h-3" />
																															</Button>
																														)}
																												</div>
																												{/* Le note per-serie non si scrivono piu' (la
																												    nota di un esercizio e' una sola, in fondo),
																												    ma quelle gia' scritte dall'AI le vede solo
																												    l'atleta: qui si leggono e si tolgono. */}
																												{String(
																													row.notes || "",
																												).trim() && (
																													<div className="flex items-start gap-1 mt-1 ml-6">
																														<span className="text-[11px] md:text-[10px] text-muted-foreground italic">
																															{t("pbSetRowNote")}:{" "}
																															{String(
																																row.notes,
																															).trim()}
																														</span>
																														{!isSharedReadonly && (
																															<Button
																																variant="ghost"
																																size="icon"
																																className="h-6 w-6 text-muted-foreground hover:text-blaze flex-shrink-0"
																																title={t(
																																	"pbRemoveSetRowNote",
																																)}
																																onClick={() =>
																																	handleUpdateSetRow(
																																		dayIndex,
																																		exIndex,
																																		rowIndex,
																																		"notes",
																																		"",
																																	)
																																}
																															>
																																<X className="w-3 h-3" />
																																<span className="sr-only">
																																	{t(
																																		"pbRemoveSetRowNote",
																																	)}
																																</span>
																															</Button>
																														)}
																													</div>
																												)}
																											</div>
																										),
																									)}
																									{isStrengthComplex(exercise) && (
																										<div className="pl-7 pt-1">
																											<ComplexLoadReference
																												exercise={exercise}
																												rows={displayRows}
																												disabled={isSharedReadonly}
																												t={t}
																												onSelect={(referenceId, referenceName) =>
																													handleUpdateSetRowsLoadReference(
																														dayIndex,
																														exIndex,
																														referenceId,
																														referenceName,
																													)
																												}
																												onPickOther={() =>
																													openLoadReferenceDialog({
																														dayIndex,
																														exIndex,
																													})
																												}
																											/>
																										</div>
																									)}
																								</>
																							)}

																						{/* SUPERSET inline */}
																						{exType === "strength" &&
																							exercise.methodology ===
																								"superset" && (
																								<div className="pl-7 space-y-2">
																									<div className="flex flex-wrap gap-3 text-xs mb-1">
																										{exercise.superset_sets && (
																											<span className="text-volt font-heading">
																												{exercise.superset_sets}{" "}
																												{getRoundUnitLabel(
																													exercise.superset_sets,
																												)}
																											</span>
																										)}
																										{exercise.superset_rest_seconds && (
																											<span className="text-muted-foreground">
																												Rest{" "}
																												{formatRestDisplay(
																													exercise.superset_rest_seconds,
																												)}
																											</span>
																										)}
																									</div>
																									{(
																										exercise.superset_exercises ||
																										[]
																									).map((se, sei) => (
																										<div
																											key={sei}
																											className="flex items-baseline gap-2 text-xs"
																										>
																											<span className="text-volt font-heading font-bold w-5 flex-shrink-0">
																												{String.fromCharCode(
																													65,
																												)}
																												{sei + 1}
																											</span>
																											<span className="font-medium">
																												{se.exercise_name}
																											</span>
																											{(se.set_rows || []).map(
																												(row, ri) => (
																													<span
																														key={ri}
																														className="text-muted-foreground"
																													>
																														{row.reps &&
																															(isTimedStrengthReps(
																																row.reps,
																															) ||
																															isDistanceStrengthReps(
																																row.reps,
																															)
																																? row.reps
																																: `${row.reps} reps`)}
																														{row.load_percentage &&
																															` @${row.load_percentage}`}
																														{row.rpe &&
																															` RPE ${row.rpe}`}
																														{row.rir &&
																															` RIR ${row.rir}`}
																													</span>
																												),
																											)}
																										</div>
																									))}
																								</div>
																							)}

																						{/* EMOM inline */}
																						{exType === "strength" &&
																							exercise.methodology ===
																								"emom" && (
																								<div className="pl-7 space-y-1">
																									<div className="flex flex-wrap gap-3 text-xs mb-1">
																										{getEmomStructureParts(exercise, t).map((part, pi) => (
																											<span
																												key={part}
																												className={
																													pi === 0
																														? "text-volt font-heading"
																														: "text-muted-foreground"
																												}
																											>
																												{part}
																											</span>
																										))}
																									</div>
																									{(
																										normalizeEmomRoundTemplates(
																											exercise,
																										) || []
																									).map((round, ri) => (
																										<div
																											key={round.round_id || ri}
																											className="flex items-baseline gap-2 text-xs"
																										>
																											<span className="text-volt font-heading font-bold min-w-16 flex-shrink-0 whitespace-nowrap">
																												{getEmomRoundLabel(
																													round,
																													ri,
																													language,
																												)}
																											</span>
																											<span className="text-muted-foreground">
																												{round.input_mode ===
																													"library" &&
																												(round.items || [])
																													.length > 0
																													? (round.items || [])
																															.map((item) =>
																																[
																																	item.reps,
																																	item.exercise_name,
																																	item.load
																																		? `@${item.load}`
																																		: "",
																																	item.notes
																																		? `(${item.notes})`
																																		: "",
																																]
																																	.filter(
																																		Boolean,
																																	)
																																	.join(" "),
																															)
																															.join(" + ")
																													: round.content}
																											</span>
																										</div>
																									))}
																								</div>
																							)}

																						{/* STRENGTH CUSTOM inline */}
																						{exType === "strength" &&
																							exercise.methodology ===
																								"custom" &&
																							exercise.custom_workout_text && (
																								<div className="pl-7 space-y-1">
																									<p className="text-xs text-muted-foreground whitespace-pre-line">
																										{
																											exercise.custom_workout_text
																										}
																									</p>
																								</div>
																							)}

																						{/* RUNNING inline */}
																						{exType === "running" && (
																							<div className="pl-7 space-y-1 text-xs text-muted-foreground">
																								{/* Warmup */}
																								{exercise.warmup_description && (
																									<div className="flex items-center gap-1">
																										<span className="text-green-400/60">
																											{t("pbWarmupShort")}
																										</span>
																										<span>
																											{
																												exercise.warmup_description
																											}
																										</span>
																									</div>
																								)}

																								{/* Ripetute / Salite — multi-row */}
																								{[
																									"ripetute",
																									"salite",
																								].includes(
																									exercise.running_type,
																								) &&
																									exercise.interval_rows
																										?.length > 0 && (
																										<div className="space-y-0.5">
																											{exercise.interval_rows.map(
																												(row, ri) => (
																													<div
																														key={ri}
																														className="flex flex-wrap gap-2"
																													>
																														<span className="text-foreground font-medium">
																															{row.repetitions >
																															1
																																? `${row.repetitions}x`
																																: ""}
																															{row.distance ||
																																""}
																														</span>
																														{(row.target_pace ||
																															row.target_pace_resolved) && (
																															<span>
																																@{" "}
																																{row.target_pace ||
																																	row.target_pace_resolved}
																															</span>
																														)}
																														{row.recovery_time && (
																															<span className="text-blue-400/70">
																																Rest{" "}
																																{
																																	row.recovery_time
																																}
																																{row.recovery_type ===
																																"rest"
																																	? " fermo"
																																	: ""}
																															</span>
																														)}
																													</div>
																												),
																											)}
																										</div>
																									)}
																								{/* Ripetute — legacy single-row */}
																								{exercise.running_type ===
																									"ripetute" &&
																									(!exercise.interval_rows ||
																										exercise.interval_rows
																											.length === 0) &&
																									exercise.intervals && (
																										<span>
																											{exercise.intervals}x
																											{exercise.distance_meters
																												? `${exercise.distance_meters}m`
																												: ""}
																											{exercise.target_pace
																												? ` @ ${exercise.target_pace}`
																												: ""}
																											{exercise.rest_seconds
																												? ` Rest ${formatRestDisplay(exercise.rest_seconds)}`
																												: ""}
																										</span>
																									)}

																								{/* Fartlek — segment-based */}
																								{exercise.running_type ===
																									"fartlek" &&
																									exercise.fartlek_segments
																										?.length > 0 && (
																										<div className="space-y-0.5">
																											{exercise.fartlek_repeat &&
																												exercise.fartlek_repeat >
																													1 && (
																													<span className="text-green-400 font-medium">
																														{
																															exercise.fartlek_repeat
																														}
																														x blocco:
																													</span>
																												)}
																											{exercise.fartlek_segments.map(
																												(seg, si) => {
																													const normalizedSegmentType =
																														normalizeFartlekSegmentType(
																															seg.segment_type,
																														);
																													const segmentPaceMode =
																														getFartlekSegmentPaceMode(
																															seg,
																														);
																													return (
																														<div
																															key={si}
																															className="flex gap-2"
																														>
																															<span
																																className={
																																	normalizedSegmentType ===
																																	"fast"
																																		? "text-green-400"
																																		: normalizedSegmentType ===
																																			  "medium"
																																			? "text-yellow-400"
																																			: "text-blue-400"
																																}
																															>
																																{normalizedSegmentType ===
																																"fast"
																																	? "▸"
																																	: normalizedSegmentType ===
																																		  "medium"
																																		? "•"
																																		: "▹"}
																															</span>
																															<span>
																																{seg.duration ||
																																	""}
																															</span>
																															{segmentPaceMode ===
																																"manual" &&
																																seg.pace && (
																																	<span>
																																		@ {seg.pace}
																																	</span>
																																)}
																															{segmentPaceMode ===
																																"reference" &&
																																seg.pace_resolved && (
																																	<span>
																																		@{" "}
																																		{
																																			seg.pace_resolved
																																		}
																																	</span>
																																)}
																														</div>
																													);
																												},
																											)}
																										</div>
																									)}

																								{/* Lungo — simple fields */}
																								{["lungo"].includes(
																									exercise.running_type,
																								) && (
																									<div className="flex flex-wrap gap-3">
																										{exercise.distance_meters && (
																											<span>
																												{
																													exercise.distance_meters
																												}
																											</span>
																										)}
																										{exercise.duration_minutes && (
																											<span className="flex items-center gap-1">
																												<Clock className="w-3 h-3" />
																												{
																													exercise.duration_minutes
																												}
																												&apos;
																											</span>
																										)}
																										{(exercise.target_pace ||
																											exercise.target_pace_resolved) && (
																											<span>
																												@{" "}
																												{exercise.target_pace ||
																													exercise.target_pace_resolved}
																											</span>
																										)}
																									</div>
																								)}

																								{/* Progressivo — segments */}
																								{exercise.running_type ===
																									"progressivo" && (
																									<div className="space-y-0.5">
																										{exercise.distance_meters && (
																											<span>
																												{
																													exercise.distance_meters
																												}
																											</span>
																										)}
																										{exercise.fartlek_segments?.map(
																											(seg, si) => (
																												<div
																													key={si}
																													className="flex gap-2"
																												>
																													<span className="text-green-400">
																														{si + 1}.
																													</span>
																													<span>
																														{seg.duration || ""}
																													</span>
																													{(seg.pace ||
																														seg.pace_resolved) && (
																														<span>
																															@{" "}
																															{seg.pace ||
																																seg.pace_resolved}
																														</span>
																													)}
																												</div>
																											),
																										)}
																									</div>
																								)}

																								{/* Custom — free text */}
																								{exercise.running_type ===
																									"custom" &&
																									exercise.custom_workout_text && (
																										<p className="text-xs text-muted-foreground whitespace-pre-line">
																											{
																												exercise.custom_workout_text
																											}
																										</p>
																									)}

																								{/* Cooldown */}
																								{exercise.cooldown_description && (
																									<div className="flex items-center gap-1">
																										<span className="text-blue-400/60">
																											{t("pbCooldownShort")}
																										</span>
																										<span>
																											{
																												exercise.cooldown_description
																											}
																										</span>
																									</div>
																								)}
																							</div>
																						)}

																						{/* SWIM inline */}
																						{exType === "swim" && (
																							<div className="pl-7 space-y-1 text-xs text-muted-foreground">
																								{/* Warmup */}
																								{exercise.warmup_description && (
																									<div className="flex items-center gap-1">
																										<span className="text-orange-400/60">
																											{t("pbWarmupShort")}
																										</span>
																										<span>
																											{
																												exercise.warmup_description
																											}
																										</span>
																									</div>
																								)}

																								{/* Swim sets (intervals/threshold/sprint) */}
																								{exercise.swim_sets?.length >
																									0 && (
																									<div className="space-y-0.5">
																										{exercise.swim_sets.map(
																											(s, si) => (
																												<div
																													key={si}
																													className="flex gap-2"
																												>
																													<span className="text-cyan-400">
																														{s.repetitions > 1
																															? `${s.repetitions}x`
																															: ""}
																														{s.distance || ""}
																													</span>
																													{s.target_time && (
																														<span>
																															@ {s.target_time}
																														</span>
																													)}
																													{s.rest && (
																														<span className="text-muted-foreground/60">
																															Rest {s.rest}
																														</span>
																													)}
																													{s.stroke && (
																														<span className="text-cyan-400/50">
																															{s.stroke ===
																															"stile_libero"
																																? "SL"
																																: s.stroke ===
																																	  "dorso"
																																	? "DO"
																																	: s.stroke ===
																																		  "rana"
																																		? "RA"
																																		: s.stroke ===
																																			  "delfino"
																																			? "DE"
																																			: "MI"}
																														</span>
																													)}
																													{s.intensity && (
																														<span className="text-cyan-400/70">
																															{s.intensity}
																														</span>
																													)}
																												</div>
																											),
																										)}
																									</div>
																								)}

																								{/* Simple fields (endurance/technique/mixed) */}
																								{!exercise.swim_sets
																									?.length && (
																									<div className="flex flex-wrap gap-3">
																										{exercise.distance_meters && (
																											<span>
																												{exercise.distance_meters >=
																												1000
																													? `${(exercise.distance_meters / 1000).toFixed(1)}km`
																													: `${exercise.distance_meters}m`}
																											</span>
																										)}
																										{exercise.duration_minutes && (
																											<span className="flex items-center gap-1">
																												<Clock className="w-3 h-3" />
																												{
																													exercise.duration_minutes
																												}
																												&apos;
																											</span>
																										)}
																										{exercise.target_pace && (
																											<span>
																												@ {exercise.target_pace}
																											</span>
																										)}
																										{exercise.swim_equipment && (
																											<span className="text-cyan-400/50">
																												{
																													exercise.swim_equipment
																												}
																											</span>
																										)}
																									</div>
																								)}

																								{/* Custom — free text */}
																								{exercise.swim_type ===
																									"custom" &&
																									exercise.custom_workout_text && (
																										<p className="text-xs text-muted-foreground whitespace-pre-line">
																											{
																												exercise.custom_workout_text
																											}
																										</p>
																									)}

																								{/* Cooldown */}
																								{exercise.cooldown_description && (
																									<div className="flex items-center gap-1">
																										<span className="text-blue-400/60">
																											{t("pbCooldownShort")}
																										</span>
																										<span>
																											{
																												exercise.cooldown_description
																											}
																										</span>
																									</div>
																								)}
																							</div>
																						)}

																						{/* BIKE/ERG inline */}
																						{["bike", "erg"].includes(
																							exType,
																						) && (
																							<div className="pl-7 space-y-1 text-xs text-muted-foreground">
																								{/* Terrain */}
																								{exType === "bike" &&
																									exercise.bike_terrain && (
																										<span className="text-orange-400/50 uppercase text-[11px] md:text-[10px] tracking-wider">
																											{exercise.bike_terrain}
																										</span>
																									)}
																								{/* Warmup */}
																								{exercise.warmup_description && (
																									<div className="flex items-center gap-1">
																										<span className="text-orange-400/60">
																											{t("pbWarmupShort")}
																										</span>
																										<span>
																											{
																												exercise.warmup_description
																											}
																										</span>
																									</div>
																								)}

																								{/* Bike intervals (intervals/hill_repeats/threshold/vo2max) */}
																								{exercise.bike_intervals
																									?.length > 0 && (
																									<div className="space-y-0.5">
																										{exercise.bike_intervals.map(
																											(intv, ii) => (
																												<div
																													key={ii}
																													className="flex gap-2"
																												>
																													<span className="text-orange-400">
																														{intv.repetitions >
																														1
																															? `${intv.repetitions}x`
																															: ""}
																														{intv.duration ||
																															""}
																													</span>
																													{intv.intensity && (
																														<span>
																															@ {intv.intensity}
																														</span>
																													)}
																													{intv.power && (
																														<span className="text-orange-400/70">
																															{intv.power}
																														</span>
																													)}
																													{intv.cadence && (
																														<span className="text-muted-foreground/60">
																															{intv.cadence}
																														</span>
																													)}
																													{intv.recovery && (
																														<span className="text-muted-foreground/60">
																															Rest{" "}
																															{intv.recovery}
																														</span>
																													)}
																												</div>
																											),
																										)}
																									</div>
																								)}

																								{/* Simple fields (endurance/tempo/sprint/recovery) */}
																								{!exercise.bike_intervals
																									?.length && (
																									<div className="flex flex-wrap gap-3">
																										{exercise.distance_meters && (
																											<span>
																												{
																													exercise.distance_meters
																												}
																												km
																											</span>
																										)}
																										{exercise.duration_minutes && (
																											<span className="flex items-center gap-1">
																												<Clock className="w-3 h-3" />
																												{
																													exercise.duration_minutes
																												}
																												&apos;
																											</span>
																										)}
																										{exercise.target_pace &&
																											exercise.exercise_type !==
																												"erg" && (
																												<span>
																													@{" "}
																													{exercise.target_pace}
																												</span>
																											)}
																										{exercise.bike_power_target && (
																											<span className="text-orange-400/60">
																												{
																													exercise.bike_power_target
																												}
																											</span>
																										)}
																										{exercise.bike_cadence_target && (
																											<span className="text-muted-foreground/60">
																												{
																													exercise.bike_cadence_target
																												}
																											</span>
																										)}
																									</div>
																								)}

																								{/* Custom — free text */}
																								{exercise.bike_type ===
																									"custom" &&
																									exercise.custom_workout_text && (
																										<p className="text-xs text-muted-foreground whitespace-pre-line">
																											{
																												exercise.custom_workout_text
																											}
																										</p>
																									)}

																								{/* Cooldown */}
																								{exercise.cooldown_description && (
																									<div className="flex items-center gap-1">
																										<span className="text-blue-400/60">
																											{t("pbCooldownShort")}
																										</span>
																										<span>
																											{
																												exercise.cooldown_description
																											}
																										</span>
																									</div>
																								)}
																							</div>
																						)}

																						{/* CONDITIONING inline */}
																						{exType === "conditioning" && (
																							<div className="pl-7 space-y-1">
																								<div className="flex flex-wrap gap-2 text-xs mb-1">
																									{exercise.conditioning_type ===
																										"amrap" &&
																										(() => {
																											const amrapDuration =
																												formatConditioningDuration(
																													exercise.time_cap_seconds,
																												);
																											if (!amrapDuration)
																												return null;
																											return (
																												<span className="text-yellow-400 font-heading">
																													{amrapDuration}
																												</span>
																											);
																										})()}
																									{exercise.conditioning_type ===
																										"for_time" && (
																										<>
																											{getForTimeRoundsDisplay(
																												exercise.rounds,
																											) && (
																												<span className="text-yellow-400 font-heading">
																													{getForTimeRoundsDisplay(
																														exercise.rounds,
																													)}
																												</span>
																											)}
																											{(() => {
																												const timeCapDuration =
																													formatConditioningDuration(
																														exercise.time_cap_seconds,
																													);
																												if (!timeCapDuration)
																													return null;
																												return (
																													<span className="text-muted-foreground">
																														TC {timeCapDuration}
																													</span>
																												);
																											})()}
																										</>
																									)}
																							{exercise.conditioning_type === "emom" &&
																								getEmomStructureParts(exercise, t).map((part, pi) => (
																									<span
																										key={part}
																										className={
																											pi === 0
																												? "text-yellow-400 font-heading"
																												: "text-muted-foreground"
																										}
																									>
																										{part}
																									</span>
																								))}
																									{exercise.conditioning_type ===
																										"hiit" && (
																										<>
																											{(() => {
																												const workPerRound =
																													getHiitWorkSecondsPerRound(
																														exercise,
																													);
																												const onLabel =
																													workPerRound
																														? `${formatHiitIntervalShort(workPerRound)} ON`
																														: "";
																												const offLabel =
																													getHiitOffLabel(
																														exercise,
																													);
																												return (
																													<>
																														{exercise.hiit_cycles && (
																															<span className="text-yellow-400 font-heading">
																																{
																																	exercise.hiit_cycles
																																}{" "}
																																cicli
																															</span>
																														)}
																														{(onLabel ||
																															offLabel) && (
																															<span className="text-muted-foreground">
																																{exercise.hiit_cycles
																																	? ": "
																																	: ""}
																																{onLabel}
																																{onLabel &&
																																offLabel
																																	? " : "
																																	: ""}
																																{offLabel}
																															</span>
																														)}
																													</>
																												);
																											})()}
																										</>
																									)}
																									{exercise.conditioning_template_id &&
																										(() => {
																											const scopeTag =
																												resolveProgramExerciseScopeTag(
																													{
																														...exercise,
																														exercise_name:
																															exercise.exercise_name ||
																															exercise.name,
																													},
																												) || null;
																											if (!scopeTag)
																												return null;
																											const isScopeGlobal =
																												scopeTag === "global";
																											return (
																												<span
																													className={`px-1.5 py-0.5 rounded-sm text-[11px] md:text-[10px] uppercase tracking-wider ${
																														isScopeGlobal
																															? "bg-amber-500/20 border border-amber-500/40 text-amber-300"
																															: "bg-emerald-500/20 border border-emerald-500/40 text-emerald-300"
																													}`}
																												>
																													{getExerciseLibraryScopeLabelByTag(
																														scopeTag,
																													)}
																												</span>
																											);
																										})()}
																								</div>
																								{exercise.conditioning_type ===
																									"emom" &&
																									(() => {
																										const conditioningEmomRounds =
																											normalizeEmomRoundTemplates(
																												exercise,
																											) || [];
																										if (
																											conditioningEmomRounds.length ===
																											0
																										) {
																											return null;
																										}
																										return (
																											<div className="space-y-0.5">
																												{conditioningEmomRounds.map(
																													(round, ri) => {
																														const roundItems =
																															Array.isArray(
																																round.items,
																															)
																																? round.items
																																		.map(
																																			(
																																				item,
																																			) => {
																																				const itemParts =
																																					[];
																																				if (
																																					item?.reps
																																				)
																																					itemParts.push(
																																						item.reps,
																																					);
																																				if (
																																					item?.exercise_name
																																				)
																																					itemParts.push(
																																						item.exercise_name,
																																					);
																																				if (
																																					item?.load
																																				)
																																					itemParts.push(
																																						`@${item.load}`,
																																					);
																																				if (
																																					item?.notes
																																				)
																																					itemParts.push(
																																						`(${item.notes})`,
																																					);
																																				return itemParts
																																					.join(
																																						" ",
																																					)
																																					.trim();
																																			},
																																		)
																																		.filter(
																																			Boolean,
																																		)
																																: [];
																														const roundPreview =
																															round.content ||
																															roundItems.join(
																																" + ",
																															);
																														const roundTitle = (
																															round.label ||
																															round.custom_label ||
																															""
																														).trim();
																														return (
																															<div
																																key={
																																	round.round_id ||
																																	ri
																																}
																																className="grid grid-cols-[minmax(7rem,max-content)_minmax(0,1fr)] items-start gap-x-2 text-xs"
																															>
																																<span className="whitespace-nowrap text-yellow-400 font-heading font-bold">
																																	{getEmomRoundAssignmentLabel(
																																		round,
																																		ri,
																																		language,
																																	)}
																																</span>
																																<div className="min-w-0 text-muted-foreground">
																																	{roundTitle && (
																																		<div className="mb-0.5">
																																			<span className="mr-1 text-[10px] uppercase tracking-wider text-muted-foreground/70">
																																				{t("titleLabel")}
																																			</span>
																																			<span className="text-foreground">
																																				{
																																					roundTitle
																																				}
																																			</span>
																																		</div>
																																	)}
																																	{roundPreview && (
																																		<div>
																																			{
																																				roundPreview
																																			}
																																		</div>
																																	)}
																																</div>
																															</div>
																														);
																													},
																												)}
																											</div>
																										);
																									})()}
																								{exercise.conditioning_exercises
																									?.length > 0 && (
																									<div className="space-y-0.5">
																										{exercise.conditioning_exercises.map(
																											(ce, ci) => (
																												<div
																													key={ci}
																													className="text-xs text-muted-foreground"
																												>
																													{ce.reps && (
																														<span className="text-foreground">
																															{ce.reps}
																														</span>
																													)}{" "}
																													{ce.exercise_name}
																													{ce.load && (
																														<span className="text-volt ml-1">
																															@{ce.load}
																														</span>
																													)}
																													{ce.notes && (
																														<span className="italic ml-1">
																															— {ce.notes}
																														</span>
																													)}
																												</div>
																											),
																										)}
																									</div>
																								)}
																								{exercise.conditioning_free_text && (
																									<p className="text-xs text-muted-foreground whitespace-pre-wrap font-mono">
																										{
																											exercise.conditioning_free_text
																										}
																									</p>
																								)}
																							</div>
																						)}

																						{/* Notes (all types) */}
																						{exercise.notes && (
																							<div className="pl-7 mt-1">
																								<p className="text-xs text-muted-foreground italic">
																									{exercise.notes}
																								</p>
																							</div>
																						)}
																					</div>
																				</div>
																			);
																		})}
																	</div>
																) : (
																	<p className="text-sm text-muted-foreground text-center py-4">
																		{t("pbNoExercises")}
																	</p>
																)}

																{/* Add Exercise Button */}
																<Dialog
																	open={
																		isAddExerciseOpen &&
																		selectedDayIndex === dayIndex
																	}
																	onOpenChange={(open) => {
																		handleExerciseDialogChange(open);
																		setSelectedDayIndex(dayIndex);
																	}}
																>
																	<DialogTrigger asChild>
																		<Button
																			variant="outline"
																			className="w-full border-dashed border-border rounded-sm"
																			data-testid={`add-exercise-day-${dayIndex}`}
																		>
																			<Plus className="w-4 h-4 mr-2" />
																			{t("addExerciseButton")}
																		</Button>
																	</DialogTrigger>
																	<DialogContent
																		onPointerDownOutside={(event) =>
																			event.preventDefault()
																		}
																		onInteractOutside={(event) =>
																			event.preventDefault()
																		}
																		onEscapeKeyDown={(event) =>
																			event.preventDefault()
																		}
																		onCloseAutoFocus={(event) =>
																			event.preventDefault()
																		}
																		className="bg-card border-border rounded-sm grid-cols-1 w-[calc(100vw-1rem)] sm:w-[calc(100vw-2rem)] max-w-6xl max-h-[calc(100dvh-1rem)] sm:max-h-[calc(100dvh-2rem)] overflow-x-hidden overflow-y-auto"
																	>
																		<DialogDescription className="hidden" />
																		<DialogHeader className="pr-8 sm:pr-0">
																			<DialogTitle className="font-heading">
																				{isCreatingNewExercise
																					? t("newExercise")
																					: editingExerciseIndex !== null
																						? t("editExercise")
																						: t("addExerciseButton")}
																			</DialogTitle>
																		</DialogHeader>

																		{/* New Exercise Creation Form */}
																		{isCreatingNewExercise ? (
																			<NewExerciseForm
																				newExerciseDetails={newExerciseDetails}
																				setNewExerciseDetails={
																					setNewExerciseDetails
																				}
																				savingNewExercise={savingNewExercise}
																				onBack={() => {
																					setIsCreatingNewExercise(false);
																					setCreateExerciseTarget("standard");
																					resetNewExerciseDetailsState();
																				}}
																				onCreate={handleCreateNewExercise}
																				t={t}
																			/>
																		) : (
																			/* Exercise Selection Form */
																			<div className="space-y-4">
																				{(
																					currentWeekDays[selectedDayIndex]
																						?.sessions || []
																				).length > 0 && (
																					<div className="flex items-center gap-3">
																						<Label className="label-tactical whitespace-nowrap">
																							{t("sessionLabel")}
																						</Label>
																						<Select
																							value={
																								selectedSessionIndex !== null
																									? String(selectedSessionIndex)
																									: "none"
																							}
																							onValueChange={(value) =>
																								setSelectedSessionIndex(
																									value === "none"
																										? null
																										: Number(value),
																								)
																							}
																						>
																							<SelectTrigger className="ml-auto h-8 flex-1 min-w-0 md:flex-none md:w-[210px] bg-secondary border-transparent rounded-sm">
																								<SelectValue placeholder={t("pbNoSession")} />
																							</SelectTrigger>
																							<SelectContent className="bg-card border-border">
																								<SelectItem value="none">
																									{t("pbNoSession")}
																								</SelectItem>
																								{(
																									currentWeekDays[
																										selectedDayIndex
																									]?.sessions || []
																								).map((session, index) => (
																									<SelectItem
																										key={session.session_id}
																										value={String(index)}
																									>
																										{session.label}
																									</SelectItem>
																								))}
																							</SelectContent>
																						</Select>
																					</div>
																				)}

																				<div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
																					<div className="rounded-sm border border-border/70 bg-secondary/20 px-3 py-2">
																						<div className="flex items-center justify-between gap-3">
																							<p className="text-xs md:text-[11px] font-medium text-foreground uppercase tracking-wider whitespace-nowrap">
																								{t("allowResultEntry")}
																							</p>
																							<Button
																								variant="ghost"
																								size="sm"
																								onClick={() =>
																									setNewExercise((prev) => ({
																										...prev,
																										allow_result_entry:
																											prev.allow_result_entry ===
																											false,
																									}))
																								}
																								className={`h-8 rounded-sm border ${
																									newExercise.allow_result_entry ===
																									false
																										? "border-volt/40 text-volt"
																										: "border-border text-muted-foreground"
																								} shrink-0 whitespace-nowrap px-2`}
																							>
																								<EyeOff className="w-4 h-4 mr-1" />
																								{newExercise.allow_result_entry ===
																								false
																									? t("resultEntryLocked")
																									: t("resultEntryOpen")}
																							</Button>
																						</div>
																					</div>

																					<div className="rounded-sm border border-border/70 bg-secondary/20 px-3 py-2">
																						<div className="flex items-center justify-between gap-3">
																							<p className="text-xs md:text-[11px] font-medium text-foreground uppercase tracking-wider whitespace-nowrap">
																								{t("showInLeaderboard")}
																							</p>
																							<Button
																								variant="ghost"
																								size="sm"
																								onClick={() =>
																									setNewExercise((prev) => ({
																										...prev,
																										leaderboard_enabled:
																											prev.leaderboard_enabled ===
																											false,
																									}))
																								}
																								className={`h-8 rounded-sm border ${
																									newExercise.leaderboard_enabled ===
																									false
																										? "border-volt/40 text-volt"
																										: "border-border text-muted-foreground"
																								} shrink-0 whitespace-nowrap px-2`}
																							>
																								<Trophy className="w-4 h-4 mr-1" />
																								{newExercise.leaderboard_enabled ===
																								false
																									? t("leaderboardHidden")
																									: t("leaderboardVisible")}
																							</Button>
																						</div>
																					</div>
																				</div>

																				{/* Exercise Type Selector */}
																				<div className="space-y-1.5">
																					<Label className="label-tactical">
																						{t("type")}
																					</Label>
																					{/* Filtrato per ambito al RENDERING, non alla fonte:
																					    EXERCISE_TYPES resta completa perché serve a
																					    risolvere label e icone anche nei percorsi di
																					    sola lettura (docs/shooting/07 §5). */}
																					<div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-4 gap-1">
																						{filterByDomain(
																							EXERCISE_TYPES,
																							user,
																						).map((t) => {
																							const Icon = exerciseTypeIcon(
																								t.value,
																							);
																							return (
																								<button
																									key={t.value}
																									onClick={() =>
																										setNewExercise({
																											...newExercise,
																											exercise_type: t.value,
																											exercise_id: "",
																											exercise_name: "",
																										})
																									}
																									className={`flex h-9 items-center justify-center gap-1.5 px-2 py-1 rounded-sm border text-xs md:text-[11px] leading-none transition-colors ${
																										newExercise.exercise_type ===
																										t.value
																											? "border-volt bg-volt/10 text-volt"
																											: "border-border text-muted-foreground hover:border-volt/50"
																									}`}
																								>
																									<Icon className="w-3.5 h-3.5 flex-shrink-0" />
																									{t.label}
																								</button>
																							);
																						})}
																					</div>
																				</div>

																				{/* === METHODOLOGY SELECTOR (only for strength) === */}
																				{newExercise.exercise_type ===
																					"strength" && (
																					<div className="space-y-1.5">
																						<Label className="label-tactical">
																							{t("pbMethodology")}
																						</Label>
																						<div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-1.5">
																							<button
																								onClick={() =>
																									setNewExercise({
																										...newExercise,
																										methodology: "standard",
																										superset_exercises: [],
																										complex_exercises: [],
																										emom_rounds: [],
																									})
																								}
																								className={`flex h-9 items-center justify-center gap-1.5 px-2 py-1 rounded-sm border text-xs md:text-[11px] leading-none transition-colors ${
																									newExercise.methodology ===
																									"standard"
																										? "border-volt bg-volt/10 text-volt"
																										: "border-border text-muted-foreground hover:border-volt/50"
																								}`}
																							>
																								<Dumbbell className="w-3.5 h-3.5 flex-shrink-0" />
																								{t("pbMethodStandard")}
																							</button>
																							<button
																								onClick={() =>
																									setNewExercise({
																										...newExercise,
																										methodology: "superset",
																										exercise_id: "",
																										exercise_name:
																											newExercise.methodology ===
																											"superset"
																												? newExercise.exercise_name
																												: "",
																										set_rows: [],
																										complex_exercises: [],
																										superset_exercises:
																											newExercise
																												.superset_exercises
																												?.length > 0
																												? newExercise.superset_exercises
																												: [],
																										superset_sets:
																											newExercise.superset_sets ||
																											3,
																										superset_rest_seconds:
																											newExercise.superset_rest_seconds ||
																											90,
																									})
																								}
																								className={`flex h-9 items-center justify-center gap-1.5 px-2 py-1 rounded-sm border text-xs md:text-[11px] leading-none transition-colors ${
																									newExercise.methodology ===
																									"superset"
																										? "border-volt bg-volt/10 text-volt"
																										: "border-border text-muted-foreground hover:border-volt/50"
																								}`}
																							>
																								<Link2 className="w-3.5 h-3.5 flex-shrink-0" />
																								{t("pbMethodSuperset")}
																							</button>
																							<button
																								onClick={() =>
																									setNewExercise({
																										...newExercise,
																										methodology: "emom",
																										exercise_id: "",
																										exercise_name:
																											newExercise.methodology === "emom"
																												? newExercise.exercise_name
																												: "",
																										set_rows: [],
																										complex_exercises: [],
																										emom_intervals:
																											newExercise.emom_intervals ||
																											10,
																										emom_interval:
																											newExercise.emom_interval ||
																											"1:00",
																										emom_rounds:
																											newExercise.emom_rounds
																												?.length > 0
																												? newExercise.emom_rounds
																												: normalizeEmomRoundTemplates(
																														newExercise,
																													),
																									})
																								}
																								className={`flex h-9 items-center justify-center gap-1.5 px-2 py-1 rounded-sm border text-xs md:text-[11px] leading-none transition-colors ${
																									newExercise.methodology ===
																									"emom"
																										? "border-volt bg-volt/10 text-volt"
																										: "border-border text-muted-foreground hover:border-volt/50"
																								}`}
																							>
																								<Timer className="w-3.5 h-3.5 flex-shrink-0" />
																								{t("pbMethodEmom")}
																							</button>
																							<button
																								onClick={() =>
																									setNewExercise({
																										...newExercise,
																										methodology: "custom",
																										exercise_id: "",
																										superset_exercises: [],
																										complex_exercises: [],
																										emom_rounds: [],
																										set_rows: [],
																										custom_workout_text:
																											newExercise.custom_workout_text ||
																											"",
																									})
																								}
																								className={`flex h-9 items-center justify-center gap-1.5 px-2 py-1 rounded-sm border text-xs md:text-[11px] leading-none transition-colors ${
																									newExercise.methodology ===
																									"custom"
																										? "border-volt bg-volt/10 text-volt"
																										: "border-border text-muted-foreground hover:border-volt/50"
																								}`}
																							>
																								<Pencil className="w-3.5 h-3.5 flex-shrink-0" />
																								{t("pbCustomLabel")}
																							</button>
																							<button
																								onClick={() =>
																									setNewExercise({
																										...newExercise,
																										methodology: "complex",
																										exercise_id: "",
																										emom_rounds: [],
																										superset_exercises: [],
																										complex_exercises:
																											newExercise.complex_exercises ||
																											[],
																										set_rows:
																											(
																												newExercise.set_rows ||
																												[]
																											).length > 0
																												? newExercise.set_rows
																												: [
																														createStrengthSetRow(
																															{
																																sets: 3,
																																reps: "10",
																															},
																														),
																													],
																									})
																								}
																								className={`flex h-9 items-center justify-center gap-1.5 px-2 py-1 rounded-sm border text-xs md:text-[11px] leading-none transition-colors ${
																									newExercise.methodology ===
																									"complex"
																										? "border-volt bg-volt/10 text-volt"
																										: "border-border text-muted-foreground hover:border-volt/50"
																								}`}
																							>
																								<Layers className="w-3.5 h-3.5 flex-shrink-0" />
																								{t("complexMethodLabel")}
																							</button>
																						</div>
																					</div>
																				)}

																				{/* === STRENGTH STANDARD FORM === */}
																				{newExercise.exercise_type ===
																					"strength" &&
																					(newExercise.methodology ||
																						"standard") === "standard" && (
																						<>
																							<div className="space-y-2">
																								<Label className="label-tactical">
																									{t("exerciseLabel")}
																								</Label>
																								<div
																									className="relative"
																									ref={exerciseSearchRef}
																								>
																									<div className="relative">
																										<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
																										<Input
																											value={
																												exerciseSearchQuery
																											}
																											onChange={(e) => {
																												setExerciseSearchQuery(
																													e.target.value,
																												);
																												setShowExerciseDropdown(
																													true,
																												);
																												if (
																													newExercise.exercise_name &&
																													e.target.value !==
																														newExercise.exercise_name
																												) {
																													setNewExercise({
																														...newExercise,
																														exercise_id: "",
																														exercise_name: "",
																													});
																												}
																											}}
																											onFocus={() =>
																												setShowExerciseDropdown(
																													true,
																												)
																											}
																											className="bg-secondary border-transparent rounded-sm pl-10 pr-10"
																											placeholder={t("pbSearchOrAddExercise")}
																										/>
																										{exerciseSearchQuery && (
																											<Button
																												variant="ghost"
																												size="sm"
																												className="absolute right-1 top-1/2 -translate-y-1/2 h-9 w-9 md:h-7 md:w-7 p-0"
																												onClick={() => {
																													setExerciseSearchQuery(
																														"",
																													);
																													setNewExercise({
																														...newExercise,
																														exercise_id: "",
																														exercise_name: "",
																													});
																												}}
																											>
																												<X className="w-4 h-4" />
																											</Button>
																										)}
																									</div>

																									{/* Dropdown */}
																									{showExerciseDropdown &&
																										exerciseSearchQuery && (
																											<div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-sm shadow-lg">
																												<ScrollArea className="h-60">
																													{filteredExercises.length >
																													0 ? (
																														<div className="p-1">
																															{filteredExercises.map(
																																(ex) => (
																																	<div
																																		key={
																																			ex.exercise_id
																																		}
																																		className={`flex items-center gap-1 rounded-sm px-1 ${
																																			newExercise.exercise_id ===
																																			ex.exercise_id
																																				? "bg-secondary"
																																				: ""
																																		}`}
																																	>
																																		<button
																																			type="button"
																																			onClick={() =>
																																				handleSelectExercise(
																																					ex,
																																				)
																																			}
																																			className="flex-1 text-left px-2 py-2 rounded-sm hover:bg-secondary flex items-center gap-2"
																																		>
																																			<Dumbbell className="w-4 h-4 text-volt flex-shrink-0" />
																																			<div className="flex-1 min-w-0">
																																				<p className="text-sm font-medium truncate">
																																					{
																																						ex.name
																																					}
																																				</p>
																																				<div className="flex items-center gap-1.5">
																																					<p className="text-xs text-muted-foreground">
																																						{
																																							ex.category
																																						}
																																					</p>
																																					<span
																																						className={`text-xs md:text-[11px] px-1.5 py-0.5 rounded-sm uppercase tracking-wider ${
																																							getExerciseScopeBadgeMeta(
																																								ex,
																																							)
																																								.className
																																						}`}
																																					>
																																						{
																																							getExerciseScopeBadgeMeta(
																																								ex,
																																							)
																																								.label
																																						}
																																					</span>
																																				</div>
																																			</div>
																																		</button>
																																		<Button
																																			type="button"
																																			variant="ghost"
																																			size="icon"
																																			className="h-9 w-9 md:h-7 md:w-7 text-muted-foreground hover:text-volt"
																																			disabled={
																																				!getYouTubeEmbedUrl(
																																					ex.video_url,
																																				)
																																			}
																																			onClick={(
																																				event,
																																			) => {
																																				event.stopPropagation();
																																				openExerciseVideoPreview(
																																					"standard-search",
																																					ex,
																																				);
																																			}}
																																		>
																																			<Play className="w-3.5 h-3.5" />
																																		</Button>
																																	</div>
																																),
																															)}
																														</div>
																													) : null}

																													{!exactMatch &&
																														exerciseSearchQuery.trim() && (
																															<div className="border-t border-border p-1">
																																<button
																																	onClick={
																																		handleOpenNewExerciseForm
																																	}
																																	className="w-full text-left px-3 py-2 rounded-sm hover:bg-volt/10 flex items-center gap-2 text-volt"
																																>
																																	<PlusCircle className="w-4 h-4 flex-shrink-0" />
																																	<span className="text-sm font-medium">
																																		Aggiungi "
																																		{
																																			exerciseSearchQuery
																																		}
																																		" alla
																																		libreria
																																		personale
																																	</span>
																																</button>
																															</div>
																														)}

																													{filteredExercises.length ===
																														0 &&
																														!exerciseSearchQuery.trim() && (
																															<div className="p-4 text-center text-sm text-muted-foreground">
																																{t("pbStartTypingToSearch")}
																															</div>
																														)}
																												</ScrollArea>
																											</div>
																										)}
																									{renderInlineVideoPreview(
																										"standard-search",
																									)}
																								</div>

																								{newExercise.exercise_id && (
																									<p className="text-xs text-volt flex items-center gap-1">
																										<Dumbbell className="w-3 h-3" />
																										{t("selectedExerciseLabel")}
																										:{" "}
																										{newExercise.exercise_name}
																										{(() => {
																											const selectedExercise =
																												getExerciseScopeById(
																													newExercise.exercise_id,
																												);
																											const selectedScopeTag =
																												resolveProgramExerciseScopeTag(
																													{
																														exercise_id:
																															newExercise.exercise_id,
																														source_exercise_id:
																															newExercise.source_exercise_id,
																														exercise_name:
																															newExercise.exercise_name,
																													},
																												) ||
																												(selectedExercise
																													? getExerciseLibraryScopeTag(
																															selectedExercise,
																														)
																													: null);
																											if (!selectedScopeTag)
																												return "";
																											return ` (${getExerciseLibraryScopeLabelByTag(selectedScopeTag)})`;
																										})()}
																									</p>
																								)}
																							</div>

																							<div className="space-y-3">
																								<Label className="label-tactical">
																									{t("sets")}
																								</Label>
																								<div className="space-y-3">
																									<div className="hidden md:flex items-center gap-2 text-[11px] md:text-[10px] text-muted-foreground uppercase tracking-wider">
																										<span className="w-4"></span>
																										<span className="w-20 text-center">
																											{t("sets")}
																										</span>
																										<span className="w-32 text-center">
																											{t("repsLabel")}
																										</span>
																										<span className="w-40 text-center">
																											{t("load")}
																										</span>
																										<span className="w-28 text-center">
																											{t("restLabel")}
																										</span>
																										<span className="w-16 text-center">
																											{t("rpePlaceholder")}
																										</span>
																										<span className="w-16 text-center">
																											{t("rirPlaceholder")}
																										</span>
																										<span className="w-7"></span>
																									</div>
																									{(
																										newExercise.set_rows || []
																									).map((row, ri) => (
																										<div key={ri}>
																											<div className="flex flex-wrap md:flex-nowrap items-center gap-2">
																												<span className="text-xs text-muted-foreground w-4 text-right">
																													{ri + 1}.
																												</span>
																												<Input
																													type="text"
																													inputMode="numeric"
																													value={row.sets ?? ""}
																													onChange={(e) => {
																														const rows = [
																															...newExercise.set_rows,
																														];
																														rows[ri] = {
																															...rows[ri],
																															sets:
																																e.target
																																	.value === ""
																																	? ""
																																	: e.target
																																			.value,
																														};
																														setNewExercise({
																															...newExercise,
																															set_rows: rows,
																														});
																													}}
																													className="bg-secondary border-transparent rounded-sm h-8 text-xs w-16 md:w-20"
																													placeholder="3"
																												/>
																												<Input
																													value={row.reps ?? ""}
																													onChange={(e) => {
																														const rows = [
																															...newExercise.set_rows,
																														];
																														rows[ri] = {
																															...rows[ri],
																															reps: e.target
																																.value,
																														};
																														setNewExercise({
																															...newExercise,
																															set_rows: rows,
																														});
																													}}
																													className="bg-secondary border-transparent rounded-sm h-8 text-xs w-24 md:w-32"
																													placeholder={t("repsLabel")}
																												/>
																												<Input
																													type="text"
																													value={
																														row.load_percentage ??
																														""
																													}
																													onChange={(e) => {
																														const rows = [
																															...newExercise.set_rows,
																														];
																														rows[ri] = {
																															...rows[ri],
																															load_percentage:
																																e.target
																																	.value === ""
																																	? ""
																																	: e.target
																																			.value,
																															...(e.target
																																.value === ""
																																? {
																																		load_reference_exercise_id:
																																			null,
																																		load_reference_exercise_name:
																																			null,
																																	}
																																: {}),
																														};
																														setNewExercise({
																															...newExercise,
																															set_rows: rows,
																														});
																													}}
																													className="bg-secondary border-transparent rounded-sm h-8 text-xs w-24 md:w-40"
																													placeholder={t("pbPercentOrLoad")}
																												/>
																												<Input
																													type="text"
																													inputMode="text"
																													value={
																														row.rest_seconds ??
																														""
																													}
																													onChange={(e) => {
																														const rows = [
																															...newExercise.set_rows,
																														];
																														rows[ri] = {
																															...rows[ri],
																															rest_seconds:
																																e.target.value,
																														};
																														setNewExercise({
																															...newExercise,
																															set_rows: rows,
																														});
																													}}
																													onBlur={(e) => {
																														const rows = [
																															...newExercise.set_rows,
																														];
																														rows[ri] = {
																															...rows[ri],
																															rest_seconds:
																																normalizeRestFieldValue(
																																	e.target
																																		.value,
																																),
																														};
																														setNewExercise({
																															...newExercise,
																															set_rows: rows,
																														});
																													}}
																													className="bg-secondary border-transparent rounded-sm h-8 text-xs w-20 md:w-28"
																													placeholder={t("pbEg230")}
																												/>
																												<Input
																													value={row.rpe ?? ""}
																													onChange={(e) => {
																														const rows = [
																															...newExercise.set_rows,
																														];
																														rows[ri] = {
																															...rows[ri],
																															rpe: e.target
																																.value,
																														};
																														setNewExercise({
																															...newExercise,
																															set_rows: rows,
																														});
																													}}
																													className="bg-secondary border-transparent rounded-sm h-8 text-xs w-14 md:w-16"
																													placeholder={t("rpePlaceholder")}
																												/>
																												<Input
																													value={row.rir ?? ""}
																													onChange={(e) => {
																														const rows = [
																															...newExercise.set_rows,
																														];
																														rows[ri] = {
																															...rows[ri],
																															rir: e.target
																																.value,
																														};
																														setNewExercise({
																															...newExercise,
																															set_rows: rows,
																														});
																													}}
																													className="bg-secondary border-transparent rounded-sm h-8 text-xs w-14 md:w-16"
																													placeholder={t("rirPlaceholder")}
																												/>
																												{newExercise.set_rows
																													.length > 1 && (
																													<Button
																														variant="ghost"
																														size="icon"
																														className="h-9 w-9 md:h-7 md:w-7 ml-auto md:ml-0 text-muted-foreground hover:text-blaze flex-shrink-0"
																														onClick={() => {
																															setNewExercise({
																																...newExercise,
																																set_rows:
																																	newExercise.set_rows.filter(
																																		(_, i) =>
																																			i !== ri,
																																	),
																															});
																														}}
																													>
																														<X className="w-3 h-3" />
																													</Button>
																												)}
																											</div>
																											{row.load_percentage && (
																												<div className="pl-6 pt-1">
																													<Select
																														value={
																															row.load_reference_exercise_id ||
																															newExercise.load_reference_exercise_id ||
																															"__current__"
																														}
																														onValueChange={(
																															selectedValue,
																														) => {
																															const rows = [
																																...newExercise.set_rows,
																															];
																															const selectedExercise =
																																strengthReferenceOptions.find(
																																	(option) =>
																																		option.value ===
																																		selectedValue,
																																);
																															const nextReferenceId =
																																selectedValue ===
																																"__current__"
																																	? null
																																	: selectedExercise?.value ||
																																		null;
																															const nextReferenceName =
																																selectedValue ===
																																"__current__"
																																	? null
																																	: selectedExercise?.label ||
																																		null;
																															const updatedRows =
																																rows.map(
																																	(setRow) => ({
																																		...setRow,
																																		load_reference_exercise_id:
																																			nextReferenceId,
																																		load_reference_exercise_name:
																																			nextReferenceName,
																																	}),
																																);
																															setNewExercise({
																																...newExercise,
																																set_rows:
																																	updatedRows,
																															});
																														}}
																													>
																														<SelectTrigger className="bg-secondary border-transparent rounded-sm h-8 text-xs">
																															<SelectValue
																																placeholder={t(
																																	"loadReferenceExercise",
																																)}
																															/>
																														</SelectTrigger>
																														<SelectContent>
																															<SelectItem value="__current__">
																																{t(
																																	"currentExerciseLoadReference",
																																)}
																															</SelectItem>
																															{strengthReferenceOptions.map(
																																(option) => (
																																	<SelectItem
																																		key={
																																			option.value
																																		}
																																		value={
																																			option.value
																																		}
																																	>
																																		{
																																			option.label
																																		}
																																	</SelectItem>
																																),
																															)}
																														</SelectContent>
																													</Select>
																													<p className="mt-1 text-[11px] md:text-[10px] text-muted-foreground uppercase tracking-wider">
																														{t(
																															"loadReferenceSummary",
																														)}
																														:{" "}
																														{row.load_reference_exercise_name ||
																															newExercise.load_reference_exercise_name ||
																															t(
																																"currentExerciseLoadReference",
																															)}
																													</p>
																												</div>
																											)}
																										</div>
																									))}
																									<Button
																										variant="ghost"
																										size="sm"
																										className="w-full border border-dashed border-border rounded-sm h-7 text-xs text-muted-foreground"
																										onClick={() =>
																											setNewExercise({
																												...newExercise,
																												set_rows: [
																													...newExercise.set_rows,
																													createStrengthSetRow(),
																												],
																											})
																										}
																									>
																										<Plus className="w-3 h-3 mr-1" />
																										{t("pbAddRow")}
																									</Button>
																									<div className="space-y-1">
																										<span className="text-[11px] md:text-[10px] text-muted-foreground uppercase tracking-wider">
																											{t("pbExerciseNotes")}
																										</span>
																										<Textarea
																											value={
																												newExercise.notes ?? ""
																											}
																											onChange={(e) =>
																												setNewExercise({
																													...newExercise,
																													notes: e.target.value,
																												})
																											}
																											className="bg-secondary border-transparent rounded-sm text-xs min-h-[48px] resize-none"
																											placeholder={t("pbExerciseNotesPlaceholder")}
																										/>
																									</div>
																								</div>
																							</div>
																						</>
																					)}

																				{/* === STRENGTH COMPLEX FORM === */}
																				{newExercise.exercise_type ===
																					"strength" &&
																					newExercise.methodology ===
																						"complex" && (
																						<div className="space-y-4">
																							<div className="space-y-2">
																								<Label className="label-tactical">
																									{t("pbExerciseSequence")}
																								</Label>
																								{(
																									newExercise.complex_exercises ||
																									[]
																								).length > 0 && (
																									<div className="space-y-2">
																										{(
																											newExercise.complex_exercises ||
																											[]
																										).map((item, idx) => (
																											<div
																												key={`${item.exercise_id || item.exercise_name}_${idx}`}
																												className="flex items-center gap-2 p-2 rounded-sm bg-background border border-border"
																											>
																												<span className="text-xs text-volt font-heading w-6">
																													{idx + 1}.
																												</span>
																												<span className="text-sm flex-1 truncate">
																													{item.exercise_name}
																												</span>
																												{(() => {
																													const scopeTag =
																														resolveProgramExerciseScopeTag(
																															{
																																exercise_id:
																																	item.exercise_id,
																																source_exercise_id:
																																	item.source_exercise_id,
																																exercise_name:
																																	item.exercise_name,
																																library_scope:
																																	item.library_scope,
																															},
																														);
																													if (!scopeTag)
																														return null;
																													return (
																														<span
																															className={`text-xs md:text-[11px] px-1.5 py-0.5 rounded-sm uppercase tracking-wider ${
																																scopeTag ===
																																"global"
																																	? "bg-amber-500/20 text-amber-300"
																																	: "bg-emerald-500/20 text-emerald-300"
																															}`}
																														>
																															{getExerciseLibraryScopeLabelByTag(
																																scopeTag,
																															)}
																														</span>
																													);
																												})()}
																												<Button
																													variant="ghost"
																													size="icon"
																													className="h-9 w-9 md:h-7 md:w-7 text-muted-foreground hover:text-volt"
																													onClick={() =>
																														setEditingComplexLinkIndex(
																															(prev) =>
																																prev === idx
																																	? null
																																	: idx,
																														)
																													}
																												>
																													<Pencil className="w-3.5 h-3.5" />
																												</Button>
																												{editingComplexLinkIndex ===
																													idx && (
																													<ExerciseLinkCombobox
																														options={
																															libraryExerciseOptions
																														}
																														valueId={
																															item.exercise_id
																														}
																														currentLabel={
																															item.exercise_name
																														}
																														t={t}
																														placeholder={t("pbChangeExercise")}
																														keepCurrentLabel={t("pbKeepCurrent")}
																														triggerClassName="h-7 text-[11px] md:text-[10px] bg-secondary border-transparent rounded-sm w-[140px] justify-between font-normal"
																														onKeepCurrent={() =>
																															setEditingComplexLinkIndex(
																																null,
																															)
																														}
																														onSelect={(
																															selectedExercise,
																														) => {
																															const updated = [
																																...(newExercise.complex_exercises ||
																																	[]),
																															];
																															updated[idx] = {
																																...updated[idx],
																																exercise_id:
																																	selectedExercise.value,
																																source_exercise_id:
																																	selectedExercise.value,
																																exercise_name:
																																	selectedExercise.label,
																																library_scope:
																																	selectedExercise.library_scope ||
																																	null,
																																load_reference_exercise_id:
																																	selectedExercise.load_reference_exercise_id ||
																																	null,
																																load_reference_exercise_name:
																																	selectedExercise.load_reference_exercise_name ||
																																	null,
																															};
																															setNewExercise({
																																...newExercise,
																																complex_exercises:
																																	updated,
																															});
																															setEditingComplexLinkIndex(
																																null,
																															);
																														}}
																													/>
																												)}
																												<Button
																													variant="ghost"
																													size="icon"
																													className="h-9 w-9 md:h-7 md:w-7"
																													onClick={() => {
																														if (idx === 0)
																															return;
																														const updated = [
																															...(newExercise.complex_exercises ||
																																[]),
																														];
																														[
																															updated[idx - 1],
																															updated[idx],
																														] = [
																															updated[idx],
																															updated[idx - 1],
																														];
																														setNewExercise({
																															...newExercise,
																															complex_exercises:
																																updated,
																														});
																													}}
																												>
																													<ChevronUp className="w-3.5 h-3.5" />
																												</Button>
																												<Button
																													variant="ghost"
																													size="icon"
																													className="h-9 w-9 md:h-7 md:w-7"
																													onClick={() => {
																														if (
																															idx >=
																															(
																																newExercise.complex_exercises ||
																																[]
																															).length -
																																1
																														) {
																															return;
																														}
																														const updated = [
																															...(newExercise.complex_exercises ||
																																[]),
																														];
																														[
																															updated[idx + 1],
																															updated[idx],
																														] = [
																															updated[idx],
																															updated[idx + 1],
																														];
																														setNewExercise({
																															...newExercise,
																															complex_exercises:
																																updated,
																														});
																													}}
																												>
																													<ChevronDown className="w-3.5 h-3.5" />
																												</Button>
																												<Button
																													variant="ghost"
																													size="icon"
																													className="h-9 w-9 md:h-7 md:w-7 text-muted-foreground hover:text-blaze"
																													onClick={() => {
																														setNewExercise({
																															...newExercise,
																															complex_exercises:
																																(
																																	newExercise.complex_exercises ||
																																	[]
																																).filter(
																																	(_, i) =>
																																		i !== idx,
																																),
																														});
																													}}
																												>
																													<X className="w-3.5 h-3.5" />
																												</Button>
																											</div>
																										))}
																									</div>
																								)}

																								<div
																									className="relative"
																									ref={complexSearchRef}
																								>
																									<div className="relative">
																										<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
																										<Input
																											value={complexSearchQuery}
																											onChange={(e) => {
																												setComplexSearchQuery(
																													e.target.value,
																												);
																												setShowComplexDropdown(
																													true,
																												);
																											}}
																											onFocus={() =>
																												setShowComplexDropdown(
																													true,
																												)
																											}
																											className="bg-secondary border-transparent rounded-sm pl-10"
																											placeholder={t("pbAddExerciseToSequence")}
																										/>
																									</div>
																									{showComplexDropdown &&
																										complexSearchQuery && (
																											<div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-sm shadow-lg">
																												<ScrollArea className="h-60">
																													{filteredComplexExercises.length >
																													0 ? (
																														<div className="p-1">
																															{filteredComplexExercises
																																.slice(0, 50)
																																.map((ex) => (
																																	<div
																																		key={
																																			ex.exercise_id
																																		}
																																		className="flex items-center gap-1 px-1 rounded-sm"
																																	>
																																		<button
																																			type="button"
																																			onClick={() => {
																																				setNewExercise(
																																					(
																																						prev,
																																					) => {
																																						const alreadyIncluded =
																																							(
																																								prev.complex_exercises ||
																																								[]
																																							).some(
																																								(
																																									item,
																																								) =>
																																									item.exercise_id ===
																																									ex.exercise_id,
																																							);
																																						if (
																																							alreadyIncluded
																																						)
																																							return prev;
																																						return {
																																							...prev,
																																							complex_exercises:
																																								[
																																									...(prev.complex_exercises ||
																																										[]),
																																									{
																																										exercise_id:
																																											ex.exercise_id,
																																										source_exercise_id:
																																											ex.exercise_id,
																																										exercise_name:
																																											ex.name,
																																										library_scope:
																																											ex.library_scope ||
																																											null,
																																									},
																																								],
																																						};
																																					},
																																				);
																																				setComplexSearchQuery(
																																					"",
																																				);
																																				setShowComplexDropdown(
																																					false,
																																				);
																																				setVideoPreviewState(
																																					null,
																																				);
																																			}}
																																			className="flex-1 text-left px-2 py-2 rounded-sm hover:bg-secondary flex items-center gap-2"
																																		>
																																			<Dumbbell className="w-4 h-4 text-volt flex-shrink-0" />
																																			<div className="flex-1 min-w-0">
																																				<p className="text-sm font-medium truncate">
																																					{
																																						ex.name
																																					}
																																				</p>
																																				<div className="flex items-center gap-1.5">
																																					<p className="text-xs text-muted-foreground">
																																						{
																																							ex.category
																																						}
																																					</p>
																																					<span
																																						className={`text-xs md:text-[11px] px-1.5 py-0.5 rounded-sm uppercase tracking-wider ${
																																							getExerciseScopeBadgeMeta(
																																								ex,
																																							)
																																								.className
																																						}`}
																																					>
																																						{
																																							getExerciseScopeBadgeMeta(
																																								ex,
																																							)
																																								.label
																																						}
																																					</span>
																																				</div>
																																			</div>
																																		</button>
																																		<Button
																																			type="button"
																																			variant="ghost"
																																			size="icon"
																																			className="h-9 w-9 md:h-7 md:w-7 text-muted-foreground hover:text-volt"
																																			disabled={
																																				!getYouTubeEmbedUrl(
																																					ex.video_url,
																																				)
																																			}
																																			onClick={(
																																				event,
																																			) => {
																																				event.stopPropagation();
																																				openExerciseVideoPreview(
																																					"complex-search",
																																					ex,
																																				);
																																			}}
																																		>
																																			<Play className="w-3.5 h-3.5" />
																																		</Button>
																																	</div>
																																))}
																														</div>
																													) : (
																														<div className="p-3 text-center text-xs text-muted-foreground">
																															{t("pbNoExercises")}
																															trovato
																														</div>
																													)}
																													{!complexExactMatch &&
																														complexSearchQuery.trim() && (
																															<div className="border-t border-border p-1">
																																<button
																																	onClick={
																																		handleOpenNewExerciseFormForComplex
																																	}
																																	className="w-full text-left px-3 py-2 rounded-sm hover:bg-volt/10 flex items-center gap-2 text-volt"
																																>
																																	<PlusCircle className="w-4 h-4 flex-shrink-0" />
																																	<span className="text-sm font-medium">
																																		Aggiungi "
																																		{
																																			complexSearchQuery
																																		}
																																		" alla
																																		libreria
																																		personale
																																	</span>
																																</button>
																															</div>
																														)}
																												</ScrollArea>
																											</div>
																										)}
																									{renderInlineVideoPreview(
																										"complex-search",
																									)}
																								</div>
																							</div>

																							<div className="space-y-3">
																								<Label className="label-tactical">
																									{t("sets")}
																								</Label>
																								<div className="space-y-3">
																									<div className="hidden md:flex items-center gap-2 text-[11px] md:text-[10px] text-muted-foreground uppercase tracking-wider">
																										<span className="w-4"></span>
																										<span className="w-20 text-center">
																											{t("sets")}
																										</span>
																										<span className="w-32 text-center">
																											{t("repsLabel")}
																										</span>
																										<span className="w-40 text-center">
																											{t("load")}
																										</span>
																										<span className="w-28 text-center">
																											{t("restLabel")}
																										</span>
																										<span className="w-16 text-center">
																											{t("rpePlaceholder")}
																										</span>
																										<span className="w-16 text-center">
																											{t("rirPlaceholder")}
																										</span>
																										<span className="w-7"></span>
																									</div>
																									{(
																										newExercise.set_rows || []
																									).map((row, ri) => (
																										<div key={ri}>
																											<div className="flex flex-wrap md:flex-nowrap items-center gap-2">
																												<span className="text-xs text-muted-foreground w-4 text-right">
																													{ri + 1}.
																												</span>
																												<Input
																													type="text"
																													inputMode="numeric"
																													value={row.sets ?? ""}
																													onChange={(e) => {
																														const rows = [
																															...newExercise.set_rows,
																														];
																														rows[ri] = {
																															...rows[ri],
																															sets:
																																e.target
																																	.value === ""
																																	? ""
																																	: e.target
																																			.value,
																														};
																														setNewExercise({
																															...newExercise,
																															set_rows: rows,
																														});
																													}}
																													className="bg-secondary border-transparent rounded-sm h-8 text-xs w-16 md:w-20"
																													placeholder="3"
																												/>
																												<Input
																													value={row.reps ?? ""}
																													onChange={(e) => {
																														const rows = [
																															...newExercise.set_rows,
																														];
																														rows[ri] = {
																															...rows[ri],
																															reps: e.target
																																.value,
																														};
																														setNewExercise({
																															...newExercise,
																															set_rows: rows,
																														});
																													}}
																													className="bg-secondary border-transparent rounded-sm h-8 text-xs w-24 md:w-32"
																													placeholder={t("repsLabel")}
																												/>
																												<Input
																													type="text"
																													value={
																														row.load_percentage ??
																														""
																													}
																													onChange={(e) => {
																														const rows = [
																															...newExercise.set_rows,
																														];
																														rows[ri] = {
																															...rows[ri],
																															load_percentage:
																																e.target
																																	.value === ""
																																	? ""
																																	: e.target
																																			.value,
																															...(e.target
																																.value === ""
																																? {
																																		load_reference_exercise_id:
																																			null,
																																		load_reference_exercise_name:
																																			null,
																																	}
																																: {}),
																														};
																														setNewExercise({
																															...newExercise,
																															set_rows: rows,
																														});
																													}}
																													className="bg-secondary border-transparent rounded-sm h-8 text-xs w-24 md:w-40"
																													placeholder={t("pbPercentOrLoad")}
																												/>
																												<Input
																													type="text"
																													inputMode="text"
																													value={
																														row.rest_seconds ??
																														""
																													}
																													onChange={(e) => {
																														const rows = [
																															...newExercise.set_rows,
																														];
																														rows[ri] = {
																															...rows[ri],
																															rest_seconds:
																																e.target.value,
																														};
																														setNewExercise({
																															...newExercise,
																															set_rows: rows,
																														});
																													}}
																													onBlur={(e) => {
																														const rows = [
																															...newExercise.set_rows,
																														];
																														rows[ri] = {
																															...rows[ri],
																															rest_seconds:
																																normalizeRestFieldValue(
																																	e.target
																																		.value,
																																),
																														};
																														setNewExercise({
																															...newExercise,
																															set_rows: rows,
																														});
																													}}
																													className="bg-secondary border-transparent rounded-sm h-8 text-xs w-20 md:w-28"
																													placeholder={t("pbEg230")}
																												/>
																												<Input
																													value={row.rpe ?? ""}
																													onChange={(e) => {
																														const rows = [
																															...newExercise.set_rows,
																														];
																														rows[ri] = {
																															...rows[ri],
																															rpe: e.target
																																.value,
																														};
																														setNewExercise({
																															...newExercise,
																															set_rows: rows,
																														});
																													}}
																													className="bg-secondary border-transparent rounded-sm h-8 text-xs w-14 md:w-16"
																													placeholder={t("rpePlaceholder")}
																												/>
																												<Input
																													value={row.rir ?? ""}
																													onChange={(e) => {
																														const rows = [
																															...newExercise.set_rows,
																														];
																														rows[ri] = {
																															...rows[ri],
																															rir: e.target
																																.value,
																														};
																														setNewExercise({
																															...newExercise,
																															set_rows: rows,
																														});
																													}}
																													className="bg-secondary border-transparent rounded-sm h-8 text-xs w-14 md:w-16"
																													placeholder={t("rirPlaceholder")}
																												/>
																												{newExercise.set_rows
																													.length > 1 && (
																													<Button
																														variant="ghost"
																														size="icon"
																														className="h-9 w-9 md:h-7 md:w-7 ml-auto md:ml-0 text-muted-foreground hover:text-blaze flex-shrink-0"
																														onClick={() => {
																															setNewExercise({
																																...newExercise,
																																set_rows:
																																	newExercise.set_rows.filter(
																																		(_, i) =>
																																			i !== ri,
																																	),
																															});
																														}}
																													>
																														<X className="w-3 h-3" />
																													</Button>
																												)}
																											</div>
																										</div>
																									))}
																									<ComplexLoadReference
																										exercise={newExercise}
																										t={t}
																										onSelect={(referenceId, referenceName) =>
																											setNewExercise((prev) => ({
																												...prev,
																												set_rows: (prev.set_rows || []).map(
																													(setRow) => ({
																														...setRow,
																														load_reference_exercise_id: referenceId,
																														load_reference_exercise_name: referenceName,
																													}),
																												),
																											}))
																										}
																										onPickOther={() =>
																											openLoadReferenceDialog({
																												target: "newExercise",
																											})
																										}
																									/>
																									<Button
																										variant="ghost"
																										size="sm"
																										className="w-full border border-dashed border-border rounded-sm h-7 text-xs text-muted-foreground"
																										onClick={() =>
																											setNewExercise({
																												...newExercise,
																												set_rows: [
																													...(newExercise.set_rows ||
																														[]),
																													createStrengthSetRow(),
																												],
																											})
																										}
																									>
																										<Plus className="w-3 h-3 mr-1" />
																										{t("pbAddRow")}
																									</Button>
																								</div>
																							</div>

																							<div className="space-y-1">
																								<span className="text-[11px] md:text-[10px] text-muted-foreground uppercase tracking-wider">
																									{t("pbComplexNotes")}
																								</span>
																								<Textarea
																									value={
																										newExercise.notes ?? ""
																									}
																									onChange={(e) =>
																										setNewExercise({
																											...newExercise,
																											notes: e.target.value,
																										})
																									}
																									className="bg-secondary border-transparent rounded-sm text-xs min-h-[48px] resize-none"
																									placeholder={t("pbComplexNotesPlaceholder")}
																								/>
																							</div>
																						</div>
																					)}

																				{/* === STRENGTH CUSTOM FORM === */}
																				{newExercise.exercise_type ===
																					"strength" &&
																					newExercise.methodology ===
																						"custom" && (
																						<div className="space-y-3">
																							<div className="space-y-1">
																								<span className="text-[11px] md:text-[10px] text-muted-foreground uppercase tracking-wider">
																									{t("exerciseName")}
																								</span>
																								<Input
																									value={
																										newExercise.exercise_name ??
																										""
																									}
																									onChange={(e) =>
																										setNewExercise({
																											...newExercise,
																											exercise_name:
																												e.target.value,
																										})
																									}
																									className="bg-secondary border-transparent rounded-sm text-xs"
																									placeholder={t("pbComplexNamePlaceholder")}
																								/>
																							</div>
																							<div className="space-y-1">
																								<div className="flex items-center justify-between">
																									<span className="text-[11px] md:text-[10px] text-muted-foreground uppercase tracking-wider">
																										{t("workoutDescription")}
																									</span>
																									<Button
																										type="button"
																										size="sm"
																										variant="outline"
																										className="h-7 px-2.5 gap-1.5 text-[11px] md:text-[10px] border-dashed border-acid/50 text-acid hover:text-acid/80 hover:bg-acid/10 rounded-sm"
																										disabled={
																											aiStrengthParsing ||
																											!newExercise.custom_workout_text?.trim()
																										}
																										onClick={
																											handleParseStrength
																										}
																									>
																										{aiStrengthParsing ? (
																											<>
																												<Loader2 className="w-3 h-3 animate-spin" />
																												{t("pbAiParsing")}
																											</>
																										) : (
																											<>
																												<Wand2 className="w-3 h-3" />
																												{t("pbAiWand")}
																											</>
																										)}
																									</Button>
																								</div>
																								<Textarea
																									value={
																										newExercise.custom_workout_text ??
																										""
																									}
																									onChange={(e) =>
																										setNewExercise({
																											...newExercise,
																											custom_workout_text:
																												e.target.value,
																										})
																									}
																									className="bg-secondary border-transparent rounded-sm text-xs min-h-[120px] resize-y md:resize overflow-auto"
																									placeholder={t("pbAiFreeTextPlaceholder")}
																								/>
																							</div>
																							<div className="space-y-1">
																								<span className="text-[11px] md:text-[10px] text-muted-foreground uppercase tracking-wider">
																									{t("notes")}
																								</span>
																								<Textarea
																									value={
																										newExercise.notes ?? ""
																									}
																									onChange={(e) =>
																										setNewExercise({
																											...newExercise,
																											notes: e.target.value,
																										})
																									}
																									className="bg-secondary border-transparent rounded-sm text-xs min-h-[48px] resize-none"
																									placeholder={t("pbAthleteCuesPlaceholder")}
																								/>
																							</div>
																						</div>
																					)}

																				{/* === SUPERSET / GIANT SET FORM === */}
																				{newExercise.exercise_type ===
																					"strength" &&
																					newExercise.methodology ===
																						"superset" && (
																						<div className="space-y-4">
																							<div className="space-y-1">
																								<span className="text-[11px] md:text-[10px] text-muted-foreground uppercase tracking-wider">
																									{t("exerciseName")}
																								</span>
																								<Input
																									value={
																										newExercise.exercise_name ??
																										""
																									}
																									onChange={(e) =>
																										setNewExercise({
																											...newExercise,
																											exercise_name:
																												e.target.value,
																										})
																									}
																									className="bg-secondary border-transparent rounded-sm text-xs"
																									placeholder={t(
																										"exerciseNamePlaceholder",
																									)}
																								/>
																								<p className="text-[11px] md:text-[10px] text-muted-foreground">
																									{t("supersetNameAutoHint")}
																								</p>
																							</div>

																							{/* Round + Rest config */}
																							<div className="grid grid-cols-2 gap-3">
																								<div className="space-y-1">
																									<span className="text-[11px] md:text-[10px] text-muted-foreground uppercase tracking-wider">
																										{t("pbTotalRounds")}
																									</span>
																									<Input
																										type="text"
																										inputMode="text"
																										value={
																											newExercise.superset_sets ??
																											""
																										}
																										onChange={(e) =>
																											setNewExercise({
																												...newExercise,
																												superset_sets:
																													e.target.value === ""
																														? null
																														: e.target.value,
																											})
																										}
																										className="bg-secondary border-transparent rounded-sm"
																										placeholder={t("pbRoundsPlaceholder")}
																									/>
																								</div>
																								<div className="space-y-1">
																									<span className="text-[11px] md:text-[10px] text-muted-foreground uppercase tracking-wider">
																										{t("pbRestBetweenRounds")}
																									</span>
																									<Input
																										type="text"
																										inputMode="text"
																										value={
																											newExercise.superset_rest_seconds ??
																											""
																										}
																										onChange={(e) =>
																											setNewExercise({
																												...newExercise,
																												superset_rest_seconds:
																													e.target.value === ""
																														? null
																														: e.target.value,
																											})
																										}
																										onBlur={(e) =>
																											setNewExercise({
																												...newExercise,
																												superset_rest_seconds:
																													normalizeSmartRestInput(
																														e.target.value,
																													),
																											})
																										}
																										className="bg-secondary border-transparent rounded-sm"
																										placeholder={t("pbEg230")}
																									/>
																								</div>
																							</div>

																							{/* Superset exercise list */}
																							<div className="space-y-2">
																								<span className="text-[11px] md:text-[10px] text-muted-foreground uppercase tracking-wider">
																									{t("supersetExercises")}
																								</span>
																								{(
																									newExercise.superset_exercises ||
																									[]
																								).map((se, seIndex) => (
																									<div
																										key={seIndex}
																										className="p-3 bg-background rounded-sm border border-border space-y-2"
																									>
																										<div className="flex items-center gap-2">
																											<span className="text-xs font-heading text-volt font-bold w-6">
																												{String.fromCharCode(
																													65 + seIndex,
																												)}
																												{seIndex + 1}
																											</span>
																											<span className="flex-1 text-sm font-medium truncate">
																												{se.exercise_name}
																											</span>
																											{(() => {
																												const scopeTag =
																													resolveProgramExerciseScopeTag(
																														{
																															exercise_id:
																																se.exercise_id,
																															source_exercise_id:
																																se.source_exercise_id,
																															exercise_name:
																																se.exercise_name,
																															library_scope:
																																se.library_scope,
																														},
																													);
																												if (!scopeTag)
																													return null;
																												return (
																													<span
																														className={`text-xs md:text-[11px] px-1.5 py-0.5 rounded-sm uppercase tracking-wider ${
																															scopeTag ===
																															"global"
																																? "bg-amber-500/20 text-amber-300"
																																: "bg-emerald-500/20 text-emerald-300"
																														}`}
																													>
																														{getExerciseLibraryScopeLabelByTag(
																															scopeTag,
																														)}
																													</span>
																												);
																											})()}
																											<Button
																												variant="ghost"
																												size="icon"
																												className="h-8 w-8 md:h-6 md:w-6 text-muted-foreground hover:text-volt flex-shrink-0"
																												onClick={() =>
																													setEditingSupersetLinkIndex(
																														(prev) =>
																															prev === seIndex
																																? null
																																: seIndex,
																													)
																												}
																											>
																												<Pencil className="w-3.5 h-3.5" />
																											</Button>
																											{editingSupersetLinkIndex ===
																												seIndex && (
																												<ExerciseLinkCombobox
																													options={
																														libraryExerciseOptions
																													}
																													valueId={
																														se.exercise_id
																													}
																													currentLabel={
																														se.exercise_name
																													}
																													t={t}
																													placeholder={t("pbChangeExercise")}
																													keepCurrentLabel={t("pbKeepCurrent")}
																													triggerClassName="h-6 text-[11px] md:text-[10px] bg-secondary border-transparent rounded-sm w-[140px] justify-between font-normal"
																													onKeepCurrent={() =>
																														setEditingSupersetLinkIndex(
																															null,
																														)
																													}
																													onSelect={(
																														selectedExercise,
																													) => {
																														const updated = [
																															...(newExercise.superset_exercises ||
																																[]),
																														];
																														updated[seIndex] = {
																															...updated[
																																seIndex
																															],
																															exercise_id:
																																selectedExercise.value,
																															source_exercise_id:
																																selectedExercise.value,
																															exercise_name:
																																selectedExercise.label,
																															library_scope:
																																selectedExercise.library_scope ||
																																null,
																															load_reference_exercise_id:
																																selectedExercise.load_reference_exercise_id ||
																																null,
																															load_reference_exercise_name:
																																selectedExercise.load_reference_exercise_name ||
																																null,
																														};
																														setNewExercise({
																															...newExercise,
																															superset_exercises:
																																updated,
																														});
																														setEditingSupersetLinkIndex(
																															null,
																														);
																													}}
																												/>
																											)}
																											<Button
																												variant="ghost"
																												size="icon"
																												className="h-8 w-8 md:h-6 md:w-6 text-muted-foreground hover:text-volt flex-shrink-0"
																												disabled={seIndex === 0}
																												onClick={() =>
																													handleMoveSupersetExercise(
																														seIndex,
																														-1,
																													)
																												}
																											>
																												<ChevronUp className="w-3 h-3" />
																											</Button>
																											<Button
																												variant="ghost"
																												size="icon"
																												className="h-8 w-8 md:h-6 md:w-6 text-muted-foreground hover:text-volt flex-shrink-0"
																												disabled={
																													seIndex ===
																													(
																														newExercise.superset_exercises ||
																														[]
																													).length -
																														1
																												}
																												onClick={() =>
																													handleMoveSupersetExercise(
																														seIndex,
																														1,
																													)
																												}
																											>
																												<ChevronDown className="w-3 h-3" />
																											</Button>
																											<Button
																												variant="ghost"
																												size="icon"
																												className="h-8 w-8 md:h-6 md:w-6 text-muted-foreground hover:text-blaze flex-shrink-0"
																												onClick={() => {
																													const updated = [
																														...newExercise.superset_exercises,
																													];
																													updated.splice(
																														seIndex,
																														1,
																													);
																													setNewExercise({
																														...newExercise,
																														superset_exercises:
																															updated,
																													});
																												}}
																											>
																												<X className="w-3 h-3" />
																											</Button>
																										</div>
																										{/* Set rows for this superset exercise */}
																										<div className="space-y-1">
																											<div className="hidden md:flex items-center gap-2 text-[11px] md:text-[10px] text-muted-foreground uppercase tracking-wider">
																												<span className="w-32 text-center">
																													{t("repsLabel")}
																												</span>
																												<span className="w-40 text-center">
																													{t("load")}
																												</span>
																												<span className="w-20 text-center">
																													{t("rpePlaceholder")}
																												</span>
																												<span className="w-20 text-center">
																													{t("rirPlaceholder")}
																												</span>
																											</div>
																											{(se.set_rows || []).map(
																												(row, ri) => (
																													<div key={ri}>
																														<div className="flex flex-wrap md:flex-nowrap items-center gap-2">
																															<Input
																																value={
																																	row.reps ?? ""
																																}
																																onChange={(
																																	e,
																																) => {
																																	const updated =
																																		[
																																			...newExercise.superset_exercises,
																																		];
																																	const rows = [
																																		...(updated[
																																			seIndex
																																		]
																																			.set_rows ||
																																			[]),
																																	];
																																	rows[ri] = {
																																		...rows[ri],
																																		reps: e
																																			.target
																																			.value,
																																	};
																																	updated[
																																		seIndex
																																	] = {
																																		...updated[
																																			seIndex
																																		],
																																		set_rows:
																																			rows,
																																	};
																																	setNewExercise(
																																		{
																																			...newExercise,
																																			superset_exercises:
																																				updated,
																																		},
																																	);
																																}}
																																className="bg-secondary border-transparent rounded-sm h-8 text-xs w-24 md:w-32"
																																placeholder={t("repsLabel")}
																															/>
																															<Input
																																value={
																																	row.load_percentage ??
																																	""
																																}
																																onChange={(
																																	e,
																																) => {
																																	const updated =
																																		[
																																			...newExercise.superset_exercises,
																																		];
																																	const rows = [
																																		...(updated[
																																			seIndex
																																		]
																																			.set_rows ||
																																			[]),
																																	];
																																	rows[ri] = {
																																		...rows[ri],
																																		load_percentage:
																																			e.target
																																				.value ===
																																			""
																																				? null
																																				: e
																																						.target
																																						.value,
																																		...(e.target
																																			.value ===
																																		""
																																			? {
																																					load_reference_exercise_id:
																																						null,
																																					load_reference_exercise_name:
																																						null,
																																				}
																																			: {}),
																																	};
																																	updated[
																																		seIndex
																																	] = {
																																		...updated[
																																			seIndex
																																		],
																																		set_rows:
																																			rows,
																																	};
																																	setNewExercise(
																																		{
																																			...newExercise,
																																			superset_exercises:
																																				updated,
																																		},
																																	);
																																}}
																																className="bg-secondary border-transparent rounded-sm h-8 text-xs w-24 md:w-40"
																																placeholder={t("pbPercentOrLoad")}
																															/>
																															<Input
																																value={
																																	row.rpe ?? ""
																																}
																																onChange={(
																																	e,
																																) => {
																																	const updated =
																																		[
																																			...newExercise.superset_exercises,
																																		];
																																	const rows = [
																																		...(updated[
																																			seIndex
																																		]
																																			.set_rows ||
																																			[]),
																																	];
																																	rows[ri] = {
																																		...rows[ri],
																																		rpe: e
																																			.target
																																			.value,
																																	};
																																	updated[
																																		seIndex
																																	] = {
																																		...updated[
																																			seIndex
																																		],
																																		set_rows:
																																			rows,
																																	};
																																	setNewExercise(
																																		{
																																			...newExercise,
																																			superset_exercises:
																																				updated,
																																		},
																																	);
																																}}
																																className="bg-secondary border-transparent rounded-sm h-8 text-xs w-16 md:w-20"
																																placeholder={t("rpePlaceholder")}
																															/>
																															<Input
																																value={
																																	row.rir ?? ""
																																}
																																onChange={(
																																	e,
																																) => {
																																	const updated =
																																		[
																																			...newExercise.superset_exercises,
																																		];
																																	const rows = [
																																		...(updated[
																																			seIndex
																																		]
																																			.set_rows ||
																																			[]),
																																	];
																																	rows[ri] = {
																																		...rows[ri],
																																		rir: e
																																			.target
																																			.value,
																																	};
																																	updated[
																																		seIndex
																																	] = {
																																		...updated[
																																			seIndex
																																		],
																																		set_rows:
																																			rows,
																																	};
																																	setNewExercise(
																																		{
																																			...newExercise,
																																			superset_exercises:
																																				updated,
																																		},
																																	);
																																}}
																																className="bg-secondary border-transparent rounded-sm h-8 text-xs w-16 md:w-20"
																																placeholder={t("rirPlaceholder")}
																															/>
																														</div>
																														{row.load_percentage && (
																															<div className="pl-2 pt-1">
																																<Select
																																	value={
																																		row.load_reference_exercise_id ||
																																		"__current__"
																																	}
																																	onValueChange={(
																																		selectedValue,
																																	) => {
																																		const updated =
																																			[
																																				...newExercise.superset_exercises,
																																			];
																																		const rows =
																																			[
																																				...(updated[
																																					seIndex
																																				]
																																					.set_rows ||
																																					[]),
																																			];
																																		const selectedExercise =
																																			strengthReferenceOptions.find(
																																				(
																																					option,
																																				) =>
																																					option.value ===
																																					selectedValue,
																																			);
																																		rows[ri] = {
																																			...rows[
																																				ri
																																			],
																																			load_reference_exercise_id:
																																				selectedValue ===
																																				"__current__"
																																					? null
																																					: selectedExercise?.value ||
																																						null,
																																			load_reference_exercise_name:
																																				selectedValue ===
																																				"__current__"
																																					? null
																																					: selectedExercise?.label ||
																																						null,
																																		};
																																		updated[
																																			seIndex
																																		] = {
																																			...updated[
																																				seIndex
																																			],
																																			set_rows:
																																				rows,
																																		};
																																		setNewExercise(
																																			{
																																				...newExercise,
																																				superset_exercises:
																																					updated,
																																			},
																																		);
																																	}}
																																>
																																	<SelectTrigger className="bg-secondary border-transparent rounded-sm h-8 text-xs">
																																		<SelectValue
																																			placeholder={t(
																																				"loadReferenceExercise",
																																			)}
																																		/>
																																	</SelectTrigger>
																																	<SelectContent>
																																		<SelectItem value="__current__">
																																			{t(
																																				"currentExerciseLoadReference",
																																			)}
																																		</SelectItem>
																																		{strengthReferenceOptions.map(
																																			(
																																				option,
																																			) => (
																																				<SelectItem
																																					key={
																																						option.value
																																					}
																																					value={
																																						option.value
																																					}
																																				>
																																					{
																																						option.label
																																					}
																																				</SelectItem>
																																			),
																																		)}
																																	</SelectContent>
																																</Select>
																																<p className="mt-1 text-[11px] md:text-[10px] text-muted-foreground uppercase tracking-wider">
																																	{t(
																																		"loadReferenceSummary",
																																	)}
																																	:{" "}
																																	{row.load_reference_exercise_name ||
																																		t(
																																			"currentExerciseLoadReference",
																																		)}
																																</p>
																															</div>
																														)}
																													</div>
																												),
																											)}
																										</div>
																								{/* Nota del movimento: non se ne scrivono di nuove (la nota
																								    e' una, su tutto il superset). Quelle gia' nei programmi
																								    l'atleta le legge, quindi restano visibili con la X. */}
																								{String(se.notes || "").trim() && (
																									<div className="flex items-start gap-1">
																										<span className="text-xs text-muted-foreground italic">
																											{t("pbMovementNote")}: {String(se.notes).trim()}
																										</span>
																										<Button
																											variant="ghost"
																											size="icon"
																											className="h-6 w-6 flex-shrink-0 text-muted-foreground hover:text-blaze"
																											title={t("pbRemoveMovementNote")}
																											onClick={() => {
																												const updated = [
																													...newExercise.superset_exercises,
																												];
																												updated[seIndex] = {
																													...updated[seIndex],
																													notes: "",
																												};
																												setNewExercise({
																													...newExercise,
																													superset_exercises: updated,
																												});
																											}}
																										>
																											<X className="w-3 h-3" />
																											<span className="sr-only">
																												{t("pbRemoveMovementNote")}
																											</span>
																										</Button>
																									</div>
																								)}
																									</div>
																								))}

																								{/* Search to add exercise to superset */}
																								<div
																									className="relative"
																									ref={supersetSearchRef}
																								>
																									<div className="relative">
																										<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
																										<Input
																											value={
																												supersetSearchQuery
																											}
																											onChange={(e) => {
																												setSupersetSearchQuery(
																													e.target.value,
																												);
																												setShowSupersetDropdown(
																													true,
																												);
																											}}
																											onFocus={() =>
																												setShowSupersetDropdown(
																													true,
																												)
																											}
																											className="bg-secondary border-transparent rounded-sm pl-10"
																											placeholder={t("pbSearchExerciseToAdd")}
																										/>
																									</div>
																									{showSupersetDropdown &&
																										supersetSearchQuery && (
																											<div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-sm shadow-lg">
																												<ScrollArea className="h-60">
																													{filteredSupersetExercises.length >
																													0 ? (
																														<div className="p-1">
																															{filteredSupersetExercises
																																.slice(0, 50)
																																.map((ex) => (
																																	<div
																																		key={
																																			ex.exercise_id
																																		}
																																		className="flex items-center gap-1 px-1 rounded-sm"
																																	>
																																		<button
																																			type="button"
																																			onClick={() => {
																																				const newSe =
																																					{
																																						exercise_id:
																																							ex.exercise_id,
																																						source_exercise_id:
																																							ex.exercise_id,
																																						exercise_name:
																																							ex.name,
																																						library_scope:
																																							ex.library_scope ||
																																							null,
																																						set_rows:
																																							[
																																								{
																																									sets: 1,
																																									reps: "10",
																																									load_percentage:
																																										null,
																																									load_reference_exercise_id:
																																										null,
																																									load_reference_exercise_name:
																																										null,
																																									rpe: null,
																																									rir: null,
																																									notes:
																																										"",
																																								},
																																							],
																																						notes:
																																							"",
																																					};
																																				setNewExercise(
																																					{
																																						...newExercise,
																																						superset_exercises:
																																							[
																																								...(newExercise.superset_exercises ||
																																									[]),
																																								newSe,
																																							],
																																					},
																																				);
																																				setSupersetSearchQuery(
																																					"",
																																				);
																																				setShowSupersetDropdown(
																																					false,
																																				);
																																				setVideoPreviewState(
																																					null,
																																				);
																																			}}
																																			className="flex-1 text-left px-2 py-2 rounded-sm hover:bg-secondary flex items-center gap-2"
																																		>
																																			<Dumbbell className="w-4 h-4 text-volt flex-shrink-0" />
																																			<div className="flex-1 min-w-0">
																																				<p className="text-sm font-medium truncate">
																																					{
																																						ex.name
																																					}
																																				</p>
																																				<div className="flex items-center gap-1.5">
																																					<p className="text-xs text-muted-foreground">
																																						{
																																							ex.category
																																						}
																																					</p>
																																					<span
																																						className={`text-xs md:text-[11px] px-1.5 py-0.5 rounded-sm uppercase tracking-wider ${
																																							getExerciseScopeBadgeMeta(
																																								ex,
																																							)
																																								.className
																																						}`}
																																					>
																																						{
																																							getExerciseScopeBadgeMeta(
																																								ex,
																																							)
																																								.label
																																						}
																																					</span>
																																				</div>
																																			</div>
																																		</button>
																																		<Button
																																			type="button"
																																			variant="ghost"
																																			size="icon"
																																			className="h-9 w-9 md:h-7 md:w-7 text-muted-foreground hover:text-volt"
																																			disabled={
																																				!getYouTubeEmbedUrl(
																																					ex.video_url,
																																				)
																																			}
																																			onClick={(
																																				event,
																																			) => {
																																				event.stopPropagation();
																																				openExerciseVideoPreview(
																																					"superset-search",
																																					ex,
																																				);
																																			}}
																																		>
																																			<Play className="w-3.5 h-3.5" />
																																		</Button>
																																	</div>
																																))}
																														</div>
																													) : (
																														<div className="p-3 text-center text-xs text-muted-foreground">
																															{t("pbNoExercises")}
																															trovato
																														</div>
																													)}
																													{!supersetExactMatch &&
																														supersetSearchQuery.trim() && (
																															<div className="border-t border-border p-1">
																																<button
																																	onClick={
																																		handleOpenNewExerciseFormForSuperset
																																	}
																																	className="w-full text-left px-3 py-2 rounded-sm hover:bg-volt/10 flex items-center gap-2 text-volt"
																																>
																																	<PlusCircle className="w-4 h-4 flex-shrink-0" />
																																	<span className="text-sm font-medium">
																																		Aggiungi "
																																		{
																																			supersetSearchQuery
																																		}
																																		" alla
																																		libreria
																																		personale
																																	</span>
																																</button>
																															</div>
																														)}
																												</ScrollArea>
																											</div>
																										)}
																									{renderInlineVideoPreview(
																										"superset-search",
																									)}
																								</div>
																							</div>

																							{/* Superset notes */}
																							<div className="space-y-1">
																								<span className="text-[11px] md:text-[10px] text-muted-foreground uppercase tracking-wider">
																									{t("pbSupersetNotes")}
																								</span>
																								<Textarea
																									value={
																										newExercise.notes ?? ""
																									}
																									onChange={(e) =>
																										setNewExercise({
																											...newExercise,
																											notes: e.target.value,
																										})
																									}
																									className="bg-secondary border-transparent rounded-sm text-xs min-h-[48px] resize-none"
																									placeholder={t("pbSupersetNotesPlaceholder")}
																								/>
																							</div>
																						</div>
																					)}

																				{/* === EMOM FORM (strength only) === */}
																				{newExercise.exercise_type ===
																					"strength" &&
																					newExercise.methodology ===
																						"emom" && (
																						<div className="space-y-4">
																							{/* Titolo del blocco, come nel conditioning: lasciandolo
																							    vuoto resta quello generato dalla prescrizione. */}
																							<div className="space-y-1">
																								<span className="text-[11px] md:text-[10px] text-muted-foreground uppercase tracking-wider">
																									{t("exerciseName")}
																								</span>
																								<Input
																									value={newExercise.exercise_name ?? ""}
																									onChange={(e) =>
																										setNewExercise({
																											...newExercise,
																											exercise_name: e.target.value,
																										})
																									}
																									className="bg-secondary border-transparent rounded-sm text-xs"
																									placeholder={buildEmomAutoName()}
																								/>
																								<p className="text-[11px] md:text-[10px] text-muted-foreground">
																									{t("emomNameAutoHint")}
																								</p>
																							</div>
																							{/* Intervals + Interval duration config */}
																							<div className="grid grid-cols-2 gap-3">
																								<div className="space-y-1">
																									<span className="text-[11px] md:text-[10px] text-muted-foreground uppercase tracking-wider">
																										{t("roundsLabel")}
																									</span>
																									<Input
																										type="text"
																										inputMode="numeric"
																										value={
																											(newExercise.exercise_type ===
																											"strength"
																												? newExercise.emom_intervals
																												: newExercise.rounds) ??
																											""
																										}
																										onChange={(e) => {
																											const val =
																												e.target.value === ""
																													? null
																													: e.target.value;
																											if (
																												newExercise.exercise_type ===
																												"strength"
																											) {
																												setNewExercise({
																													...newExercise,
																													emom_intervals: val
																														? parseInt(val)
																														: null,
																												});
																											} else {
																												setNewExercise({
																													...newExercise,
																													rounds: val,
																												});
																											}
																										}}
																										className="bg-secondary border-transparent rounded-sm"
																										placeholder={
																											newExercise.exercise_type ===
																											"strength"
																												? "10"
																												: "10"
																										}
																									/>
																								</div>
																								<div className="space-y-1">
																									<span className="text-[11px] md:text-[10px] text-muted-foreground uppercase tracking-wider">
																										{newExercise.exercise_type ===
																										"strength"
																											? t("pbRoundDuration")
																											: t("pbRoundDuration")}
																									</span>
																									{newExercise.exercise_type ===
																									"strength" ? (
																										<Input
																											type="text"
																											value={
																												newExercise.emom_interval ??
																												""
																											}
																											onChange={(e) => {
																												setNewExercise({
																													...newExercise,
																													emom_interval:
																														e.target.value,
																												});
																											}}
																											className="bg-secondary border-transparent rounded-sm"
																											placeholder="1:30"
																										/>
																									) : (
																										<Input
																											type="text"
																											value={(() => {
																												const mins = Math.floor(
																													(newExercise.interval_seconds ||
																														0) / 60,
																												);
																												const secs =
																													(newExercise.interval_seconds ||
																														0) % 60;
																												return `${mins}:${secs
																													.toString()
																													.padStart(2, "0")}`;
																											})()}
																											onChange={(e) => {
																												const value =
																													e.target.value ||
																													"0:00";
																												const [minStr, secStr] =
																													value.split(":");
																												const mins =
																													parseInt(
																														minStr || "0",
																													) || 0;
																												const secs =
																													parseInt(
																														secStr || "0",
																													) || 0;
																												const totalSeconds =
																													mins * 60 + secs;
																												setNewExercise({
																													...newExercise,
																													interval_seconds:
																														totalSeconds,
																												});
																											}}
																											className="bg-secondary border-transparent rounded-sm"
																											placeholder="1:00"
																										/>
																									)}
																								</div>
																							</div>

																							{/* New EMOM round templates */}
																							<div className="space-y-2 rounded-sm border border-volt/30 bg-volt/5 p-3">
																								<div className="flex items-center justify-between">
																									<span className="text-[11px] md:text-[10px] uppercase tracking-wider text-volt font-heading">
																										{t("pbEmomRoundBuilder")}
																									</span>
																									<Button
																										variant="outline"
																										size="sm"
																										className="h-7 text-xs rounded-sm"
																										onClick={() =>
																											setNewExercise({
																												...newExercise,
																												emom_rounds: [
																													...(newExercise.emom_rounds ||
																														[]),
																													createEmomRoundTemplate(
																														{
																															input_mode:
																																"library",
																														},
																													),
																												],
																											})
																										}
																									>
																										<Plus className="w-3 h-3 mr-1" />
																										{t("pbAddRound")}
																									</Button>
																								</div>
																								{(
																									newExercise.emom_rounds || []
																								).map((round, ri) => (
																									<div
																										key={round.round_id || ri}
																										className="space-y-2 rounded-sm border border-border/60 p-2 bg-background/60"
																									>
																										<div className="flex items-center gap-2">
																											<Select
																												value={
																													round.assignment_type ||
																													"cycle"
																												}
																												onValueChange={(
																													value,
																												) => {
																													const updated = [
																														...(newExercise.emom_rounds ||
																															[]),
																													];
																													updated[ri] = {
																														...updated[ri],
																														assignment_type:
																															value,
																													};
																													setNewExercise({
																														...newExercise,
																														emom_rounds:
																															updated,
																													});
																												}}
																											>
																												<SelectTrigger className="h-7 w-[6.5rem] flex-shrink-0 bg-secondary border-transparent rounded-sm text-[11px] md:text-[10px] uppercase tracking-wider">
																													<SelectValue />
																												</SelectTrigger>
																												<SelectContent>
																													<SelectItem value="cycle">
																														{t("pbRoundLabel")}
																													</SelectItem>
																													<SelectItem value="odd">
																														{t("pbEmomOddRound")}
																													</SelectItem>
																													<SelectItem value="even">
																														{t("pbEmomEvenRound")}
																													</SelectItem>
																													<SelectItem value="specific">
																														{t("pbEmomSpecificRound")}
																													</SelectItem>
																													<SelectItem value="custom">
																														{t("pbCustomLabel")}
																													</SelectItem>
																												</SelectContent>
																											</Select>

																											<div className="flex flex-col">
																												<button
																													type="button"
																													disabled={ri === 0}
																													onClick={() => {
																														const updated = [
																															...(newExercise.emom_rounds ||
																																[]),
																														];
																														[
																															updated[ri - 1],
																															updated[ri],
																														] = [
																															updated[ri],
																															updated[ri - 1],
																														];
																														setNewExercise({
																															...newExercise,
																															emom_rounds:
																																updated,
																														});
																														if (
																															activeEmomRoundIndex ===
																															ri
																														) {
																															setActiveEmomRoundIndex(
																																ri - 1,
																															);
																														} else if (
																															activeEmomRoundIndex ===
																															ri - 1
																														) {
																															setActiveEmomRoundIndex(
																																ri,
																															);
																														}
																													}}
																													className="text-xs md:text-[11px] text-muted-foreground hover:text-foreground disabled:opacity-30 leading-none px-1"
																													aria-label={t("pbMoveRoundUp")}
																												>
																													▲
																												</button>
																												<button
																													type="button"
																													disabled={
																														ri ===
																														(
																															newExercise.emom_rounds ||
																															[]
																														).length -
																															1
																													}
																													onClick={() => {
																														const updated = [
																															...(newExercise.emom_rounds ||
																																[]),
																														];
																														[
																															updated[ri],
																															updated[ri + 1],
																														] = [
																															updated[ri + 1],
																															updated[ri],
																														];
																														setNewExercise({
																															...newExercise,
																															emom_rounds:
																																updated,
																														});
																														if (
																															activeEmomRoundIndex ===
																															ri
																														) {
																															setActiveEmomRoundIndex(
																																ri + 1,
																															);
																														} else if (
																															activeEmomRoundIndex ===
																															ri + 1
																														) {
																															setActiveEmomRoundIndex(
																																ri,
																															);
																														}
																													}}
																													className="text-xs md:text-[11px] text-muted-foreground hover:text-foreground disabled:opacity-30 leading-none px-1"
																													aria-label={t("pbMoveRoundDown")}
																												>
																													▼
																												</button>
																											</div>

																											<Input
																												value={
																													round.label || ""
																												}
																												onChange={(e) => {
																													const updated = [
																														...(newExercise.emom_rounds ||
																															[]),
																													];
																													updated[ri] = {
																														...updated[ri],
																														label:
																															e.target.value,
																													};
																													setNewExercise({
																														...newExercise,
																														emom_rounds:
																															updated,
																													});
																												}}
																												className="flex-1 min-w-0 h-7 text-[11px] md:text-[10px] bg-transparent border-transparent focus-visible:ring-0 focus-visible:bg-secondary/50 rounded-sm placeholder:text-muted-foreground/40"
																												placeholder={t("pbRoundTitlePlaceholder")}
																											/>

																											<div className="flex bg-secondary/50 rounded-sm p-0.5 border border-border/40 flex-shrink-0">
																												<button
																													onClick={() => {
																														const updated = [
																															...(newExercise.emom_rounds ||
																																[]),
																														];
																														updated[ri] = {
																															...updated[ri],
																															input_mode:
																																"library",
																														};
																														setNewExercise({
																															...newExercise,
																															emom_rounds:
																																updated,
																														});
																													}}
																													className={`px-2 py-1 text-xs md:text-[11px] uppercase tracking-tighter rounded-sm transition-all ${
																														round.input_mode ===
																														"library"
																															? "bg-volt text-white shadow-sm"
																															: "text-muted-foreground hover:text-foreground"
																													}`}
																												>
																													{t("pbLibraryLabel")}
																												</button>
																												<button
																													onClick={() => {
																														const updated = [
																															...(newExercise.emom_rounds ||
																																[]),
																														];
																														updated[ri] = {
																															...updated[ri],
																															input_mode:
																																"custom",
																														};
																														setNewExercise({
																															...newExercise,
																															emom_rounds:
																																updated,
																														});
																													}}
																													className={`px-2 py-1 text-xs md:text-[11px] uppercase tracking-tighter rounded-sm transition-all ${
																														round.input_mode ===
																														"custom"
																															? "bg-volt text-white shadow-sm"
																															: "text-muted-foreground hover:text-foreground"
																													}`}
																												>
																													{t("pbCustomLabel")}
																												</button>
																											</div>

																											<Button
																												variant="ghost"
																												size="icon"
																												className="h-8 w-8 md:h-6 md:w-6 ml-1"
																												onClick={() => {
																													const updated = [
																														...(newExercise.emom_rounds ||
																															[]),
																													];
																													updated.splice(ri, 1);
																													setNewExercise({
																														...newExercise,
																														emom_rounds:
																															updated,
																													});
																												}}
																											>
																												<X className="w-3 h-3 text-destructive" />
																											</Button>
																										</div>

																										{round.input_mode ===
																										"library" ? (
																											<div className="space-y-2 pt-1 border-t border-border/20">
																												{(
																													round.items || []
																												).map((item, ii) => (
																													<div
																														key={ii}
																														className="space-y-1.5"
																													>
																														<div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_28px] md:grid-cols-[minmax(160px,2.2fr)_minmax(72px,0.6fr)_minmax(116px,1fr)_24px] gap-1.5 items-center">
																															<div className="col-span-3 md:col-span-1 min-w-0">
																																<div className="flex min-w-0 items-center gap-1.5">
																																	<div className="flex min-w-0 flex-1 items-center gap-1.5 text-xs md:text-[11px] bg-background/40 py-1 px-2 rounded-sm border border-border/20 overflow-hidden">
																																		<span className="truncate min-w-0">
																																			{
																																				item.exercise_name
																																			}
																																		</span>
																																		{(() => {
																																			const scopeTag =
																																				resolveProgramExerciseScopeTag(
																																					{
																																						exercise_id:
																																							item.exercise_id,
																																						source_exercise_id:
																																							item.source_exercise_id,
																																						exercise_name:
																																							item.exercise_name,
																																						library_scope:
																																							item.library_scope,
																																					},
																																				);
																																			if (
																																				!scopeTag
																																			)
																																				return null;
																																			return (
																																				<span
																																					className={`text-xs md:text-[11px] px-1 py-0.5 rounded-sm uppercase tracking-wider ${
																																						scopeTag ===
																																						"global"
																																							? "bg-amber-500/20 text-amber-300"
																																							: "bg-emerald-500/20 text-emerald-300"
																																					}`}
																																				>
																																					{getExerciseLibraryScopeLabelByTag(
																																						scopeTag,
																																					)}
																																				</span>
																																			);
																																		})()}
																																	</div>
																																	<Button
																																		variant="ghost"
																																		size="icon"
																																		className="h-8 w-8 md:h-6 md:w-6 shrink-0 text-muted-foreground hover:text-volt"
																																		onClick={() =>
																																			setEditingEmomLinkTarget(
																																				(
																																					prev,
																																				) => {
																																					if (
																																						prev.ri ===
																																							ri &&
																																						prev.ii ===
																																							ii
																																					) {
																																						return {
																																							ri: null,
																																							ii: null,
																																						};
																																					}
																																					return {
																																						ri,
																																						ii,
																																					};
																																				},
																																			)
																																		}
																																	>
																																		<Pencil className="w-3.5 h-3.5" />
																																	</Button>
																																</div>
																																{editingEmomLinkTarget.ri ===
																																	ri &&
																																	editingEmomLinkTarget.ii ===
																																		ii && (
																																		<ExerciseLinkCombobox
																																			options={
																																				libraryExerciseOptions
																																			}
																																			valueId={
																																				item.exercise_id
																																			}
																																			currentLabel={
																																				item.exercise_name
																																			}
																																			t={t}
																																			placeholder={t("pbChangeExercise")}
																																			keepCurrentLabel={t("pbKeepCurrent")}
																																			triggerClassName="h-6 text-[11px] md:text-[10px] bg-secondary border-transparent rounded-sm w-full justify-between font-normal"
																																			onKeepCurrent={() =>
																																				setEditingEmomLinkTarget(
																																					{
																																						ri: null,
																																						ii: null,
																																					},
																																				)
																																			}
																																			onSelect={(
																																				selectedExercise,
																																			) => {
																																				const updated =
																																					[
																																						...(newExercise.emom_rounds ||
																																							[]),
																																					];
																																				const updatedItems =
																																					[
																																						...(updated[
																																							ri
																																						]
																																							.items ||
																																							[]),
																																					];
																																				updatedItems[
																																					ii
																																				] = {
																																					...updatedItems[
																																						ii
																																					],
																																					exercise_id:
																																						selectedExercise.value,
																																					source_exercise_id:
																																						selectedExercise.value,
																																					exercise_name:
																																						selectedExercise.label,
																																					library_scope:
																																						selectedExercise.library_scope ||
																																						null,
																																					load_reference_exercise_id:
																																						selectedExercise.load_reference_exercise_id ||
																																						null,
																																					load_reference_exercise_name:
																																						selectedExercise.load_reference_exercise_name ||
																																						null,
																																				};
																																				updated[
																																					ri
																																				] = {
																																					...updated[
																																						ri
																																					],
																																					items:
																																						updatedItems,
																																				};
																																				setNewExercise(
																																					{
																																						...newExercise,
																																						emom_rounds:
																																							updated,
																																					},
																																				);
																																				setEditingEmomLinkTarget(
																																					{
																																						ri: null,
																																						ii: null,
																																					},
																																				);
																																			}}
																																		/>
																																	)}
																															</div>
																															<Input
																																value={
																																	item.reps ||
																																	""
																																}
																																onChange={(
																																	e,
																																) => {
																																	const updated =
																																		[
																																			...(newExercise.emom_rounds ||
																																				[]),
																																		];
																																	const updatedItems =
																																		[
																																			...updated[
																																				ri
																																			].items,
																																		];
																																	updatedItems[
																																		ii
																																	] = {
																																		...updatedItems[
																																			ii
																																		],
																																		reps: e
																																			.target
																																			.value,
																																	};
																																	updated[ri] =
																																		{
																																			...updated[
																																				ri
																																			],
																																			items:
																																				updatedItems,
																																		};
																																	setNewExercise(
																																		{
																																			...newExercise,
																																			emom_rounds:
																																				updated,
																																		},
																																	);
																																}}
																																className="bg-secondary border-transparent rounded-sm h-8 text-xs w-full min-w-[64px] md:min-w-[72px]"
																																placeholder={getConditioningQuantityPlaceholder(
																																	exercises.find(
																																		(ex) =>
																																			ex.exercise_id ===
																																			item.exercise_id,
																																	)?.category,
																																)}
																																title={
																																	isEnduranceCategory(
																																		exercises.find(
																																			(ex) =>
																																				ex.exercise_id ===
																																				item.exercise_id,
																																		)?.category,
																																	)
																																		? t("pbCaloriesMaleFemale")
																																		: t("reps")
																																}
																															/>
																															{!isEnduranceCategory(
																																exercises.find(
																																	(ex) =>
																																		ex.exercise_id ===
																																		item.exercise_id,
																																)?.category,
																															) ? (
																																<Input
																																	value={
																																		item.load ??
																																		joinEmomItemLoads(
																																			item.load_male,
																																			item.load_female,
																																		)
																																	}
																																	onChange={(
																																		e,
																																	) => {
																																		const updated =
																																			[
																																				...(newExercise.emom_rounds ||
																																					[]),
																																			];
																																		const updatedItems =
																																			[
																																				...updated[
																																					ri
																																				].items,
																																			];
																																		const loadValue =
																																			e.target
																																				.value;
																																		const split =
																																			splitEmomItemLoad(
																																				loadValue,
																																			);
																																		updatedItems[
																																			ii
																																		] = {
																																			...updatedItems[
																																				ii
																																			],
																																			load: loadValue,
																																			load_male:
																																				split.load_male,
																																			load_female:
																																				split.load_female,
																																			...(!loadValue.trim()
																																				? {
																																						load_reference_exercise_id:
																																							null,
																																						load_reference_exercise_name:
																																							null,
																																					}
																																				: {}),
																																		};
																																		updated[
																																			ri
																																		] = {
																																			...updated[
																																				ri
																																			],
																																			items:
																																				updatedItems,
																																		};
																																		setNewExercise(
																																			{
																																				...newExercise,
																																				emom_rounds:
																																					updated,
																																			},
																																		);
																																	}}
																																	className="bg-secondary border-transparent rounded-sm h-8 text-xs w-full min-w-0 text-volt"
																																	placeholder={t("pbLoadMaleFemale")}
																																	aria-label={t("pbLoadMaleFemale")}
																																/>
																															) : (
																																<div aria-hidden="true" />
																															)}
																															<Button
																																variant="ghost"
																																size="icon"
																																className="h-7 w-7 md:h-5 md:w-5 text-muted-foreground hover:text-destructive"
																																onClick={() => {
																																	const updated =
																																		[
																																			...(newExercise.emom_rounds ||
																																				[]),
																																		];
																																	const updatedItems =
																																		[
																																			...updated[
																																				ri
																																			].items,
																																		];
																																	updatedItems.splice(
																																		ii,
																																		1,
																																	);
																																	updated[ri] =
																																		{
																																			...updated[
																																				ri
																																			],
																																			items:
																																				updatedItems,
																																		};
																																	setNewExercise(
																																		{
																																			...newExercise,
																																			emom_rounds:
																																				updated,
																																		},
																																	);
																																}}
																															>
																																<Trash2 className="w-3 h-3" />
																															</Button>
																														</div>
																												{/* La nota e' una sola, su tutto l'esercizio: qui non se ne
																												    scrivono di nuove. Quelle gia' nei programmi l'atleta le
																												    legge sotto il round, quindi restano visibili con la X
																												    per toglierle. */}
																												{String(item.notes || "").trim() && (
																													<div className="pl-6 flex items-start gap-1">
																														<span className="text-[11px] md:text-[10px] text-muted-foreground italic">
																															{t("pbMovementNote")}: {String(item.notes).trim()}
																														</span>
																														<Button
																															variant="ghost"
																															size="icon"
																															className="h-6 w-6 flex-shrink-0 text-muted-foreground hover:text-blaze"
																															title={t("pbRemoveMovementNote")}
																															onClick={() => {
																																const updated = [
																																	...(newExercise.emom_rounds || []),
																																];
																																const updatedItems = [...updated[ri].items];
																																updatedItems[ii] = {
																																	...updatedItems[ii],
																																	notes: "",
																																};
																																updated[ri] = {
																																	...updated[ri],
																																	items: updatedItems,
																																};
																																setNewExercise({
																																	...newExercise,
																																	emom_rounds: updated,
																																});
																															}}
																														>
																															<X className="w-3 h-3" />
																															<span className="sr-only">
																																{t("pbRemoveMovementNote")}
																															</span>
																														</Button>
																													</div>
																												)}
																														{item.load &&
																															extractPercentageNumbers(
																																item.load,
																															).length > 0 && (
																																<div className="pl-6">
																																	<Select
																																		value={
																																			item.load_reference_exercise_id ||
																																			"__current__"
																																		}
																																		onValueChange={(
																																			selectedValue,
																																		) => {
																																			const updated =
																																				[
																																					...(newExercise.emom_rounds ||
																																						[]),
																																				];
																																			const updatedItems =
																																				[
																																					...(updated[
																																						ri
																																					]
																																						.items ||
																																						[]),
																																				];
																																			const selectedExercise =
																																				strengthReferenceOptions.find(
																																					(
																																						option,
																																					) =>
																																						option.value ===
																																						selectedValue,
																																				);
																																			updatedItems[
																																				ii
																																			] = {
																																				...updatedItems[
																																					ii
																																				],
																																				load_reference_exercise_id:
																																					selectedValue ===
																																					"__current__"
																																						? null
																																						: selectedExercise?.value ||
																																							null,
																																				load_reference_exercise_name:
																																					selectedValue ===
																																					"__current__"
																																						? null
																																						: selectedExercise?.label ||
																																							null,
																																			};
																																			updated[
																																				ri
																																			] = {
																																				...updated[
																																					ri
																																				],
																																				items:
																																					updatedItems,
																																			};
																																			setNewExercise(
																																				{
																																					...newExercise,
																																					emom_rounds:
																																						updated,
																																				},
																																			);
																																		}}
																																	>
																																		<SelectTrigger className="bg-secondary border-transparent rounded-sm h-8 text-xs">
																																			<SelectValue
																																				placeholder={t(
																																					"loadReferenceExercise",
																																				)}
																																			/>
																																		</SelectTrigger>
																																		<SelectContent>
																																			<SelectItem value="__current__">
																																				{t(
																																					"currentExerciseLoadReference",
																																				)}
																																			</SelectItem>
																																			{strengthReferenceOptions.map(
																																				(
																																					option,
																																				) => (
																																					<SelectItem
																																						key={
																																							option.value
																																						}
																																						value={
																																							option.value
																																						}
																																					>
																																						{
																																							option.label
																																						}
																																					</SelectItem>
																																				),
																																			)}
																																		</SelectContent>
																																	</Select>
																																	<p className="mt-1 text-[11px] md:text-[10px] text-muted-foreground uppercase tracking-wider">
																																		{t(
																																			"loadReferenceSummary",
																																		)}
																																		:{" "}
																																		{item.load_reference_exercise_name ||
																																			t(
																																				"currentExerciseLoadReference",
																																			)}
																																	</p>
																																</div>
																															)}
																													</div>
																												))}

																												<div className="relative">
																													<div className="flex items-center gap-1.5">
																														<Search className="w-3.5 h-3.5 text-muted-foreground absolute left-2" />
																														<Input
																															value={
																																activeEmomRoundIndex ===
																																ri
																																	? emomSearchQuery
																																	: ""
																															}
																															onChange={(e) => {
																																setEmomSearchQuery(
																																	e.target
																																		.value,
																																);
																																setActiveEmomRoundIndex(
																																	ri,
																																);
																															}}
																															onFocus={() =>
																																setActiveEmomRoundIndex(
																																	ri,
																																)
																															}
																															placeholder={t("searchExercisePlaceholder")}
																															className="h-7 text-[11px] md:text-[10px] pl-7 bg-secondary/80 border-transparent rounded-sm"
																														/>
																													</div>

																													{activeEmomRoundIndex ===
																														ri &&
																														emomSearchQuery.length >
																															0 && (
																															<div className="absolute top-full left-0 right-0 z-50 mt-1 bg-popover border border-border rounded-sm shadow-xl">
																																<ScrollArea className="h-[200px]">
																																	{rankAndFilterExercises(
																																		scopeFilteredExercises,
																																		emomSearchQuery,
																																	)
																																		.slice(
																																			0,
																																			30,
																																		)
																																		.map(
																																			(ex) => (
																																				<div
																																					key={
																																						ex.exercise_id
																																					}
																																					className="flex items-center gap-1 px-1 border-b border-border/40 last:border-0"
																																				>
																																					<button
																																						type="button"
																																						onClick={() => {
																																							const updated =
																																								[
																																									...(newExercise.emom_rounds ||
																																										[]),
																																								];
																																							const updatedItems =
																																								[
																																									...(updated[
																																										ri
																																									]
																																										.items ||
																																										[]),
																																									{
																																										exercise_id:
																																											ex.exercise_id,
																																										source_exercise_id:
																																											ex.exercise_id,
																																										exercise_name:
																																											ex.name,
																																										library_scope:
																																											ex.library_scope ||
																																											null,
																																										reps: "",
																																										load: "",
																																										load_reference_exercise_id:
																																											null,
																																										load_reference_exercise_name:
																																											null,
																																									},
																																								];
																																							updated[
																																								ri
																																							] =
																																								{
																																									...updated[
																																										ri
																																									],
																																									input_mode:
																																										"library",
																																									items:
																																										updatedItems,
																																								};
																																							setNewExercise(
																																								{
																																									...newExercise,
																																									emom_rounds:
																																										updated,
																																								},
																																							);
																																							setEmomSearchQuery(
																																								"",
																																							);
																																							setActiveEmomRoundIndex(
																																								null,
																																							);
																																							setVideoPreviewState(
																																								null,
																																							);
																																						}}
																																						className="flex-1 text-left px-2 py-1.5 text-xs md:text-[11px] hover:bg-volt/10 transition-colors rounded-sm"
																																					>
																																						{
																																							ex.name
																																						}
																																					</button>
																																					<Button
																																						type="button"
																																						variant="ghost"
																																						size="icon"
																																						className="h-8 w-8 md:h-6 md:w-6 text-muted-foreground hover:text-volt"
																																						disabled={
																																							!getYouTubeEmbedUrl(
																																								ex.video_url,
																																							)
																																						}
																																						onClick={(
																																							event,
																																						) => {
																																							event.stopPropagation();
																																							openExerciseVideoPreview(
																																								`emom-round-${ri}`,
																																								ex,
																																							);
																																						}}
																																					>
																																						<Play className="w-3 h-3" />
																																					</Button>
																																				</div>
																																			),
																																		)}
																																	<button
																																		onClick={() =>
																																			handleOpenNewExerciseFormForEmom(
																																				ri,
																																			)
																																		}
																																		className="w-full text-left px-2 py-2 text-xs md:text-[11px] text-volt hover:bg-volt/10 transition-colors flex items-center gap-1.5 border-t border-border/40"
																																	>
																																		<Plus className="w-3 h-3" />
																																		{`${t("add")} "${emomSearchQuery}" ${t("pbAddToPersonalLibrary")}`}
																																	</button>
																																</ScrollArea>
																															</div>
																														)}
																													{renderInlineVideoPreview(
																														`emom-round-${ri}`,
																													)}
																												</div>
																											</div>
																										) : (
																											<Textarea
																												value={
																													round.content || ""
																												}
																												onChange={(e) => {
																													const updated = [
																														...(newExercise.emom_rounds ||
																															[]),
																													];
																													updated[ri] = {
																														...updated[ri],
																														content:
																															e.target.value,
																													};
																													setNewExercise({
																														...newExercise,
																														emom_rounds:
																															updated,
																													});
																												}}
																												className="bg-secondary border-transparent rounded-sm text-xs min-h-[56px] resize-y md:resize overflow-auto"
																												placeholder={t("pbRoundContentPlaceholder")}
																											/>
																										)}
																									</div>
																								))}
																							</div>

																							{/* EMOM notes */}
																							<div className="space-y-1">
																								<span className="text-[11px] md:text-[10px] text-muted-foreground uppercase tracking-wider">
																									{t("pbEmomNotes")}
																								</span>
																								<Textarea
																									value={
																										newExercise.notes ?? ""
																									}
																									onChange={(e) =>
																										setNewExercise({
																											...newExercise,
																											notes: e.target.value,
																										})
																									}
																									className="bg-secondary border-transparent rounded-sm text-xs min-h-[48px] resize-none"
																									placeholder={t("pbEmomNotesPlaceholder")}
																								/>
																							</div>

																							<ConditioningResultSchemaEditor
																								value={
																									newExercise.result_schema ||
																									[]
																								}
																								onChange={(next) =>
																									setNewExercise((prev) => ({
																										...prev,
																										result_schema: next,
																									}))
																								}
																								defaultUnit="reps"
																								disabled={
																									editingConditioningHasResults
																								}
																								disabledHint={t("pbSchemaLockedHint")}
																							/>
																						</div>
																					)}

																				{/* === DRY FIRE / LIVE FIRE === */}
																				{(newExercise.exercise_type ===
																					"dry_fire" ||
																					newExercise.exercise_type ===
																						"live_fire") && (
																					<ShootingForm
																						mode={newExercise.exercise_type}
																						newExercise={newExercise}
																						setNewExercise={setNewExercise}
																						suggestedFirearmType={suggestedFirearmType(
																							day.exercises,
																						)}
																						t={t}
																					/>
																				)}

																				{/* === RUNNING FORM === */}
																				{newExercise.exercise_type ===
																					"running" && (
																					<RunningForm
																						newExercise={newExercise}
																						setNewExercise={setNewExercise}
																						benchmarkOptions={benchmarkOptions}
																						aiEnduranceParsing={
																							aiEnduranceParsing
																						}
																						handleParseEnduranceCustom={
																							handleParseEnduranceCustom
																						}
																						t={t}
																					/>
																				)}

																				{/* === SWIM FORM === */}
																				{newExercise.exercise_type ===
																					"swim" && (
																					<SwimForm
																						newExercise={newExercise}
																						setNewExercise={setNewExercise}
																						benchmarkOptions={benchmarkOptions}
																						t={t}
																					/>
																				)}

																				{/* === BIKE / ERG FORM === */}
																				{["bike", "erg"].includes(
																					newExercise.exercise_type,
																				) && (
																					<BikeErgForm
																						newExercise={newExercise}
																						setNewExercise={setNewExercise}
																						benchmarkOptions={benchmarkOptions}
																						exercises={exercises}
																						setExercises={setExercises}
																						aiEnduranceParsing={
																							aiEnduranceParsing
																						}
																						handleParseEnduranceCustom={
																							handleParseEnduranceCustom
																						}
																						t={t}
																					/>
																				)}

																				{/* === CONDITIONING FORM === */}
																				{newExercise.exercise_type ===
																					"conditioning" && (
																					<ConditioningForm
																						newExercise={newExercise}
																						setNewExercise={setNewExercise}
																						resultSchemaFocusToken={
																							conditioningSchemaFocusToken
																						}
																						conditioningTemplates={
																							conditioningTemplates
																						}
																						exercises={exercises}
																						setExercises={setExercises}
																						onQuickSaveTemplate={
																							handleQuickSaveConditioningTemplate
																						}
																						isSavingTemplate={
																							isSavingConditioningTemplate
																						}
																						t={t}
																					/>
																				)}

																				{/* Pinned to the dialog's scroll container below md: the form is
																				    long enough that the confirm button was several screens away. */}
																				<div className="sticky bottom-0 z-10 -mx-6 -mb-6 px-6 py-3 bg-card border-t border-border md:static md:z-auto md:mx-0 md:mb-0 md:px-0 md:py-0 md:bg-transparent md:border-0">
																					<Button
																						onClick={handleAddExercise}
																						disabled={
																							newExercise.exercise_type ===
																							"strength"
																								? newExercise.methodology ===
																									"superset"
																									? (
																											newExercise.superset_exercises ||
																											[]
																										).length < 2
																									: newExercise.methodology ===
																										  "complex"
																										? (
																												newExercise.complex_exercises ||
																												[]
																											).length < 2
																										: newExercise.methodology ===
																											  "emom"
																											? !newExercise.emom_intervals ||
																												!newExercise.emom_interval ||
																												!hasConfiguredEmomRounds
																											: newExercise.methodology ===
																												  "custom"
																												? !newExercise.exercise_name ||
																													!newExercise.custom_workout_text
																												: !newExercise.exercise_id
																								: newExercise.exercise_type ===
																									  "conditioning"
																									? !newExercise.conditioning_type
																									: newExercise.exercise_type ===
																												"erg" &&
																										  isErgMixedType(
																												newExercise.bike_type,
																										  )
																										? !newExercise.conditioning_type
																										: isShootingExerciseIncomplete(newExercise)
																						}
																						className="w-full bg-volt hover:bg-volt/90 rounded-sm"
																					>
																						{editingExerciseIndex !== null
																							? t("saveChanges")
																							: t("add")}
																					</Button>
																				</div>
																			</div>
																		)}
																	</DialogContent>
																</Dialog>
															</div>

															{/* Day Actions */}
															{(hasCopyableExercises ||
																clipboardItemCount > 0) && (
																<div className="flex items-center gap-2 pt-2 border-t border-border flex-wrap">
																	{hasCopyableExercises && (
																		<Button
																			variant="outline"
																			size="sm"
																			className="rounded-sm text-muted-foreground hover:text-volt"
																			onClick={() => handleCopyDay(dayIndex)}
																		>
																			<ClipboardCopy className="w-4 h-4 mr-1" />
																			{t("pbCopyDay")}
																		</Button>
																	)}
																	{clipboardItemCount > 0 && (
																		<Button
																			variant="outline"
																			size="sm"
																			className="rounded-sm border-volt/50 text-volt ml-auto"
																			onClick={() =>
																				handlePasteExercises(dayIndex)
																			}
																		>
																			<ClipboardPaste className="w-4 h-4 mr-1" />
																			{clipboardItemCount === 1
																				? t("pbPaste")
																				: `${t("pbPaste")} ${clipboardItemCount}`}
																		</Button>
																	)}
																</div>
															)}
														</CardContent>
													)}
												</Card>
											);
										})}
									</div>
								) : (
									<Card className="bg-card border-border rounded-sm">
										<CardContent className="flex flex-col items-center justify-center py-12 text-center">
											<CalendarIcon className="w-12 h-12 text-muted-foreground mb-4" />
											<p className="font-heading text-lg mb-2">{t("pbNoDay")}</p>
											<p className="text-sm text-muted-foreground">
												{t("addFirstDayWeek")}
											</p>
										</CardContent>
									</Card>
								)}
							</div>
						</div>
					</div>
				</div>

				{/* Dock launchers. On desktop one per tenant, so a coach lands
				    directly on the panel they want. On a phone a single one: two
				    stacked FABs cover ~104px of the right edge on top of the bottom
				    nav, and the dock's own tab strip is already the switch. */}
				{!isDockOpen && (
					<div className="fixed right-4 md:right-6 bottom-[calc(62px_+_var(--mobile-nav-bottom-offset)_+_12px)] md:bottom-6 z-[60] md:z-40 flex flex-col items-center gap-2 md:gap-3">
						<button
							onClick={() => openDock("reference")}
							className="hidden md:flex w-12 h-12 rounded-full shadow-lg items-center justify-center transition-transform hover:scale-105 bg-card border border-volt/40 text-volt hover:bg-volt/10"
							title={t("pbDockReference")}
							aria-label={t("pbDockReference")}
						>
							<Columns2 className="w-5 h-5" />
						</button>
						{!isCoachAiDisabled && (
							<button
								onClick={() => openDock("ai")}
								className={`hidden md:flex w-14 h-14 rounded-full shadow-lg items-center justify-center transition-transform hover:scale-105 ${isCoachAiDisabled ? "bg-blaze hover:bg-blaze/90 shadow-blaze/20" : "bg-volt hover:bg-volt/90 shadow-volt/20"}`}
								title={t("pbAiCoachAssistant")}
							>
								<Bot className="w-6 h-6 text-white" />
							</button>
						)}
						<button
							onClick={() => openDock(dockTab)}
							className={`md:hidden w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-transform active:scale-95 ${
								dockTab === "ai" && isCoachAiDisabled
									? "bg-blaze shadow-blaze/20"
									: "bg-volt shadow-volt/20"
							}`}
							aria-label={
								dockTab === "ai" ? t("pbDockAi") : t("pbDockReference")
							}
						>
							{dockTab === "ai" ? (
								<Bot className="w-6 h-6 text-white" />
							) : (
								<Columns2 className="w-6 h-6 text-white" />
							)}
						</button>
					</div>
				)}

				{/* Builder dock — AI assistant or read-only reference, never both */}
				<BuilderDock
					open={isDockOpen}
					tab={
						dockTab === "agenda" && !isAgendaDockAvailable
							? "reference"
							: dockTab
					}
					aiAvailable={!isCoachAiDisabled}
					agendaAvailable={isAgendaDockAvailable}
					onTabChange={handleDockTabChange}
					onClose={() => setIsDockOpen(false)}
				>
					{isAgendaDockShowing ? (
						<SeasonAgendaPanel
							scope={seasonScope}
							subjectOptions={seasonSubjectOptions}
							onSelectSubject={handleSelectSeasonSubject}
							memberAthleteIds={seasonGroupMemberIds}
							mesocycles={seasonAgendaData.mesocycles}
							competitions={seasonCalendarEvents}
							loading={seasonAgendaData.loading}
							onChanged={refreshSeasonAgenda}
							selectedWeekRange={selectedWeekCalendarRange}
							t={t}
						/>
					) : dockTab === "ai" && !isCoachAiDisabled ? (
						<AgentChatPanel
							// Su /programs/new il parametro vale la stringa "new": non e'
							// un id, e passarlo farebbe cercare al modello un programma
							// inesistente. Finche' la bozza non e' salvata non c'e'
							// contesto da dare.
							programId={programId === "new" ? null : programId}
							athleteIds={program?.athlete_ids || []}
							onProgramCreated={(createdId) => {
								// Se l'agente ha scritto sul programma aperto, la
								// copia in pagina e' stantia: va ricaricata, o il
								// prossimo salvataggio del coach sovrascriverebbe
								// cio' che l'assistente ha appena aggiunto.
								if (createdId && createdId === programId) loadData();
							}}
						/>
					) : (
						<ReferencePanel
							program={program}
							programId={programId}
							selectedWeekIndex={selectedWeekIndex}
							isDesktopViewport={isSplitViewport}
							initialReference={incomingReference}
							onCopy={handleCopyFromReference}
							onNavigate={handleNavigateFromReference}
							onSwap={handleSwapWithReference}
							onRequestWeeks={loadWeeksContent}
						/>
					)}
				</BuilderDock>
			</div>

			{/* Program setup, moved into a left Sheet while the column is collapsed.
			    Mutually exclusive with the in-column mount, so ids stay unique. */}
			<Sheet
				open={!isSettingsColumnVisible && isSettingsSheetOpen}
				onOpenChange={setIsSettingsSheetOpen}
			>
				<SheetContent
					side="left"
					className="bg-background border-border w-[calc(100vw-2rem)] sm:max-w-[440px] overflow-y-auto"
				>
					<SheetDescription className="hidden" />
					<SheetHeader className="mb-4">
						<SheetTitle className="font-heading uppercase tracking-tight text-left">
							{t("pbProgramSettings")}
						</SheetTitle>
					</SheetHeader>
					{renderProgramSettings()}
					{isDockOpen && isSplitViewport ? (
						// Restoring the column here would be a no-op while the dock holds
						// the width, so we explain instead of offering a dead button.
						<p className="mt-4 text-xs md:text-[11px] text-muted-foreground">
							{t("pbSettingsInSheetHint")}
						</p>
					) : (
						<Button
							type="button"
							variant="outline"
							onClick={() => {
								setIsSettingsSheetOpen(false);
								toggleSettingsCollapsed();
							}}
							className="w-full mt-4 rounded-sm border-dashed border-volt/50 text-volt hover:bg-volt/10"
						>
							<PanelLeftClose className="w-4 h-4 mr-2 rotate-180" />
							{t("pbShowSettingsColumn")}
						</Button>
					)}
				</SheetContent>
			</Sheet>

			<Dialog
				open={isAthleteViewPreviewOpen}
				onOpenChange={setIsAthleteViewPreviewOpen}
			>
				<DialogContent className="bg-card border-border w-[calc(100vw-2rem)] max-w-5xl max-h-[90vh] overflow-hidden">
					<DialogDescription className="hidden" />
					<DialogHeader>
						<DialogTitle className="font-heading">
							{t("pbAthletePreview")}
						</DialogTitle>
					</DialogHeader>

					<div className="grid grid-cols-1 lg:grid-cols-[260px_minmax(0,1fr)] gap-4 h-full">
						<div className="space-y-3 min-w-0 lg:max-w-[260px]">
							<div className="rounded-sm border border-border bg-secondary/30 p-3 space-y-2">
								<p className="text-xs uppercase tracking-wider text-muted-foreground">
									{t("pbPreviewContext")}
								</p>
								<div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
									<div className="rounded-sm bg-background border border-border px-2 py-1.5">
										<span className="text-muted-foreground">{t("pbProgramLabel")}:</span>{" "}
										{program?.title || t("pbUntitled")}
									</div>
									<div className="rounded-sm bg-background border border-border px-2 py-1.5">
										<span className="text-muted-foreground">{t("type")}:</span>{" "}
										{program?.type === "group" ? t("group") : t("individual")}
									</div>
									<div className="rounded-sm bg-background border border-border px-2 py-1.5 sm:col-span-2">
										<span className="text-muted-foreground">{t("week")}:</span>{" "}
										{localizeWeekLabel(currentWeek?.label, t, selectedWeekIndex)}
									</div>
								</div>
							</div>
						</div>

						<div className="mx-auto w-[390px] max-w-full rounded-[28px] border border-border bg-black p-2 shadow-2xl shadow-black/60">
							<div className="rounded-[22px] border border-border bg-background h-[72vh] overflow-y-auto">
								<AthleteDashboard
									previewState={{
										program,
										exercises,
										weekIndex: selectedWeekIndex,
									}}
									containerClassName="max-w-none mx-0 px-3 pb-6"
								/>
							</div>
						</div>
					</div>
				</DialogContent>
			</Dialog>

			{/* Swap with unsaved changes: save first or stay. Never a silent loss. */}
			<Dialog
				open={Boolean(pendingSwap)}
				onOpenChange={(open) => {
					if (!open) setPendingSwap(null);
				}}
			>
				<DialogContent className="bg-card border-border rounded-sm max-w-md">
					<DialogDescription className="hidden" />
					<DialogHeader>
						<DialogTitle className="font-heading">
							{t("pbSwapConfirmTitle")}
						</DialogTitle>
					</DialogHeader>
					<p className="text-sm text-muted-foreground">
						{t("pbSwapConfirmBody")}
					</p>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-1">
						<Button
							variant="outline"
							onClick={() => setPendingSwap(null)}
							className="w-full rounded-sm"
						>
							{t("cancel")}
						</Button>
						<Button
							disabled={saving}
							onClick={async () => {
								const target = pendingSwap;
								if (!target) return;
								const saved = await handleSaveProgram();
								if (!saved) return;
								performSwapWithReference(
									target.referenceProgramId,
									target.referenceWeekIndex,
								);
							}}
							className="w-full bg-volt hover:bg-volt/90 rounded-sm"
						>
							<Save className="w-4 h-4 mr-2" />
							{saving ? t("savingLabel") : t("pbSwapSaveAndSwap")}
						</Button>
					</div>
				</DialogContent>
			</Dialog>

			{/* Hiding a week athletes currently see is confirmed: the same button
			    also publishes, and a slip took a running week away from everyone at
			    the next save. Showing a week never asks. */}
			<Dialog
				open={Boolean(pendingWeekHide)}
				onOpenChange={(open) => {
					if (!open) setPendingWeekHide(null);
				}}
			>
				<DialogContent className="bg-card border-border rounded-sm max-w-md">
					<DialogDescription className="hidden" />
					<DialogHeader>
						<DialogTitle className="font-heading">
							{t("pbHideWeekTitle")} {(pendingWeekHide?.weekIndex ?? 0) + 1}{" "}
							{t("pbHideWeekTo")} {programAthleteCount}{" "}
							{(programAthleteCount === 1 ? t("athlete") : t("athletes")).toLowerCase()}?
						</DialogTitle>
					</DialogHeader>
					<p className="text-sm text-muted-foreground">{t("pbHideWeekBody")}</p>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-1">
						<Button
							variant="outline"
							onClick={() => setPendingWeekHide(null)}
							className="w-full rounded-sm min-h-[44px]"
						>
							{t("cancel")}
						</Button>
						<Button
							variant="outline"
							onClick={() => {
								const { weekIndex } = pendingWeekHide;
								setPendingWeekHide(null);
								toggleWeekPublished(weekIndex);
							}}
							className="w-full rounded-sm min-h-[44px] border-amber-500/50 text-amber-300 hover:bg-amber-500/10"
							data-testid="hide-week-confirm"
						>
							<EyeOff className="w-4 h-4 mr-2" />
							{t("hide")}
						</Button>
					</div>
				</DialogContent>
			</Dialog>
			{/* Switching the automatic release on: what athletes stop seeing, and
			    when each of those weeks comes back by itself. */}
			<Dialog
				open={Boolean(pendingAutoPublish)}
				onOpenChange={(open) => {
					if (!open) setPendingAutoPublish(null);
				}}
			>
				<DialogContent className="bg-card border-border rounded-sm max-w-md">
					<DialogDescription className="hidden" />
					<DialogHeader>
						<DialogTitle className="font-heading">
							{t("pbAutoPublishDialogTitle")}
						</DialogTitle>
					</DialogHeader>
					<p className="text-sm text-muted-foreground">{t("pbAutoPublishDialogBody")}</p>
					<ul className="text-sm space-y-1 max-h-48 overflow-y-auto" data-testid="auto-publish-plan">
						{(pendingAutoPublish?.weeks || []).map((entry) => (
							<li
								key={entry.week.week_id || entry.index}
								className="flex items-center justify-between gap-2 rounded-sm bg-secondary/60 px-2 py-1.5"
							>
								<span className="truncate">
									{localizeWeekLabel(entry.week.label, t, entry.index)}
								</span>
								<span className="text-xs text-sky-400 shrink-0 flex items-center gap-1">
									<CalendarIcon className="w-3.5 h-3.5" aria-hidden="true" />
									{t("pbWeekStatusScheduled").toLowerCase()} {formatDayShort(entry.releaseDate)}
								</span>
							</li>
						))}
					</ul>
					<p className="text-xs text-muted-foreground">{t("pbAutoPublishDialogFooter")}</p>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-1">
						<Button
							variant="outline"
							onClick={() => setPendingAutoPublish(null)}
							className="w-full rounded-sm min-h-[44px]"
						>
							{t("cancel")}
						</Button>
						<Button
							onClick={() => {
								setPendingAutoPublish(null);
								setProgram((prev) => applyAutoPublish(prev, true));
							}}
							className="w-full rounded-sm min-h-[44px]"
							data-testid="auto-publish-confirm"
						>
							<CheckCircle2 className="w-4 h-4 mr-2" />
							{t("pbAutoPublishActivate")}
						</Button>
					</div>
				</DialogContent>
			</Dialog>
			<Dialog
				open={Boolean(pendingWeekDeletion)}
				onOpenChange={(open) => {
					if (!open) setPendingWeekDeletion(null);
				}}
			>
				<DialogContent className="bg-card border-border rounded-sm max-w-md">
					<DialogDescription className="hidden" />
					<DialogHeader>
						<DialogTitle className="font-heading">
							{t("pbDeleteWeekTitle")}{" "}
							{(pendingWeekDeletion?.weekIndex ?? 0) + 1}
						</DialogTitle>
					</DialogHeader>
					<p className="text-sm text-muted-foreground">{t("pbDeleteWeekBody")}</p>
					{pendingDeletionUnloadedWeekIds.length > 0 && (
						<p className="text-xs text-muted-foreground">
							{t("pbDeleteWeekLoading")}
						</p>
					)}
					<div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-1">
						<Button
							variant="outline"
							onClick={() => {
								const { weekIndex } = pendingWeekDeletion;
								setPendingWeekDeletion(null);
								handleDeleteWeek(weekIndex);
							}}
							className="w-full rounded-sm h-auto min-h-[44px] px-3 py-2 text-xs sm:text-sm leading-tight whitespace-normal text-center"
						>
							{t("pbDeleteWeekKeepDates")}
						</Button>
						<Button
							disabled={pendingDeletionUnloadedWeekIds.length > 0}
							onClick={() => {
								const { weekIndex } = pendingWeekDeletion;
								setPendingWeekDeletion(null);
								handleDeleteWeek(weekIndex, { shiftFollowing: true });
							}}
							className="w-full bg-volt hover:bg-volt/90 rounded-sm h-auto min-h-[44px] px-3 py-2 text-xs sm:text-sm leading-tight whitespace-normal text-center"
						>
							{t("pbDeleteWeekShiftDates")}{" "}
							{weekGapAfterDelete(
								program?.weeks || [],
								pendingWeekDeletion?.weekIndex ?? 0,
							)}{" "}
							{t("daysLower")}
						</Button>
					</div>
				</DialogContent>
			</Dialog>

			<Dialog
				open={isConditioningSchemaConfirmOpen}
				onOpenChange={setIsConditioningSchemaConfirmOpen}
			>
				<DialogContent className="bg-card border-border rounded-sm max-w-md">
					<DialogDescription className="hidden" />
					<DialogHeader>
						<DialogTitle className="font-heading">
							{t("pbConfigureConditioningResult")}
						</DialogTitle>
					</DialogHeader>
					<p className="text-sm text-muted-foreground">
						{t("pbConfigureConditioningResultBody")}
					</p>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-1">
						<Button
							variant="outline"
							onClick={() => {
								setIsConditioningSchemaConfirmOpen(false);
								allowCustomConditioningWithoutSchemaRef.current = true;
								handleAddExercise();
							}}
							className="w-full rounded-sm h-auto min-h-[44px] px-3 py-2 text-xs sm:text-sm leading-tight whitespace-normal text-center"
						>
							{t("pbContinueWithoutSchema")}
						</Button>
						<Button
							onClick={() => {
								setIsConditioningSchemaConfirmOpen(false);
								setConditioningSchemaFocusToken((prev) => prev + 1);
								toast.info(t("pbConfigureSchemaNow"));
							}}
							className="w-full bg-volt hover:bg-volt/90 rounded-sm h-auto min-h-[44px] px-3 py-2 text-xs sm:text-sm leading-tight whitespace-normal text-center"
						>
							{t("pbConfigureSchemaNowCta")}
						</Button>
					</div>
				</DialogContent>
			</Dialog>

			<Dialog
				open={isExportDialogOpen}
				onOpenChange={(open) => {
					setIsExportDialogOpen(open);
					if (!open) {
						resetExportDialogState();
					}
				}}
			>
				<DialogContent className="bg-card border-border w-[calc(100vw-2rem)] max-w-4xl max-h-[88vh] overflow-hidden">
					<DialogDescription className="hidden" />
					<DialogHeader>
						<DialogTitle className="font-heading">
							{exportType === "week"
								? t("pbExportWeekTitle")
								: exportType === "week_share"
									? t("pbShareWeekTitle")
									: exportType === "day"
										? t("pbExportDayTitle")
										: exportType === "exercise"
											? t("pbShareExerciseTitle")
											: exportType === "exercise_copy"
												? t("pbExportExerciseTitle")
												: t("pbExportToOtherProgram")}
						</DialogTitle>
					</DialogHeader>

					<div className="space-y-3">
						<div className="relative">
							<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
							<Input
								value={exportSearchQuery}
								onChange={(e) => setExportSearchQuery(e.target.value)}
								className="bg-secondary border-transparent rounded-sm pl-10"
								placeholder={t("programSearchPlaceholder")}
							/>
						</div>

						<div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
							<button
								type="button"
								onClick={() => setExportCurrentFolderId(null)}
								className="hover:text-volt"
							>
								{t("root")}
							</button>
							{exportBreadcrumb.map((folder) => (
								<div
									key={folder.folder_id}
									className="inline-flex items-center gap-2"
								>
									<ChevronRight className="w-3 h-3" />
									<button
										type="button"
										onClick={() => setExportCurrentFolderId(folder.folder_id)}
										className="hover:text-volt"
									>
										{folder.name}
									</button>
								</div>
							))}
						</div>

						<div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-3">
							<div className="border border-border rounded-sm p-2">
								<ScrollArea className="h-[48vh]">
									<div className="space-y-1.5 pr-2">
										{loadingExportTargets ? (
											<div className="text-sm text-muted-foreground py-8 text-center">
												{t("pbLoadingPrograms")}
											</div>
										) : (
											<>
												{exportCurrentFolderId &&
													exportFolderMap[exportCurrentFolderId] && (
														<button
															type="button"
															onClick={() =>
																setExportCurrentFolderId(
																	exportFolderMap[exportCurrentFolderId]
																		.parent_id || null,
																)
															}
															className="w-full px-2 py-2 rounded-sm border border-border text-left text-xs text-muted-foreground hover:text-volt inline-flex items-center gap-2"
														>
															<ArrowLeft className="w-3.5 h-3.5" />
															{t("pbParentFolder")}
														</button>
													)}

												{exportCurrentFolders.map((folder) => (
													<button
														key={folder.folder_id}
														type="button"
														onClick={() =>
															setExportCurrentFolderId(folder.folder_id)
														}
														className="w-full px-3 py-2 rounded-sm border border-border text-left hover:border-volt/40 hover:bg-secondary/40"
													>
														<div className="inline-flex items-center gap-2">
															<FolderOpen className="w-4 h-4 text-volt" />
															<span className="text-sm">{folder.name}</span>
														</div>
													</button>
												))}

												{exportVisiblePrograms.map((programOption) => (
													<button
														key={programOption.program_id}
														type="button"
														onClick={() =>
															handleSelectExportProgram(
																programOption.program_id,
															)
														}
														className={`w-full px-3 py-2 rounded-sm border text-left transition-colors ${
															exportSelectedProgramId ===
															programOption.program_id
																? "border-volt bg-volt/10"
																: "border-border hover:border-volt/40 hover:bg-secondary/30"
														}`}
													>
														<div className="flex items-center gap-2">
															<FolderInput className="w-4 h-4 text-muted-foreground" />
															<div className="min-w-0">
																<p className="text-sm font-medium truncate">
																	{programOption.title || t("pbUntitledProgram")}
																</p>
																<p className="text-xs md:text-[11px] text-muted-foreground truncate">
																	{programOption.coach_name ||
																		programOption.shared_by?.name ||
																		"-"}
																</p>
															</div>
														</div>
													</button>
												))}

												{!loadingExportTargets &&
													exportCurrentFolders.length === 0 &&
													exportVisiblePrograms.length === 0 && (
														<div className="text-sm text-muted-foreground py-8 text-center">
															{t("pbNoProgramInFolder")}
														</div>
													)}
											</>
										)}
									</div>
								</ScrollArea>
							</div>

							<div className="border border-border rounded-sm p-3 space-y-3">
								<div>
									<p className="text-[11px] md:text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
										{t("pbTargetProgram")}
									</p>
									<p className="text-sm">
										{selectedExportProgram?.title || t("pbSelectAProgram")}
									</p>
								</div>

								{exportType !== "week" && (
									<div className="space-y-1.5">
										<Label className="label-tactical text-[11px] md:text-[10px]">
											{t("pbTargetWeek")}
										</Label>
										<Select
											value={exportSelectedWeekIndex}
											onValueChange={(value) => {
												setExportSelectedWeekIndex(value);
												setExportSelectedDayIndex("");
											}}
											disabled={!selectedExportProgram}
										>
											<SelectTrigger className="bg-secondary border-transparent rounded-sm h-9 text-sm">
												<SelectValue placeholder={t("pbPickWeek")} />
											</SelectTrigger>
											<SelectContent className="bg-card border-border">
												{(selectedExportProgram?.weeks || []).map(
													(week, idx) => (
														<SelectItem
															key={week.week_id || idx}
															value={String(idx)}
														>
															{localizeWeekLabel(week.label, t, idx)}
														</SelectItem>
													),
												)}
											</SelectContent>
										</Select>
									</div>
								)}

								{(exportType === "exercise" ||
									exportType === "exercise_copy") && (
									<div className="space-y-1.5">
										<Label className="label-tactical text-[11px] md:text-[10px]">
											{t("pbTargetDay")}
										</Label>
										<Select
											value={exportSelectedDayIndex}
											onValueChange={setExportSelectedDayIndex}
											disabled={!selectedExportWeek}
										>
											<SelectTrigger className="bg-secondary border-transparent rounded-sm h-9 text-sm">
												<SelectValue placeholder={t("pbPickDay")} />
											</SelectTrigger>
											<SelectContent className="bg-card border-border">
												{(selectedExportWeek?.days || []).map((day, idx) => (
													<SelectItem
														key={day.day_id || idx}
														value={String(idx)}
													>
														{localizeDayLabel(day.title, t, idx)}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>
								)}

								{exportType === "week_share" && (
									<div className="rounded-sm border border-volt/30 bg-volt/5 p-2 text-xs md:text-[11px] text-muted-foreground leading-snug">
										{t("pbShareWeekExplainFull")}
									</div>
								)}

								{(exportType === "exercise" || exportType === "week_share") &&
									isDirty && (
										<div className="rounded-sm border border-blaze/40 bg-blaze/10 p-2 text-xs md:text-[11px] text-blaze leading-snug">
											{t("pbUnsavedBeforeSharing")}
										</div>
									)}

								<div className="pt-2 border-t border-border space-y-2">
									<Button
										onClick={handleConfirmExport}
										disabled={
											savingExport ||
											!exportSelectedProgramId ||
											((exportType === "exercise" ||
												exportType === "week_share") &&
												isDirty) ||
											(exportType !== "week" &&
												exportSelectedWeekIndex === "") ||
											((exportType === "exercise" ||
												exportType === "exercise_copy") &&
												exportSelectedDayIndex === "")
										}
										className="w-full bg-volt hover:bg-volt/90 rounded-sm"
									>
										{savingExport
											? exportType === "exercise" || exportType === "week_share"
												? t("pbSharingInProgress")
												: t("pbExportInProgress")
											: exportType === "exercise" || exportType === "week_share"
												? t("pbConfirmShare")
												: t("pbConfirmExport")}
									</Button>
									<Button
										variant="outline"
										onClick={() => setIsExportDialogOpen(false)}
										className="w-full rounded-sm"
									>
										{t("cancel")}
									</Button>
								</div>
							</div>
						</div>
					</div>
				</DialogContent>
			</Dialog>

			{/* Reference Exercise Selection Dialog */}
			<Dialog
				open={isReferenceExerciseDialogOpen}
				onOpenChange={setIsReferenceExerciseDialogOpen}
			>
				<DialogContent className="bg-card border-border w-[calc(100vw-2rem)] max-w-md">
					<DialogDescription className="hidden" />
					<DialogHeader>
						<DialogTitle className="font-heading">
							{t("loadReferenceExercise")}
						</DialogTitle>
					</DialogHeader>
					<div className="space-y-3">
						<div className="relative">
							<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
							<Input
								value={referenceExerciseSearchQuery}
								onChange={(e) =>
									setReferenceExerciseSearchQuery(e.target.value)
								}
								className="bg-secondary border-transparent rounded-sm pl-10"
								placeholder={t("searchExercise")}
								autoFocus
							/>
						</div>
						<ScrollArea className="h-64 border border-border/50 rounded-sm p-1">
							<div className="space-y-1">
								{/* Current exercise option — not for a complex, which has
								    no max of its own: there the reference is a movement. */}
								{!isStrengthComplex(
									pendingReferenceUpdate?.target === "newExercise"
										? newExercise
										: currentWeekDays[pendingReferenceUpdate?.dayIndex]
												?.exercises?.[pendingReferenceUpdate?.exIndex],
								) && (
									<button
										onClick={() => applyPendingLoadReference(null, null)}
										className="w-full text-left px-3 py-2 rounded-sm hover:bg-volt/10 flex items-center gap-2 text-sm font-medium mb-2 pb-2 border-b border-border"
									>
										<span className="text-volt">○</span>
										{t("currentExerciseLoadReference")}
									</button>
								)}

								{/* Exercise options */}
								{rankAndFilterExercises(
									strengthReferenceOptions,
									referenceExerciseSearchQuery,
									"label",
								).map((option) => (
									<button
										key={option.value}
										onClick={() =>
											applyPendingLoadReference(option.value, option.label)
										}
										className="w-full text-left px-3 py-2 rounded-sm hover:bg-secondary flex items-center gap-2 text-sm transition-colors"
									>
										{option.value === LOAD_REFERENCE_BODYWEIGHT_ID ? (
											<span className="w-4 h-4 flex items-center justify-center text-[11px] md:text-[10px] font-bold text-acid flex-shrink-0">
												{t("bodyweightShort")}
											</span>
										) : (
											<Dumbbell className="w-4 h-4 text-volt flex-shrink-0" />
										)}
										<span className="truncate">{option.label}</span>
									</button>
								))}

								{referenceExerciseSearchQuery &&
									rankAndFilterExercises(
										strengthReferenceOptions,
										referenceExerciseSearchQuery,
										"label",
									).length === 0 && (
										<div className="text-center py-6 text-sm text-muted-foreground">
											{t("noExercisesFound")}
										</div>
									)}
							</div>
						</ScrollArea>
					</div>
				</DialogContent>
			</Dialog>
		</Layout>
	);
}
